/* eslint-disable */
/* ══════════════════════════════════════════════════════════════════
   YAPIDERMA CRM — Business Suite
   Built for Yapiderma Private Limited
   Modules: Purchase, Sales, Invoicing, Payments, Ledger,
            Party/Doctor/Product Masters, Rates, Schemes, Stock
   ══════════════════════════════════════════════════════════════════ */

import React, { useState, useEffect, useRef } from "react";
import { createClient } from "@supabase/supabase-js";

// ── SUPABASE CONFIG ────────────────────────────────────────────────────────────
// TODO: Replace with your Yapiderma Supabase project credentials
// 1. Create project at https://supabase.com
// 2. Run this SQL:
//    CREATE TABLE app_data (key TEXT PRIMARY KEY, value JSONB);
//    ALTER TABLE app_data ENABLE ROW LEVEL SECURITY;
//    CREATE POLICY "allow_all" ON app_data FOR ALL USING (true) WITH CHECK (true);
// 3. Also create: users, parties, products, doctors tables (see Rivexia schema)
// 4. Paste your URL and anon key below
const SUPABASE_URL = "https://xbdsbknenlpsqwovqbzu.supabase.co";
const SUPABASE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InhiZHNia25lbmxwc3F3b3ZxYnp1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzc2MjQwNTIsImV4cCI6MjA5MzIwMDA1Mn0.408hgpIA6zDGvRVMyV7z6rbN5fmQcdIh9dkSpmRt7Jo";

const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);
const SUPABASE_HEADERS = {
  apikey: SUPABASE_KEY,
  Authorization: `Bearer ${SUPABASE_KEY}`
};
const SUPABASE_SYNC_TABLES = new Set(["users", "parties", "products", "doctors"]);

// ── CRM DATA KEYS synced to app_data table ─────────────────────
// These are stored as JSON blobs in Supabase app_data table
// SQL to create: CREATE TABLE app_data (key TEXT PRIMARY KEY, value JSONB);
//                ALTER TABLE app_data ENABLE ROW LEVEL SECURITY;
//                CREATE POLICY "allow_all" ON app_data FOR ALL USING (true) WITH CHECK (true);
const CRM_SYNC_KEYS = [
  "invoices","purchases","purchaseOrders","purchaseReturns",
  "payments","vendorPayments","creditDebitNotes","salesReturns",
  "paymentAdjustments","challans","batches","batchMovements",
  "parties","products","doctors","manufacturers","vendors",
  "partyRates","doctorRates","schemeMaster","settings","users"
];

// Save all CRM data as a single UPSERT — avoids 38 separate requests
const saveBulkCRMData = async (dataObj) => {
  try {
    const value = {};
    CRM_SYNC_KEYS.forEach(k => { if(dataObj[k] !== undefined) value[k] = dataObj[k]; });
    await supabaseRequest(`/rest/v1/app_data?on_conflict=key`, {
      method: "POST",
      headers: { Prefer: "resolution=merge-duplicates,return=minimal" },
      body: JSON.stringify({ key: "yap_bulk", value })
    });
    return true;
  } catch(e) {
    console.error("saveBulkCRMData failed:", e?.message||e);
    return false;
  }
};

const loadAllCRMData = async () => {
  try {
    const rows = await supabaseRequest(`/rest/v1/app_data?key=eq.crm_bulk&select=value`);
    if(Array.isArray(rows) && rows.length && rows[0].value) return rows[0].value;
  } catch(e) { console.warn("loadAllCRMData failed:", e?.message||e); }
  return {};
};

// Shared master-data caches used by legacy modules declared outside App().
let users = [];
let parties = [];
let products = [];
let doctors = [];

// ── NORMALIZATION HELPERS ───────────────────────────────────────
const normalizeUser = (u = {}) => {
  // Destructure out any password field — it must never live in the profile store
  const { password: _pw, ...rest } = u;
  return {
    ...rest,
    name: u.name || u.username || u.full_name || u.email || "",
    email: u.email || "",
    role: (u.role || "mr").toLowerCase(),
    phone: u.phone || "",
    region: u.region || "",
    asmId: u.asmId || u.asm_id || "",
    active: u.active !== false && u.is_active !== false
  };
};

const normalizeParty = (p = {}) => ({
  ...p,
  address: p.address || p.address1 || "",
  address1: p.address1 || p.address || "",
  address2: p.address2 || "",
  partyType: p.party_type ?? p.partyType ?? "",
  creditDays: Number(p.credit_days ?? p.creditDays ?? 30),
  creditLimit: Number(p.credit_limit ?? p.creditLimit ?? 0),
  stateCode: p.state_code ?? p.stateCode ?? "",
  dl1: p.dl_no ?? p.dl1 ?? "",
  dl2: p.dl2 || "",
  panNo: p.pan_no ?? p.panNo ?? ""
});

const normalizeProduct = (p = {}) => ({
  ...p,
  gst: Number(p.gst ?? 18),
  mrp: Number(p.mrp ?? 0),
  ptr: Number(p.ptr ?? 0),
  pts: Number(p.pts ?? 0),
  costPrice: Number(p.cost_price ?? p.costPrice ?? 0),
  reorderLevel: Number(p.reorder_level ?? p.reorderLevel ?? 0),
  minStock: Number(p.min_stock ?? p.minStock ?? 0)
});

const normalizeDoctor = (d = {}) => ({
  ...d,
  name: d.name || "",
  spec: d.spec || d.specialization || "",
  specialization: d.specialization || d.spec || "",
  clinic: d.clinic || d.clinic_name || "",
  clinic_name: d.clinic_name || d.clinic || "",
  address1: d.address1 || d.address || "",
  city: d.city || "",
  state: d.state || "",
  region: d.region || "",
  phone: d.phone || "",
  email: d.email || "",
  category: d.category || "B",
  lastVisit: d.lastVisit || d.last_visit || "",
  billingChannel: d.billingChannel || d.billing_channel || "Direct",
  linkedPartyId: d.linkedPartyId || d.linked_party_id || ""
});

const normalizeTableRows = (table, rows = []) => {
  const list = Array.isArray(rows) ? rows : [];
  if (table === "users") return list.map(normalizeUser);
  if (table === "parties") return list.map(normalizeParty);
  if (table === "products") return list.map(normalizeProduct);
  if (table === "doctors") return list.map(normalizeDoctor);
  return list;
};

const mapUserToSupabase = (u = {}) => ({
  id: u.id,
  name: u.name || "",
  email: u.email || "",
  // NOTE: passwords are managed exclusively through Supabase Auth.
  // We never write a plaintext password field to the users table.
  role: (u.role || "mr").toLowerCase(),
  phone: u.phone || "",
  region: u.region || "",
  asm_id: u.asmId || u.asm_id || null,
  active: u.active !== false && u.is_active !== false,
  is_active: u.active !== false && u.is_active !== false
});

const mapPartyToSupabase = (p = {}) => ({
  id: p.id,
  name: p.name || "",
  address1: p.address1 || p.address || "",
  address2: p.address2 || "",
  city: p.city || "",
  state: p.state || "",
  state_code: p.stateCode || p.state_code || "",
  region: p.region || "",
  party_type: p.partyType || p.party_type || "",
  gstin: p.gstin || "",
  pan_no: p.panNo || p.pan_no || "",
  phone: p.phone || "",
  email: p.email || "",
  // Default 30 — matches normalizeParty so null DB values don't silently reset to 0
  credit_days: Number(p.creditDays ?? p.credit_days ?? 30),
  credit_limit: Number(p.creditLimit ?? p.credit_limit ?? 0),
  dl_no: p.dl1 || p.dl_no || "",
  dl2: p.dl2 || ""
  // dl2 added to DB via ALTER TABLE
});

const mapProductToSupabase = (p = {}) => ({
  id: p.id,
  name: p.name || "",
  code: p.code || "",
  hsn: p.hsn || "",
  gst: Number(p.gst ?? 18),
  mrp: Number(p.mrp ?? 0),
  ptr: Number(p.ptr ?? 0),
  pts: Number(p.pts ?? 0),
  cost_price: Number(p.costPrice ?? p.cost_price ?? 0),
  unit: p.unit || "",
  pack: p.pack || "",
  category: p.category || "",
  manufacturer: p.manufacturer || "",
  reorder_level: Number(p.reorderLevel ?? p.reorder_level ?? 0),
  min_stock: Number(p.minStock ?? p.min_stock ?? 0),
  notes: p.notes || ""
});

const mapDoctorToSupabase = (d = {}) => ({
  id: d.id,
  name: d.name || "",
  // DB column: specialization
  specialization: d.spec || d.specialization || "",
  // DB column: clinic_name
  clinic_name: d.clinic || d.clinic_name || "",
  address1: d.address1 || d.address || "",
  city: d.city || "",
  state: d.state || "",
  region: d.region || "",
  phone: d.phone || "",
  email: d.email || "",
  // Fields that were previously dropped — must round-trip cleanly
  category: d.category || "B",
  last_visit: d.lastVisit || d.last_visit || "",
  billing_channel: d.billingChannel || "Direct",
  linked_party_id: d.linkedPartyId || d.linked_party_id || ""
});

const mapTableRowsToSupabase = (table, rows = []) => {
  const list = Array.isArray(rows) ? rows : [];
  if (table === "users") return list.map(mapUserToSupabase);
  if (table === "parties") return list.map(mapPartyToSupabase);
  if (table === "products") return list.map(mapProductToSupabase);
  if (table === "doctors") return list.map(mapDoctorToSupabase);
  return list;
};

const supabaseRequest = async (path, options = {}) => {
  // Prefer authenticated JWT over anon key — required for RLS-protected tables
  let authBearer = SUPABASE_KEY; // fallback: anon key
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) authBearer = session.access_token;
  } catch {}
  const headers = {
    apikey: SUPABASE_KEY,
    Authorization: `Bearer ${authBearer}`,
    ...(options.body ? { "Content-Type": "application/json" } : {}),
    ...(options.headers || {})
  };
  const res = await fetch(`${SUPABASE_URL}${path}`, { ...options, headers });
  const text = await res.text();
  const payload = text ? (() => { try { return JSON.parse(text); } catch { return text; } })() : null;
  if (!res.ok) {
    const detail = typeof payload === "string" ? payload : payload?.message || payload?.error || res.statusText;
    throw new Error(`Supabase ${res.status} ${detail}`);
  }
  return payload;
};

const getSupabaseRows = async (table) =>
  normalizeTableRows(table, await supabaseRequest(`/rest/v1/${table}?select=*&order=id.asc`));

// Load all CRM master data from Supabase
const loadFromSupabase = async () => {
  const [usersData, partiesData, productsData, doctorsData] = await Promise.all([
    getSupabaseRows("users"),
    getSupabaseRows("parties"),
    getSupabaseRows("products"),
    getSupabaseRows("doctors")
  ]);
  return { users: usersData, parties: partiesData, products: productsData, doctors: doctorsData };
};

// ── NEW DATA LOADERS ───────────────────────────────────────────
const getUsers = async () => await getSupabaseRows("users");
const getParties = async () => await getSupabaseRows("parties");
const getProducts = async () => await getSupabaseRows("products");
const getDoctors = async () => await getSupabaseRows("doctors");

const insertSupabaseRows = async (table, rows) => {
  const payload = mapTableRowsToSupabase(table, rows);
  return await supabaseRequest(`/rest/v1/${table}`, {
    method: "POST",
    headers: { Prefer: "return=representation" },
    body: JSON.stringify(payload)
  });
};

// ── INSERT APIs ─────────────────────────────────────────────────
const addParty = async (party) => {
  try {
    return await insertSupabaseRows("parties", [party]);
  } catch (err) {
    console.error("Error adding party:", err);
    return null;
  }
};

const addProduct = async (product) => {
  try {
    return await insertSupabaseRows("products", [product]);
  } catch (err) {
    console.error("Error adding product:", err);
    return null;
  }
};

const addDoctor = async (doctor) => {
  try {
    return await insertSupabaseRows("doctors", [doctor]);
  } catch (err) {
    console.error("Error adding doctor:", err);
    return null;
  }
};

// Upsert supported master tables to Supabase
let _rlsBlocked = false;
// Module-scope interval: resets the RLS back-off flag every 30 s so transient
// auth errors don't permanently block sync. The handle is exported so test
// environments and HMR can clear it and avoid "can't perform state update on
// unmounted component" warnings.
const _rlsResetInterval = setInterval(() => { _rlsBlocked = false; }, 30000);
// Call this in your test teardown or if you ever unmount the whole app:
// export const teardownRlsInterval = () => clearInterval(_rlsResetInterval);
const upsertSupabase = async (table, rows) => {
  if (!SUPABASE_SYNC_TABLES.has(table)) return true;
  if (_rlsBlocked) return false;
  // SAFETY: never sync an empty snapshot — a partial load could otherwise
  // appear to have "deleted" all records and we would propagate that deletion.
  if (!Array.isArray(rows) || rows.length === 0) return true;
  const normalized = normalizeTableRows(table, rows);
  const payload = mapTableRowsToSupabase(table, normalized);
  if (!payload.length) return true;
  try {
    // UPSERT ONLY — we NEVER bulk-delete rows that are absent from the local
    // snapshot.  Deletions must be explicit single-row operations triggered by
    // a deliberate user action (deleteSupabaseRow), not inferred from a diff.
    await supabaseRequest(`/rest/v1/${table}?on_conflict=id`, {
      method: "POST",
      headers: { Prefer: "resolution=merge-duplicates,return=minimal" },
      body: JSON.stringify(payload)
    });
    return true;
  } catch (err) {
    const msg = String(err?.message || "");
    if (msg.includes("401") || msg.includes("403")) _rlsBlocked = true;
    console.error(`Supabase sync failed for ${table}:`, err);
    return false;
  }
};

// Explicit single-row hard-delete — call this instead of relying on upsertSupabase diffs.
const deleteSupabaseRow = async (table, id) => {
  if (!SUPABASE_SYNC_TABLES.has(table) || !id) return true;
  try {
    await supabaseRequest(`/rest/v1/${table}?id=eq.${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: { Prefer: "return=minimal" }
    });
    return true;
  } catch (err) {
    console.error(`deleteSupabaseRow failed for ${table}/${id}:`, err);
    return false;
  }
};


const YAPIDERMA_LOGO = "data:image/svg+xml;base64,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";

const Styles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400&family=DM+Sans:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;600&display=swap');
    *{font-family:'DM Sans',sans-serif;box-sizing:border-box;margin:0;padding:0;-webkit-tap-highlight-color:transparent}
    html{-webkit-text-size-adjust:100%}
    .mono{font-family:'JetBrains Mono',monospace;font-size:0.92em}
    .serif{font-family:'Cormorant Garamond',Georgia,serif}
    ::-webkit-scrollbar{width:4px;height:4px}
    ::-webkit-scrollbar-track{background:#1C3353}
    ::-webkit-scrollbar-thumb{background:#B8972A66;border-radius:4px}
    ::-webkit-scrollbar-thumb:hover{background:#B8972A}
    .fade{animation:fadeIn .18s ease}
    @keyframes fadeIn{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}
    @keyframes slideInRight{from{opacity:0;transform:translateX(20px)}to{opacity:1;transform:none}}
    @keyframes pulse{0%,100%{opacity:1}50%{opacity:.6}}
    input,select,textarea{outline:none;font-family:inherit;font-size:16px}
    input:focus,select:focus,textarea:focus{border-color:#B8972A!important;box-shadow:0 0 0 3px rgba(184,151,42,.18)!important}
    tr:hover td{background:#1C335318!important}
    button:hover{opacity:.9}
    button,select,input,textarea{touch-action:manipulation}
    /* ── PRO UI POLISH ── */
    select,input[type="text"],input[type="number"],input[type="date"],input[type="month"],textarea{
      border:1.5px solid #E4DDD1!important;border-radius:8px!important;
      transition:border-color .15s,box-shadow .15s!important;background:#fff!important;
    }
    select:hover,input:hover{border-color:#B8972A66!important}
    .rx-sidebar-item{border-radius:8px;transition:all .14s;margin:1px 6px}
    .rx-sidebar-item:hover{background:rgba(184,151,42,.10)!important}
    .rx-sidebar-item.active{background:rgba(184,151,42,.18)!important;font-weight:700!important}
    /* ── GLASSMORPHISM — works on all browsers ── */
    .glass{
      background:rgba(255,255,255,0.18)!important;
      backdrop-filter:blur(16px) saturate(180%)!important;
      -webkit-backdrop-filter:blur(16px) saturate(180%)!important;
      border:1px solid rgba(255,255,255,0.28)!important;
      box-shadow:0 8px 32px rgba(31,38,135,0.15)!important;
    }
    .glass-dark{
      background:rgba(30,42,75,0.55)!important;
      backdrop-filter:blur(20px) saturate(160%)!important;
      -webkit-backdrop-filter:blur(20px) saturate(160%)!important;
      border:1px solid rgba(255,255,255,0.12)!important;
      box-shadow:0 8px 32px rgba(0,0,0,0.25)!important;
    }
    .glass-card{
      background:rgba(255,255,255,0.85)!important;
      backdrop-filter:blur(12px)!important;
      -webkit-backdrop-filter:blur(12px)!important;
      border:1px solid rgba(255,255,255,0.6)!important;
      box-shadow:0 4px 24px rgba(0,0,0,0.08)!important;
      border-radius:16px!important;
    }
    /* Month KPI card hover lift */
    .month-kpi:hover{transform:translateY(-3px)!important;box-shadow:0 10px 30px rgba(0,0,0,0.2)!important}
    /* ── MOBILE RESPONSIVE ── */
    @media (max-width: 768px) {
      .mr-portal-header{padding:12px 16px!important}
      .mr-stats-grid{grid-template-columns:1fr 1fr!important;gap:8px!important}
      .mr-nav-tabs{gap:0!important;overflow-x:auto!important;-webkit-overflow-scrolling:touch!important}
      .mr-nav-tabs button{padding:10px 10px!important;font-size:11px!important;flex-shrink:0!important;min-width:60px!important}
      .mr-content{padding:12px!important}
      .mr-form-grid{grid-template-columns:1fr!important}
      .mr-form-grid-2{grid-template-columns:1fr 1fr!important}
      .asm-tab-bar{gap:0!important;overflow-x:auto!important;-webkit-overflow-scrolling:touch!important;scrollbar-width:none!important}
      .asm-tab-bar::-webkit-scrollbar{display:none!important}
      .asm-tab-bar button{padding:9px 10px!important;font-size:10px!important;flex-shrink:0!important;min-width:60px!important}
      .asm-grid-4{grid-template-columns:1fr 1fr!important;gap:8px!important}
      .asm-grid-3{grid-template-columns:1fr 1fr!important;gap:8px!important}
      .asm-grid-2{grid-template-columns:1fr!important;gap:8px!important}
      .asm-content{padding:12px!important}
      .hide-mobile{display:none!important}
      .full-mobile{grid-column:1/-1!important;width:100%!important}
      table{display:block!important;overflow-x:auto!important;-webkit-overflow-scrolling:touch!important;font-size:11px!important}
      table th,table td{padding:6px 8px!important;white-space:nowrap!important}
    }
    @media (max-width:480px){
      .mr-stats-grid{grid-template-columns:1fr 1fr!important}
      .asm-grid-4{grid-template-columns:1fr 1fr!important}
      .asm-grid-3{grid-template-columns:1fr!important}
      .mr-form-grid-2{grid-template-columns:1fr!important}
      .asm-grid-2{grid-template-columns:1fr!important}
    }
    @media print{
      *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}
      body{margin:0!important;padding:0!important;background:#fff!important}
      @page{size:A4 portrait;margin:0mm}
      #rx-app-shell{display:none!important}
      #rx-print-portal{display:block!important;position:static!important;overflow:visible!important;background:#fff!important;width:100%!important;height:auto!important;padding:0!important;margin:0!important}
      #rx-print-toolbar{display:none!important}
      #rx-print-portal > div:last-child{padding:0!important;background:#fff!important;display:block!important;margin:0!important}
      #rx-print-portal > div:last-child > div{
        width:100%!important;max-width:100%!important;
        padding:10mm 12mm!important;
        margin:0!important;
        box-shadow:none!important;border-radius:0!important;
        min-height:unset!important;box-sizing:border-box!important;
      }
      .invoice-print{
        width:100%!important;max-width:100%!important;font-size:8pt!important;
        padding:0!important;margin:0!important;box-sizing:border-box!important;display:block!important;
      }
      .invoice-print table{width:100%!important;border-collapse:collapse!important;table-layout:fixed!important}
      .invoice-print td,.invoice-print th{font-size:7.5pt!important;padding:2px 3px!important;overflow:hidden;word-wrap:break-word;line-height:1.3!important}
      .invoice-print tr{page-break-inside:avoid!important}
      .invoice-print img{max-height:40px!important}
    }
    .print-only{display:none}
    .invoice-print{display:block}
    .rx-nav-btn:hover{background:#B8972A33!important}

    /* ── GLASSMORPHIC BOTTOM NAV ── */
    .rx-glass-nav{
      position:fixed;bottom:16px;left:50%;transform:translateX(-50%);
      background:rgba(10,23,41,0.82);
      backdrop-filter:blur(20px) saturate(200%);
      -webkit-backdrop-filter:blur(20px) saturate(200%);
      border:1px solid rgba(184,151,42,0.22);
      border-radius:24px;
      padding:6px 8px;
      display:flex;align-items:center;gap:2px;
      z-index:4000;
      box-shadow:0 20px 60px rgba(0,0,0,0.45),0 1px 0 rgba(184,151,42,0.08) inset;
      max-width:92vw;
      overflow-x:auto;
      scrollbar-width:none;
    }
    .rx-glass-nav::-webkit-scrollbar{display:none}
    .rx-glass-nav-item{
      display:flex;align-items:center;gap:6px;
      padding:8px 14px;border-radius:18px;
      cursor:pointer;white-space:nowrap;
      font-size:12px;font-weight:500;
      color:rgba(255,255,255,0.50);
      border:1px solid transparent;
      transition:all 0.22s cubic-bezier(0.4,0,0.2,1);
      user-select:none;flex-shrink:0;
      position:relative;
    }
    .rx-glass-nav-item:hover:not(.active){
      background:rgba(184,151,42,0.12);
      color:rgba(255,255,255,0.9);
    }
    .rx-glass-nav-item.active{
      background:linear-gradient(135deg,rgba(184,151,42,0.30),rgba(184,151,42,0.18));
      color:#E8D9A0;
      font-weight:700;
      border-color:rgba(184,151,42,0.45);
      box-shadow:0 4px 14px rgba(184,151,42,0.20);
    }
    .rx-glass-nav-close{
      margin-left:4px;opacity:0.4;font-size:14px;line-height:1;
      padding:1px 3px;border-radius:4px;
      transition:opacity .15s,background .15s;
    }
    .rx-glass-nav-close:hover{opacity:1;background:rgba(255,255,255,0.15)}
    .rx-glass-nav-divider{
      width:1px;height:22px;background:rgba(255,255,255,0.1);
      margin:0 4px;flex-shrink:0;
    }
    @media(max-width:768px){
      .rx-glass-nav{bottom:10px;padding:5px 6px;}
      .rx-glass-nav-item{padding:7px 10px;font-size:11px;}
    }
    @media print{.rx-glass-nav{display:none!important}}

    .scheme-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      gap: 20px;
      padding: 10px 0;
    }
    .scheme-card {
      background: #ffffff;
      border: 1px solid #e5e7eb;
      border-radius: 16px;
      padding: 20px;
      transition: all 0.3s ease;
      position: relative;
      overflow: hidden;
    }
    .scheme-card:hover {
      transform: translateY(-5px);
      box-shadow: 0 12px 24px rgba(0,0,0,0.05);
      border-color: #1A73E8;
    }
    .scheme-badge {
      font-size: 10px;
      font-weight: 800;
      text-transform: uppercase;
      padding: 4px 10px;
      border-radius: 20px;
      letter-spacing: 0.05em;
    }
    .badge-active { background: #e8f5e9; color: #2e7d32; }
    .badge-inactive { background: #fef2f2; color: #991b1b; }
    .scheme-type-icon {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      background: #f1f5f9;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      margin-bottom: 15px;
    }
  `}</style>
);

// ── CONSTANTS ────────────────────────────────────────────────

const C = {
  // ── RIVEXIA LOCKED BRAND PALETTE ──────────────────────────────
  navy:"#0F1E38",          // Primary dark background
  navyMid:"#1C3353",       // Cards / panels
  accent:"#B8972A",        // Gold — primary accent
  accentL:"#D4AF5A",       // Gold Light
  accentM:"#E8D9A0",       // Gold Muted — table cells, subtle text
  accentD:"#B8972A",       // Gold Dark alias
  // Backgrounds
  bg:"#F2EFE9",            // Cream-tinted app background
  sectionBg:"#EDE9E1",     // Section background
  card:"#FFFFFF",          // Card white
  cream:"#F9F7F2",         // Cream
  // Text
  text:"#2C2C2C",          // Body text
  dark:"#141414",          // Near-black headings
  muted:"#717171",         // Muted / secondary text
  // Border
  border:"#E4DDD1",        // Warm border
  // Semantic colors
  teal:"#1A7A6E",          // Paid / positive
  rose:"#8B3A52",          // Overdue / negative / alert
  amber:"#C07A1A",         // Pending / warning
  sciBlue:"#1A5FA8",       // Science Blue — data / info
  violet:"#5B3FA8",        // Violet — purchases
  green:"#2E7D52",         // Green — CRM positive
  // KPI card palettes — Rivexia brand-aligned
  card_sales_bg:"#0F1E3812",  card_sales_fg:"#B8972A",
  card_owed_bg:"#C07A1A12",   card_owed_fg:"#C07A1A",
  card_alert_bg:"#8B3A5212",  card_alert_fg:"#8B3A52",
  card_purchase_bg:"#5B3FA812",card_purchase_fg:"#5B3FA8",
  card_crm_bg:"#1A7A6E12",    card_crm_fg:"#1A7A6E",
  // Chart palette — brand-consistent
  chart1:"#B8972A", chart2:"#1A7A6E", chart3:"#C07A1A", chart4:"#1A5FA8",
  chart5:"#8B3A52", chart6:"#5B3FA8", chart7:"#2E7D52", chart8:"#D4AF5A",
  // Legacy aliases
  gold:"#B8972A", goldD:"#D4AF5A",
};


const INDIAN_STATES = ["Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Andaman and Nicobar Islands","Chandigarh","Dadra and Nagar Haveli","Daman and Diu","Delhi","Lakshadweep","Puducherry","Jammu and Kashmir","Ladakh"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const YEARS = ["2023","2024","2025","2026","2027"];
// ══════════════════════════════════════════════════════════════
// AMOUNT VISIBILITY SYSTEM
// ══════════════════════════════════════════════════════════════
const AmountCtx = React.createContext({show:true,toggle:()=>{}});
const useAmt = () => React.useContext(AmountCtx);

// Eye icon SVG components
const EyeOpen = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const EyeOff = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

// AMT — renders value or ••••• based on global visibility
// Usage: <AMT v={totalSales} pre="₹" /> or <AMT v={fmt(x)} pre="₹" />
const AMT = ({v, pre="₹", suf="", style={}}) => {
  const {show} = useAmt();
  if (!show) return (
    <span style={{letterSpacing:3,opacity:.5,...style}}>••••••</span>
  );
  return <span style={style}>{pre}{typeof v==="number"?fmt(v):v}{suf}</span>;
};

// Global toggle button — small eye icon
const AmtToggleBtn = ({color="rgba(255,255,255,0.7)", size=15}) => {
  const {show,toggle} = useAmt();
  return (
    <button onClick={toggle} title={show?"Hide amounts":"Show amounts"} style={{
      background:"none",border:"none",cursor:"pointer",
      color,display:"inline-flex",alignItems:"center",justifyContent:"center",
      padding:4,borderRadius:6,transition:"all .15s",flexShrink:0,
      opacity:.75,
    }}
    onMouseEnter={e=>{e.currentTarget.style.opacity="1";e.currentTarget.style.background="rgba(255,255,255,0.1)";}}
    onMouseLeave={e=>{e.currentTarget.style.opacity=".75";e.currentTarget.style.background="none";}}>
      {show ? <EyeOpen/> : <EyeOff/>}
    </button>
  );
};

const EXPENSE_CATS = ["Travel","Marketing","Regulatory","Lab Testing","Logistics","Office","Salary","Utilities","Other"];
const PAY_MODES = ["NEFT","RTGS","Cheque","Cash","UPI","DD"];
const SPECIALIZATIONS = ["Dermatologist","General Physician","Diabetologist","Pulmonologist","Gastroenterologist","Cardiologist","Orthopedic","Gynecologist","Pediatrician","ENT","Ophthalmologist","Oncologist","Neurologist","Other"];
const REGIONS = ["North","South","East","West","Central"];
const CHANNELS = ["Doctor Prescription","Own Website / D2C","Distributor","Retail Pharmacy","Hospital","Franchise","Stockist","Retailer"];
const PARTY_TYPES = ["Retail Pharmacy","Hospital","Distributor","Stockist","Franchise","Retailer","Doctor / Clinic","Online / D2C","Clinic","Nursing Home","Government"];
const VENDOR_TYPES = ["Contract Manufacturer","Packaging Supplier","Raw Material","Logistics","Label / Print","Other"];
const _PO_STATUSES = ["Draft","Confirmed","Partially Received","Fully Received","Cancelled"];

// ── HELPERS ──────────────────────────────────────────────────
const uid = () => Math.random().toString(36).slice(2,9);
const fmt = (n,d=0) => (Number(n)||0).toLocaleString("en-IN",{minimumFractionDigits:d,maximumFractionDigits:d});
const today = () => { const d=new Date(); return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`; };
const parseDate = s => { if(!s||!s.includes("/")) return null; const [dd,mm,yyyy]=s.split("/"); return new Date(+yyyy,+mm-1,+dd); };

const daysLeft = s => { const d=parseDate(s); if(!d) return 9999; return Math.ceil((d-new Date())/86400000); };
const getInvStatus = (inv, parties) => {
  if(inv.status==="Paid"||inv.status==="Cancelled") return inv.status;
  if(inv.paid>=inv.total) return "Paid";
  const party=parties?.find(p=>p.name===inv.party)||{};
  const cd=Number(party.creditDays||0);
  const invDate=parseDate(inv.date);
  if(invDate&&cd>0){const due=new Date(invDate);due.setDate(due.getDate()+cd);if(new Date()>due) return "Overdue";}
  else if(inv.dueDate){const due=parseDate(inv.dueDate);if(due&&new Date()>due) return "Overdue";}
  return inv.paid>0?"Partial":"Current";
};
const getMonth = s => { if(!s||!s.includes("/")) return ""; return MONTHS[parseInt(s.split("/")[1])-1]||""; };
const getYear  = s => { if(!s||!s.includes("/")) return ""; return s.split("/")[2]||""; };
const getParty = (name,parties) => parties.find(p=>p.name===name)||{};
// Sort invoices ascending by invoice number (numeric suffix)
const sortByInvNo=(arr)=>[...arr].sort((a,b)=>{
  const n=s=>{const m=(s||"").match(/(\d+)$/);return m?parseInt(m[1]):0;};
  return n(b.no)-n(a.no);
});

const downloadCSV = (rows,headers,filename) => {
  const e = v=>`"${String(v||"").replace(/"/g,'""')}"`;
  const csv=[headers.map(e).join(","),...rows.map(r=>r.map(e).join(","))].join("\n");
  const a=document.createElement("a");
  a.href=URL.createObjectURL(new Blob([csv],{type:"text/csv"}));
  a.download=filename+".csv"; a.click();
};


// ── DEFAULT DATA ─────────────────────────────────────────────

const DEFAULT = {
  appPassword:"yapiderma2024",
  settings:{
    companyName:"YAPIDERMA PRIVATE LIMITED",
    address1:"",
    city:"",state:"Delhi",pin:"",mobile:"",
    gstin:"",pan:"",cin:"",
    dl1:"",dl2:"",stateCode:"07",
    bankName:"",bankAcc:"",
    ifsc:"",branch:"",
    terms:"1. Goods once sold cannot be returned without prior approval.\n2. Payment as per agreed credit terms.\n3. Interest @18% p.a. on overdue amounts.",
  },
  users:[{
    id:"owner001",
    name:"Admin",
    email:"admin@yapiderma.com",
    role:"owner",
    phone:"",
    active:true,
    password:"yapiderma@2024"
  }],
  parties:[],
  products:[],
  doctors:[],
  manufacturers:[],
  vendors:[],
  batches:[],
  batchMovements:[],
  invoices:[],
  purchases:[],
  purchaseOrders:[],
  purchaseReturns:[],
  payments:[],
  vendorPayments:[],
  creditDebitNotes:[],
  salesReturns:[],
  paymentAdjustments:[],
  challans:[],
  partyRates:[],
  doctorRates:[],
  schemeMaster:[],
  auditLog:[],
  deleteRequests:[],
};

// ── UI PRIMITIVES ─────────────────────────────────────────────

const SC = {
  // Payment / Invoice status
  Paid:"#D4EDDA:#2E6F4F",
  Partial:"#FFF3CD:#856404",
  Overdue:"#F8D7DA:#842029",
  Current:"#D1ECF1:#0C5460",
  Unpaid:"#F8D7DA:#842029",
  // Approval status
  Approved:"#D4EDDA:#2E6F4F",
  Pending:"#FFF3CD:#856404",
  Rejected:"#F8D7DA:#842029",
  "Credit Issued":"#D1ECF1:#0C5460",
  // Notes
  "Credit Note":"#EDE9FE:#4C1D95",
  "Debit Note":"#FFF3CD:#856404",
  // Stock
  IN:"#D4EDDA:#2E6F4F",OUT:"#F8D7DA:#842029",
  "IN STOCK":"#D4EDDA:#2E6F4F",
  "LOW STOCK":"#FFF3CD:#856404",
  "OUT OF STOCK":"#F8D7DA:#842029",
  "REORDER":"#FFF3CD:#856404",
  // Sales channels
  "Doctor Prescription":"#D1ECF1:#0C5460",
  "Own Website / D2C":"#D4EDDA:#2E6F4F",
  "Distributor":"#FFF3CD:#856404",
  "Retail Pharmacy":"#EDE9FE:#4C1D95",
  "Hospital":"#E8F4FB:#1A5276",
  // PO status
  "Fully Received":"#D4EDDA:#2E6F4F",
  "Partially Received":"#FFF3CD:#856404",
  Confirmed:"#D1ECF1:#0C5460",
  Draft:"#EEF2F6:#415A77",
  Cancelled:"#F8D7DA:#842029",
  // MR order status
  Billed:"#D4EDDA:#2E6F4F",
};


const Badge = ({s}) => {
  const [bg,fg]=(SC[s]||"#f1f5f9:#475569").split(":");
  return <span style={{display:"inline-flex",alignItems:"center",padding:"2px 8px",borderRadius:20,fontSize:10,fontWeight:700,background:bg,color:fg,whiteSpace:"nowrap"}}>{s}</span>;
};

const Btn = ({onClick,children,v="navy",sz="md",disabled,full}) => {
  const vs={
    navy:  {bg:C.navy,      col:"#F9F7F2",bdr:C.navy},
    gold:  {bg:C.accent,    col:"#0F1E38", bdr:C.accent},
    outline:{bg:"transparent",col:C.navy,  bdr:C.border},
    danger:{bg:"#8B3A5222",  col:"#8B3A52",bdr:"#8B3A5244"},
    green: {bg:"#1A7A6E22",  col:"#1A7A6E",bdr:"#1A7A6E44"},
    ghost: {bg:"transparent",col:C.muted,  bdr:"transparent"},
    purple:{bg:"#5B3FA812",  col:"#5B3FA8", bdr:"#5B3FA844"},
  };
  const st=vs[v]||vs.navy;
  return <button onClick={onClick} disabled={disabled}
    style={{padding:sz==="sm"?"4px 10px":"8px 16px",background:st.bg,color:st.col,
      border:`1px solid ${st.bdr}`,borderRadius:8,fontSize:sz==="sm"?11:13,fontWeight:600,
      cursor:disabled?"not-allowed":"pointer",display:"inline-flex",alignItems:"center",
      gap:5,opacity:disabled?.5:1,fontFamily:"inherit",width:full?"100%":"auto",
      justifyContent:"center",transition:"all .15s",letterSpacing:.1}}>{children}</button>;
};

const Card = ({children,style={}}) => (
  <div style={{background:"#FFFFFF",borderRadius:14,border:`1px solid ${C.border}`,padding:20,boxShadow:"0 2px 12px rgba(15,30,56,0.06)",transition:"box-shadow .2s",...style}}>{children}</div>
);

const PH = ({title,sub,action}) => (
  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:22,paddingBottom:18,borderBottom:`1px solid ${C.border}`}}>
    <div style={{display:"flex",alignItems:"center",gap:12}}>
      <div style={{width:3,height:32,background:`linear-gradient(180deg,${C.accent},${C.accentL})`,borderRadius:4,flexShrink:0}}/>
      <div>
        <h2 style={{margin:0,fontSize:18,fontWeight:700,color:C.navy,letterSpacing:-.3,lineHeight:1.2,fontFamily:"'Cormorant Garamond',Georgia,serif"}}>{title}</h2>
        {sub&&<p style={{margin:"3px 0 0",fontSize:12,color:C.muted,fontWeight:400,letterSpacing:.1}}>{sub}</p>}
      </div>
    </div>
    {action&&<div style={{flexShrink:0}}>{action}</div>}
  </div>
);

const Field = ({label,children,req,style={}}) => (
  <div style={{marginBottom:0,...style}}>
    <label style={{display:"block",fontSize:10,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:.5,marginBottom:5,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{label}{req&&<span style={{color:"#C0392B",marginLeft:2}}>*</span>}</label>
    {children}
  </div>
);

const Inp = ({value,onChange,placeholder,type="text",style={},onKey}) => (
  <input type={type} value={value||""} onChange={e=>onChange(e.target.value)} onKeyDown={onKey} placeholder={placeholder}
    style={{width:"100%",padding:"8px 11px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,
      background:"#fff",color:C.text,transition:"border .15s",...style}}/>
);

// MonthYearInp — month+year only picker, stores as MM/YYYY
const MonthYearInp = ({value,onChange}) => {
  // value format: "MM/YYYY" e.g. "03/2025"
  const toHtml = v => {
    if(!v) return "";
    const p=(v||"").split("/");
    if(p.length===2&&p[1]?.length===4) return p[1]+"-"+p[0].padStart(2,"0");
    // handle DD/MM/YYYY — extract month/year
    if(p.length===3&&p[2]?.length===4) return p[2]+"-"+p[1].padStart(2,"0");
    return "";
  };
  const fromHtml = v => {
    if(!v) return "";
    const p=v.split("-");
    return p.length>=2?p[1]+"/"+p[0]:"";
  };
  return(
    <input type="month" value={toHtml(value)||""}
      onChange={e=>onChange(fromHtml(e.target.value))}
      style={{width:"100%",padding:"8px 11px",border:`1px solid ${C.border}`,borderRadius:8,
        fontSize:13,background:"#fff",color:C.text,fontFamily:"inherit",
        outline:"none",boxSizing:"border-box",cursor:"pointer",colorScheme:"light"}}/>
  );
};
const DateInp = ({value,onChange,style={}}) => {
  const toHtml = (v) => {
    if(!v) return "";
    const p = (v||"").split("/");
    if(p.length===3&&p[2]&&p[2].length===4) return p[2]+"-"+p[1].padStart(2,"0")+"-"+p[0].padStart(2,"0");
    return "";
  };
  const fromHtml = (v) => {
    if(!v) return "";
    const p = v.split("-");
    return p.length===3 ? p[2]+"/"+p[1]+"/"+p[0] : "";
  };
  const ref = useRef(null);
  return (
    <div style={{position:"relative",width:"100%",display:"flex",alignItems:"center"}}>
      <input
        ref={ref}
        type="date"
        value={toHtml(value)||""}
        onChange={e=>onChange(fromHtml(e.target.value))}
        style={{
          width:"100%",padding:"8px 36px 8px 11px",
          border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,
          background:"#fff",color:C.text,fontFamily:"inherit",
          outline:"none",boxSizing:"border-box",cursor:"pointer",
          colorScheme:"light",...style
        }}
      />
      <span
        onClick={()=>{ try{ ref.current.showPicker(); }catch(e){ ref.current.click(); } }}
        style={{position:"absolute",right:10,cursor:"pointer",fontSize:15,userSelect:"none",color:C.muted,pointerEvents:"all"}}>
        📅
      </span>
    </div>
  );
};

const Sel = ({value,onChange,options,placeholder}) => {
  const opts=options.map(o=>({v:typeof o==="object"?(o.v||o.value):o,l:typeof o==="object"?(o.l||o.label):o}));
  return(<select value={value||""} onChange={e=>onChange(e.target.value)}
    style={{width:"100%",padding:"8px 11px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,
      background:"#fff",color:value?C.text:C.muted,transition:"border .15s"}}>
    <option value="">{placeholder||"Select..."}</option>
    {opts.map(o=><option key={String(o.v)} value={o.v}>{o.l}</option>)}
  </select>);
};

const Tbl = ({headers,rows,empty,onExport}) => (
  <div>
    {onExport&&<div style={{display:"flex",justifyContent:"flex-end",marginBottom:8}}>
      <Btn v="outline" sz="sm" onClick={onExport}>⬇ Download CSV</Btn>
    </div>}
    <div style={{overflowX:"auto",borderRadius:10,border:`1px solid ${C.border}`,boxShadow:"0 2px 8px rgba(0,0,0,0.04)",WebkitOverflowScrolling:"touch"}}>
      <table style={{width:"100%",minWidth:480,borderCollapse:"collapse",fontSize:13}}>
        <thead>
          <tr style={{background:"#F8FAFC"}}>
            {headers.map((h,i)=><th key={i} style={{padding:"9px 13px",color:"#64748B",fontWeight:700,fontSize:10,textAlign:i===0?"left":"center",whiteSpace:"nowrap",textTransform:"uppercase",letterSpacing:.6,borderBottom:"2px solid #E2E8F0"}}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.length===0&&<tr><td colSpan={headers.length} style={{padding:32,textAlign:"center",color:C.muted,fontSize:13}}>{empty||"No records found"}</td></tr>}
          {rows.map((r,ri)=>(
            <tr key={ri} style={{background:"#fff",borderBottom:"1px solid #F1F5F9",transition:"background .1s"}}
              onMouseEnter={e=>e.currentTarget.style.background="#F8FBFF"}
              onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
              {r.map((cell,ci)=><td key={ci} style={{padding:"10px 13px",textAlign:ci===0?"left":"center",verticalAlign:"middle",color:"#1E2A3B",fontSize:12.5}}>{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const Modal = ({title,children,onClose,w=580}) => (
  <div style={{position:"fixed",inset:0,background:"rgba(47,74,109,.45)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
    <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:w,maxHeight:"90vh",display:"flex",flexDirection:"column",boxShadow:"0 20px 60px rgba(47,74,109,.25)"}}>
      <div style={{padding:"16px 22px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
        <h3 style={{margin:0,fontSize:15,fontWeight:600,color:C.navy}}>{title}</h3>
        <button onClick={onClose} style={{background:C.sectionBg,border:"none",fontSize:16,cursor:"pointer",color:C.muted,lineHeight:1,borderRadius:6,width:28,height:28,display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
      </div>
      <div style={{padding:"16px 20px",overflowY:"auto",flex:1}}>{children}</div>
    </div>
  </div>
);

const KPI = ({label,val,sub,icon,accent,onClick,bg,fg,trend,isAmt=true}) => {
  const bgC = bg || "#EEF6FF";
  const fgC = fg || "#2563EB";
  const {show} = useAmt();
  const displayVal = isAmt && !show ? <span style={{letterSpacing:4,fontSize:22,opacity:.45}}>••••••</span> : val;
  return (
    <div onClick={onClick} style={{
      background: bgC,
      borderRadius: 20,
      padding: "20px 20px 16px",
      flex: 1, minWidth: 150,
      border: "none",
      boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
      cursor: onClick ? "pointer" : "default",
      position: "relative", overflow: "hidden",
      transition: "transform .18s, box-shadow .18s",
    }}
    onMouseEnter={e=>{if(onClick){e.currentTarget.style.transform="translateY(-3px)";e.currentTarget.style.boxShadow="0 8px 28px rgba(0,0,0,0.1)";}}}
    onMouseLeave={e=>{if(onClick){e.currentTarget.style.transform="none";e.currentTarget.style.boxShadow="0 2px 12px rgba(0,0,0,0.06)";}}}
    >
      {/* Icon — top right with matching pastel circle */}
      <div style={{
        position: "absolute", top: 16, right: 16,
        width: 44, height: 44, borderRadius: "50%",
        background: fgC + "18",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 22,
      }}>{icon}</div>

      {/* Label */}
      <div style={{
        fontSize: 10, fontWeight: 700, color: fgC,
        textTransform: "uppercase", letterSpacing: 1.2,
        marginBottom: 12, opacity: .75,
      }}>{label}</div>

      {/* Value — hidden if amounts hidden */}
      <div style={{
        fontSize: 28, fontWeight: 800, color: fgC,
        letterSpacing: "-.03em", lineHeight: 1, marginBottom: 6,
        fontFamily: "monospace", minHeight: 34, display:"flex", alignItems:"center",
      }}>{displayVal}</div>

      {/* Sub */}
      {sub != null && <div style={{fontSize: 11, color: fgC, opacity: .65, fontWeight: 500, marginBottom: 8}}>{sub}</div>}

      {/* Link */}
      {onClick && <div style={{fontSize: 11, color: fgC, opacity: .6, fontWeight: 600, display:"flex", alignItems:"center", gap: 4}}>
        View details →
      </div>}
    </div>
  );
};

const KPIc = ({label,val,icon,onClick,bg,fg,isAmt=false}) => {
  const bgC = bg || "#F5F3FF";
  const fgC = fg || "#7C3AED";
  const {show} = useAmt();
  const displayVal = isAmt && !show ? <span style={{letterSpacing:4,fontSize:22,opacity:.45}}>•••</span> : val;
  return (
    <div onClick={onClick} style={{
      background: bgC,
      borderRadius: 20,
      padding: "18px 18px 14px",
      flex: 1, minWidth: 120,
      border: "none",
      boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
      cursor: onClick ? "pointer" : "default",
      position: "relative", overflow: "hidden",
      transition: "transform .18s, box-shadow .18s",
    }}
    onMouseEnter={e=>{if(onClick){e.currentTarget.style.transform="translateY(-3px)";e.currentTarget.style.boxShadow="0 8px 28px rgba(0,0,0,0.1)";}}}
    onMouseLeave={e=>{if(onClick){e.currentTarget.style.transform="none";e.currentTarget.style.boxShadow="0 2px 12px rgba(0,0,0,0.06)";}}}
    >
      {/* Icon top right */}
      <div style={{
        position: "absolute", top: 14, right: 14,
        width: 38, height: 38, borderRadius: "50%",
        background: fgC + "18",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 20,
      }}>{icon}</div>

      {/* Label */}
      <div style={{
        fontSize: 10, fontWeight: 700, color: fgC,
        textTransform: "uppercase", letterSpacing: 1.1,
        marginBottom: 10, opacity: .75,
      }}>{label}</div>

      {/* Value */}
      <div style={{
        fontSize: 30, fontWeight: 800, color: fgC,
        lineHeight: 1, letterSpacing: "-.03em", marginBottom: 6,
        fontFamily: "monospace", minHeight:36, display:"flex", alignItems:"center",
      }}>{displayVal}</div>

      {onClick && <div style={{fontSize: 11, color: fgC, opacity: .55, fontWeight: 600}}>View →</div>}
    </div>
  );
};


// ── BAR CHART (SVG, no dependencies) ──────────────────────────

const BarChart = ({data:bdata, valueKey="value", labelKey="label", color=C.chart1, prefix="₹", height=160, showValues=true}) => {
  if(!bdata||!bdata.length) return <div style={{textAlign:"center",padding:32,color:C.muted,fontSize:12}}>No data</div>;
  const max=Math.max(...bdata.map(d=>d[valueKey]||0),1);
  const barW=Math.max(20,Math.min(48,Math.floor(520/bdata.length)-8));
  const total=bdata.reduce((s,d)=>s+(d[valueKey]||0),0);
  return (
    <div style={{width:"100%",overflowX:"auto"}}>
      <div style={{display:"flex",alignItems:"flex-end",gap:6,height:height+40,padding:"0 4px",minWidth:bdata.length*54}}>
        {bdata.map((d,i)=>{
          const v=d[valueKey]||0;
          const pct=max>0?v/max*100:0;
          const barH=Math.max(4,Math.round(pct/100*height));
          const colors=[C.chart1,C.chart2,"#A78BFA",C.chart3,"#6EE7B7","#F87171","#60A5FA","#FBBF24"];
          const bg=Array.isArray(color)?color[i%color.length]:color||colors[i%colors.length];
          return (
            <div key={i} style={{display:"flex",flexDirection:"column",alignItems:"center",flex:1,minWidth:barW}}>
              {showValues&&v>0&&<div style={{fontSize:9,color:C.muted,marginBottom:3,fontWeight:600,whiteSpace:"nowrap"}}>{prefix}{v>=100000?Math.round(v/1000)+"k":fmt(v)}</div>}
              <div style={{width:"100%",maxWidth:barW,height:barH,background:bg,borderRadius:"6px 6px 0 0",transition:"height .3s ease",position:"relative",cursor:"default"}}
                title={`${d[labelKey]}: ${prefix}${fmt(v)}`}/>
              <div style={{fontSize:9,color:C.muted,marginTop:4,textAlign:"center",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:barW+8,width:"100%"}}>{d[labelKey]}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── LINE CHART (SVG) ───────────────────────────────────────────
const LineChart = ({data:ld, color=C.chart1, height=120, prefix="₹"}) => {
  if(!ld||ld.length<2) return <div style={{textAlign:"center",padding:24,color:C.muted,fontSize:12}}>Not enough data</div>;
  const vals=ld.map(d=>d.value||0);
  const max=Math.max(...vals,1), min=Math.min(...vals,0);
  const W=500, H=height;
  const pad=20;
  const pts=ld.map((d,i)=>{
    const x=pad+(i/(ld.length-1))*(W-pad*2);
    const y=H-pad-(((d.value||0)-min)/(max-min||1))*(H-pad*2);
    return [x,y];
  });
  const path=pts.map((p,i)=>`${i===0?"M":"L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const fillPath=path+` L${pts[pts.length-1][0]},${H-pad} L${pad},${H-pad} Z`;
  return (
    <div style={{width:"100%",overflowX:"auto"}}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",height:H}}>
        <defs>
          <linearGradient id="lg1" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.2"/>
            <stop offset="100%" stopColor={color} stopOpacity="0"/>
          </linearGradient>
        </defs>
        <path d={fillPath} fill="url(#lg1)"/>
        <path d={path} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        {pts.map((p,i)=>(
          <g key={i}>
            <circle cx={p[0]} cy={p[1]} r="4" fill="#fff" stroke={color} strokeWidth="2"/>
            <text x={p[0]} y={H-2} textAnchor="middle" fontSize="9" fill={C.muted}>{ld[i].label}</text>
          </g>
        ))}
      </svg>
    </div>
  );
};

// ── REMINDER POPUP ─────────────────────────────────────────────
const ReminderPopup = ({data, onClose}) => {
  const parties=data.parties||[];
  const products=data.products||[];
  const today_d = new Date();
  const reminders = [];

  // Overdue invoices
  const overdue = data.invoices.filter(i=>getInvStatus(i,parties)==="Overdue");
  if(overdue.length>0) reminders.push({
    type:"danger", icon:"💸", title:`${overdue.length} Overdue Invoice${overdue.length>1?"s":""}`,
    desc:`₹${fmt(overdue.reduce((s,i)=>s+(i.total-i.paid),0))} pending`,
    action:"outstanding"
  });

  // Invoices due in 7 days
  const dueSoon = data.invoices.filter(i=>{
    if(i.status==="Paid"||i.status==="Cancelled") return false;
    const dl = i.dueDate?daysLeft(i.dueDate):null;
    return dl!==null&&dl>=0&&dl<=7;
  });
  if(dueSoon.length>0) reminders.push({
    type:"warn", icon:"⏰", title:`${dueSoon.length} Invoice${dueSoon.length>1?"s":""} Due in 7 Days`,
    desc:`₹${fmt(dueSoon.reduce((s,i)=>s+(i.total-i.paid),0))} to collect`,
    action:"outstanding"
  });

  // Expiring batches
  const expiring = data.batches.filter(b=>{const d=daysLeft(b.exp);return d>=0&&d<=30;});
  if(expiring.length>0) reminders.push({
    type:"warn", icon:"📅", title:`${expiring.length} Batch${expiring.length>1?"es":""} Expiring in 30 Days`,
    desc:expiring.slice(0,3).map(b=>b.product).join(", "),
    action:"batches"
  });

  // Low stock
  const lowS = products.filter(p=>{
    const stock=data.batches.filter(x=>x.product===p.name).reduce((s,x)=>s+(x.opening+x.in-x.out),0);
    return stock<=(p.reorderLevel||0);
  });
  if(lowS.length>0) reminders.push({
    type:"info", icon:"📦", title:`${lowS.length} Product${lowS.length>1?"s":""} Below Reorder Level`,
    desc:lowS.slice(0,3).map(p=>p.name).join(", "),
    action:"batches"
  });

  // Pending orders
  const pendO=(data.mrOrders||[]).filter(o=>o.status==="Pending");
  if(pendO.length>0) reminders.push({
    type:"info", icon:"📋", title:`${pendO.length} Order${pendO.length>1?"s":""} Pending`,
    desc:"MR/ASM orders awaiting invoice",
    action:"allorders"
  });

  if(reminders.length===0) return null;

  const bgMap={danger:"#FFF1F2",warn:"#FFFBEB",info:"#EFF6FF"};
  const fgMap={danger:"#E11D48",warn:"#D97706",info:"#2563EB"};
  const borderMap={danger:"#FECDD3",warn:"#FDE68A",info:"#BFDBFE"};

  return (
    <div style={{position:"fixed",bottom:24,right:24,width:340,zIndex:5000,display:"flex",flexDirection:"column",gap:8}}>
      {reminders.slice(0,4).map((r,i)=>(
        <div key={i} onClick={()=>onClose(r.action)} style={{
          background:bgMap[r.type],border:`1px solid ${borderMap[r.type]}`,
          borderRadius:12,padding:"12px 16px",cursor:"pointer",
          boxShadow:"0 4px 20px rgba(0,0,0,0.12)",
          display:"flex",gap:10,alignItems:"flex-start",
          animation:"slideInRight .3s ease"
        }}>
          <div style={{width:36,height:36,borderRadius:10,background:`${fgMap[r.type]}15`,
            display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>{r.icon}</div>
          <div style={{flex:1}}>
            <div style={{fontSize:12,fontWeight:700,color:fgMap[r.type]}}>{r.title}</div>
            <div style={{fontSize:11,color:C.muted,marginTop:2}}>{r.desc}</div>
          </div>
          <button onClick={e=>{e.stopPropagation();onClose(null);}} style={{background:"none",border:"none",color:C.muted,fontSize:16,cursor:"pointer",lineHeight:1,padding:0,flexShrink:0}}>×</button>
        </div>
      ))}
    </div>
  );
};


// ══════════════════════════════════════════════════════════════
// BULK UPLOAD COMPONENT (reusable)
// ══════════════════════════════════════════════════════════════
const BulkUpload = ({title, templateHeaders, templateRows, onUpload, parseRow}) => {
  const [show,setShow]=useState(false);
  const [mode,setMode]=useState("merge"); // merge | replace
  const [preview,setPreview]=useState(null);
  const [error,setError]=useState("");
  const fileRef=useRef(null);

  const downloadTemplate=()=>{
    const rows=[templateHeaders,...(templateRows||[])];
    const csv=rows.map(r=>r.map(c=>typeof c==="string"&&c.includes(",")?`"${c}"`:c).join(",")).join("\n");
    const blob=new Blob([csv],{type:"text/csv"});
    const url=URL.createObjectURL(blob);
    const a=document.createElement("a");
    a.href=url; a.download=`${title.replace(/\s+/g,"_")}_Template.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const handleFile=(e)=>{
    setError("");setPreview(null);
    const file=e.target.files[0];
    if(!file) return;
    const reader=new FileReader();
    reader.onload=(ev)=>{
      try{
        const text=ev.target.result;
        const lines=text.split("\n").map(l=>l.trim()).filter(Boolean);
        if(lines.length<2){setError("File is empty or has only headers");return;}
        const headers=lines[0].split(",").map(h=>h.replace(/"/g,"").trim());
        const rows=lines.slice(1).map(line=>{
          const vals=[];
          let cur="",inQ=false;
          for(let i=0;i<line.length;i++){
            const ch=line[i];
            if(ch==='"'){inQ=!inQ;}
            else if(ch===","&&!inQ){vals.push(cur.trim());cur="";}
            else cur+=ch;
          }
          vals.push(cur.trim());
          const obj={};
          headers.forEach((h,i)=>obj[h]=vals[i]||"");
          return obj;
        });
        const parsed=rows.map((r,i)=>parseRow(r,i)).filter(Boolean)
          .map((r,i)=>r.id?r:{...r,id:r.id||("BLK"+Date.now()+"_"+i)});
        setPreview({count:parsed.length,rows:parsed,raw:rows});
      } catch(err){setError("Could not parse file: "+err.message);}
    };
    reader.readAsText(file);
    e.target.value="";
  };

  const doUpload=()=>{
    if(!preview) return;
    onUpload(preview.rows, mode);
    setPreview(null);setShow(false);
    alert(`✅ ${preview.count} records ${mode==="replace"?"imported (old data cleared)":"merged"} successfully!`);
  };

  return (
    <div style={{display:"inline-block"}}>
      <Btn v="outline" sz="sm" onClick={()=>setShow(s=>!s)}>
        📤 Bulk Upload
      </Btn>
      {show&&(
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.5)",zIndex:9000,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:"#fff",borderRadius:14,padding:24,width:500,maxWidth:"95vw",boxShadow:"0 20px 60px rgba(0,0,0,0.3)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <div style={{fontWeight:700,fontSize:15,color:C.navy}}>📤 Bulk Upload — {title}</div>
              <button onClick={()=>{setShow(false);setPreview(null);setError("");}} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:C.muted}}>×</button>
            </div>

            {/* Step 1 */}
            <div style={{background:C.sectionBg,borderRadius:8,padding:"12px 14px",marginBottom:12}}>
              <div style={{fontWeight:600,fontSize:12,color:C.navy,marginBottom:8}}>Step 1 — Download Template</div>
              <div style={{fontSize:11,color:C.muted,marginBottom:8}}>Download the CSV template, fill it in Excel, save as CSV.</div>
              <Btn v="navy" sz="sm" onClick={downloadTemplate}>⬇️ Download CSV Template</Btn>
            </div>

            {/* Step 2 */}
            <div style={{background:C.sectionBg,borderRadius:8,padding:"12px 14px",marginBottom:12}}>
              <div style={{fontWeight:600,fontSize:12,color:C.navy,marginBottom:8}}>Step 2 — Choose Upload Mode</div>
              <div style={{display:"flex",gap:10}}>
                {[["merge","🔀 Merge","Add to existing data (duplicates skipped)"],["replace","🔄 Replace","Clear all existing data, import fresh"]].map(([val,label,desc])=>(
                  <div key={val} onClick={()=>setMode(val)}
                    style={{flex:1,padding:"10px 12px",borderRadius:8,border:`2px solid ${mode===val?val==="replace"?"#dc2626":C.accent:C.border}`,
                      background:mode===val?val==="replace"?"#FEF2F2":"#F0FDFA":"#fff",cursor:"pointer"}}>
                    <div style={{fontWeight:700,fontSize:12,color:mode===val?val==="replace"?"#dc2626":C.accent:C.text}}>{label}</div>
                    <div style={{fontSize:10,color:C.muted,marginTop:2}}>{desc}</div>
                  </div>
                ))}
              </div>
              {mode==="replace"&&<div style={{marginTop:8,fontSize:11,color:"#dc2626",fontWeight:600}}>⚠️ This will permanently delete all existing {title} data!</div>}
            </div>

            {/* Step 3 */}
            <div style={{background:C.sectionBg,borderRadius:8,padding:"12px 14px",marginBottom:12}}>
              <div style={{fontWeight:600,fontSize:12,color:C.navy,marginBottom:8}}>Step 3 — Upload Filled File</div>
              <input ref={fileRef} type="file" accept=".csv" onChange={handleFile} style={{display:"none"}}/>
              <Btn v="outline" sz="sm" onClick={()=>fileRef.current?.click()}>📁 Choose CSV File</Btn>
              {error&&<div style={{color:"#dc2626",fontSize:11,marginTop:8}}>❌ {error}</div>}
              {preview&&(
                <div style={{marginTop:10,background:"#fff",borderRadius:6,padding:"10px 12px",border:`1px solid ${C.accent}`}}>
                  <div style={{fontSize:12,fontWeight:700,color:"#166534"}}>✅ {preview.count} records ready to import</div>
                  <div style={{fontSize:10,color:C.muted,marginTop:4}}>Preview (first 3 rows):</div>
                  {preview.rows.slice(0,3).map((r,i)=>(
                    <div key={i} style={{fontSize:10,color:C.text,padding:"2px 0",borderBottom:`1px solid ${C.border}`}}>
                      {Object.entries(r).slice(0,4).map(([k,v])=>`${k}: ${v}`).join(" · ")}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Upload button */}
            {preview&&(
              <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
                <Btn v="outline" onClick={()=>{setPreview(null);setError("");}}>Clear</Btn>
                <Btn v={mode==="replace"?"danger":"navy"} onClick={doUpload}>
                  {mode==="replace"?"🔄 Replace & Import":"🔀 Merge & Import"} ({preview.count} records)
                </Btn>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── PIE CHART (pure SVG, no dependencies) ────────────────────
const PieChart = ({slices,size=160,title,legend=true}) => {
  if(!slices||!slices.length||slices.every(s=>!s.value)) return (
    <div style={{textAlign:"center",padding:20,color:C.muted,fontSize:12}}>No data yet</div>
  );
  const total=slices.reduce((s,x)=>s+(x.value||0),0);
  if(!total) return <div style={{textAlign:"center",padding:20,color:C.muted,fontSize:12}}>No data yet</div>;
  const r=size/2-8, cx=size/2, cy=size/2;
  let startAngle=-Math.PI/2;
  const paths=slices.filter(s=>s.value>0).map(s=>{
    const angle=(s.value/total)*Math.PI*2;
    const endAngle=startAngle+angle;
    const x1=cx+r*Math.cos(startAngle), y1=cy+r*Math.sin(startAngle);
    const x2=cx+r*Math.cos(endAngle),   y2=cy+r*Math.sin(endAngle);
    const large=angle>Math.PI?1:0;
    const d=`M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${large},1 ${x2},${y2} Z`;
    const midA=startAngle+angle/2;
    const tx=cx+(r*0.65)*Math.cos(midA), ty=cy+(r*0.65)*Math.sin(midA);
    const pct=Math.round(s.value/total*100);
    startAngle=endAngle;
    return {d,color:s.color,label:s.label,value:s.value,pct,tx,ty};
  });
  return (
    <div style={{display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
      <div style={{position:"relative",flexShrink:0}}>
        <svg width={size} height={size} style={{filter:"drop-shadow(0 4px 8px rgba(0,0,0,0.10))"}}>
          {paths.map((p,i)=>(
            <path key={i} d={p.d} fill={p.color} stroke="#fff" strokeWidth={2}/>
          ))}
          {paths.filter(p=>p.pct>=8).map((p,i)=>(
            <text key={i} x={p.tx} y={p.ty} textAnchor="middle" dominantBaseline="middle"
              style={{fontSize:10,fontWeight:700,fill:"#fff",pointerEvents:"none"}}>{p.pct}%</text>
          ))}
        </svg>
        {title&&<div style={{position:"absolute",bottom:-18,left:0,right:0,textAlign:"center",fontSize:10,color:C.muted,fontWeight:600}}>{title}</div>}
      </div>
      {legend&&(
        <div style={{flex:1,minWidth:100}}>
          {paths.map((p,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:7,marginBottom:5}}>
              <div style={{width:10,height:10,borderRadius:"50%",background:p.color,flexShrink:0}}/>
              <div style={{flex:1,fontSize:11,color:C.text,fontWeight:500,lineHeight:1.3}}>{p.label}</div>
              <div style={{fontSize:11,fontFamily:"monospace",fontWeight:700,color:C.navy,flexShrink:0}}>{p.pct}%</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── PRINT INVOICE ─────────────────────────────────────────────

const PrintInvoice = ({inv,settings,parties,doctors=[]}) => {
  const _partyRaw=getParty(inv.party,parties);
  // Doctor billing: party won't be in parties list — fallback to doctors[]
  const _doctor=(!_partyRaw?.name&&inv.billDoctor)?doctors.find(d=>d.name===inv.billDoctor||("Dr. "+d.name)===inv.party):null;
  const party=_partyRaw?.name?_partyRaw:_doctor?{
    name:"Dr. "+(_doctor.name||""),
    address1:_doctor.address1||"",
    address2:_doctor.address2||"",
    city:_doctor.city||"",
    state:_doctor.state||"",
    phone:_doctor.phone||"",
    email:_doctor.email||"",
    gstin:"",dl1:"",dl2:"",
  }:{};
  const toWords=(n)=>{
    const a=["","ONE","TWO","THREE","FOUR","FIVE","SIX","SEVEN","EIGHT","NINE","TEN","ELEVEN","TWELVE","THIRTEEN","FOURTEEN","FIFTEEN","SIXTEEN","SEVENTEEN","EIGHTEEN","NINETEEN"];
    const b=["","","TWENTY","THIRTY","FORTY","FIFTY","SIXTY","SEVENTY","EIGHTY","NINETY"];
    const convert=(num)=>{
      if(num===0) return "";
      if(num<20) return a[num]+" ";
      if(num<100) return b[Math.floor(num/10)]+" "+(num%10?a[num%10]+" ":"");
      if(num<1000) return a[Math.floor(num/100)]+" HUNDRED "+(num%100?convert(num%100):"");
      if(num<100000) return convert(Math.floor(num/1000))+"THOUSAND "+convert(num%1000);
      if(num<10000000) return convert(Math.floor(num/100000))+"LAKH "+convert(num%100000);
      return convert(Math.floor(num/10000000))+"CRORE "+convert(num%10000000);
    };
    const amt=Math.round(n);
    return (convert(amt)||"ZERO")+"ONLY";
  };

  // Stockist: PTR + Rate (PTS) both shown. Others: only Rate (=PTR).
  const isStockist=(party?.partyType||"").toLowerCase().includes("stockist");

  if(!inv) return null;
  const totalTaxable=inv.items?.reduce((s,it)=>s+(it.taxable||0),0)||inv.taxable||0;
  const totalCGST=inv.items?.reduce((s,it)=>s+(it.cgst||0),0)||inv.cgst||0;
  const totalSGST=inv.items?.reduce((s,it)=>s+(it.sgst||it.cgst||0),0)||inv.sgst||inv.cgst||0;
  // For inter-state invoices, IGST is stored in it.igst per item and inv.igst at header level
  const totalIGSTFromItems=inv.items?.reduce((s,it)=>s+(it.igst||0),0)||0;
  const grandTotal=inv.total||Math.round(totalTaxable+totalCGST+totalSGST+totalIGSTFromItems);
  // GST: same state (intra) = CGST + SGST split. Different state (inter) = IGST only.
  const isInterState=!!(party?.state&&settings?.state&&party.state.trim().toLowerCase()!==settings.state.trim().toLowerCase());
  // For display: if inter-state use igst fields; if intra-state use cgst+sgst fields
  const totalIGST=isInterState?(totalIGSTFromItems||inv.igst||totalCGST+totalSGST):0;

  // GST class grouping
  const gstGroups={};
  (inv.items||[]).forEach(it=>{
    const key=`GST ${it.gst||18}%`;
    if(!gstGroups[key]) gstGroups[key]={taxable:0,cgst:0,sgst:0,igst:0};
    gstGroups[key].taxable+=(it.taxable||0);
    gstGroups[key].cgst+=(it.cgst||0);
    gstGroups[key].sgst+=(it.sgst||it.cgst||0);
    gstGroups[key].igst+=(it.igst||0);
  });

  const B={border:"1px solid #000"};
  const TD=({children,style={},colSpan,rowSpan})=><td colSpan={colSpan} rowSpan={rowSpan} style={{border:"1px solid #000",padding:"2px 4px",fontSize:9,verticalAlign:"top",...style}}>{children}</td>;
  const _TH=({children,style={},colSpan,rowSpan})=><th colSpan={colSpan} rowSpan={rowSpan} style={{border:"1px solid #000",padding:"3px 4px",fontSize:9,fontWeight:700,background:"#fff",textAlign:"left",verticalAlign:"middle",...style}}>{children}</th>;

  return (
    <div className="print-only invoice-print" style={{fontFamily:"Arial,sans-serif",fontSize:9,color:"#000",width:"100%",margin:0,padding:0,lineHeight:1.35,boxSizing:"border-box",background:"#fff"}}>

      {/* TOP STRIP */}
      <table style={{width:"100%",borderCollapse:"collapse",marginBottom:0}}>
        <tbody>
          <tr>
            <td style={{...B,padding:"4px 8px",fontWeight:800,fontSize:16,letterSpacing:1,width:"70%",verticalAlign:"middle"}}>
              TAX INVOICE <span style={{fontSize:10,fontWeight:400}}>GST</span>
            </td>
            <td style={{...B,padding:"4px 8px",textAlign:"center",fontWeight:700,fontSize:11,background:"#fff"}}>ORIGINAL</td>
          </tr>
        </tbody>
      </table>

      {/* COMPANY HEADER — logo left, company details right */}
      <table style={{width:"100%",borderCollapse:"collapse",marginBottom:0}}>
        <tbody>
          <tr>
            <td style={{...B,padding:"6px 10px",verticalAlign:"middle",width:"28%",textAlign:"center"}}>
              <img src={YAPIDERMA_LOGO} alt="Logo" style={{maxHeight:50,maxWidth:130,objectFit:"contain"}}/>
            </td>
            <td style={{...B,padding:"5px 10px",verticalAlign:"middle",width:"72%"}}>
              <div style={{fontWeight:800,fontSize:13,letterSpacing:.5}}>{settings.companyName||"YAPIDERMA PRIVATE LIMITED"}</div>
              <div style={{fontSize:8.5}}>{settings.address1||"F-34/8, 2ND FLOOR, OKHLA IND AREA PHASE 2"}</div>
              {settings.address2&&<div style={{fontSize:8.5}}>{settings.address2}</div>}
              <div style={{fontSize:8.5}}>{settings.city||"NEW DELHI"} {settings.pin||"110020"}</div>
              <div style={{fontSize:8.5,marginTop:1}}>Mob: {settings.mobile||""} &nbsp;|&nbsp; Email: {settings.email||""}</div>
            </td>
          </tr>
          <tr>
            <td style={{...B,padding:"3px 6px"}} colSpan={1}>
              <div><strong>GSTIN NO:</strong> {settings.gstin||""} &nbsp;&nbsp; <strong>PAN:</strong> {settings.pan||""}</div>
              <div><strong>CIN:</strong> {settings.cin||""}</div>
              <div><strong>DL NO 1:</strong> {settings.dl1||""} &nbsp;&nbsp; <strong>DL NO 2:</strong> {settings.dl2||""}</div>
              <div style={{marginTop:2,fontSize:8}}>Tax is Payable on Reverse Charges: NO</div>
            </td>
            <td style={{...B,padding:"3px 6px"}}>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <tbody>
                  {[
                    ["Invoice No",inv.no||""],["Invoice Date",inv.date||""],
                    ["Transportation",inv.transport||""],["Vehicle No",inv.vehicleNo||"NONE"],
                    ["Order Date",inv.orderDate||inv.date||""],["Due Date",inv.dueDate||""],
                    ["Credit Period",party.creditDays?`${party.creditDays} Days`:""],
                    ["State",settings.state||"Delhi"],
                    ["State Code",settings.stateCode||({"Andhra Pradesh":"28","Arunachal Pradesh":"12","Assam":"18","Bihar":"10","Chhattisgarh":"22","Goa":"30","Gujarat":"24","Haryana":"06","Himachal Pradesh":"02","Jharkhand":"20","Karnataka":"29","Kerala":"32","Madhya Pradesh":"23","Maharashtra":"27","Manipur":"14","Meghalaya":"17","Mizoram":"15","Nagaland":"13","Odisha":"21","Punjab":"03","Rajasthan":"08","Sikkim":"11","Tamil Nadu":"33","Telangana":"36","Tripura":"16","Uttar Pradesh":"09","Uttarakhand":"05","West Bengal":"19","Delhi":"07","Chandigarh":"04","Puducherry":"34","Jammu and Kashmir":"01","Ladakh":"38"}[settings.state])||"07"],
                  ].map(([l,v])=>v!==undefined&&(
                    <tr key={l}>
                      <td style={{fontSize:8.5,fontWeight:600,width:"45%",paddingRight:4,whiteSpace:"nowrap"}}>{l} :</td>
                      <td style={{fontSize:8.5,fontWeight:700}}>{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>

      {/* BILLED TO / SHIPPED TO */}
      <table style={{width:"100%",borderCollapse:"collapse"}}>
        <tbody>
          <tr>
            <th colSpan={2} style={{...B,padding:"3px 6px",fontSize:9,fontWeight:700,background:"#fff",textAlign:"center"}}>Details of Receiver (Billed To)</th>
            <th colSpan={2} style={{...B,padding:"3px 6px",fontSize:9,fontWeight:700,background:"#fff",textAlign:"center"}}>Details of Consignee (Shipped To)</th>
          </tr>
          <tr>
            <td style={{...B,padding:"6px 8px",width:"50%",verticalAlign:"top",fontSize:9}} colSpan={2}>
              <div style={{marginBottom:3}}><strong>Name :</strong> {party.name||inv.party||""}</div>
              <div style={{marginBottom:3}}><strong>Address :</strong> {[party.address1||party.address,party.address2,party.city,party.state].filter(Boolean).join(", ")}</div>
              <div style={{marginBottom:3}}><strong>State :</strong> {party.state||""} &nbsp;&nbsp; <strong>State Code :</strong> {party.stateCode||""}</div>
              <div style={{marginBottom:3}}><strong>GSTIN No :</strong> {party.gstin||""}</div>
              <div style={{marginBottom:3}}><strong>DL No :</strong> {party.dl1||""}{party.dl2?" / "+party.dl2:""}</div>
              <div style={{marginBottom:3}}><strong>PAN No :</strong> {party.gstin?party.gstin.substring(2,12):""}</div>
              <div><strong>Email :</strong> {party.email||""}</div>
            </td>
            <td style={{...B,padding:"6px 8px",width:"50%",verticalAlign:"top",fontSize:9}} colSpan={2}>
              <div style={{marginBottom:3}}><strong>Name :</strong> {party.name||inv.party||""}</div>
              <div style={{marginBottom:3}}><strong>Address :</strong> {[party.address1||party.address,party.address2,party.city,party.state].filter(Boolean).join(", ")}</div>
              <div style={{marginBottom:3}}><strong>State :</strong> {party.state||""} &nbsp;&nbsp; <strong>State Code :</strong> {party.stateCode||""}</div>
              <div style={{marginBottom:3}}><strong>GSTIN No :</strong> {party.gstin||""}</div>
              <div style={{marginBottom:3}}><strong>DL No :</strong> {party.dl1||""}{party.dl2?" / "+party.dl2:""}</div>
              <div style={{marginBottom:3}}><strong>PAN No :</strong> {party.gstin?party.gstin.substring(2,12):""}</div>
              <div><strong>Phone :</strong> {party.phone||""}</div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* ITEMS TABLE */}
      <table style={{width:"100%",borderCollapse:"collapse",marginTop:0,tableLayout:"fixed"}}>
        <colgroup>
          {isInterState
            ? (isStockist
                ? [18,38,85,36,26,38,28,20,20,28,28,32,44,20,38]
                : [18,38,90,38,28,40,30,22,22,32,34,46,22,38]
              ).map((w,i)=><col key={i} style={{width:w}}/>)
            : (isStockist
                ? [16,34,78,34,24,36,26,18,18,28,28,30,42,18,32,18,32]
                : [16,36,82,36,26,38,28,20,20,30,32,44,20,34,20,34]
              ).map((w,i)=><col key={i} style={{width:w}}/>)
          }
        </colgroup>
        <thead>
          <tr>
            {(isInterState
              ?(isStockist
                ?["S.NO","PRODUCT\nCODE","PRODUCT NAME","HSN","PACK","BATCH","EXP","QTY","FREE","MRP","PTR","RATE","TAXABLE\nVALUE","IGST\n%","IGST\nAMT"]
                :["S.NO","PRODUCT\nCODE","PRODUCT NAME","HSN","PACK","BATCH","EXP","QTY","FREE","MRP","RATE","TAXABLE\nVALUE","IGST\n%","IGST\nAMT"])
              :(isStockist
                ?["S.NO","PRODUCT\nCODE","PRODUCT NAME","HSN","PACK","BATCH","EXP","QTY","FREE","MRP","PTR","RATE","TAXABLE\nVALUE","CGST\n%","CGST\nAMT","SGST\n%","SGST\nAMT"]
                :["S.NO","PRODUCT\nCODE","PRODUCT NAME","HSN","PACK","BATCH","EXP","QTY","FREE","MRP","RATE","TAXABLE\nVALUE","CGST\n%","CGST\nAMT","SGST\n%","SGST\nAMT"])
            ).map((h,i)=>(
              <th key={i} style={{...B,padding:"2px 1px",fontSize:7,fontWeight:700,background:"#fff",color:"#000",textAlign:"center",whiteSpace:"pre-line"}}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(inv.items||[]).map((it,i)=>(
            <tr key={i} style={{background:"#fff"}}>
              <TD style={{textAlign:"center"}}>{i+1}</TD>
              <TD>{it.code||""}</TD>
              <TD style={{fontWeight:600}}>{it.product}</TD>
              <TD style={{textAlign:"center"}}>{it.hsn||""}</TD>
              <TD style={{textAlign:"center"}}>{it.pack||""}</TD>
              <TD style={{textAlign:"center"}}>{it.batch||""}</TD>
              <TD style={{textAlign:"center"}}>{(()=>{if(!it.exp||!it.exp.includes("/"))return it.exp||"";const p=it.exp.split("/");const mn=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];return `${mn[parseInt(p[1])-1]||""}-${(p[2]||"").slice(-2)}`;})()}</TD>
              <TD style={{textAlign:"center",fontWeight:700}}>{it.qty}</TD>
              <TD style={{textAlign:"center"}}>{it.free||0}</TD>
              <TD style={{textAlign:"right"}}>₹{fmt(it.mrp)}</TD>
              {isStockist&&<TD style={{textAlign:"right",color:"#1d4ed8"}}>₹{fmt(it._ptr||0,2)}</TD>}
              <TD style={{textAlign:"right",fontWeight:700}}>₹{fmt(it.rate,2)}</TD>
              <TD style={{textAlign:"right",fontWeight:700}}>₹{fmt(it.taxable,2)}</TD>
              {isInterState?(
                <><TD style={{textAlign:"center"}}>{it.gst||18}%</TD>
                <TD style={{textAlign:"right"}}>₹{fmt(it.igst||((it.cgst||0)+(it.sgst||it.cgst||0)),2)}</TD></>
              ):(
                <><TD style={{textAlign:"center"}}>{it.gst/2||9}%</TD>
                <TD style={{textAlign:"right"}}>₹{fmt(it.cgst,2)}</TD>
                <TD style={{textAlign:"center"}}>{it.gst/2||9}%</TD>
                <TD style={{textAlign:"right"}}>₹{fmt(it.sgst||it.cgst,2)}</TD></>
              )}
            </tr>
          ))}
          {Array.from({length:Math.max(0,8-(inv.items||[]).length)}).map((_,i)=>(
            <tr key={"empty"+i} style={{height:20}}>
              {Array.from({length:isInterState?(isStockist?15:14):(isStockist?17:16)}).map((_,j)=>(
                <td key={j} style={{borderLeft:"1px solid #000",borderRight:"1px solid #000",padding:"2px 4px",fontSize:9}}>&nbsp;</td>
              ))}
            </tr>
          ))}
          <tr style={{background:"#fff",fontWeight:700}}>
            <TD colSpan={7} style={{textAlign:"right",fontWeight:700}}>TOTAL</TD>
            <TD style={{textAlign:"center",fontWeight:700}}>{(inv.items||[]).reduce((s,it)=>s+(it.qty||0),0)}</TD>
            <TD style={{textAlign:"center",fontWeight:700}}>{(inv.items||[]).reduce((s,it)=>s+(it.free||0),0)}</TD>
            <TD></TD>{isStockist&&<TD></TD>}<TD></TD>
            <TD style={{textAlign:"right",fontWeight:700}}>₹{fmt(totalTaxable,2)}</TD>
            <TD></TD>
            <TD style={{textAlign:"right",fontWeight:700}}>₹{fmt(isInterState?totalIGST:totalCGST,2)}</TD>
            {!isInterState&&<><TD></TD><TD style={{textAlign:"right",fontWeight:700}}>₹{fmt(totalSGST,2)}</TD></>}
          </tr>
        </tbody>
      </table>

      {/* SUMMARY SECTION */}
      <table style={{width:"100%",borderCollapse:"collapse"}}>
        <tbody>
          <tr>
            <td style={{...B,padding:"4px",width:"55%",verticalAlign:"top"}}>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead>
                  <tr>
                    {["CLASS","TAXABLE AMT","DISC","SGST","CGST","TOTAL GST"].map(h=>(
                      <th key={h} style={{...B,padding:"2px 4px",fontSize:8,fontWeight:700,background:"#fff",textAlign:"center"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(gstGroups).map(([cls,g])=>(
                    <tr key={cls}>
                      <TD style={{fontWeight:600}}>{cls}</TD>
                      <TD style={{textAlign:"right"}}>₹{fmt(g.taxable,2)}</TD>
                      <TD style={{textAlign:"right"}}>—</TD>
                      {isInterState?(
                        <TD colSpan={3} style={{textAlign:"right",fontWeight:700}}>₹{fmt(g.igst||(g.cgst+g.sgst),2)} (IGST)</TD>
                      ):(
                        <><TD style={{textAlign:"right"}}>₹{fmt(g.sgst,2)}</TD>
                        <TD style={{textAlign:"right"}}>₹{fmt(g.cgst,2)}</TD>
                        <TD style={{textAlign:"right",fontWeight:700}}>₹{fmt(g.cgst+g.sgst,2)}</TD></>
                      )}
                    </tr>
                  ))}
                  <tr style={{fontWeight:700}}>
                    <TD style={{fontWeight:700}}>TOTAL</TD>
                    <TD style={{textAlign:"right",fontWeight:700}}>₹{fmt(totalTaxable,2)}</TD>
                    <TD style={{textAlign:"right"}}>—</TD>
                    {isInterState?(
                      <TD colSpan={3} style={{textAlign:"right",fontWeight:700}}>₹{fmt(totalIGST,2)} (IGST)</TD>
                    ):(
                      <><TD style={{textAlign:"right",fontWeight:700}}>₹{fmt(totalSGST,2)}</TD>
                      <TD style={{textAlign:"right",fontWeight:700}}>₹{fmt(totalCGST,2)}</TD>
                      <TD style={{textAlign:"right",fontWeight:700}}>₹{fmt(totalCGST+totalSGST,2)}</TD></>
                    )}
                  </tr>
                </tbody>
              </table>
            </td>
            <td style={{...B,padding:"6px 8px",width:"45%",verticalAlign:"top"}}>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <tbody>
                  {[
                    ["Taxable Amount",`₹${fmt(totalTaxable,2)}`],
                    isInterState?["IGST",`₹${fmt(totalIGST,2)}`]:null,
                    !isInterState?["CGST",`₹${fmt(totalCGST,2)}`]:null,
                    !isInterState?["SGST",`₹${fmt(totalSGST,2)}`]:null,
                    ["Total GST",`₹${fmt(isInterState?totalIGST:totalCGST+totalSGST,2)}`],
                    ["Round Off",`₹${fmt(grandTotal-(totalTaxable+(isInterState?totalIGST:totalCGST+totalSGST)),2)}`],
                  ].filter(Boolean).map(([l,v])=>(
                    <tr key={l}>
                      <td style={{fontSize:8.5,fontWeight:500,paddingBottom:2}}>{l}</td>
                      <td style={{fontSize:8.5,fontWeight:600,textAlign:"right",paddingBottom:2,fontFamily:"monospace"}}>{v}</td>
                    </tr>
                  ))}
                  <tr style={{borderTop:"2px solid #000"}}>
                    <td style={{fontSize:11,fontWeight:800,paddingTop:4}}>INVOICE TOTAL</td>
                    <td style={{fontSize:13,fontWeight:800,textAlign:"right",paddingTop:4,fontFamily:"monospace"}}>₹{fmt(grandTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>

      {/* AMOUNT IN WORDS */}
      <table style={{width:"100%",borderCollapse:"collapse"}}>
        <tbody>
          <tr>
            <TD colSpan={2} style={{fontWeight:700,fontSize:8.5}}>
              Amount in Words : <span style={{fontWeight:800,textTransform:"uppercase"}}>{toWords(grandTotal)}</span>
            </TD>
          </tr>
        </tbody>
      </table>

      {/* BANK DETAILS + TERMS */}
      <table style={{width:"100%",borderCollapse:"collapse"}}>
        <tbody>
          <tr>
            <td style={{...B,padding:"4px 6px",width:"60%",fontSize:8.5}}>
              <div style={{fontWeight:700,marginBottom:3}}>Bank Details :</div>
              <div><strong>Bank Name :</strong> {settings.bankName||"PUNJAB NATIONAL BANK"}</div>
              <div><strong>Account No :</strong> {settings.bankAcc||""} &nbsp;&nbsp; <strong>IFSC :</strong> {settings.ifsc||""}</div>
              <div><strong>Branch :</strong> {settings.branch||""}</div>
            </td>
            <td style={{...B,padding:"4px 6px",width:"40%",fontSize:8}}>
              <div style={{fontWeight:700,marginBottom:3}}>Terms & Conditions :</div>
              <div>• Transportation service is not our responsibility.</div>
              <div>• Goods once sold cannot be returned unless quality issue.</div>
              <div>• Bills paid after credit period will be charged 5% penalty per day.</div>
              {settings.terms&&<div>• {settings.terms}</div>}
            </td>
          </tr>
        </tbody>
      </table>

      {/* SIGNATURES */}
      <table style={{width:"100%",borderCollapse:"collapse"}}>
        <tbody>
          <tr>
            <td style={{...B,padding:"6px",textAlign:"center",width:"33%",height:50,fontSize:8.5,verticalAlign:"bottom"}}>
              <div>Prepared By</div>
            </td>
            <td style={{...B,padding:"6px",textAlign:"center",width:"34%",height:50,fontSize:8.5,verticalAlign:"bottom"}}>
              <div>Checked By</div>
            </td>
            <td style={{...B,padding:"6px",textAlign:"center",width:"33%",height:50,fontSize:8.5,verticalAlign:"bottom"}}>
              <div style={{fontWeight:700,fontSize:9}}>{settings.companyName||"YAPIDERMA PRIVATE LIMITED"}</div>
              <div>Authorised Signatory</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};



const POModule = ({data,setData,requestDelete}) => {
  const [tab,setTab]=useState("list");
  const [editId,setEditId]=useState(null);
  const emptyF={no:"",date:today(),manufacturer:"",expectedDate:"",items:[{material:"",qty:1,unit:"Pcs",rate:""}],status:"Draft",notes:""};
  const [form,setForm]=useState(emptyF);

  const upItem=(idx,k,v)=>setForm(f=>({...f,items:f.items.map((it,i)=>i===idx?{...it,[k]:v}:it)}));
  const calcItem=it=>({...it,amount:(it.qty||0)*(it.rate||0)});
  const items=form.items.map(calcItem);
  const total=items.reduce((s,i)=>s+(i.amount||0),0);

  const save=()=>{
    if(!form.no) return alert("PO Number required");
    if(editId){
      setData(d=>({...d,purchaseOrders:d.purchaseOrders.map(x=>x.id===editId?{...x,...form,items,total}:x)}));
      setEditId(null);
    } else {
      setData(d=>({...d,purchaseOrders:[...d.purchaseOrders,{id:uid(),...form,items,total,peRef:""}]}));
    }
    setForm(emptyF); setTab("list");
  };
  const updateStatus=(id,status)=>setData(d=>({...d,purchaseOrders:d.purchaseOrders.map(p=>p.id===id?{...p,status}:p)}));
  const del=id=>{if(requestDelete("purchaseOrders",id,"Purchase Order"))setData(d=>({...d,purchaseOrders:d.purchaseOrders.filter(p=>p.id!==id)}));};

  return (
    <div className="fade">
      <PH title="Purchase Orders" sub="Raise POs to manufacturers before purchase entry"
        action={<div style={{display:"flex",gap:8}}>
          <Btn v={tab==="list"?"navy":"outline"} onClick={()=>setTab("list")}>📋 All POs</Btn>
          <Btn v={tab==="new"?"gold":"outline"} onClick={()=>setTab("new")}>+ New PO</Btn>
        </div>}
      />
      {tab==="list"&&(<>
        {/* ── PO Filters ── */}
        <div style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:10,padding:"12px 16px",marginBottom:12}}>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,alignItems:"start"}}>
            <Field label="Manufacturer"><Inp value={pfMfr} onChange={setPfMfr} placeholder="Filter by manufacturer..."/></Field>
            <Field label="Status"><Sel value={pfStatus} onChange={setPfStatus} options={["Draft","Ordered","Partially Received","Received","Cancelled"]} placeholder="All Status"/></Field>
            <Field label="From Date"><DateInp value={pfFrom} onChange={setPfFrom}/></Field>
            <Field label="To Date"><DateInp value={pfTo} onChange={setPfTo}/></Field>
          </div>
          {(pfMfr||pfStatus||pfFrom||pfTo)&&<button onClick={()=>{setPfMfr("");setPfStatus("");setPfFrom("");setPfTo("");}} style={{marginTop:8,fontSize:11,color:"#dc2626",background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>✕ Clear Filters</button>}
        </div>
        <Tbl headers={["PO No","Date","Manufacturer","Expected Date","Total Amount","PE Reference","Status","Action"]}
          onExport={()=>downloadCSV((()=>{const pos=data.purchaseOrders||[];return pos;})().map(p=>[p.no,p.date,p.manufacturer||p.vendor||"",p.expectedDate,p.total,p.peRef||"",p.status]),["PO No","Date","Manufacturer","Expected Date","Total","PE Ref","Status"],"Purchase_Orders")}
          rows={data.purchaseOrders.map(po=>[
            <span className="mono" style={{fontWeight:700,color:"#7c3aed",fontSize:11}}>{po.no}</span>,
            po.date,po.manufacturer||po.vendor||"",
            <span style={{color:C.accent}}>{po.expectedDate}</span>,
            <span className="mono" style={{fontWeight:700}}>₹{fmt(po.total)}</span>,
            <span className="mono" style={{fontSize:11}}>{po.peRef||"—"}</span>,
            <Badge s={po.status}/>,
            <div style={{display:"flex",gap:3}}>
              {po.status==="Draft"&&<Btn sz="sm" v="green" onClick={()=>updateStatus(po.id,"Confirmed")}>Confirm</Btn>}
              {po.status==="Confirmed"&&<Btn sz="sm" v="outline" onClick={()=>updateStatus(po.id,"Fully Received")}>Received</Btn>}
              <Btn sz="sm" v="danger" onClick={()=>del(po.id)}>Del</Btn>
            </div>
          ])}
        />
      </>
      )}
      {tab==="new"&&(
        <Card>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:12,alignItems:"start"}}>
            <Field label="PO Number" req><Inp value={form.no} onChange={v=>setForm(f=>({...f,no:v}))} placeholder="PO-003"/></Field>
            <Field label="PO Date"><DateInp value={form.date} onChange={v=>setForm(f=>({...f,date:v}))}/></Field>
            <Field label="Manufacturer"><Sel value={form.manufacturer} onChange={v=>setForm(f=>({...f,manufacturer:v}))} options={data.manufacturers.map(v=>v.name)} placeholder="Select Manufacturer (Optional)"/></Field>
            <Field label="Expected Delivery Date"><DateInp value={form.expectedDate} onChange={v=>setForm(f=>({...f,expectedDate:v}))}/></Field>
          </div>
          <div style={{borderTop:`1px solid ${C.border}`,paddingTop:12,marginBottom:12}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
              <div style={{fontSize:13,fontWeight:700,color:C.navy}}>Items Ordered</div>
              <Btn sz="sm" v="outline" onClick={()=>setForm(f=>({...f,items:[...f.items,{material:"",qty:1,unit:"Pcs",rate:""}]}))}>+ Add Row</Btn>
            </div>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead><tr style={{background:C.sectionBg}}>
                {["#","Material / Item","Qty","Unit","Rate (₹)","Amount",""].map((h,i)=>(
                  <th key={i} style={{padding:"6px 8px",fontWeight:600,fontSize:10,color:C.muted,textAlign:"left"}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>{form.items.map((it,idx)=>{
                const ci=calcItem(it);
                return <tr key={idx} style={{borderBottom:`1px solid ${C.border}`}}>
                  <td style={{padding:"3px 8px",color:C.muted,fontWeight:600}}>{idx+1}</td>
                  <td style={{padding:"3px 3px",minWidth:180}}><Inp value={it.material} onChange={v=>upItem(idx,"material",v)} placeholder="Item name"/></td>
                  <td style={{padding:"3px 3px",width:70}}><Inp value={it.qty} onChange={v=>upItem(idx,"qty",Number(v))} type="number"/></td>
                  <td style={{padding:"3px 3px",width:80}}><Inp value={it.unit} onChange={v=>upItem(idx,"unit",v)} placeholder="Kg / Pcs"/></td>
                  <td style={{padding:"3px 3px",width:90}}><Inp value={it.rate} onChange={v=>upItem(idx,"rate",Number(v))} type="number"/></td>
                  <td className="mono" style={{padding:"3px 8px",fontWeight:700}}>₹{fmt(ci.amount,2)}</td>
                  <td><button onClick={()=>setForm(f=>({...f,items:f.items.filter((_,i)=>i!==idx)}))} style={{background:"none",border:"none",color:"#dc2626",cursor:"pointer",fontSize:16}}>×</button></td>
                </tr>;
              })}</tbody>
            </table>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:12}}>
            <Field label="Notes" style={{width:300}}><Inp value={form.notes} onChange={v=>setForm(f=>({...f,notes:v}))} placeholder="Notes to manufacturer"/></Field>
            <div style={{background:C.navy,color:"#fff",borderRadius:10,padding:"12px 20px"}}>
              <div style={{opacity:.8,fontSize:11,marginBottom:3}}>PO TOTAL</div>
              <div className="mono" style={{fontSize:22,fontWeight:800}}>₹{fmt(total)}</div>
            </div>
          </div>
          <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:14}}>
            <Btn v="outline" onClick={()=>setTab("list")}>Cancel</Btn>
            <Btn v="outline" onClick={()=>{save();setForm(f=>({...f,status:"Draft"}));}}>Save as Draft</Btn>
            <Btn v="gold" onClick={()=>{setForm(f=>({...f,status:"Confirmed"}));save();}}>✅ Confirm PO</Btn>
          </div>
        </Card>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// SALES INVOICES
// ══════════════════════════════════════════════════════════════

const InvoiceModule = ({data,setData,setPrintInv:globalSetPrintInv,requestDelete,prefillOrder,onPrefillDone}) => {
  const parties=data.parties||[];
  const products=data.products||[];
  const {show:showAmts} = useAmt();
  const [tab,setTab]=useState(prefillOrder?"new":"list");
  const [editInvId,setEditInvId]=useState(null);
  const setPrintInv = globalSetPrintInv || (()=>{});

  const nextInvNo=()=>{
    const existing=data.invoices.map(i=>i.no).filter(n=>n&&n.startsWith("YAP/SALE/"));
    if(!existing.length) return "YAP/SALE/0435";
    const nums=existing.map(n=>parseInt(n.replace("YAP/SALE/",""))||0);
    return "YAP/SALE/"+String(nums.reduce((a,b)=>a>b?a:b,0)+1).padStart(4,"0");
  };

  const emptyF=()=>({no:nextInvNo(),date:today(),party:"",partyId:"",doctor:"",channel:"Doctor Prescription",transport:"",vehicleNo:"",orderDate:today(),dueDate:"",notes:"",mrId:"",asmId:"",sourceOrderId:"",items:[{product:"",batch:"",qty:1,free:0,mrp:"",rate:"",gst:18,hsn:"",pack:"",exp:"",code:""}]});

  const prefillFromOrder=(o)=>{
    if(!o) return emptyF();
    const party=parties.find(p=>p.id===o.partyId);
    const cd=Number(party?.creditDays||0);
    let dueDate="";
    if(cd>0){const d=new Date();d.setDate(d.getDate()+cd);dueDate=`${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;}
    return {
      no:nextInvNo(), date:today(),
      party:party?.name||o.partyName||"",
      partyId:o.partyId||"",
      doctor:o.doctorName||"",
      channel:"Doctor Prescription",
      transport:"",vehicleNo:"",
      orderDate:o.date||today(),
      dueDate,
      notes:`Order: ${o.no} | By: ${o.mrName||""}${o.notes?" | "+o.notes:""}`,
      mrId:o.mrId||"",
      asmId:o.asmId||o.mrId||"",
      sourceOrderId:o.id||"",
      items:(o.items||[]).map(it=>{
        const prod=products.find(x=>x.name===it.product)||{};
        return {
          product:it.product||"",batch:"",
          qty:Number(it.qty)||1,
          free:Number(it.free)||0,
          mrp:prod.mrp||"",
          rate:"",  // billing will fill rate
          gst:prod.gst||18,
          hsn:prod.hsn||"",
          pack:prod.pack||"",
          exp:"",code:prod.code||""
        };
      })
    };
  };

  const [form,setForm]=useState(()=>prefillOrder?prefillFromOrder(prefillOrder):emptyF());

  // Re-seed the form whenever a NEW prefill order arrives (e.g. user clicks
  // "Convert to Invoice" on a second order while the module is already mounted).
  // useState lazy initialiser only runs at mount — this effect handles subsequent changes.
  const prevPrefillId = useRef(prefillOrder?.id ?? null);
  useEffect(()=>{
    if(prefillOrder && prefillOrder.id !== prevPrefillId.current){
      prevPrefillId.current = prefillOrder.id;
      setForm(prefillFromOrder(prefillOrder));
      setTab("new");
      setEditInvId(null);
    }
  // prefillFromOrder is stable within a render; include prefillOrder as dep.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[prefillOrder]);
  // Filters
  const [fParty,setFParty]=useState("");
  const [fDoctor,setFDoctor]=useState("");
  const [fStatus,setFStatus]=useState("");
  const [fChannel,setFChannel]=useState("");
  const [fFrom,setFFrom]=useState("");
  const [fTo,setFTo]=useState("");
  const [fProduct,setFProduct]=useState("");
  const [fSearch,setFSearch]=useState("");
  const [fState,setFState]=useState("");
  const [fRegion,setFRegion]=useState("");
  const [showFilters,setShowFilters]=useState(false);

  const calcItem=(it,pState)=>{
    const p=products.find(x=>x.name===it.product)||{gst:18,mrp:0};
    const taxable=(it.qty||0)*(it.rate||0);
    const gstRate=it.gst||p.gst||18;
    const compState=(data.settings?.state||"").trim().toLowerCase();
    const partyState=(pState||"").trim().toLowerCase();
    const inter=partyState&&compState&&partyState!==compState;
    const cgst=inter?0:taxable*gstRate/200;
    const igst=inter?taxable*gstRate/100:0;
    return {...it,gst:gstRate,mrp:it.mrp||p.mrp,taxable,cgst,sgst:cgst,igst};
  };
  const partyObj=parties.find(p=>p.name===form.party)||{};
  const items=form.items.map(it=>calcItem(it,partyObj.state));
  const taxable=items.reduce((s,i)=>s+(i.taxable||0),0);
  const cgst=items.reduce((s,i)=>s+(i.cgst||0),0);
  const igst=items.reduce((s,i)=>s+(i.igst||0),0);
  const total=Math.round(taxable+cgst*2+igst);
  const upItem=(idx,k,v)=>setForm(f=>({...f,items:f.items.map((it,i)=>i===idx?{...it,[k]:v}:it)}));

  const save=()=>{
    if(!form.no) return alert("Invoice No required");
    if(!form.party&&!form.billDoctor) return alert("Party or Doctor required");
    // ── WORKFLOW: Stock availability check ──
    const _stockWarn=[];
    (items||[]).forEach(it=>{
      if(!it.product||!it.batch) return;
      const _b=(data.batches||[]).find(x=>x.product===it.product&&x.batch===it.batch);
      if(!_b){_stockWarn.push(it.product+" / Batch "+it.batch+": not in stock");return;}
      const _avail=(_b.qty||0)-(_b.out||0)-(_b.damaged||0);
      if(_avail<(Number(it.qty)||0)) _stockWarn.push(it.product+" ("+it.batch+"): only "+_avail+" units available, billing "+it.qty);
    });
    if(_stockWarn.length>0&&!window.confirm("⚠️ Stock Warning:\n\n"+_stockWarn.join("\n")+"\n\nProceed anyway?")) return;
    const finalParty=form.billTo==="Doctor"?(form.billDoctor?"Dr. "+form.billDoctor:form.party):form.party;
    // ── EDIT MODE ──
    if(editInvId){
      setData(d=>{
        // 1. Reverse the OLD item stock-out before applying the new lines.
        //    Without this, every edit compounds the batch.out count.
        const oldInv=d.invoices.find(i=>i.id===editInvId);
        let batches=[...d.batches];
        (oldInv?.items||[]).forEach(it=>{
          if(!it.product||!it.batch) return;
          const idx=batches.findIndex(b=>b.product===it.product&&b.batch===it.batch);
          if(idx>=0){
            const reversalQty=Number(it.qty||0)+Number(it.free||0);
            batches[idx]={...batches[idx],out:Math.max(0,(batches[idx].out||0)-reversalQty)};
          }
        });
        // 2. Apply the NEW item stock-out.
        (items||[]).forEach(it=>{
          if(!it.product||!it.batch) return;
          const idx=batches.findIndex(b=>b.product===it.product&&b.batch===it.batch);
          if(idx>=0){
            const outQty=Number(it.qty||0)+Number(it.free||0);
            batches[idx]={...batches[idx],out:(batches[idx].out||0)+outQty};
          }
        });
        return {...d,
          invoices:d.invoices.map(i=>
            i.id===editInvId
              ?{...i,...form,party:finalParty,items,taxable,cgst,sgst:cgst,igst,total}
              :i
          ),
          batches
        };
      });
      setEditInvId(null); setForm(emptyF()); setTab("list");
      return;
    }
    // ── CREATE MODE ──
    const newInv={
      id:uid(),...form,party:finalParty,items,taxable,cgst,sgst:cgst,igst,total,paid:0,status:"Current",
      mrId:form.mrId||"",asmId:form.asmId||""
    };
    setData(d=>{
      let batches=[...d.batches];
      (items||[]).forEach(it=>{
        if(!it.product||!it.batch) return;
        const idx=batches.findIndex(b=>b.product===it.product&&b.batch===it.batch);
        if(idx>=0){
          // Free qty is a real stock movement — include it in OUT
          const outQty=Number(it.qty||0)+Number(it.free||0);
          batches[idx]={...batches[idx],out:(batches[idx].out||0)+outQty};
        }
      });
      // Mark source order as Completed
      let mrOrders=d.mrOrders;
      if(form.sourceOrderId){
        mrOrders=d.mrOrders.map(o=>o.id===form.sourceOrderId?{...o,status:"Completed",invoiceNo:newInv.no}:o);
      }
      return {...d,
        invoices:[...d.invoices,newInv],
        batches, mrOrders
      };
    });
    if(onPrefillDone) onPrefillDone(newInv);
    setForm(emptyF()); setTab("list");
  };
  const del=id=>{if(requestDelete("invoices",id,"Invoice"))setData(d=>{
    const inv=d.invoices.find(i=>i.id===id);
    let batches=[...d.batches];
    (inv?.items||[]).forEach(it=>{
      if(!it.product||!it.batch) return;
      const idx=batches.findIndex(b=>b.product===it.product&&b.batch===it.batch);
      // Reverse both sold qty AND free qty — both were deducted from stock on create
      if(idx>=0) batches[idx]={...batches[idx],out:Math.max(0,(batches[idx].out||0)-Number(it.qty||0)-Number(it.free||0))};
    });
    return {...d,invoices:d.invoices.filter(i=>i.id!==id),batches};
  });};
  const markPaid=id=>setData(d=>{
    // Create a real payment entry so ledgers stay consistent.
    // Simply flipping status without a payment record causes MIS/bank mismatches.
    const inv=d.invoices.find(i=>i.id===id);
    if(!inv) return d;
    const balance=Math.max(0,inv.total-(inv.paid||0));
    if(balance<=0) return {...d,invoices:d.invoices.map(i=>i.id===id?{...i,status:"Paid"}:i)};
    const syntheticPayment={
      id:uid(),no:"ADJ-"+Date.now(),date:today(),
      party:inv.party,invoiceNo:inv.no,
      amount:balance,mode:"Adjustment",
      utr:"Mark-Paid",bank:"",
      note:"Marked Paid via Invoice list"
    };
    const updatedPayments=[...d.payments,syntheticPayment];
    const updatedInvoices=d.invoices.map(i=>i.id===id?{...i,paid:i.total,status:"Paid"}:i);
    return {...d,payments:updatedPayments,invoices:updatedInvoices};
  });
  const cancelInv=id=>{if(window.confirm("Cancel this invoice? Party has refused delivery."))setData(d=>{
    const inv=d.invoices.find(i=>i.id===id);
    let batches=[...d.batches];
    (inv?.items||[]).forEach(it=>{
      if(!it.product||!it.batch) return;
      const idx=batches.findIndex(b=>b.product===it.product&&b.batch===it.batch);
      // Reverse both sold qty AND free qty
      if(idx>=0) batches[idx]={...batches[idx],out:Math.max(0,(batches[idx].out||0)-Number(it.qty||0)-Number(it.free||0))};
    });
    return {...d,invoices:d.invoices.map(i=>i.id===id?{...i,status:"Cancelled",cancelledAt:new Date().toLocaleDateString("en-IN")}:i),batches};
  });};


  return (
    <div className="fade">
      <PH title="Sales Invoices" sub="Create and manage all GST invoices with channel tracking"
        action={<div style={{display:"flex",gap:8}}>
          <Btn v={tab==="list"?"navy":"outline"} onClick={()=>setTab("list")}>📋 All Invoices</Btn>
          <Btn v={tab==="new"?"gold":"outline"} onClick={()=>{setForm(emptyF());setEditInvId(null);setTab("new");}}>+ New Invoice</Btn>
        </div>}
      />
      {prefillOrder&&tab==="new"&&(
        <div style={{background:"#dbeafe",border:"1px solid #93c5fd",borderRadius:8,padding:"10px 16px",marginBottom:14,fontSize:13,color:"#1e40af",fontWeight:600}}>
          📋 Pre-filled from Order <strong>{prefillOrder.no}</strong> by {prefillOrder.mrName} — Fill batch numbers and rates, then save.
        </div>
      )}
      {tab==="list"&&(()=>{
        const parseD=s=>{if(!s||!s.includes("/"))return null;const[dd,mm,yyyy]=s.split("/");return new Date(+yyyy,+mm-1,+dd);};
        const fromD=parseD(fFrom),toD=parseD(fTo);
        const filteredInvs=sortByInvNo(data.invoices).filter(inv=>{
          if(fParty&&inv.party!==fParty) return false;
          if(fDoctor&&inv.doctor!==fDoctor) return false;
          if(fStatus&&getInvStatus(inv,parties)!==fStatus) return false;
          if(fChannel&&inv.channel!==fChannel) return false;
          if(fProduct&&!(inv.items||[]).some(it=>it.product===fProduct)) return false;
          if(fSearch){const q=fSearch.toLowerCase();if(!inv.no.toLowerCase().includes(q)&&!inv.party.toLowerCase().includes(q)&&!(inv.doctor||"").toLowerCase().includes(q)&&!(inv.no||"").toLowerCase().includes(q)) return false;}
          const d=parseD(inv.date);
          if(fromD&&d&&d<fromD) return false;
          if(toD&&d&&d>toD) return false;
          // State filter — look up party
          if(fState){const p=parties.find(x=>x.name===inv.party);if(!p||p.state!==fState) return false;}
          // Region filter — look up party
          if(fRegion){const p=parties.find(x=>x.name===inv.party);if(!p||p.region!==fRegion) return false;}
          return true;
        });
        const fTotal=filteredInvs.reduce((s,i)=>s+i.total,0);
        const fPaid=filteredInvs.reduce((s,i)=>s+i.paid,0);
        const fBal=filteredInvs.reduce((s,i)=>s+(i.total-i.paid),0);
        const clearFilters=()=>{setFParty("");setFDoctor("");setFStatus("");setFChannel("");setFFrom("");setFTo("");setFProduct("");setFSearch("");setFState("");setFRegion("");};
        const hasFilter=fParty||fDoctor||fStatus||fChannel||fFrom||fTo||fProduct||fSearch||fState||fRegion;

        // Dropdown options
        const allDoctors=[...new Set(data.invoices.map(i=>i.doctor).filter(Boolean))].sort();
        const allStates=[...new Set(parties.map(p=>p.state).filter(Boolean))].sort();
        const allRegions=[...new Set(parties.map(p=>p.region).filter(Boolean))].sort();

        // Status config
        const STATUS_CFG={
          Paid:    {bg:"#D1FAE5",fg:"#065F46",dot:"#10B981"},
          Partial: {bg:"#FEF3C7",fg:"#92400E",dot:"#F59E0B"},
          Current: {bg:"#DBEAFE",fg:"#1E40AF",dot:"#3B82F6"},
          Overdue: {bg:"#FEE2E2",fg:"#991B1B",dot:"#EF4444"},
          Cancelled:{bg:"#F3F4F6",fg:"#6B7280",dot:"#9CA3AF"},
        };

        return (
          <>
            {/* KPI Summary Row */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}}>
              {(()=>{const H=<span style={{letterSpacing:4,opacity:.35,fontSize:18}}>••••••</span>;return[
                {label:"Total Invoiced",val:showAmts?`₹${fmt(data.invoices.reduce((s,i)=>s+i.total,0))}`:H,sub:`${data.invoices.length} invoices`,bg:"#EFF6FF",fg:"#1E40AF",icon:"🧾"},
                {label:"Collected",val:showAmts?`₹${fmt(data.invoices.reduce((s,i)=>s+i.paid,0))}`:H,sub:`${data.invoices.filter(i=>i.status==="Paid").length} paid`,bg:"#F0FDF4",fg:"#166534",icon:"✅"},
                {label:"Outstanding",val:showAmts?`₹${fmt(data.invoices.reduce((s,i)=>s+(i.total-i.paid),0))}`:H,sub:`${data.invoices.filter(i=>getInvStatus(i,parties)==="Overdue").length} overdue`,bg:"#FFF7ED",fg:"#C2410C",icon:"⚠️"},
                {label:"This Month",val:showAmts?`₹${fmt((()=>{const cm=new Date().getMonth()+1,cy=new Date().getFullYear();return data.invoices.filter(i=>{const p=i.date?.split("/");return p&&+p[1]===cm&&+p[2]===cy;}).reduce((s,i)=>s+i.total,0);})())}`:H,sub:`${(()=>{const cm=new Date().getMonth()+1,cy=new Date().getFullYear();return data.invoices.filter(i=>{const p=i.date?.split("/");return p&&+p[1]===cm&&+p[2]===cy;}).length;})()}`,bg:"#F5F3FF",fg:"#6D28D9",icon:"📅"},
              ];})().map(({label,val,sub,bg,fg,icon})=>(
                <div key={label} style={{
                  background:bg,borderRadius:18,padding:"18px 20px 14px",
                  boxShadow:"0 2px 10px rgba(0,0,0,0.05)",
                  position:"relative",overflow:"hidden",border:"none"
                }}>
                  {/* Icon top right */}
                  <div style={{position:"absolute",top:14,right:14,width:40,height:40,borderRadius:"50%",background:fg+"18",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>{icon}</div>
                  <div style={{fontSize:10,fontWeight:700,color:fg,textTransform:"uppercase",letterSpacing:1.1,opacity:.75,marginBottom:10}}>{label}</div>
                  <div style={{fontSize:24,fontWeight:800,color:fg,letterSpacing:"-.03em",fontFamily:"monospace",marginBottom:4}}>{val}</div>
                  <div style={{fontSize:11,color:fg,opacity:.6,fontWeight:500}}>{sub}</div>
                </div>
              ))}
            </div>

            {/* Status Filter Pills */}
            <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
              {["All","Current","Paid","Partial","Overdue","Cancelled"].map(s=>{
                const cfg=STATUS_CFG[s]||{bg:"#EFF6FF",fg:"#1E40AF"};
                const cnt=s==="All"?data.invoices.length:data.invoices.filter(i=>getInvStatus(i,parties)===s).length;
                const active=fStatus===s||(s==="All"&&!fStatus);
                return (
                  <button key={s} onClick={()=>setFStatus(s==="All"?"":s)}
                    style={{padding:"6px 14px",borderRadius:20,border:`1.5px solid ${active?cfg.fg:"#E5E7EB"}`,
                      background:active?cfg.bg:"#fff",color:active?cfg.fg:"#6B7280",
                      fontSize:11,fontWeight:active?700:400,cursor:"pointer",fontFamily:"inherit",
                      display:"flex",alignItems:"center",gap:5,transition:"all .15s"}}>
                    {s!=="All"&&<span style={{width:7,height:7,borderRadius:"50%",background:cfg.dot,display:"inline-block"}}/>}
                    {s} <span style={{background:active?cfg.fg+"22":"#F3F4F6",color:active?cfg.fg:"#9CA3AF",borderRadius:10,padding:"0 6px",fontSize:10,fontWeight:700}}>{cnt}</span>
                  </button>
                );
              })}
              <div style={{flex:1}}/>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <input value={fSearch} onChange={e=>setFSearch(e.target.value)}
                  placeholder="🔍 Search invoice, party..."
                  style={{padding:"7px 14px",borderRadius:20,border:"1.5px solid #E5E7EB",fontSize:11,outline:"none",width:220,fontFamily:"inherit"}}/>
                <button onClick={()=>setShowFilters(s=>!s)}
                  style={{padding:"7px 14px",borderRadius:20,border:`1.5px solid ${showFilters?"#3B82F6":"#E5E7EB"}`,
                    background:showFilters?"#EFF6FF":"#fff",fontSize:11,cursor:"pointer",fontFamily:"inherit",
                    color:showFilters?"#1E40AF":"#6B7280",fontWeight:showFilters?700:400,display:"flex",alignItems:"center",gap:5}}>
                  🔽 Filters {hasFilter&&<span style={{background:"#EF4444",color:"#fff",borderRadius:10,padding:"1px 6px",fontSize:9,fontWeight:800}}>{[fParty,fDoctor,fState,fRegion,fChannel,fProduct,fFrom,fTo].filter(Boolean).length}</span>}
                </button>
                {hasFilter&&<button onClick={clearFilters} style={{fontSize:11,color:"#EF4444",background:"#FEE2E2",border:"none",borderRadius:20,padding:"6px 12px",cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>✕ Clear</button>}
                <button onClick={()=>downloadCSV(filteredInvs.map(i=>{const p=parties.find(x=>x.name===i.party)||{};return[i.no,i.date,i.party,p.state||"",p.region||"",i.doctor||"",i.channel,i.taxable,i.cgst,i.sgst,i.total,i.paid,i.total-i.paid,getInvStatus(i,parties)];}),["Invoice No","Date","Party","State","Region","Doctor","Channel","Taxable","CGST","SGST","Total","Paid","Balance","Status"],"Sales_Invoices")}
                  style={{padding:"7px 14px",borderRadius:20,border:"1.5px solid #E5E7EB",background:"#fff",fontSize:11,cursor:"pointer",fontFamily:"inherit",color:"#6B7280"}}>
                  ⬇️ Export
                </button>
              </div>
            </div>

            {/* Advanced Filters — always accessible via toggle */}
            {showFilters&&(
              <div style={{background:"#F8FAFC",borderRadius:12,padding:"14px 16px",marginBottom:14,border:"1px solid #E5E7EB"}}>
                <div style={{fontSize:11,fontWeight:700,color:"#6B7280",marginBottom:12,textTransform:"uppercase",letterSpacing:.5}}>🔍 Advanced Filters</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:10}}>
                  <Field label="Party">
                    <Sel value={fParty} onChange={setFParty} options={parties.map(p=>p.name)} placeholder="All Parties"/>
                  </Field>
                  <Field label="Doctor">
                    <Sel value={fDoctor} onChange={setFDoctor} options={allDoctors} placeholder="All Doctors"/>
                  </Field>
                  <Field label="State">
                    <Sel value={fState} onChange={setFState} options={allStates} placeholder="All States"/>
                  </Field>
                  <Field label="Region">
                    <Sel value={fRegion} onChange={setFRegion} options={allRegions} placeholder="All Regions"/>
                  </Field>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,alignItems:"start"}}>
                  <Field label="Channel">
                    <Sel value={fChannel} onChange={setFChannel} options={CHANNELS} placeholder="All Channels"/>
                  </Field>
                  <Field label="Product">
                    <Sel value={fProduct} onChange={setFProduct} options={products.map(p=>p.name)} placeholder="Any Product"/>
                  </Field>
                  <Field label="From Date"><DateInp value={fFrom} onChange={setFFrom}/></Field>
                  <Field label="To Date"><DateInp value={fTo} onChange={setFTo}/></Field>
                </div>
              </div>
            )}

            {/* Invoice Summary when filtered */}
            {hasFilter&&(
              <div style={{display:"flex",gap:16,marginBottom:12,padding:"10px 14px",background:"#EFF6FF",borderRadius:8,fontSize:12,flexWrap:"wrap"}}>
                <span style={{color:"#1E40AF",fontWeight:600}}>{filteredInvs.length} invoices found</span>
                <span style={{fontWeight:700,color:"#1E3A8A"}}>{showAmts?`₹${fmt(fTotal)} total`:<span style={{letterSpacing:3,opacity:.4}}>••••••</span>}</span>
                <span style={{color:"#166534",fontWeight:700}}>{showAmts?`₹${fmt(fPaid)} collected`:<span style={{letterSpacing:3,opacity:.4}}>••••••</span>}</span>
                <span style={{color:"#DC2626",fontWeight:700}}>{showAmts?`₹${fmt(fBal)} pending`:<span style={{letterSpacing:3,opacity:.4}}>••••••</span>}</span>
              </div>
            )}

            {/* Invoice Cards */}
            {filteredInvs.length===0&&(
              <div style={{textAlign:"center",padding:60,background:"#F9FAFB",borderRadius:14,color:"#9CA3AF"}}>
                <div style={{fontSize:36,marginBottom:8}}>🧾</div>
                <div style={{fontWeight:600,fontSize:14}}>No invoices found</div>
                <div style={{fontSize:12,marginTop:4}}>Try clearing filters or create a new invoice</div>
              </div>
            )}
            {/* Invoice Table — clean professional */}
            <div style={{background:"#fff",borderRadius:14,border:"1px solid #F0F2F8",overflow:"hidden",boxShadow:"0 1px 4px rgba(0,0,0,0.04)"}}>
              {/* Table header */}
              <div style={{display:"grid",gridTemplateColumns:"minmax(160px,1.8fr) minmax(80px,0.9fr) minmax(130px,1.4fr) minmax(100px,0.9fr) minmax(80px,0.9fr) minmax(80px,0.8fr) minmax(80px,0.8fr) 152px",gap:0,background:"#F8FAFC",borderBottom:"1.5px solid #EAECF5",padding:"10px 20px",alignItems:"center"}}>
                {["Invoice No","Date","Party / Doctor","Channel","Amount","Paid","Balance","Actions"].map((h,i)=>(
                  <div key={h} style={{fontSize:10,fontWeight:700,color:"#8A9BB5",textTransform:"uppercase",letterSpacing:.9,textAlign:i>=4&&i<=6?"right":i===7?"center":"left",paddingRight:i>=4&&i<=6?2:0}}>{h}</div>
                ))}
              </div>
              {/* Rows */}
              {filteredInvs.length===0&&(
                <div style={{textAlign:"center",padding:"48px 0",color:"#B0BAD0"}}>
                  <div style={{fontSize:32,marginBottom:8}}>🧾</div>
                  <div style={{fontWeight:600,fontSize:13,color:"#8A9BB5"}}>No invoices found</div>
                  <div style={{fontSize:11,marginTop:4,color:"#B0BAD0"}}>Try clearing filters or create a new invoice</div>
                </div>
              )}
              {filteredInvs.map((inv,idx)=>{
                const status=getInvStatus(inv,parties);
                const cfg=STATUS_CFG[status]||STATUS_CFG.Current;
                const balance=inv.total-inv.paid;
                return (
                  <div key={inv.id} style={{
                    display:"grid",gridTemplateColumns:"minmax(160px,1.8fr) minmax(80px,0.9fr) minmax(130px,1.4fr) minmax(100px,0.9fr) minmax(80px,0.9fr) minmax(80px,0.8fr) minmax(80px,0.8fr) 152px",
                    gap:0,padding:"12px 20px",
                    borderBottom:idx<filteredInvs.length-1?"1px solid #F4F6FB":"none",
                    background:"#fff",
                    transition:"background .12s",
                    alignItems:"center",
                  }}
                  onMouseEnter={e=>e.currentTarget.style.background="#FAFBFF"}
                  onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                    {/* Invoice No + status badge */}
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontFamily:"monospace",fontWeight:700,fontSize:12,color:"#1E40AF"}}>{inv.no}</span>
                      <span style={{
                        background:cfg.bg,color:cfg.fg,
                        padding:"2px 8px",borderRadius:20,
                        fontSize:9,fontWeight:700,
                        display:"flex",alignItems:"center",gap:3,flexShrink:0,
                      }}>
                        <span style={{width:5,height:5,borderRadius:"50%",background:cfg.dot,display:"inline-block"}}/>
                        {status}
                      </span>
                    </div>
                    {/* Date */}
                    <div style={{fontSize:12,color:"#6B7280",display:"flex",alignItems:"center"}}>{inv.date}</div>
                    {/* Party */}
                    <div style={{display:"flex",flexDirection:"column",justifyContent:"center",minWidth:0}}>
                      <span style={{fontSize:12,fontWeight:600,color:"#111827",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{inv.party}</span>
                      {inv.doctor&&<span style={{fontSize:10,color:"#7C3AED",marginTop:1}}>👨‍⚕️ {inv.doctor}</span>}
                    </div>
                    {/* Channel */}
                    <div style={{display:"flex",alignItems:"center"}}>
                      <span style={{fontSize:10,color:"#374151",background:"#F1F5F9",padding:"2px 8px",borderRadius:6,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:110}}>{inv.channel||"—"}</span>
                    </div>
                    {/* Amount */}
                    <div style={{fontFamily:"monospace",fontWeight:700,fontSize:13,color:"#111827",display:"flex",alignItems:"center",justifyContent:"flex-end"}}>
                      {showAmts?`₹${fmt(inv.total)}`:<span style={{letterSpacing:3,opacity:.35}}>•••••</span>}
                    </div>
                    {/* Paid */}
                    <div style={{fontFamily:"monospace",fontSize:12,color:"#166534",fontWeight:600,display:"flex",alignItems:"center",justifyContent:"flex-end"}}>
                      {showAmts?`₹${fmt(inv.paid)}`:<span style={{letterSpacing:3,opacity:.35}}>•••••</span>}
                    </div>
                    {/* Balance */}
                    <div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:balance>0?"#DC2626":"#166534",display:"flex",alignItems:"center",justifyContent:"flex-end"}}>
                      {showAmts?(balance>0?`₹${fmt(balance)}`:"—"):<span style={{letterSpacing:3,opacity:.35}}>•••••</span>}
                    </div>
                    {/* Actions */}
                    <div style={{display:"flex",gap:4,alignItems:"center",justifyContent:"center",paddingLeft:8}}>
                      <button onClick={()=>setPrintInv(inv)} title="Print"
                        style={{width:28,height:28,borderRadius:7,border:"1px solid #E5E7EB",background:"#fff",cursor:"pointer",fontSize:13,display:"flex",alignItems:"center",justifyContent:"center"}}>🖨️</button>
                      <button onClick={()=>{const p=parties.find(x=>x.name===inv.party);setForm({...inv,billTo:"Party",partyId:inv.partyId||p?.id||"",billDoctor:""});setEditInvId(inv.id);setTab("new");}} title="Edit"
                        style={{width:28,height:28,borderRadius:7,border:"1px solid #E5E7EB",background:"#fff",cursor:"pointer",fontSize:13,display:"flex",alignItems:"center",justifyContent:"center"}}>✏️</button>
                      {inv.status!=="Paid"&&inv.status!=="Cancelled"&&(
                        <button onClick={()=>markPaid(inv.id)} title="Mark Paid"
                          style={{height:28,padding:"0 10px",borderRadius:7,border:"1px solid #A7F3D0",background:"#ECFDF5",cursor:"pointer",fontSize:11,fontWeight:700,color:"#065F46",fontFamily:"inherit"}}>✓</button>
                      )}
                      {inv.status!=="Cancelled"&&(
                        <button onClick={()=>cancelInv(inv.id)} title="Cancel"
                          style={{width:28,height:28,borderRadius:7,border:"1px solid #FECACA",background:"#FEF2F2",cursor:"pointer",fontSize:13,color:"#DC2626",display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
                      )}
                      <button onClick={()=>{if(requestDelete("invoices",inv.id,inv.no))setData(d=>{const ii=d.invoices.find(i=>i.id===inv.id);let b=[...d.batches];(ii?.items||[]).forEach(it=>{if(!it.product||!it.batch)return;const ix=b.findIndex(x=>x.product===it.product&&x.batch===it.batch);if(ix>=0)b[ix]={...b[ix],out:Math.max(0,(b[ix].out||0)-Number(it.qty||0))};});return{...d,invoices:d.invoices.filter(i=>i.id!==inv.id),batches:b};});}} title="Delete"
                        style={{width:28,height:28,borderRadius:7,border:"1px solid #E5E7EB",background:"#fff",cursor:"pointer",fontSize:12,color:"#9CA3AF",display:"flex",alignItems:"center",justifyContent:"center"}}>🗑️</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        );
      })()}
      {tab==="new"&&(
        <Card>
          {/* Bill To Toggle */}
          <div style={{display:"flex",gap:8,marginBottom:14,alignItems:"center"}}>
            <span style={{fontSize:12,fontWeight:600,color:C.muted}}>Bill To:</span>
            {["Party","Doctor"].map(t=>(
              <button key={t} onClick={()=>setForm(f=>({...f,billTo:t,party:t==="Party"?f.party:"",billDoctor:t==="Doctor"?f.billDoctor:""})) }
                style={{padding:"5px 16px",borderRadius:20,border:`1.5px solid ${(form.billTo||"Party")===t?C.navy:C.border}`,
                  background:(form.billTo||"Party")===t?C.navy:"#fff",
                  color:(form.billTo||"Party")===t?"#fff":C.text,
                  fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
                {t==="Party"?"🏢 Party / Chemist":"👨‍⚕️ Doctor"}
              </button>
            ))}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:12,alignItems:"start"}}>
            <Field label="Invoice No" req><Inp value={form.no} onChange={v=>setForm(f=>({...f,no:v}))} placeholder="YAP/SALE/0435"/></Field>
            <Field label="Invoice Date" req><DateInp value={form.date} onChange={v=>setForm(f=>({...f,date:v}))}/></Field>
            <Field label="Order Date"><DateInp value={form.orderDate||form.date} onChange={v=>setForm(f=>({...f,orderDate:v}))}/></Field>
            {(form.billTo||"Party")==="Party" ? (
              <Field label="Party" req>
                <Sel value={form.party} onChange={v=>{
                  const p=parties.find(x=>x.name===v);
                  const cd=Number(p?.creditDays||0);
                  let dueDate="";
                  if(cd>0){const d=new Date();d.setDate(d.getDate()+cd);dueDate=`${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;}
                  setForm(f=>({...f,party:v,partyId:(parties.find(p=>p.name===v)||{}).id||"",dueDate:dueDate||f.dueDate}));
                }} options={parties.map(p=>p.name)} placeholder="Select Party"/>
                {form.party&&(()=>{
                  const party=parties.find(p=>p.name===form.party);
                  const activeSchemes=(data.schemeMaster||[]).filter(s=>s.status==="Active"&&(s.partyId===party?.id||!s.partyId));
                  if(!activeSchemes.length) return null;
                  return (
                    <div style={{marginTop:6,background:"#F5F3FF",border:"1px solid #DDD6FE",borderRadius:7,padding:"7px 10px"}}>
                      <div style={{fontSize:9,fontWeight:700,color:"#7c3aed",textTransform:"uppercase",letterSpacing:.5,marginBottom:4}}>🎁 Active Schemes for this Party</div>
                      {activeSchemes.map(s=>(
                        <div key={s.id} style={{fontSize:10,color:"#374151",marginBottom:2}}>
                          <span style={{fontWeight:700,color:"#7c3aed"}}>
                            {s.buyQty&&s.freeQty?`Buy ${s.buyQty} Get ${s.freeQty} Free`:""}
                            {s.discPct?` + ${s.discPct}% Discount`:""}
                          </span>
                          {" "}on <span style={{fontWeight:600}}>{s.product||"All Products"}</span>
                          <span style={{color:"#6b7280",fontSize:9,marginLeft:4}}>→ Free qty auto-fills as you type</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </Field>
            ) : (
              <Field label="Doctor" req>
                <Sel value={form.billDoctor||""} onChange={v=>setForm(f=>({...f,billDoctor:v,party:"Dr. "+v}))}
                  options={data.doctors.map(d=>d.name)} placeholder="Select Doctor"/>
              </Field>
            )}
            <Field label={(form.billTo||"Party")==="Doctor"?"Referred By":"Prescribing Doctor"}>
              <Sel value={form.doctor} onChange={v=>setForm(f=>({...f,doctor:v}))}
                options={data.doctors.map(d=>d.name)} placeholder="Select Doctor (optional)"/>
            </Field>
            <Field label="Sales Channel"><Sel value={form.channel} onChange={v=>setForm(f=>({...f,channel:v}))} options={CHANNELS}/></Field>
            <Field label="Assign to MR / ASM">
              <Sel
                value={form.mrId||""}
                onChange={v=>setForm(f=>({...f,mrId:v}))}
                options={users.filter(u=>["mr","asm"].includes(u.role)&&u.active!==false).map(u=>({v:u.id,l:`${u.name} (${u.role.toUpperCase()})`}))}
                placeholder="Select MR / ASM (optional)"
              />
            </Field>
            <Field label="Transporter"><Inp value={form.transport||""} onChange={v=>setForm(f=>({...f,transport:v}))} placeholder="TRACKON / DTDC..."/></Field>
            <Field label="Vehicle No"><Inp value={form.vehicleNo||""} onChange={v=>setForm(f=>({...f,vehicleNo:v}))} placeholder="DL01AB1234 / NONE"/></Field>
            <Field label="Due Date"><DateInp value={form.dueDate||""} onChange={v=>setForm(f=>({...f,dueDate:v}))}/></Field>
          </div>
          <div style={{borderTop:`1px solid ${C.border}`,paddingTop:12,marginBottom:12}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
              <div style={{fontSize:13,fontWeight:700,color:C.navy}}>Invoice Items</div>
              <Btn sz="sm" v="outline" onClick={()=>setForm(f=>({...f,items:[...f.items,{product:"",batch:"",qty:1,free:0,mrp:"",rate:"",gst:18,hsn:"",pack:"",exp:"",code:""}]}))}>+ Add Row</Btn>
            </div>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                <thead><tr style={{background:C.sectionBg}}>
                  {(()=>{
                    const p=parties.find(x=>x.name===form.party);
                    const _pt=(p?.partyType||"").toLowerCase();
                    const isSt=_pt.includes("stockist")||_pt.includes("distributor")||_pt.includes("franchise");
                    const cols=["#","Code","Product","Batch","Exp","Qty","Free","MRP","Rate(₹)","GST%","Taxable","CGST","SGST","Total",""];
                    if(isSt){
                      cols.splice(6,0,"PTR"); // PTR reference only — PTS IS the Rate
                      const ri=cols.indexOf("Rate(₹)");
                      if(ri!==-1) cols[ri]="PTS (Rate)";
                    }
                    return cols;
                  })().map((h,i)=>(
                    <th key={i} style={{padding:"5px 6px",fontWeight:600,fontSize:10,color:C.muted,textAlign:"left",whiteSpace:"nowrap"}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>{form.items.map((it,idx)=>{
                  const ci=calcItem(it);
                  // Resolve party and scheme once per row
                  const rowParty=parties.find(x=>x.name===form.party);
                  const _rowPR=rowParty?((data.partyRates||{})[rowParty.id]||{})[it.product]:null;
                  const rowScheme=rowParty?(data.schemeMaster||[]).find(s=>
                    s.status==="Active"&&s.partyId===rowParty.id&&
                    (s.product===it.product||!s.product)
                  ):null;
                  const isSt=(rowParty?.partyType||"").toLowerCase().includes("stockist")||(rowParty?.partyType||"").toLowerCase().includes("distributor")||(rowParty?.partyType||"").toLowerCase().includes("franchise");
                  return <tr key={idx} style={{borderBottom:`1px solid ${C.border}`}}>
                    <td style={{padding:"3px 6px",color:C.muted,fontWeight:600}}>{idx+1}</td>
                    <td style={{padding:"2px 4px",fontSize:10,fontFamily:"monospace",color:"#6b7280",whiteSpace:"nowrap"}}>{(()=>{const _p=products.find(x=>x.name===it.product);return _p?.code||"—";})()}</td>
                    <td style={{padding:"2px 2px",minWidth:155}}>
                      <Sel value={it.product} onChange={v=>{
                        const prod=products.find(x=>x.name===v);
                        if(!prod){upItem(idx,"product",v);return;}
                        // Use partyId directly — most reliable, no name matching issues
                        const partyId=form.partyId||(parties.find(x=>x.name===form.party||x.name.trim().toLowerCase()===form.party.trim().toLowerCase())?.id)||"";
                        const pr=partyId?((data.partyRates||{})[partyId]||{})[v]:null;
                        // Doctor rates
                        const billDoctor=data.doctors.find(d=>d.name===form.billDoctor||("Dr. "+d.name)===form.party||d.name===form.doctor);
                        const dr=billDoctor?((data.doctorRates||{})[billDoctor.id]||{})[v]:null;
                        // Priority: doctorRates > partyRates > product rates
                        const activeRate=dr||pr;
                        const ptsVal=(activeRate&&activeRate.pts!==undefined&&activeRate.pts!=="")
                          ?Number(activeRate.pts)
                          :(prod.pts!==undefined&&prod.pts!==""&&Number(prod.pts)>0)
                          ?Number(prod.pts)
                          :0;
                        const ptrVal=(activeRate&&activeRate.ptr!==undefined&&activeRate.ptr!=="")
                          ?Number(activeRate.ptr)
                          :(prod.ptr!==undefined&&prod.ptr!==""&&Number(prod.ptr)>0)
                          ?Number(prod.ptr)
                          :0;
                        // Rate priority for Stockist/Distributor/Franchise: PTS (billing rate); PTR shown as reference only
                        const _isDistFranch=(rowParty?.partyType||"").toLowerCase().includes("distributor")||(rowParty?.partyType||"").toLowerCase().includes("franchise")||(rowParty?.partyType||"").toLowerCase().includes("stockist");
                        const autoRate=_isDistFranch
                          ?(ptsVal>0?ptsVal:ptrVal>0?Math.round(ptrVal*0.95):Math.round(Number(prod.mrp||0)*0.80))
                          :(ptrVal>0?ptrVal:ptsVal>0?ptsVal:Math.round(Number(prod.mrp||activeRate?.mrp||0)*0.85));
                        // Check scheme for free qty
                        const partyForScheme=parties.find(x=>x.id===partyId||x.name===form.party);
                        const scheme=partyForScheme?(data.schemeMaster||[]).find(s=>
                          s.status==="Active"&&s.partyId===partyForScheme.id&&
                          (s.product===v||!s.product)
                        ):null;
                        const curQty=Number(it.qty)||0;
                        const autoFree=scheme&&scheme.buyQty&&scheme.freeQty&&curQty>0
                          ?Math.floor(curQty/Number(scheme.buyQty))*Number(scheme.freeQty)
                          :it.free;
                        setForm(f=>({...f,items:f.items.map((it2,i)=>i===idx?{
                          ...it2,product:v,mrp:prod.mrp,gst:prod.gst,
                          rate:autoRate,hsn:prod.hsn||"",pack:prod.pack||"",
                          code:prod.code||"",_ptr:ptrVal,_pts:ptsVal,free:autoFree,
                          _rateSource:dr?"Doctor Rate":pr?(_isDistFranch?"Party PTS":"Party PTR"):_isDistFranch?(ptsVal>0?"Product PTS":ptrVal>0?"Product PTR (est)":"80% MRP"):(ptrVal>0?"Product PTR":ptsVal>0?"Product PTS":"85% MRP")
                        }:it2)}));
                      }} options={products.map(p=>p.name)} placeholder="Select Product"/>
                    </td>
                    <td style={{padding:"2px 2px",minWidth:120}}>
                      {(()=>{
                        // Show ALL batches for this product (stock>0 first, then zero-stock), sorted FEFO
                        const allProductBatches=it.product
                          ?[
                            ...data.batches.filter(b=>b.product===it.product&&(b.opening+b.in-b.out)>0).sort((a,b)=>a.exp>b.exp?1:-1),
                            ...data.batches.filter(b=>b.product===it.product&&(b.opening+b.in-b.out)<=0).sort((a,b)=>a.exp>b.exp?1:-1)
                          ]
                          :[];
                        if(allProductBatches.length>0){
                          return (
                            <select value={it.batch||""} onChange={e=>{
                              const selBatch=allProductBatches.find(b=>b.batch===e.target.value);
                              setForm(f=>({...f,items:f.items.map((it2,i)=>i===idx?{
                                ...it2,
                                batch:e.target.value,
                                exp:selBatch?.exp||it2.exp,
                                mrp:it2.mrp||(selBatch?.mrp?String(selBatch.mrp):"")
                              }:it2)}));
                            }}
                              style={{width:"100%",padding:"7px 6px",border:`1px solid ${it.batch?"#5FA8A4":"#e5e7eb"}`,borderRadius:7,fontSize:11,fontFamily:"inherit",background:"#fff"}}>
                              <option value="">-- Select Batch --</option>
                              {allProductBatches.map(b=>{
                                const stock=b.opening+b.in-b.out;
                                const dl=daysLeft(b.exp);
                                const flag=stock<=0?"[0] ":dl<=0?"[EXP] ":dl<=60?"[!] ":"";
                                return <option key={b.batch} value={b.batch}>{flag}{b.batch} | Qty:{stock} | Exp:{b.exp}</option>;
                              })}
                            </select>
                          );
                        }
                        // No batches at all for this product — free-type input; auto-fetch exp across all batches
                        return <Inp value={it.batch||""} onChange={v=>{
                          const matched=data.batches.find(b=>b.batch===v);
                          setForm(f=>({...f,items:f.items.map((it2,i)=>i===idx?{
                            ...it2,batch:v,
                            exp:matched?.exp||it2.exp,
                            mrp:it2.mrp||(matched?.mrp?String(matched.mrp):"")
                          }:it2)}));
                        }} placeholder="Batch No"/>;
                      })()}
                      {it.batch&&(()=>{
                        const b=data.batches.find(x=>x.batch===it.batch);
                        if(!b) return null;
                        const stock=b.opening+b.in-b.out;
                        const dl=daysLeft(b.exp);
                        const c=dl<=0?"#dc2626":dl<=60?"#f59e0b":"#166534";
                        return <div style={{fontSize:8,marginTop:2,color:c,fontWeight:600}}>Stock:{stock} | Exp:{b.exp}</div>;
                      })()}
                    </td>
                    {/* Exp column — auto-filled from batch record */}
                    <td style={{padding:"2px 2px",width:72}}>
                      {(()=>{
                        const bRec=it.batch?data.batches.find(b=>b.batch===it.batch):null;
                        if(bRec?.exp){
                          const dl=daysLeft(bRec.exp);
                          const col=dl<=0?"#dc2626":dl<=60?"#d97706":"#0f766e";
                          const bg=dl<=0?"#fef2f2":dl<=60?"#fffbeb":"#f0fafa";
                          return (
                            <div style={{
                              padding:"5px 4px",border:`1px solid ${col}`,borderRadius:7,
                              fontSize:10,background:bg,color:col,
                              fontWeight:700,textAlign:"center",
                              display:"flex",alignItems:"center",justifyContent:"center"
                            }} title="Auto-fetched from batch">{bRec.exp}</div>
                          );
                        }
                        return <Inp value={it.exp||""} onChange={v=>upItem(idx,"exp",v)} placeholder="MM/YYYY"/>;
                      })()}
                    </td>
                    {/* Qty column */}
                    <td style={{padding:"2px 2px",width:55}}>
                      <Inp value={it.qty} onChange={v=>{
                        const newQty=Number(v);
                        const autoFree=rowScheme&&rowScheme.buyQty&&rowScheme.freeQty
                          ?Math.floor(newQty/Number(rowScheme.buyQty))*Number(rowScheme.freeQty)
                          :it.free;
                        const discRate=rowScheme&&rowScheme.discPct&&Number(it.mrp)>0
                          ?Math.round(Number(it.mrp)*(1-Number(rowScheme.discPct)/100))
                          :it.rate;
                        setForm(f=>({...f,items:f.items.map((it2,i)=>i===idx?{
                          ...it2,qty:newQty,free:autoFree,rate:discRate
                        }:it2)}));
                      }} type="number"/>
                    </td>
                    {/* PTR reference — stockist only, after Qty */}
                    {isSt&&<td style={{padding:"2px 6px",width:60,textAlign:"right",color:"#1d4ed8",fontFamily:"monospace",fontSize:11,fontWeight:600}}>{it._ptr?`₹${fmt(it._ptr)}`:"—"}</td>}
                    {/* Free column */}
                    <td style={{padding:"2px 2px",width:50}}><Inp value={it.free} onChange={v=>upItem(idx,"free",Number(v))} type="number"/></td>
                    {/* MRP column */}
                    <td style={{padding:"2px 2px",width:65}}><Inp value={it.mrp} onChange={v=>upItem(idx,"mrp",Number(v))} type="number"/></td>
                    <td style={{padding:"2px 2px",width:75}}>
                      <Inp value={it.rate} onChange={v=>upItem(idx,"rate",Number(v))} type="number"/>
                      {it._rateSource&&<div style={{fontSize:8,color:it._rateSource==="Party Rate"||it._rateSource==="Doctor Rate"?"#166534":"#dc2626",fontWeight:700,marginTop:1,textAlign:"center",whiteSpace:"nowrap"}}>{it._rateSource==="85% MRP"?"⚠️ "+it._rateSource:"✅ "+it._rateSource}</div>}
                    </td>
                    <td style={{padding:"2px 2px",width:65}}><Sel value={String(it.gst)} onChange={v=>upItem(idx,"gst",Number(v))} options={["0","5","12","18","28"]}/></td>
                    <td className="mono" style={{padding:"2px 6px",fontWeight:600}}>₹{fmt(ci.taxable,2)}</td>
                    <td className="mono" style={{padding:"2px 6px",color:"#166534"}}>₹{fmt(ci.cgst,2)}</td>
                    <td className="mono" style={{padding:"2px 6px",color:"#166534"}}>₹{fmt(ci.sgst,2)}</td>
                    <td className="mono" style={{padding:"2px 6px",fontWeight:700}}>₹{fmt(ci.taxable+ci.cgst*2,2)}</td>
                    <td><button onClick={()=>setForm(f=>({...f,items:f.items.filter((_,i)=>i!==idx)}))} style={{background:"none",border:"none",color:"#dc2626",cursor:"pointer",fontSize:16,lineHeight:1}}>×</button></td>
                  </tr>;
                })}</tbody>
              </table>
            </div>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end"}}>
            <Field label="Notes"><Inp value={form.notes} onChange={v=>setForm(f=>({...f,notes:v}))} placeholder="Optional notes"/></Field>
            <div style={{background:"#2F4A6D",color:"#fff",borderRadius:12,padding:"14px 20px",minWidth:240,boxShadow:"0 4px 12px rgba(47,74,109,.15)"}}>
              {(igst>0
                ?[["Taxable",`₹${fmt(taxable,2)}`],["IGST",`₹${fmt(igst,2)}`]]
                :[["Taxable",`₹${fmt(taxable,2)}`],["CGST",`₹${fmt(cgst,2)}`],["SGST",`₹${fmt(cgst,2)}`]]
              ).map(([l,v])=>(
                <div key={l} style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:4}}>
                  <span style={{opacity:.8}}>{l}</span><span className="mono">{v}</span>
                </div>
              ))}
              <div style={{borderTop:"1px solid rgba(255,255,255,.3)",paddingTop:7,marginTop:7,display:"flex",justifyContent:"space-between",fontSize:16,fontWeight:800}}>
                <span>TOTAL</span><span className="mono">₹{fmt(total)}</span>
              </div>
            </div>
          </div>
          <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:14}}>
            <Btn v="outline" onClick={()=>{setTab("list");setEditInvId(null);setForm(emptyF());}}>Cancel</Btn>
            <Btn v="navy" onClick={save}>{editInvId?"💾 Update Invoice":"💾 Save Invoice"}</Btn>
          </div>
        </Card>
      )}
    </div>
  );
};


// ══════════════════════════════════════════════════════════════
// PRODUCT MASTER
// ══════════════════════════════════════════════════════════════

const ProductMaster = ({data,setData,requestDelete,setProducts}) => {
  const products=data.products||[];
  const empty={name:"",code:"",hsn:"3304",gst:18,mrp:"",ptr:"",pts:"",costPrice:"",unit:"Pcs",pack:"",category:"",manufacturer:"",reorderLevel:100,minStock:50,notes:""};
  const [form,setForm]=useState(empty);
  const [editing,setEditing]=useState(null);
  const [showForm,setShowForm]=useState(false);
  const [search,setSearch]=useState("");
  const [filterCat,setFilterCat]=useState("");
  const sf=k=>v=>setForm(p=>({...p,[k]:v}));

  const cats=[...new Set(products.map(p=>p.category).filter(Boolean))];

  const save=async()=>{
    if(!form.name||!form.code) return alert("Product name and code required");
    if(editing){
      const updatedProduct = normalizeProduct({ ...products.find(p=>p.id===editing), ...form });
      setProducts(prev=>prev.map(p=>p.id===editing?updatedProduct:p));
      setData(d=>({...d,products:d.products.map(p=>p.id===editing?updatedProduct:p)}));
      setEditing(null);
    } else {
      try {
        // ⚠️ Explicit snake_case mapping to match Supabase column names
        const payload = {
          name:          form.name         || "",
          code:          form.code         || "",
          hsn:           form.hsn          || "",
          unit:          form.unit         || "",
          pack:          form.pack         || "",
          category:      form.category     || "",
          manufacturer:  form.manufacturer || "",
          notes:         form.notes        || "",
          gst:           Number(form.gst   || 18),
          mrp:           Number(form.mrp   || 0),
          ptr:           Number(form.ptr   || 0),
          pts:           Number(form.pts   || 0),
          cost_price:    Number(form.costPrice   || 0),
          reorder_level: Number(form.reorderLevel || 0),
          min_stock:     Number(form.minStock     || 0),
        };
        const res = await addProduct(payload);
        if(res && res.length > 0){
          const added = normalizeProduct(res[0]);
          setProducts(prev=>[...prev, added]);
          setData(d=>({...d,products:[...d.products, added]}));
          alert("✅ Product Added");
        } else {
          alert("❌ Failed to add product — check Supabase table/RLS");
          return;
        }
      } catch(err) {
        console.error("addProduct error:", err);
        alert("❌ Network error adding product");
        return;
      }
    }
    setForm(empty);setShowForm(false);
  };
  const del=id=>{if(requestDelete("products",id,"Product"))setData(d=>({...d,products:d.products.filter(p=>p.id!==id)}));};
  const edit=p=>{
    setForm({...empty,...p,mrp:String(p.mrp),ptr:String(p.ptr||""),pts:String(p.pts||"")});
    setEditing(p.id);
    setShowForm(true);
    // Scroll to form
    setTimeout(()=>{
      const el=document.getElementById("product-edit-form");
      if(el) el.scrollIntoView({behavior:"smooth",block:"start"});
    },50);
  };

  const filtered=products.filter(p=>{
    if(search&&!p.name.toLowerCase().includes(search.toLowerCase())&&!p.code.toLowerCase().includes(search.toLowerCase())) return false;
    if(filterCat&&p.category!==filterCat) return false;
    return true;
  });

  return (
    <div className="fade">
      <PH title="Product Master" sub={`${products.length} products registered`}
        action={<div style={{display:"flex",gap:8}}>
          <BulkUpload title="Product Master"
            templateHeaders={["Name","Code","HSN","GST","MRP","PTR","PTS","Unit","Pack","Category","Manufacturer","Composition","Reorder Level","Min Stock"]}
            templateRows={[["GLURIV-C Serum","RXGC001","3304","18","899","720","630","Box","30ml","Cosmeceutical","Sun Pharma","Argireline 10%","100","50"]]}
            parseRow={(r,i)=>r.Name?{id:"PR"+Date.now()+"_"+i,name:r.Name,code:r.Code||"",hsn:r.HSN||"3304",gst:parseFloat(r.GST)||18,mrp:parseFloat(r.MRP)||0,ptr:parseFloat(r.PTR)||0,pts:parseFloat(r.PTS)||0,unit:r.Unit||"Box",pack:r.Pack||"",category:r.Category||"",manufacturer:r.Manufacturer||"",composition:r.Composition||"",reorderLevel:parseInt(r["Reorder Level"])||100,minStock:parseInt(r["Min Stock"])||50}:null}
            onUpload={(rows,mode)=>setData(d=>({...d,products:mode==="replace"?rows:[...d.products,...rows.filter(r=>!d.products.find(x=>x.name?.toLowerCase()===r.name?.toLowerCase()))]}))}
          />
          <Btn v="gold" onClick={()=>{setShowForm(s=>!s);setEditing(null);setForm(empty);}}>+ Add Product</Btn>
        </div>}
      />

      {/* Summary KPIs */}
      <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap"}}>
        <KPI label="Total Products" val={products.length} icon="💊"/>
        <KPI label="Categories" val={cats.length} icon="🗂️"/>
        <KPI label="Low Stock" val={products.filter(p=>{const b=data.batches.filter(x=>x.product===p.name);const stock=b.reduce((s,x)=>s+(x.opening+x.in-x.out),0);return stock<=p.reorderLevel;}).length} icon="⚠️"/>
        <KPI label="Avg MRP" val={`₹${products.length?Math.round(products.reduce((s,p)=>s+Number(p.mrp||0),0)/products.length):0}`} icon="₹"/>
      </div>

      {/* Add/Edit form */}
      {showForm&&(
        <div id="product-edit-form" style={{background:"#fff",borderRadius:12,padding:20,marginBottom:20,border:`2px solid ${editing?"#5FA8A4":C.border}`,boxShadow:"0 4px 16px rgba(0,0,0,0.08)"}}>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:12,alignItems:"start"}}>
            <Field label="Product Name" req><Inp value={form.name} onChange={sf("name")} placeholder="GLURIV-C Serum 30ml"/></Field>
            <Field label="Product Code" req><Inp value={form.code} onChange={sf("code")} placeholder="GLC30"/></Field>
            <Field label="HSN Code"><Inp value={form.hsn} onChange={sf("hsn")} placeholder="3304"/></Field>
            <Field label="Category"><Inp value={form.category} onChange={sf("category")} placeholder="Cosmeceutical"/></Field>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:12,alignItems:"start"}}>
            <Field label="MRP (₹)" req><Inp value={form.mrp} onChange={sf("mrp")} type="number" placeholder="0.00"/></Field>
            <Field label="PTR (₹)"><Inp value={form.ptr} onChange={sf("ptr")} type="number" placeholder="Price to Retailer"/></Field>
            <Field label="PTS (₹)"><Inp value={form.pts} onChange={sf("pts")} type="number" placeholder="Price to Stockist"/></Field>
            <Field label="Cost Price (₹)"><Inp value={form.costPrice} onChange={sf("costPrice")} type="number" placeholder="Mfg / purchase cost"/></Field>
            <Field label="GST %">
              <select value={form.gst} onChange={e=>sf("gst")(Number(e.target.value))} style={{width:"100%",padding:"8px 10px",border:`1px solid ${C.border}`,borderRadius:7,fontSize:12,fontFamily:"inherit"}}>
                {[0,5,12,18,28].map(g=><option key={g} value={g}>{g}%</option>)}
              </select>
            </Field>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:12,alignItems:"start"}}>
            <Field label="Unit"><Sel value={form.unit} onChange={sf("unit")} options={["Pcs","Strip","Box","Bottle","Tube","Sachet","Vial","Ampoule"]}/></Field>
            <Field label="Pack Size"><Inp value={form.pack} onChange={sf("pack")} placeholder="30ml / 10 Tabs"/></Field>
            <Field label="Manufacturer"><Sel value={form.manufacturer} onChange={sf("manufacturer")} options={data.manufacturers.map(m=>m.name)} placeholder="Select Manufacturer"/></Field>
            <Field label="Reorder Level"><Inp value={form.reorderLevel} onChange={sf("reorderLevel")} type="number"/></Field>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:12,alignItems:"start"}}>
            <Field label="Min Stock"><Inp value={form.minStock} onChange={sf("minStock")} type="number"/></Field>
            <Field label="Notes" style={{gridColumn:"span 3"}}><Inp value={form.notes||""} onChange={sf("notes")} placeholder="Any special storage or handling notes..."/></Field>
          </div>
          <div style={{display:"flex",gap:8}}>
            <Btn v="navy" onClick={save}>{editing?"💾 Update":"💾 Add Product"}</Btn>
            <Btn v="outline" onClick={()=>{setShowForm(false);setEditing(null);}}>Cancel</Btn>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{display:"flex",gap:10,marginBottom:12,alignItems:"center",flexWrap:"wrap"}}>
        <Inp value={search} onChange={setSearch} placeholder="🔍 Search by name or code..." style={{maxWidth:250}}/>
        <Sel value={filterCat} onChange={setFilterCat} options={cats} placeholder="All Categories"/>
        {(search||filterCat)&&<Btn v="ghost" sz="sm" onClick={()=>{setSearch("");setFilterCat("");}}>Clear</Btn>}
        <div style={{marginLeft:"auto",fontSize:11,color:C.muted}}>{filtered.length} products</div>
      </div>

      {/* Product table */}
      <div style={{background:"#fff",borderRadius:10,border:`1px solid ${C.border}`,overflow:"hidden"}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
          <thead><tr style={{background:C.navy}}>
            {["Code","Product Name","Category","HSN","GST","MRP","PTR","PTS","Pack","Manufacturer","Stock","Action"].map(h=>(
              <th key={h} style={{padding:"9px 12px",textAlign:"left",color:"#fff",fontWeight:600,fontSize:11,whiteSpace:"nowrap"}}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {filtered.map((p,i)=>{
              const batches=data.batches.filter(b=>b.product===p.name);
              const stock=batches.reduce((s,b)=>s+(b.opening+b.in-b.out),0);
              const isLow=stock<=p.reorderLevel;
              return (
                <tr key={p.id} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?"#fff":"#FAFAFA"}}>
                  <td style={{padding:"8px 12px"}}><span style={{fontFamily:"monospace",fontWeight:700,color:C.accent,fontSize:11}}>{p.code}</span></td>
                  <td style={{padding:"8px 12px",fontWeight:600,color:C.navy}}>{p.name}</td>
                  <td style={{padding:"8px 12px"}}><Badge s={p.category}/></td>
                  <td style={{padding:"8px 12px",fontFamily:"monospace",fontSize:11}}>{p.hsn}</td>
                  <td style={{padding:"8px 12px",textAlign:"center"}}><Badge s={`${p.gst}%`}/></td>
                  <td style={{padding:"8px 12px",fontFamily:"monospace",fontWeight:700}}>₹{p.mrp}</td>
                  <td style={{padding:"8px 12px",fontFamily:"monospace",color:C.muted}}>{p.ptr?`₹${p.ptr}`:"—"}</td>
                  <td style={{padding:"8px 12px",fontFamily:"monospace",color:C.muted}}>{p.pts?`₹${p.pts}`:"—"}</td>
                  <td style={{padding:"8px 12px",fontSize:11,color:C.muted}}>{p.pack}</td>
                  <td style={{padding:"8px 12px",fontSize:11}}>{p.manufacturer||"—"}</td>
                  <td style={{padding:"8px 12px"}}>
                    <span style={{fontFamily:"monospace",fontWeight:700,color:isLow?"#dc2626":"#166534"}}>{stock}</span>
                    {isLow&&<span style={{fontSize:9,color:"#dc2626",fontWeight:700,display:"block"}}>LOW</span>}
                  </td>
                  <td style={{padding:"8px 12px"}}>
                    <div style={{display:"flex",gap:4}}>
                      <Btn sz="sm" v="ghost" onClick={()=>edit(p)}>✏️</Btn>
                      <Btn sz="sm" v="danger" onClick={()=>del(p.id)}>Del</Btn>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length===0&&<tr><td colSpan={12} style={{padding:30,textAlign:"center",color:C.muted}}>No products found.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};


// ══════════════════════════════════════════════════════════════
// PURCHASE RETURN
// ══════════════════════════════════════════════════════════════

const PurchaseModule = ({data,setData,requestDelete}) => {
  const products=data.products||[];
  const [tab,setTab]=useState("list");
  const emptyF={no:"",date:today(),manufacturer:"",invoiceNo:"",poRef:"",items:[{material:"",batch:"",mfg:"",exp:"",qty:1,unit:"Kg",rate:"",gst:18}]};
  const [form,setForm]=useState(emptyF);
  const [pfVendor,setPfVendor]=useState("");
  const [pfFrom,setPfFrom]=useState("");
  const [pfTo,setPfTo]=useState("");
  const [pfStatus,setPfStatus]=useState("");
  const [pfProduct,setPfProduct]=useState("");
  const [mfgFilter,setMfgFilter]=useState("");
  const calcItem=it=>{const tx=(it.qty||0)*(it.rate||0);const cg=tx*(it.gst||18)/200;return{...it,taxable:tx,cgst:cg,sgst:cg};};
  const items=form.items.map(calcItem);
  const taxable=items.reduce((s,i)=>s+(i.taxable||0),0);
  const cgst=items.reduce((s,i)=>s+(i.cgst||0),0);
  const total=Math.round(taxable+cgst*2);
  const upItem=(idx,k,v)=>setForm(f=>({...f,items:f.items.map((it,i)=>i===idx?{...it,[k]:v}:it)}));
  const save=()=>{
    if(!form.no) return alert("PE No required");
    // ── WORKFLOW: Manufacturer must exist ──
    if(!form.manufacturer) { alert("Manufacturer required. Please select a manufacturer."); return; }
    if((data.manufacturers||[]).length===0) { alert("No manufacturers found. Please add a manufacturer first from the Vendors/Manufacturers section."); return; }
    // ── WORKFLOW: Products must exist ──
    if((data.products||[]).length===0) { alert("No products found. Add products in Product Master first."); return; }
    const peId=uid();
    setData(d=>{
      // Auto-update batch IN quantities
      let batches=[...d.batches];
      (form.items||[]).forEach(it=>{
        if(!it.material) return;
        const existIdx=batches.findIndex(b=>b.product===it.material&&b.batch===(it.batch||""));
        if(existIdx>=0){
          batches[existIdx]={...batches[existIdx],in:(batches[existIdx].in||0)+Number(it.qty||0)};
        } else if(it.batch){
          batches.push({id:"B"+Date.now()+Math.random(),product:it.material,batch:it.batch||"",
            mfg:it.mfg||"",exp:it.exp||"",opening:0,in:Number(it.qty||0),out:0,
            manufacturer:form.manufacturer||"",notes:`Auto from PE ${form.no}`});
        }
      });
      // Update PO status to Received if linked
      let purchaseOrders = d.purchaseOrders||[];
      if(form.poRef){
        purchaseOrders = purchaseOrders.map(po=>
          po.no===form.poRef ? {...po,status:"Received",peRef:form.no} : po
        );
      }
      return {...d,
        purchases:[...d.purchases,{id:peId,...form,items,taxable,cgst,sgst:cgst,total,payStatus:"Unpaid"}],
        purchaseOrders,
        batches
      };
    });
    setForm(emptyF); setTab("list");
  };
  const markPaid=id=>setData(d=>{
    // Create a real vendor payment entry so the payment ledger stays consistent.
    const pe=d.purchases.find(p=>p.id===id);
    if(!pe) return d;
    const syntheticVP={
      id:uid(),no:"ADJ-VP-"+Date.now(),date:today(),
      manufacturer:pe.manufacturer||"",peNo:pe.no,
      amount:pe.total,mode:"Adjustment",
      utr:"Mark-Paid",bank:"",
      note:"Marked Paid via Purchase list"
    };
    return {
      ...d,
      vendorPayments:[...d.vendorPayments,syntheticVP],
      purchases:d.purchases.map(p=>p.id===id?{...p,payStatus:"Paid"}:p)
    };
  });
  const del=id=>{
    if(requestDelete("purchases",id,"Purchase Entry"))
      setData(d=>{
        // Reverse the stock IN that was created when this purchase was saved.
        const pe=d.purchases.find(p=>p.id===id);
        let batches=[...d.batches];
        (pe?.items||[]).forEach(it=>{
          if(!it.material) return;
          const idx=batches.findIndex(b=>b.product===it.material&&(b.batch||"")===(it.batch||""));
          if(idx>=0) batches[idx]={...batches[idx],in:Math.max(0,(batches[idx].in||0)-Number(it.qty||0))};
        });
        return {...d,purchases:d.purchases.filter(p=>p.id!==id),batches};
      });
  };
  return (
    <div className="fade">
      <PH title="Purchase Entry" sub="Record all manufacturer purchases with ITC"
        action={<div style={{display:"flex",gap:8}}>
          <Btn v={tab==="list"?"navy":"outline"} onClick={()=>setTab("list")}>📋 All Purchases</Btn>
          <Btn v={tab==="new"?"gold":"outline"} onClick={()=>setTab("new")}>+ New Purchase</Btn>
        </div>}
      />
      {tab==="list"&&(()=>{
        const parseD=s=>{if(!s||!s.includes("/"))return null;const[dd,mm,yyyy]=s.split("/");return new Date(+yyyy,+mm-1,+dd);};
        const pfFromD=parseD(pfFrom),pfToD=parseD(pfTo);
        const filtPurch=[...data.purchases].reverse().filter(p=>{
          if(pfVendor&&!(p.manufacturer||"").toLowerCase().includes(pfVendor.toLowerCase())) return false;
          if(pfStatus&&p.payStatus!==pfStatus) return false;
          if(pfProduct&&!(p.items||[]).some(it=>it.material===pfProduct)) return false;
          const d=parseD(p.date);
          if(pfFromD&&d&&d<pfFromD) return false;
          if(pfToD&&d&&d>pfToD) return false;
          return true;
        });
        const pfHas=pfVendor||pfFrom||pfTo||pfStatus||pfProduct;
        return(<>
          <div style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:10,padding:"14px 16px",marginBottom:14}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
              <span style={{fontWeight:700,fontSize:12,color:C.navy}}>🔍 Filters</span>
              {pfHas&&<button onClick={()=>{setPfVendor("");setPfFrom("");setPfTo("");setPfStatus("");setPfProduct("");}} style={{fontSize:11,color:"#dc2626",background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>✕ Clear All</button>}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:12,alignItems:"start"}}>
              <Field label="Manufacturer"><Inp value={pfVendor} onChange={setPfVendor} placeholder="Filter by manufacturer…"/></Field>
              <Field label="Product/Material"><Sel value={pfProduct} onChange={setPfProduct} options={products.map(p=>p.name)} placeholder="Any Product"/></Field>
              <Field label="Pay Status"><Sel value={pfStatus} onChange={setPfStatus} options={["Paid","Unpaid"]} placeholder="All"/></Field>
              <Field label="From Date"><DateInp value={pfFrom} onChange={setPfFrom}/></Field>
              <Field label="To Date"><DateInp value={pfTo} onChange={setPfTo}/></Field>
            </div>
            {pfHas&&<div style={{marginTop:8,fontSize:12,color:C.muted}}>{filtPurch.length} records · Total: ₹{fmt(filtPurch.reduce((s,p)=>s+p.total,0))}</div>}
          </div>
          <Tbl headers={["PE No","Date","Manufacturer","Invoice No","PO Ref","Taxable","CGST","SGST","Total","Pay Status","Action"]}
            onExport={()=>downloadCSV(filtPurch.map(p=>[p.no,p.date,p.manufacturer||"",p.invoiceNo||"",p.poRef||"",p.taxable,p.cgst,p.sgst,p.total,p.payStatus]),["PE No","Date","Manufacturer","Invoice No","PO Ref","Taxable","CGST","SGST","Total","Pay Status"],"Purchases")}
            rows={filtPurch.map(p=>{
              return[
                <span className="mono" style={{fontWeight:700,color:"#78350f",fontSize:11}}>{p.no}</span>,
                p.date,
                <span style={{fontWeight:600,fontSize:12}}>{p.manufacturer||"—"}</span>,
                <span style={{fontSize:11}}>{p.invoiceNo||"—"}</span>,
                <span className="mono" style={{fontSize:11}}>{p.poRef||"—"}</span>,
                <span className="mono">₹{fmt(p.taxable,2)}</span>,
                <span className="mono" style={{color:"#166534"}}>₹{fmt(p.cgst,2)}</span>,
                <span className="mono" style={{color:"#166534"}}>₹{fmt(p.sgst,2)}</span>,
                <span className="mono" style={{fontWeight:700}}>₹{fmt(p.total)}</span>,
                <Badge s={p.payStatus}/>,
              <div style={{display:"flex",gap:3}}>
                {p.payStatus!=="Paid"&&<Btn sz="sm" v="green" onClick={()=>markPaid(p.id)}>Paid</Btn>}
                <Btn sz="sm" v="danger" onClick={()=>del(p.id)}>Del</Btn>
              </div>
            ];
          })}
          />
        </>);
      })()}
      {tab==="new"&&(
        <Card>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:12,alignItems:"start"}}>
            <Field label="PE No" req><Inp value={form.no} onChange={v=>setForm(f=>({...f,no:v}))} placeholder="PE-004"/></Field>
            <Field label="Date"><DateInp value={form.date} onChange={v=>setForm(f=>({...f,date:v}))}/></Field>
            <Field label="Manufacturer" req>
              <Sel value={form.manufacturer} onChange={v=>setForm(f=>({...f,manufacturer:v}))} options={data.manufacturers.map(m=>m.name)} placeholder="Select Manufacturer"/>
              {form.manufacturer&&<div style={{fontSize:10,color:"#6366F1",marginTop:3,fontWeight:600}}>
                {products.filter(p=>(p.manufacturer||"").toLowerCase()===form.manufacturer.toLowerCase()).length} products available
              </div>}
            </Field>
            <Field label="Invoice No"><Inp value={form.invoiceNo} onChange={v=>setForm(f=>({...f,invoiceNo:v}))} placeholder="Mfr Invoice No"/></Field>
            <Field label="Against PO No">
              <Sel value={form.poRef} onChange={v=>{
                // ── AUTO-FILL from PO ──
                const po=(data.purchaseOrders||[]).find(p=>p.no===v);
                if(po){
                  setForm(f=>({...f,
                    poRef:v,
                    manufacturer:po.manufacturer||po.vendor||f.manufacturer,
                    items:(po.items||[]).map(it=>({
                      material:it.material||"",
                      batch:"",mfg:"",exp:"",
                      qty:it.qty||1,
                      unit:it.unit||"Pcs",
                      rate:it.rate||"",
                      gst:18
                    }))
                  }));
                } else {
                  setForm(f=>({...f,poRef:v}));
                }
              }} options={(data.purchaseOrders||[]).filter(p=>p.status!=="Received").map(p=>p.no)} placeholder="Select PO (Optional)"/>
              {form.poRef&&<div style={{fontSize:10,color:"#166534",marginTop:3,fontWeight:600}}>✅ Items auto-filled from PO</div>}
            </Field>
          </div>

          <div style={{borderTop:`1px solid ${C.border}`,paddingTop:12,marginBottom:12}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
              <div style={{fontSize:13,fontWeight:700,color:C.navy}}>Purchase Items</div>
              <Btn sz="sm" v="outline" onClick={()=>setForm(f=>({...f,items:[...f.items,{material:"",qty:1,unit:"Kg",rate:"",gst:18}]}))}>+ Add Row</Btn>
            </div>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
              <thead><tr style={{background:C.sectionBg}}>
                {["#","Material / Product","Batch No","Mfg Date","Exp Date","Qty","Unit","Rate (₹)","GST %","Taxable","CGST","SGST","Total",""].map((h,i)=>(
                  <th key={i} style={{padding:"5px 6px",fontWeight:600,fontSize:10,color:C.muted,textAlign:"left",whiteSpace:"nowrap"}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>{form.items.map((it,idx)=>{
                const ci=calcItem(it);
                return <tr key={idx} style={{borderBottom:`1px solid ${C.border}`}}>
                  <td style={{padding:"3px 6px",color:C.muted,fontWeight:600}}>{idx+1}</td>
                  <td style={{padding:"2px 2px",minWidth:160}}>
                    {(()=>{
                      const filteredProds = mfgFilter
                        ? products.filter(p=>(p.manufacturer||"").toLowerCase().includes(mfgFilter.toLowerCase()))
                        : products;
                      return (
                        <Sel value={it.material} onChange={v=>{
                          const p=products.find(x=>x.name===v);
                          upItem(idx,"material",v);
                          if(p){upItem(idx,"rate",p.pts||p.ptr||0);upItem(idx,"gst",p.gst||18);}
                        }} options={filteredProds.map(p=>p.name)} placeholder={mfgFilter?"Filtered products":"Select Product"}/>
                      );
                    })()}
                  </td>
                  <td style={{padding:"2px 2px",width:90}}><Inp value={it.batch||""} onChange={v=>upItem(idx,"batch",v)} placeholder="Batch No"/></td>
                  <td style={{padding:"2px 2px",width:90}}><DateInp value={it.mfg||""} onChange={v=>upItem(idx,"mfg",v)}/></td>
                  <td style={{padding:"2px 2px",width:90}}><DateInp value={it.exp||""} onChange={v=>upItem(idx,"exp",v)}/></td>
                  <td style={{padding:"2px 2px",width:60}}><Inp value={it.qty} onChange={v=>upItem(idx,"qty",Number(v))} type="number"/></td>
                  <td style={{padding:"2px 2px",width:60}}><Inp value={it.unit} onChange={v=>upItem(idx,"unit",v)} placeholder="Box"/></td>
                  <td style={{padding:"2px 2px",width:80}}><Inp value={it.rate} onChange={v=>upItem(idx,"rate",Number(v))} type="number"/></td>
                  <td style={{padding:"2px 2px",width:60}}><Sel value={String(it.gst)} onChange={v=>upItem(idx,"gst",Number(v))} options={["0","5","12","18","28"]}/></td>
                  <td className="mono" style={{padding:"2px 6px",fontWeight:600}}>₹{fmt(ci.taxable,2)}</td>
                  <td className="mono" style={{padding:"2px 6px",color:"#166534"}}>₹{fmt(ci.cgst,2)}</td>
                  <td className="mono" style={{padding:"2px 6px",color:"#166534"}}>₹{fmt(ci.sgst,2)}</td>
                  <td className="mono" style={{padding:"2px 6px",fontWeight:700}}>₹{fmt(ci.taxable+ci.cgst*2,2)}</td>
                  <td><button onClick={()=>setForm(f=>({...f,items:f.items.filter((_,i)=>i!==idx)}))} style={{background:"none",border:"none",color:"#dc2626",cursor:"pointer",fontSize:16}}>×</button></td>
                </tr>;
              })}</tbody>
            </table>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",marginTop:12}}>
            <div style={{background:"#1e40af",color:"#fff",borderRadius:10,padding:"12px 16px",fontSize:12}}>
              <div style={{opacity:.8,marginBottom:3}}>ITC Claimable</div>
              <div className="mono" style={{fontSize:18,fontWeight:800}}>₹{fmt(cgst*2,2)}</div>
            </div>
            <div style={{background:C.navy,color:"#fff",borderRadius:10,padding:"12px 18px"}}>
              <div style={{opacity:.8,fontSize:11,marginBottom:3}}>PURCHASE TOTAL</div>
              <div className="mono" style={{fontSize:20,fontWeight:800}}>₹{fmt(total)}</div>
            </div>
          </div>
          <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:14}}>
            <Btn v="outline" onClick={()=>setTab("list")}>Cancel</Btn>
            <Btn v="gold" onClick={save}>💾 Save Purchase</Btn>
          </div>
        </Card>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// PAYMENTS RECEIVED
// ══════════════════════════════════════════════════════════════


const PaymentsModule = ({data,setData,requestDelete,currentUser}) => {
  const parties=data.parties||[];
  const {show:showAmts} = useAmt();
  const emptyF={no:"",date:today(),party:"",invoiceNo:"",amount:"",mode:"NEFT",utr:"",bank:""};
  const [form,setForm]=useState(emptyF);
  const [editId,setEditId]=useState(null);
  const [payFParty,setPayFParty]=useState("");
  const [payFFrom,setPayFFrom]=useState("");
  const [payFTo,setPayFTo]=useState("");
  const [payFMode,setPayFMode]=useState("");
  const save=()=>{
    if(!form.party||!form.amount) return alert("Party and amount required");
    const amt=Number(form.amount);
    // ── WORKFLOW: Check outstanding ──
    const _partyOut=(data.invoices||[]).filter(i=>i.party===form.party&&i.status!=="Paid"&&i.status!=="Cancelled").reduce((s,i)=>s+Math.max(0,(i.total||0)-(i.paid||0)),0);
    if(_partyOut===0&&!window.confirm("ℹ️ No outstanding invoices for '"+form.party+"'\nRecord payment anyway?")) return;

    // Helper: recalculate invoice paid/status from the full payments array.
    // NOTE: invoices use `inv.no` as their display key; payments reference it via
    // `p.invoiceNo`.  The old guard `!inv.invoiceNo` was always truthy (because
    // invoices don't have an `invoiceNo` field) and caused zero-payment invoices
    // to be skipped silently — leaving stale `paid` amounts after a receipt edit
    // or delete.  The guard is removed; every invoice is always recalculated.
    const recalcInvoices=(invoices,payments)=>invoices.map(inv=>{
      const totalPaid=payments
        .filter(p=>p.invoiceNo===inv.no)
        .reduce((s,p)=>s+Number(p.amount||0),0);
      const newStatus=totalPaid>=inv.total?"Paid":totalPaid>0?"Partial":"Current";
      return {...inv,paid:totalPaid,status:inv.status==="Cancelled"?"Cancelled":newStatus};
    });

    if(editId){
      setData(d=>{
        const updatedPayments=d.payments.map(x=>x.id===editId?{...x,...form,amount:amt}:x);
        return {...d,payments:updatedPayments,invoices:recalcInvoices(d.invoices,updatedPayments)};
      });
      setEditId(null);
    } else {
      setData(d=>{
        const newPayment={id:uid(),...form,amount:amt,createdBy:currentUser?.id};
        const updatedPayments=[...d.payments,newPayment];
        return {...d,payments:updatedPayments,invoices:recalcInvoices(d.invoices,updatedPayments)};
      });
    }
    setForm(emptyF);
  };

  const [showRecForm,setShowRecForm]=useState(false);

  return (
    <div className="fade">
      <PH title="Payments Received" sub="Track all inward payments from parties"
        action={<Btn v="gold" onClick={()=>{setForm(emptyF);setEditId(null);setShowRecForm(f=>!f);}}>{showRecForm?"✕ Close":"+ New Receipt"}</Btn>}
      />

      {/* Inline receipt form — slides in at top */}
      {showRecForm&&(
        <Card style={{marginBottom:16,border:"1px solid #BBF7D0",background:"#F0FDF4"}}>
          <div style={{fontWeight:700,fontSize:13,color:"#166534",marginBottom:14}}>📝 Record New Receipt</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,alignItems:"start"}}>
            <Field label="Receipt No"><Inp value={form.no} onChange={v=>setForm(f=>({...f,no:v}))} placeholder="RCP-004"/></Field>
            <Field label="Date"><DateInp value={form.date} onChange={v=>setForm(f=>({...f,date:v}))}/></Field>
            <Field label="Party" req><Sel value={form.party} onChange={v=>setForm(f=>({...f,party:v,invoiceNo:""}))} options={parties.map(p=>p.name)} placeholder="Select Party"/></Field>
            <Field label="Against Invoice"><Sel value={form.invoiceNo} onChange={v=>setForm(f=>({...f,invoiceNo:v}))} options={data.invoices.filter(i=>i.party===form.party&&i.status!=="Cancelled").map(i=>({value:i.no,label:`${i.no} — ₹${fmt(i.total)} (Bal: ₹${fmt(i.total-i.paid)})`}))} placeholder="Select Invoice"/></Field>
            <Field label="Amount (₹)" req><Inp value={form.amount} onChange={v=>setForm(f=>({...f,amount:v}))} type="number"/></Field>
            <Field label="Payment Mode"><Sel value={form.mode} onChange={v=>setForm(f=>({...f,mode:v}))} options={PAY_MODES}/></Field>
            <Field label="Cheque / UTR No"><Inp value={form.utr} onChange={v=>setForm(f=>({...f,utr:v}))} placeholder="UTR or Cheque No"/></Field>
            <Field label="Bank"><Inp value={form.bank} onChange={v=>setForm(f=>({...f,bank:v}))} placeholder="HDFC Bank"/></Field>
          </div>
          <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:12}}>
            <Btn v="outline" onClick={()=>{setShowRecForm(false);setForm(emptyF);setEditId(null);}}>Cancel</Btn>
            <Btn v="gold" onClick={()=>{save();setShowRecForm(false);}}>💾 {editId?"Update":"Save"} Receipt</Btn>
          </div>
        </Card>
      )}

      {/* Full-width filters + table */}
      {(()=>{
        const parseD=s=>{if(!s||!s.includes("/"))return null;const[dd,mm,yyyy]=s.split("/");return new Date(+yyyy,+mm-1,+dd);};
        const filtPay=[...data.payments].reverse().filter(p=>{
          if(currentUser?.role==="billing"&&p.createdBy&&p.createdBy!==currentUser?.id) return false;
          if(payFParty&&p.party!==payFParty) return false;
          if(payFMode&&p.mode!==payFMode) return false;
          const d=parseD(p.date);
          if(payFFrom&&parseD(payFFrom)&&d&&d<parseD(payFFrom)) return false;
          if(payFTo&&parseD(payFTo)&&d&&d>parseD(payFTo)) return false;
          return true;
        });
        const payHas=payFParty||payFFrom||payFTo||payFMode;
        const totalFilt=filtPay.reduce((s,p)=>s+p.amount,0);
        return(
          <>
            <Card style={{marginBottom:12}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                <span style={{fontWeight:700,fontSize:12,color:C.navy}}>🔍 Filter Payments</span>
                {payHas&&<button onClick={()=>{setPayFParty("");setPayFFrom("");setPayFTo("");setPayFMode("");}}
                  style={{fontSize:11,color:"#DC2626",background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>✕ Clear Filters</button>}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:12,alignItems:"start"}}>
                <Field label="Party"><Sel value={payFParty} onChange={setPayFParty} options={parties.map(p=>p.name)} placeholder="All Parties"/></Field>
                <Field label="Mode"><Sel value={payFMode} onChange={setPayFMode} options={["NEFT","RTGS","Cheque","Cash","UPI"]} placeholder="All Modes"/></Field>
                <Field label="From Date"><DateInp value={payFFrom} onChange={setPayFFrom}/></Field>
                <Field label="To Date"><DateInp value={payFTo} onChange={setPayFTo}/></Field>
              </div>
              {payHas&&(
                <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid #E5E7EB",display:"flex",alignItems:"center",gap:20}}>
                  <span style={{fontSize:13,fontWeight:700,color:"#166534"}}>{showAmts?`₹${fmt(totalFilt)} collected`:<span style={{letterSpacing:3,opacity:.4}}>••••••</span>}</span>
                  <span style={{fontSize:12,color:C.muted}}>{filtPay.length} receipt{filtPay.length!==1?"s":""} found</span>
                </div>
              )}
            </Card>

            <Tbl
              headers={["Receipt No","Date","Party","Invoice","Amount","Mode","UTR / Ref","Bank","Action"]}
              onExport={()=>downloadCSV(filtPay.map(p=>[p.no,p.date,p.party,p.invoiceNo,p.amount,p.mode,p.utr,p.bank]),["Receipt No","Date","Party","Invoice No","Amount","Mode","UTR","Bank"],"Payments_Received")}
              rows={filtPay.map(p=>[
                <span className="mono" style={{fontWeight:700,color:"#166534",fontSize:11}}>{p.no}</span>,
                p.date,
                <span style={{fontWeight:600,fontSize:12}}>{p.party}</span>,
                <span className="mono" style={{fontSize:11,color:C.muted}}>{p.invoiceNo||"—"}</span>,
                <span className="mono" style={{fontWeight:800,color:"#166534"}}>{showAmts?`₹${fmt(p.amount)}`:<span style={{letterSpacing:3,opacity:.4}}>•••••</span>}</span>,
                <Badge s={p.mode}/>,
                <span className="mono" style={{fontSize:11}}>{p.utr||"—"}</span>,
                <span style={{fontSize:11,color:C.muted}}>{p.bank||"—"}</span>,
                <div style={{display:"flex",gap:4}}>
                  <Btn sz="sm" v="ghost" onClick={()=>{setForm({...emptyF,...p,amount:String(p.amount)});setEditId(p.id);setShowRecForm(true);}}>✏️</Btn>
                  <Btn sz="sm" v="danger" onClick={()=>{if(requestDelete("payments",p.id,"Payment Receipt"))setData(d=>{
                    const updatedPayments=d.payments.filter(x=>x.id!==p.id);
                    const recalcedInvoices=d.invoices.map(inv=>{
                      const totalPaid=updatedPayments.filter(pm=>pm.invoiceNo===inv.no).reduce((s,pm)=>s+Number(pm.amount||0),0);
                      const newStatus=totalPaid>=inv.total?"Paid":totalPaid>0?"Partial":"Current";
                      return {...inv,paid:totalPaid,status:inv.status==="Cancelled"?"Cancelled":newStatus};
                    });
                    return {...d,payments:updatedPayments,invoices:recalcedInvoices};
                  });}}>Del</Btn>
                </div>
              ])}
              empty="No payments found. Try adjusting the filters."
            />
          </>
        );
      })()}
    </div>
  );
};


const VendorPaymentsModule = ({data,setData,requestDelete}) => {
  const emptyF={no:"",date:today(),manufacturer:"",peNo:"",amount:"",mode:"NEFT",utr:"",bank:""};
  const [form,setForm]=useState(emptyF);
  const [editId,setEditId]=useState(null);
  const [vfMfr,setVfMfr]=useState("");
  const [vfFrom,setVfFrom]=useState("");
  const [vfTo,setVfTo]=useState("");
  const [vfMode,setVfMode]=useState("");

  // Recalculate a purchase entry's payStatus from actual vendor payments
  const recalcPEStatus=(vendorPayments,purchases,peNo)=>{
    if(!peNo) return purchases;
    const pe=purchases.find(p=>p.no===peNo);
    if(!pe) return purchases;
    const totalPaid=vendorPayments
      .filter(vp=>vp.peNo===peNo)
      .reduce((s,vp)=>s+Number(vp.amount||0),0);
    const newStatus=totalPaid>=pe.total?"Paid":totalPaid>0?"Partial":"Unpaid";
    return purchases.map(p=>p.no===peNo?{...p,payStatus:newStatus}:p);
  };

  const save=()=>{
    if(!form.amount) return alert("Amount required");
    const amt=Number(form.amount);
    if(editId){
      // EDIT: update the existing row only — do not insert a new one
      setData(d=>{
        const updatedVP=d.vendorPayments.map(vp=>
          vp.id===editId?{...vp,...form,amount:amt}:vp
        );
        // Recalc PE pay-status based on real payment sum, not just "Paid" blindly
        const updatedPurchases=recalcPEStatus(updatedVP,d.purchases,form.peNo);
        return {...d,vendorPayments:updatedVP,purchases:updatedPurchases};
      });
      setEditId(null);
    } else {
      // INSERT: new payment
      setData(d=>{
        const newVP={id:uid(),...form,amount:amt};
        const updatedVP=[...d.vendorPayments,newVP];
        const updatedPurchases=recalcPEStatus(updatedVP,d.purchases,form.peNo);
        return {...d,vendorPayments:updatedVP,purchases:updatedPurchases};
      });
    }
    setForm(emptyF);
  };

  return (
    <div className="fade">
      <PH title="Manufacturer Payments" sub="Record payments made to manufacturers"/>
      <div style={{display:"grid",gridTemplateColumns:"1fr 320px",gap:16,alignItems:"start"}}>
        <div style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:10,padding:"12px 16px",marginBottom:14}}>
          <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"flex-end"}}>
            <Field label="Manufacturer"><Inp value={vfMfr} onChange={setVfMfr} placeholder="Search manufacturer..."/></Field>
            <Field label="From"><DateInp value={vfFrom} onChange={setVfFrom}/></Field>
            <Field label="To"><DateInp value={vfTo} onChange={setVfTo}/></Field>
            <Field label="Mode"><Sel value={vfMode} onChange={setVfMode} options={["NEFT","RTGS","Cheque","Cash","UPI"]} placeholder="All Modes"/></Field>
            {(vfMfr||vfFrom||vfTo||vfMode)&&<Btn v="ghost" onClick={()=>{setVfMfr("");setVfFrom("");setVfTo("");setVfMode("");}}>✕ Clear</Btn>}
          </div>
        </div>
        <Tbl headers={["Pay No","Date","Manufacturer","PE No","Amount","Mode","UTR","Bank","Action"]}
          onExport={()=>downloadCSV(data.vendorPayments.map(p=>[p.no,p.date,p.manufacturer||p.vendor||"",p.peNo,p.amount,p.mode,p.utr,p.bank]),["Pay No","Date","Manufacturer","PE No","Amount","Mode","UTR","Bank"],"Manufacturer_Payments")}
          rows={(()=>{
            const parseD2=s=>{if(!s||!s.includes("/"))return null;const[d,m,y]=s.split("/");return new Date(+y,+m-1,+d);};
            return [...(data.vendorPayments||[])].reverse().filter(p=>{
              if(vfMfr&&!(p.manufacturer||p.vendor||"").toLowerCase().includes(vfMfr.toLowerCase())) return false;
              if(vfMode&&p.mode!==vfMode) return false;
              const pd=parseD2(p.date);
              if(vfFrom&&parseD2(vfFrom)&&pd&&pd<parseD2(vfFrom)) return false;
              if(vfTo&&parseD2(vfTo)&&pd&&pd>parseD2(vfTo)) return false;
              return true;
            });
          })().map(p=>[
            <span className="mono" style={{fontWeight:700,color:"#78350f",fontSize:11}}>{p.no}</span>,
            p.date,p.manufacturer||p.vendor||"",<span className="mono" style={{fontSize:11}}>{p.peNo}</span>,
            <span className="mono" style={{fontWeight:700}}>₹{fmt(p.amount)}</span>,
            <Badge s={p.mode}/>,<span className="mono" style={{fontSize:11}}>{p.utr}</span>,
            <span style={{fontSize:11}}>{p.bank}</span>,
            <div style={{display:"flex",gap:4}}>
              <Btn sz="sm" v="ghost" onClick={()=>{setForm({...emptyF,...p,manufacturer:p.manufacturer||p.vendor||"",amount:String(p.amount)});setEditId(p.id);}}>✏️</Btn>
              <Btn sz="sm" v="danger" onClick={()=>{
                if(requestDelete("vendorPayments",p.id,"Manufacturer Payment"))
                  setData(d=>{
                    const updatedVP=d.vendorPayments.filter(x=>x.id!==p.id);
                    // Recalc PE status after deleting this payment
                    const updatedPurchases=recalcPEStatus(updatedVP,d.purchases,p.peNo);
                    return {...d,vendorPayments:updatedVP,purchases:updatedPurchases};
                  });
              }}>Del</Btn>
            </div>
          ])}
        />
        <Card style={{height:"fit-content"}}>
          <div style={{fontWeight:700,fontSize:13,color:"#78350f",marginBottom:12}}>
            {editId?"✏️ Edit Payment":"New Manufacturer Payment"}
          </div>
          <Field label="Payment No"><Inp value={form["no"]} onChange={v=>setForm(f=>({...f,no:v}))} placeholder="VPY-003"/></Field>
            <Field label="Date"><DateInp value={form["date"]} onChange={v=>setForm(f=>({...f,date:v}))}/></Field>
          <Field label="Manufacturer"><Sel value={form.manufacturer} onChange={v=>setForm(f=>({...f,manufacturer:v,peNo:""}))} options={data.manufacturers.map(v=>v.name)} placeholder="Select Manufacturer (Optional)"/></Field>
          <Field label="Against PE No"><Sel value={form.peNo} onChange={v=>setForm(f=>({...f,peNo:v}))} options={data.purchases.filter(p=>p.payStatus!=="Paid"&&(!form.manufacturer||p.manufacturer===form.manufacturer)).map(p=>p.no)} placeholder="Select PE"/></Field>
          <Field label="Amount (₹)" req><Inp value={form.amount} onChange={v=>setForm(f=>({...f,amount:v}))} type="number"/></Field>
          <Field label="Payment Mode"><Sel value={form.mode} onChange={v=>setForm(f=>({...f,mode:v}))} options={PAY_MODES}/></Field>
          <Field label="UTR / Cheque No"><Inp value={form.utr} onChange={v=>setForm(f=>({...f,utr:v}))} placeholder="UTR or Cheque"/></Field>
          <Field label="Bank"><Inp value={form.bank} onChange={v=>setForm(f=>({...f,bank:v}))} placeholder="PNB"/></Field>
          <div style={{marginTop:8,display:"flex",gap:8}}>
            {editId&&<Btn v="outline" full onClick={()=>{setEditId(null);setForm(emptyF);}}>Cancel</Btn>}
            <Btn v="gold" full onClick={save}>💾 {editId?"Update":"Save"} Payment</Btn>
          </div>
        </Card>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// PARTY / DOCTOR LEDGER
// ══════════════════════════════════════════════════════════════

const PartyLedgerModule = ({data,setData}) => {
  const parties=data.parties||[];
  const {show:showAmts} = useAmt();
  const [ledgerType,setLedgerType]=useState("party");
  const [selected,setSelected]=useState("");
  const [fromD,setFromD]=useState("");
  const [toD,setToD]=useState("");

  const parseD=s=>{if(!s||!s.includes("/"))return null;const[dd,mm,yyyy]=s.split("/");return new Date(+yyyy,+mm-1,+dd);};

  // Build ledger entries for selected party/doctor
  const buildLedger=()=>{
    if(!selected) return [];
    const entries=[];

    // Invoices (Debit — amount the party owes)
    data.invoices.filter(i=>{
      if(ledgerType==="party") return i.party===selected;
      return (i.doctor||"")=== selected;
    }).forEach(inv=>{
      entries.push({
        date:inv.date,type:"Invoice",refNo:inv.no,
        narration:`Being goods sold vide Invoice No. ${inv.no}`,
        debit:inv.total,credit:0,tag:"invoice",id:inv.id
      });
    });

    // Payments received (Credit — reduces balance)
    data.payments.filter(p=>{
      if(ledgerType==="party") return p.party===selected;
      const inv=data.invoices.find(i=>i.no===p.invoiceNo);
      return inv&&(inv.doctor||"")===selected;
    }).forEach(pay=>{
      entries.push({
        date:pay.date,type:"Payment",refNo:pay.no,
        narration:`Being payment received by ${pay.mode}${pay.utr?", Ref: "+pay.utr:""}${pay.bank?" — "+pay.bank:""}`,
        debit:0,credit:pay.amount,tag:"payment",id:pay.id
      });
    });

    // Credit Notes (Credit — reduces balance)
    (data.creditDebitNotes||[]).filter(n=>n.type==="Credit Note"&&n.party===selected&&n.status==="Approved").forEach(n=>{
      entries.push({
        date:n.date,type:"Credit Note",refNo:n.no,
        narration:`Being credit allowed vide Credit Note No. ${n.no}${n.reason?" ("+n.reason+")":""}`,
        debit:0,credit:n.total,tag:"credit",id:n.id
      });
    });

    // Debit Notes (Debit — increases balance)
    (data.creditDebitNotes||[]).filter(n=>n.type==="Debit Note"&&n.party===selected&&n.status==="Approved").forEach(n=>{
      entries.push({
        date:n.date,type:"Debit Note",refNo:n.no,
        narration:`Being debit raised vide Debit Note No. ${n.no}${n.reason?" ("+n.reason+")":""}`,
        debit:n.total,credit:0,tag:"debit",id:n.id
      });
    });

    // Payment Adjustments
    (data.paymentAdjustments||[]).filter(a=>a.party===selected).forEach(a=>{
      const isCredit=["Discount Allowed","Write-Off","Advance Adjusted","TDS Deduction"].includes(a.type);
      entries.push({
        date:a.date,type:a.type,refNo:a.refNo||"ADJ",
        narration:`Being ${a.type.toLowerCase()} allowed${a.remarks?" — "+a.remarks:""}`,
        debit:isCredit?0:a.amount,credit:isCredit?a.amount:0,tag:"adj",id:a.id
      });
    });

    // Sort by date
    entries.sort((a,b)=>{
      const da=parseD(a.date),db=parseD(b.date);
      return (da&&db)?da-db:0;
    });

    // Date range filter
    const fd=parseD(fromD),td=parseD(toD);
    const filtered=entries.filter(e=>{
      const d=parseD(e.date);
      if(fd&&d&&d<fd) return false;
      if(td&&d&&d>td) return false;
      return true;
    });

    // Add running balance (Opening balance = debit - credit BEFORE fromD)
    let opening=0;
    if(fd){
      const before=entries.filter(e=>{const d=parseD(e.date);return d&&d<fd;});
      opening=before.reduce((s,e)=>s+e.debit-e.credit,0);
    }

    let running=opening;
    return {entries:filtered,opening,
      rows:filtered.map(e=>{
        running+=e.debit-e.credit;
        return {...e,balance:running};
      })
    };
  };

  const {rows=[],opening=0}=selected?buildLedger():{};
  const totalDebit=rows.reduce((s,r)=>s+r.debit,0);
  const totalCredit=rows.reduce((s,r)=>s+r.credit,0);
  const closingBalance=opening+totalDebit-totalCredit;

  const partyOptions=parties.map(p=>p.name);
  const doctorOptions=data.doctors.map(d=>d.name);
  const options=ledgerType==="party"?partyOptions:doctorOptions;

  const TAG_COLOR={
    invoice:{bg:"#fee2e2",c:"#991b1b"},
    payment:{bg:"#dcfce7",c:"#166534"},
    credit:{bg:"#dbeafe",c:"#1e40af"},
    debit:{bg:"#fef3c7",c:"#92400e"},
    adj:{bg:"#f3e8ff",c:"#6b21a8"},
  };

  return (
    <div className="fade">
      <PH title="Party / Doctor Ledger" sub="Complete account statement with running balance for any party or doctor"/>

      {/* Controls */}
      <Card style={{marginBottom:16}}>
        <div style={{display:"grid",gridTemplateColumns:"200px 1fr 1fr 1fr auto",gap:12,alignItems:"flex-end"}}>
          <Field label="Ledger Type">
            <div style={{display:"flex",gap:6}}>
              {["party","doctor"].map(t=>(
                <button key={t} onClick={()=>{setLedgerType(t);setSelected("");}}
                  style={{flex:1,padding:"8px 10px",borderRadius:7,border:`1.5px solid ${ledgerType===t?C.navy:C.border}`,
                    background:ledgerType===t?C.navy:"#fff",color:ledgerType===t?"#fff":C.text,
                    fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit",textTransform:"capitalize"}}>
                  {t==="party"?"🏢 Party":"👨‍⚕️ Doctor"}
                </button>
              ))}
            </div>
          </Field>
          <Field label={ledgerType==="party"?"Select Party":"Select Doctor"}>
            <Sel value={selected} onChange={setSelected} options={options} placeholder={`-- Choose ${ledgerType} --`}/>
          </Field>
          <Field label="From Date"><DateInp value={fromD} onChange={setFromD}/></Field>
          <Field label="To Date"><DateInp value={toD} onChange={setToD}/></Field>
          {selected&&<Btn v="outline" onClick={()=>downloadCSV(
            rows.map(r=>[r.date,r.type,r.refNo,r.narration,r.debit||"",r.credit||"",r.balance]),
            ["Date","Type","Ref No","Narration","Debit","Credit","Balance"],
            `Ledger_${selected.replace(/\s+/g,"_")}`
          )}>⬇ Export</Btn>}
        </div>
      </Card>

      {!selected&&(
        <div style={{textAlign:"center",padding:60,background:"#fff",borderRadius:12,border:`1px solid ${C.border}`,color:C.muted}}>
          <div style={{fontSize:36,marginBottom:8}}>📒</div>
          <div style={{fontWeight:700,fontSize:15}}>Select a {ledgerType} to view ledger</div>
          <div style={{fontSize:12,marginTop:4}}>Shows all invoices, payments, adjustments and running balance</div>
        </div>
      )}

      {selected&&(
        <>
          {/* Summary KPIs */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:12,alignItems:"start"}}>
            {[
              {label:"Total Billed",val:`₹${fmt(opening>0?opening+totalDebit:totalDebit)}`,icon:"🧾",bg:"linear-gradient(135deg,#1e3a5f,#2F4A6D)",sub:"Debit (Dr)"},
              {label:"Total Received",val:`₹${fmt(totalCredit)}`,icon:"💰",bg:"linear-gradient(135deg,#064e3b,#065f46)",sub:"Credit (Cr)"},
              {label:"Closing Balance",val:`₹${fmt(Math.abs(closingBalance))}`,icon:closingBalance>0?"🔴":"🟢",bg:closingBalance>0?"linear-gradient(135deg,#7f1d1d,#991b1b)":"linear-gradient(135deg,#064e3b,#065f46)",sub:closingBalance>0?"Outstanding (Dr)":"Advance (Cr)"},
              {label:"Transactions",val:rows.length,icon:"📋",bg:"linear-gradient(135deg,#4c1d95,#6d28d9)",sub:"entries"},
            ].map(k=>(
              <div key={k.label} style={{background:k.bg,borderRadius:12,padding:"16px 18px",boxShadow:"0 4px 14px rgba(0,0,0,0.18)",border:"1px solid rgba(255,255,255,0.08)"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                  <span style={{fontSize:11,fontWeight:600,color:"rgba(255,255,255,0.6)",textTransform:"uppercase",letterSpacing:.7}}>{k.label}</span>
                  <span style={{fontSize:20}}>{k.icon}</span>
                </div>
                <div style={{fontSize:20,fontWeight:800,color:"#fff",fontFamily:"monospace"}}>{k.val}</div>
                <div style={{fontSize:10,color:"rgba(255,255,255,0.45)",marginTop:4}}>{k.sub}</div>
              </div>
            ))}
          </div>

          {/* Party info */}
          {ledgerType==="party"&&(()=>{
            const p=parties.find(x=>x.name===selected)||{};
            return p.name?(
              <div style={{background:"#f8fafc",borderRadius:10,border:`1px solid ${C.border}`,padding:"10px 16px",marginBottom:14,display:"flex",gap:24,flexWrap:"wrap",fontSize:12}}>
                <span><strong>Party:</strong> {p.name}</span>
                <span><strong>Type:</strong> {p.partyType}</span>
                <span><strong>GSTIN:</strong> {p.gstin||"—"}</span>
                <span><strong>City:</strong> {p.city}</span>
                <span><strong>Credit Days:</strong> {p.creditDays}d</span>
                <span><strong>Phone:</strong> {p.phone}</span>
              </div>
            ):null;
          })()}

          {/* Opening Balance row */}
          {fromD&&opening!==0&&(
            <div style={{background:"#fffbeb",borderRadius:8,padding:"8px 16px",marginBottom:8,display:"flex",justifyContent:"space-between",fontSize:12,fontWeight:700,border:"1px solid #fde68a"}}>
              <span>Opening Balance (as on {fromD})</span>
              <span style={{color:opening>0?"#dc2626":"#166534"}}>
                {opening>0?`₹${fmt(opening)} Dr`:`₹${fmt(Math.abs(opening))} Cr`}
              </span>
            </div>
          )}

          {/* Ledger Table */}
          <div style={{background:"#fff",borderRadius:12,border:`1px solid ${C.border}`,overflow:"hidden"}}>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead>
                <tr style={{background:C.navy}}>
                  {["Date","Type","Ref No","Narration","Debit (Dr)","Credit (Cr)","Balance"].map(h=>(
                    <th key={h} style={{padding:"10px 12px",fontSize:11,fontWeight:700,color:"#fff",textAlign:h.includes("Debit")||h.includes("Credit")||h==="Balance"?"right":"left",whiteSpace:"nowrap"}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length===0&&(
                  <tr><td colSpan={7} style={{padding:40,textAlign:"center",color:C.muted,fontSize:13}}>No transactions found for selected period</td></tr>
                )}
                {rows.map((r,i)=>(
                  <tr key={r.id+i} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?"#fff":"#fafafa"}}
                    onMouseEnter={e=>e.currentTarget.style.background="#EEF2F6"}
                    onMouseLeave={e=>e.currentTarget.style.background=i%2===0?"#fff":"#fafafa"}>
                    <td style={{padding:"9px 12px",fontSize:12,color:C.muted,whiteSpace:"nowrap"}}>{r.date}</td>
                    <td style={{padding:"9px 12px"}}>
                      <span style={{background:TAG_COLOR[r.tag]?.bg||"#f3f4f6",color:TAG_COLOR[r.tag]?.c||C.text,
                        borderRadius:6,padding:"2px 8px",fontSize:10,fontWeight:700}}>{r.type}</span>
                    </td>
                    <td style={{padding:"9px 12px",fontSize:11,fontFamily:"monospace",fontWeight:700,color:C.accent}}>{r.refNo}</td>
                    <td style={{padding:"9px 12px",fontSize:12,color:C.text,maxWidth:280}}>{r.narration}</td>
                    <td style={{padding:"9px 12px",fontSize:12,fontFamily:"monospace",fontWeight:700,color:"#dc2626",textAlign:"right"}}>
                      {r.debit>0?`₹${fmt(r.debit,2)}`:""}
                    </td>
                    <td style={{padding:"9px 12px",fontSize:12,fontFamily:"monospace",fontWeight:700,color:"#166534",textAlign:"right"}}>
                      {r.credit>0?`₹${fmt(r.credit,2)}`:""}
                    </td>
                    <td style={{padding:"9px 12px",fontSize:12,fontFamily:"monospace",fontWeight:800,
                      color:r.balance>0?"#dc2626":"#166534",textAlign:"right",whiteSpace:"nowrap"}}>
                      ₹{fmt(Math.abs(r.balance),2)} {r.balance>0?"Dr":"Cr"}
                    </td>
                  </tr>
                ))}
              </tbody>
              {rows.length>0&&(
                <tfoot>
                  <tr style={{background:"#1a3352"}}>
                    <td colSpan={4} style={{padding:"10px 12px",fontSize:12,fontWeight:700,color:"#fff"}}>CLOSING BALANCE</td>
                    <td style={{padding:"10px 12px",fontSize:12,fontFamily:"monospace",fontWeight:800,color:"#fca5a5",textAlign:"right"}}>
                      ₹{fmt(totalDebit+(opening>0?opening:0),2)}
                    </td>
                    <td style={{padding:"10px 12px",fontSize:12,fontFamily:"monospace",fontWeight:800,color:"#86efac",textAlign:"right"}}>
                      ₹{fmt(totalCredit+(opening<0?Math.abs(opening):0),2)}
                    </td>
                    <td style={{padding:"10px 12px",fontSize:13,fontFamily:"monospace",fontWeight:800,
                      color:closingBalance>0?"#fca5a5":"#86efac",textAlign:"right"}}>
                      {showAmts?`₹${fmt(Math.abs(closingBalance),2)} ${closingBalance>0?"Dr":"Cr"}`:<span style={{letterSpacing:4,opacity:.4}}>••••••</span>}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// PAYMENT ADJUSTMENTS MODULE
// ══════════════════════════════════════════════════════════════

const OutstandingModule = ({data}) => {
  const parties=data.parties||[];
  const [filter,setFilter]=useState("all");
  const [party,setParty]=useState("");
  const rows=data.invoices.filter(i=>{const s=getInvStatus(i,parties);return filter==="all"||s.toLowerCase()===filter;}).filter(i=>!party||i.party===party);
  return (
    <div className="fade">
      <PH title="Outstanding Ledger" sub="Accounts receivable — all balances and aging"/>
      <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap",alignItems:"center"}}>
        {["all","paid","partial","overdue","current"].map(f=>(
          <Btn key={f} sz="sm" v={filter===f?"navy":"outline"} onClick={()=>setFilter(f)}>{f.charAt(0).toUpperCase()+f.slice(1)}</Btn>
        ))}
        <div style={{marginLeft:"auto",width:200}}><Sel value={party} onChange={setParty} options={parties.map(p=>p.name)} placeholder="All Parties"/></div>
      </div>
      <div style={{display:"flex",gap:10,marginBottom:14}}>
        <KPI label="Total Billed" val={`₹${fmt(rows.reduce((s,i)=>s+i.total,0))}`} color={C.navy} icon="🧾"/>
        <KPI label="Outstanding" val={`₹${fmt(rows.reduce((s,i)=>s+(i.total-i.paid),0))}`} color="#991b1b" icon="⚠️"/>
        <KPI label="Collected" val={`₹${fmt(rows.reduce((s,i)=>s+i.paid,0))}`} color="#166534" icon="✅"/>
      </div>
      <Tbl headers={["Invoice No","Date","Party","Party Type","Channel","Billed","Paid","Balance","Status"]}
        onExport={()=>downloadCSV(rows.map(i=>{const p=getParty(i.party,parties);return[i.no,i.date,i.party,p.partyType||"",i.channel,i.total,i.paid,i.total-i.paid,i.status];}),["Invoice No","Date","Party","Party Type","Channel","Billed","Paid","Balance","Status"],"Outstanding")}
        rows={rows.map(inv=>{
          const p=getParty(inv.party,parties);
          return[
            <span className="mono" style={{fontWeight:700,color:C.accent,fontSize:11}}>{inv.no}</span>,
            inv.date,<strong style={{fontSize:12}}>{inv.party}</strong>,
            <span style={{fontSize:10}}>{p.partyType}</span>,
            <Badge s={inv.channel}/>,
            <span className="mono">₹{fmt(inv.total)}</span>,
            <span className="mono" style={{color:"#166534"}}>₹{fmt(inv.paid)}</span>,
            <span className="mono" style={{fontWeight:800,color:inv.total-inv.paid>0?"#dc2626":"#166534"}}>₹{fmt(inv.total-inv.paid)}</span>,
            <Badge s={getInvStatus(inv,parties)}/>
          ];
        })}
      />
    </div>
  );
};


const CDNModule = ({data,setData,requestDelete}) => {
  const parties=data.parties||[];
  const products=data.products||[];
  const emptyF={no:"",date:today(),type:"Credit Note",party:"",refInv:"",product:"",batch:"",qty:1,rate:"",gst:18,reason:"",status:"Pending"};
  const [form,setForm]=useState(emptyF);
  const [showForm,setShowForm]=useState(false);
  const [editNote,setEditNote]=useState(null);
  const [filterType,setFilterType]=useState("All");
  const [cdnSearch,setCdnSearch]=useState("");
  const [cdnFrom,setCdnFrom]=useState("");
  const [cdnTo,setCdnTo]=useState("");
  const calc=(f)=>{const tx=Number(f.qty||0)*Number(f.rate||0);const cg=Math.round(tx*(Number(f.gst)||18)/200*100)/100;return{taxable:tx,cgst:cg,sgst:cg,total:Math.round(tx+cg*2)};};

  const save=()=>{
    if(!form.no||!form.party||!form.product) return alert("Note No, Party and Product required");
    setData(d=>({...d,creditDebitNotes:[...d.creditDebitNotes,{id:uid(),...form,...calc(form)}]}));
    setForm(emptyF); setShowForm(false);
  };

  const saveEdit=()=>{
    if(!editNote) return;
    const updated={...editNote,...calc(editNote)};
    setData(d=>({...d,creditDebitNotes:d.creditDebitNotes.map(x=>x.id===updated.id?updated:x)}));
    setEditNote(null);
  };

  const approve=(n)=>setData(d=>{
    let invoices=d.invoices;
    const refKey=n.refInv||n.refReturn;
    if(refKey){
      invoices=d.invoices.map(inv=>{
        if(inv.no!==refKey) return inv;
        const adj=n.type==="Credit Note"?n.total:-(n.total);
        const newPaid=Math.min(inv.total,Math.max(0,(inv.paid||0)+adj));
        const st=newPaid>=inv.total?"Paid":newPaid>0?"Partial":inv.status;
        return {...inv,paid:newPaid,status:inv.status==="Cancelled"?"Cancelled":st};
      });
    }
    return {...d,creditDebitNotes:d.creditDebitNotes.map(x=>x.id===n.id?{...x,status:"Approved"}:x),invoices};
  });

  const notes=data.creditDebitNotes||[];
  const filtered=(()=>{
    const parseD=s=>{if(!s||!s.includes("/"))return null;const[d,m,y]=s.split("/");return new Date(+y,+m-1,+d);};
    return notes.filter(n=>{
      if(filterType!=="All"&&n.type!==filterType) return false;
      if(cdnSearch&&!((n.party||"")+(n.product||"")).toLowerCase().includes(cdnSearch.toLowerCase())) return false;
      const nd=parseD(n.date);
      if(cdnFrom&&parseD(cdnFrom)&&nd&&nd<parseD(cdnFrom)) return false;
      if(cdnTo&&parseD(cdnTo)&&nd&&nd>parseD(cdnTo)) return false;
      return true;
    });
  })();

  return (
    <div className="fade">
      <PH title="Credit / Debit Notes" sub="Returns, adjustments and corrections"
        action={<Btn v="gold" onClick={()=>setShowForm(true)}>+ New Note</Btn>}
      />

      <div style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:10,padding:"12px 16px",marginBottom:14}}>
        <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"flex-end"}}>
          <Field label="Search Party/Product"><Inp value={cdnSearch} onChange={setCdnSearch} placeholder="Search..."/></Field>
          <Field label="From"><DateInp value={cdnFrom} onChange={setCdnFrom}/></Field>
          <Field label="To"><DateInp value={cdnTo} onChange={setCdnTo}/></Field>
          {(cdnSearch||cdnFrom||cdnTo)&&<Btn v="ghost" onClick={()=>{setCdnSearch("");setCdnFrom("");setCdnTo("");}}>✕ Clear</Btn>}
        </div>
      </div>
      <div style={{display:"flex",gap:6,marginBottom:14}}>
        {["All","Credit Note","Debit Note"].map(t=>(
          <button key={t} onClick={()=>setFilterType(t)}
            style={{padding:"5px 14px",border:`1px solid ${C.border}`,borderRadius:20,
              background:filterType===t?C.navy:"#fff",color:filterType===t?"#fff":C.text,
              fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:filterType===t?700:400}}>
            {t}
          </button>
        ))}
      </div>

      <Tbl headers={["Note No","Date","Type","Category","Party","Ref","Product","Qty","Rate","Total","Reason","Status","Action"]}
        onExport={()=>downloadCSV(notes.map(n=>[n.no,n.date,n.type,n.returnCategory||"—",n.party,n.refInv||n.refReturn||"",n.product,n.qty,n.rate||0,n.total,n.reason,n.status]),["No","Date","Type","Category","Party","Ref","Product","Qty","Rate","Total","Reason","Status"],"Credit_Debit_Notes")}
        rows={filtered.slice().reverse().map(n=>[
          <span className="mono" style={{fontWeight:700,fontSize:11,color:"#5b21b6"}}>{n.no}</span>,
          <span style={{fontSize:11}}>{n.date}</span>,
          <Badge s={n.type}/>,
          n.returnCategory
            ? <span style={{background:n.isSaleable===false?"#FEF2F2":"#F0FDF4",color:n.isSaleable===false?"#991B1B":"#166534",padding:"2px 7px",borderRadius:8,fontSize:10,fontWeight:700}}>{n.isSaleable===false?"💥 Non-Sale":"↩️ Saleable"}</span>
            : <span style={{fontSize:11,color:C.muted}}>Manual</span>,
          <span style={{fontWeight:600,fontSize:12}}>{n.party}</span>,
          <span className="mono" style={{fontSize:10,color:C.muted}}>{n.refInv||n.refReturn||"—"}</span>,
          <span style={{fontSize:11}}>{n.product}</span>,
          <span className="mono">{n.qty}</span>,
          <span className="mono" style={{color:n.rate?"inherit":"#dc2626",fontWeight:n.rate?400:700}}>
            {n.rate?`₹${fmt(n.rate||0,2)}`:"⚠️ No rate"}
          </span>,
          <span className="mono" style={{fontWeight:700}}>₹{fmt(n.total||0,2)}</span>,
          <span style={{fontSize:11,color:C.muted}}>{n.reason}</span>,
          <Badge s={n.status}/>,
          <div style={{display:"flex",gap:3,flexWrap:"wrap"}}>
            {n.status==="Pending"&&<Btn sz="sm" v="green" onClick={()=>approve(n)}>✓</Btn>}
            <Btn sz="sm" v="outline" onClick={()=>setEditNote({...n})}>✏️</Btn>
            <Btn sz="sm" v="ghost" onClick={()=>{
              const w=window.open("","_blank","width=960,height=800");
              const p=parties.find(x=>x.name===n.party)||{};
              const s=data.settings||{};
              const co=s.companyName||"YAPIDERMA PRIVATE LIMITED";
              const isCredit=n.type==="Credit Note";
              const labelColor=isCredit?"#166534":"#991B1B";
              const labelBg=isCredit?"#DCFCE7":"#FEE2E2";
              const html=[
                "<html><head><title>"+n.no+"</title>",
                "<style>",
                "*{margin:0;padding:0;box-sizing:border-box}",
                "body{font-family:'Segoe UI',Arial,sans-serif;background:#f0f2f5;padding:24px}",
                "@media print{body{background:#fff;padding:0}.no-print{display:none}@page{size:A4;margin:10mm}}",
                ".page{background:#fff;width:210mm;min-height:297mm;margin:0 auto;padding:12mm;box-shadow:0 4px 24px rgba(0,0,0,.12)}",
                ".hdr{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:14px;border-bottom:3px solid #1B3A6B;margin-bottom:18px}",
                ".co-name{font-size:22px;font-weight:900;color:#1B3A6B;letter-spacing:.5px}",
                ".co-sub{font-size:10px;color:#64748b;margin-top:3px}",
                ".cn-badge{background:"+labelBg+";color:"+labelColor+";padding:4px 14px;border-radius:20px;font-size:11px;font-weight:700;display:inline-block;margin-bottom:6px}",
                ".cn-no{font-size:24px;font-weight:900;color:#1B3A6B}",
                ".cn-date{font-size:12px;color:#64748b;margin-top:4px}",
                ".parties{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:18px}",
                ".party-box{background:#f8fafc;border-radius:8px;padding:12px 14px;border:1px solid #e2e8f0}",
                ".party-label{font-size:9px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:.8px;margin-bottom:6px}",
                ".party-name{font-size:14px;font-weight:700;color:#1e293b}",
                ".party-detail{font-size:11px;color:#64748b;margin-top:2px}",
                ".ref-strip{background:#FFF7ED;border:1px solid #FED7AA;border-radius:8px;padding:10px 14px;margin-bottom:18px;font-size:12px;color:#92400E}",
                "table{width:100%;border-collapse:collapse;margin-bottom:18px;font-size:12px}",
                "thead tr{background:#1B3A6B}",
                "thead th{color:#fff;padding:9px 10px;text-align:left;font-size:11px;font-weight:600}",
                "tbody tr{border-bottom:1px solid #e2e8f0}",
                "tbody tr:last-child{border-bottom:none}",
                "tbody td{padding:9px 10px;color:#1e293b}",
                ".total-row td{background:#f8fafc;font-weight:700;border-top:2px solid #1B3A6B}",
                ".net-box{background:"+labelBg+";border:2px solid "+labelColor+";border-radius:10px;padding:14px 20px;text-align:right;margin-bottom:24px}",
                ".net-label{font-size:11px;font-weight:700;color:"+labelColor+";text-transform:uppercase;letter-spacing:.5px}",
                ".net-amt{font-size:26px;font-weight:900;color:"+labelColor+"}",
                ".footer{display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-top:32px;padding-top:16px;border-top:1px solid #e2e8f0}",
                ".sig-box{text-align:center}",
                ".sig-line{border-top:1px solid #94a3b8;padding-top:8px;margin-top:48px;font-size:11px;color:#64748b}",
                ".note-text{font-size:10px;color:#94a3b8;margin-top:8px;text-align:center}",
                "</style></head><body>",
                "<div class='page'>",
                // ── HEADER
                "<div class='hdr'>",
                  "<div>",
                    "<div class='co-name'>"+co+"</div>",
                    "<div class='co-sub'>"+[s.address1,s.city,s.state,s.pin].filter(Boolean).join(", ")+"</div>",
                    "<div class='co-sub'>GSTIN: "+(s.gstin||"")+" | PAN: "+(s.pan||"")+" | DL: "+(s.dl1||"")+"</div>",
                    "<div class='co-sub'>Ph: "+(s.mobile||"")+"</div>",
                  "</div>",
                  "<div style='text-align:right'>",
                    "<div class='cn-badge'>"+n.type+"</div>",
                    "<div class='cn-no'>"+n.no+"</div>",
                    "<div class='cn-date'>Date: "+n.date+"</div>",
                    (n.returnCategory?"<div style='margin-top:6px;font-size:11px;color:#64748b'>"+( n.isSaleable===false?"Non-Saleable Return":"Saleable Return")+"</div>":""),
                  "</div>",
                "</div>",
                // ── PARTY DETAILS
                "<div class='parties'>",
                  "<div class='party-box'>",
                    "<div class='party-label'>From (Supplier)</div>",
                    "<div class='party-name'>"+co+"</div>",
                    "<div class='party-detail'>GSTIN: "+(s.gstin||"")+"</div>",
                    "<div class='party-detail'>"+[s.address1,s.city].filter(Boolean).join(", ")+"</div>",
                  "</div>",
                  "<div class='party-box'>",
                    "<div class='party-label'>To (Party)</div>",
                    "<div class='party-name'>"+n.party+"</div>",
                    (p.gstin?"<div class='party-detail'>GSTIN: "+p.gstin+"</div>":""),
                    (p.address1?"<div class='party-detail'>"+[p.address1,p.city,p.state].filter(Boolean).join(", ")+"</div>":""),
                    (p.phone?"<div class='party-detail'>Ph: "+p.phone+"</div>":""),
                  "</div>",
                "</div>",
                // ── REF STRIP
                "<div class='ref-strip'>",
                  (n.refInv||n.refReturn?"<strong>Ref Invoice/Return:</strong> "+(n.refInv||n.refReturn)+" &nbsp;|&nbsp; ":""),
                  "<strong>Reason:</strong> "+(n.reason||"—"),
                "</div>",
                // ── TABLE
                "<table><thead><tr>",
                "<th>#</th><th>Product</th><th>Batch</th><th>Qty</th><th>Rate (&#8377;)</th><th>GST%</th><th>Taxable (&#8377;)</th><th>CGST (&#8377;)</th><th>SGST (&#8377;)</th><th>Total (&#8377;)</th>",
                "</tr></thead><tbody>",
                "<tr><td>1</td><td>"+n.product+"</td><td>"+( n.batch||"&#8212;")+"</td>",
                "<td>"+n.qty+"</td>",
                "<td>"+fmt(n.rate||0,2)+"</td>",
                "<td>"+( n.gst||18)+"%</td>",
                "<td>"+fmt(n.taxable||0,2)+"</td>",
                "<td>"+fmt(n.cgst||0,2)+"</td>",
                "<td>"+fmt(n.sgst||0,2)+"</td>",
                "<td><strong>"+fmt(n.total||0,2)+"</strong></td></tr>",
                "<tr class='total-row'><td colspan='6' style='text-align:right'>Subtotal</td>",
                "<td>"+fmt(n.taxable||0,2)+"</td><td>"+fmt(n.cgst||0,2)+"</td><td>"+fmt(n.sgst||0,2)+"</td><td><strong>"+fmt(n.total||0,2)+"</strong></td></tr>",
                "</tbody></table>",
                // ── NET BOX
                "<div class='net-box'>",
                  "<div class='net-label'>Net "+( isCredit?"Credit":"Debit")+"</div>",
                  "<div class='net-amt'>&#8377;"+fmt(n.total||0,2)+"</div>",
                  (isCredit?"<div style='font-size:11px;color:"+labelColor+";margin-top:4px'>Amount adjusted against party outstanding / ledger</div>":""),
                "</div>",
                // ── FOOTER SIGNATURES
                "<div class='footer'>",
                  "<div class='sig-box'><div class='sig-line'>Party Acknowledgement</div><div class='note-text'>Received goods as per above details</div></div>",
                  "<div class='sig-box'><div class='sig-line'>For "+co+"</div><div class='note-text'>Authorised Signatory</div></div>",
                "</div>",
                "<div class='note-text' style='margin-top:16px'>This is a computer generated "+n.type+". No signature required if digitally approved.</div>",
                "</div>",
                "<div class='no-print' style='text-align:center;margin-top:20px'>",
                "<button onclick='window.print()' style='padding:12px 32px;background:#1B3A6B;color:#fff;border:none;border-radius:8px;font-size:15px;font-weight:700;cursor:pointer;margin-right:10px'>&#128424; Print / Save PDF</button>",
                "<button onclick='window.close()' style='padding:12px 24px;background:#f1f5f9;color:#374151;border:none;border-radius:8px;font-size:15px;cursor:pointer'>Close</button>",
                "</div></body></html>"
              ].join("");
              w.document.write(html);
              w.document.close();
            }}>🖨️</Btn>
            <Btn sz="sm" v="danger" onClick={()=>{if(requestDelete("creditDebitNotes",n.id,"Credit/Debit Note"))setData(d=>({...d,creditDebitNotes:d.creditDebitNotes.filter(x=>x.id!==n.id)}));}}>Del</Btn>
          </div>
        ])}
      />

      {showForm&&(
        <Modal title="New Credit / Debit Note" onClose={()=>setShowForm(false)} w={640}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,alignItems:"start"}}>
            <Field label="Note No" req><Inp value={form.no} onChange={v=>setForm(f=>({...f,no:v}))} placeholder="CN-002"/></Field>
            <Field label="Date"><DateInp value={form.date} onChange={v=>setForm(f=>({...f,date:v}))}/></Field>
            <Field label="Type"><Sel value={form.type} onChange={v=>setForm(f=>({...f,type:v}))} options={["Credit Note","Debit Note"]}/></Field>
            <Field label="Party" req><Sel value={form.party} onChange={v=>setForm(f=>({...f,party:v}))} options={parties.map(p=>p.name)} placeholder="Select Party"/></Field>
            <Field label="Against Invoice"><Inp value={form.refInv} onChange={v=>setForm(f=>({...f,refInv:v}))} placeholder="INV-001"/></Field>
            <Field label="Status"><Sel value={form.status} onChange={v=>setForm(f=>({...f,status:v}))} options={["Pending","Approved","Rejected"]}/></Field>
            <Field label="Product" req><Sel value={form.product} onChange={v=>{const p=products.find(x=>x.name===v);setForm(f=>({...f,product:v,gst:p?.gst||18}));}} options={products.map(p=>p.name)} placeholder="Select Product"/></Field>
            <Field label="Batch No"><Inp value={form.batch} onChange={v=>setForm(f=>({...f,batch:v}))} placeholder="PCM-001"/></Field>
            <Field label="Qty"><Inp value={form.qty} onChange={v=>setForm(f=>({...f,qty:Number(v)}))} type="number"/></Field>
            <Field label="Rate (Rs.)" req><Inp value={form.rate} onChange={v=>setForm(f=>({...f,rate:Number(v)}))} type="number"/></Field>
            <Field label="GST %"><Sel value={String(form.gst)} onChange={v=>setForm(f=>({...f,gst:Number(v)}))} options={["0","5","12","18","28"]}/></Field>
          </div>
          <Field label="Reason"><Inp value={form.reason} onChange={v=>setForm(f=>({...f,reason:v}))} placeholder="Damaged / Expiry / Wrong product..."/></Field>
          <div style={{background:"#f1f5f9",borderRadius:8,padding:"10px 14px",marginTop:8,fontSize:12,display:"flex",gap:20}}>
            {(()=>{const x=calc(form);return[`Taxable: Rs.${fmt(x.taxable,2)}`,`CGST: Rs.${fmt(x.cgst,2)}`,`SGST: Rs.${fmt(x.sgst,2)}`,`Total: Rs.${fmt(x.total,2)}`].map((s,i)=><span key={i} style={{fontWeight:i===3?800:400}}>{s}</span>);})()}
          </div>
          <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:14}}>
            <Btn v="outline" onClick={()=>setShowForm(false)}>Cancel</Btn>
            <Btn v="gold" onClick={save}>Save Note</Btn>
          </div>
        </Modal>
      )}

      {editNote&&(
        <Modal title={`Edit ${editNote.type} — ${editNote.no}`} onClose={()=>setEditNote(null)} w={640}>
          <div style={{background:"#FFF7ED",border:"1px solid #FED7AA",borderRadius:8,padding:"9px 14px",marginBottom:14,fontSize:11,color:"#92400E"}}>
            Update rate, qty, GST or reason. Total will recalculate.
            {editNote.refReturn&&<span> Auto-generated from return <strong>{editNote.refReturn}</strong>.</span>}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,alignItems:"start"}}>
            <Field label="Note No"><Inp value={editNote.no} onChange={v=>setEditNote(n=>({...n,no:v}))}/></Field>
            <Field label="Date"><DateInp value={editNote.date} onChange={v=>setEditNote(n=>({...n,date:v}))}/></Field>
            <Field label="Type"><Sel value={editNote.type} onChange={v=>setEditNote(n=>({...n,type:v}))} options={["Credit Note","Debit Note"]}/></Field>
            <Field label="Party"><Sel value={editNote.party} onChange={v=>setEditNote(n=>({...n,party:v}))} options={parties.map(p=>p.name)}/></Field>
            <Field label="Ref Invoice"><Inp value={editNote.refInv||editNote.refReturn||""} onChange={v=>setEditNote(n=>({...n,refInv:v}))}/></Field>
            <Field label="Status"><Sel value={editNote.status} onChange={v=>setEditNote(n=>({...n,status:v}))} options={["Pending","Approved","Rejected"]}/></Field>
            <Field label="Product"><Sel value={editNote.product} onChange={v=>{const p=products.find(x=>x.name===v);setEditNote(n=>({...n,product:v,gst:p?.gst||n.gst||18}));}} options={products.map(p=>p.name)}/></Field>
            <Field label="Batch"><Inp value={editNote.batch||""} onChange={v=>setEditNote(n=>({...n,batch:v}))}/></Field>
            <Field label="Qty"><Inp value={editNote.qty} onChange={v=>setEditNote(n=>({...n,qty:Number(v)}))} type="number"/></Field>
            <Field label="Rate (Rs.)" req><Inp value={editNote.rate||""} onChange={v=>setEditNote(n=>({...n,rate:Number(v)}))} type="number" placeholder="Enter rate per unit"/></Field>
            <Field label="GST %"><Sel value={String(editNote.gst||18)} onChange={v=>setEditNote(n=>({...n,gst:Number(v)}))} options={["0","5","12","18","28"]}/></Field>
          </div>
          <Field label="Reason"><Inp value={editNote.reason||""} onChange={v=>setEditNote(n=>({...n,reason:v}))}/></Field>
          <div style={{background:"#f1f5f9",borderRadius:8,padding:"10px 14px",marginTop:10,fontSize:12,display:"flex",gap:20,flexWrap:"wrap"}}>
            {(()=>{const x=calc(editNote);return[`Taxable: Rs.${fmt(x.taxable,2)}`,`CGST: Rs.${fmt(x.cgst,2)}`,`SGST: Rs.${fmt(x.sgst,2)}`,`Total: Rs.${fmt(x.total,2)}`].map((s,i)=><span key={i} style={{fontWeight:i===3?800:400,color:i===3?"#166534":"inherit"}}>{s}</span>);})()}
          </div>
          <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:14}}>
            <Btn v="outline" onClick={()=>setEditNote(null)}>Cancel</Btn>
            <Btn v="gold" onClick={saveEdit}>Save Changes</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// BATCH MANAGEMENT
// ══════════════════════════════════════════════════════════════


const BatchModule = ({data,setData,requestDelete}) => {
  const products=data.products||[];
  const [tab,setTab]=useState("list");
  const [histProduct,setHistProduct]=useState("");
  const [outModal,setOutModal]=useState(null); // {product,batch,out}
  const emptyF={product:"",batch:"",mfg:"",exp:"",opening:0,in:0,out:0,manufacturer:"",notes:"",costPrice:""};
  const [form,setForm]=useState(emptyF);
  const [editBatchId,setEditBatchId]=useState(null);
  const emptyMv={date:today(),product:"",batch:"",type:"IN",qty:1,ref:"",remarks:""};
  const [mvForm,setMvForm]=useState(emptyMv);
  const [bfProduct,setBfProduct]=useState("");
  const [bfMfr,setBfMfr]=useState("");
  const [bfStatus,setBfStatus]=useState("");
  const [bfExpiry,setBfExpiry]=useState("");
  const del=id=>{if(requestDelete("batches",id,"Batch"))setData(d=>({...d,batches:d.batches.filter(b=>b.id!==id)}));};

  // ── Recalculate OUT from actual invoices (fix sync issues) ──
  const recalcOut = () => {
    setData(d=>{
      const newBatches = d.batches.map(b=>{
        // OUT: sum qty + free from all non-cancelled invoices
        const invoiceOut = (d.invoices||[]).flatMap(inv=>
          (inv.status!=="Cancelled")
            ?(inv.items||[]).filter(it=>it.product===b.product&&it.batch===b.batch)
              .map(it=>(Number(it.qty)||0)+(Number(it.free)||0))
            :[]
        ).reduce((s,q)=>s+q,0);
        const manualOut = (d.batchMovements||[])
          .filter(m=>m.product===b.product&&m.batch===b.batch&&m.type==="OUT")
          .reduce((s,m)=>s+Number(m.qty||0),0);
        // IN: purchases that have been received + manual IN movements
        const purchaseIn = (d.purchases||[]).flatMap(pe=>
          (pe.items||[]).filter(it=>it.material===b.product&&(it.batch||"")===(b.batch||""))
            .map(it=>Number(it.qty)||0)
        ).reduce((s,q)=>s+q,0);
        const manualIn = (d.batchMovements||[])
          .filter(m=>m.product===b.product&&m.batch===b.batch&&m.type==="IN")
          .reduce((s,m)=>s+Number(m.qty||0),0);
        return {...b, out: invoiceOut+manualOut, in: purchaseIn+manualIn};
      });
      return {...d, batches: newBatches};
    });
    alert("✅ Stock recalculated from all invoices, purchases and movements.");
  };

  const saveBatch=()=>{
    if(!form.product||!form.batch) return alert("Product and batch required");
    // ── WORKFLOW: Purchase invoice must exist ──
    const _hasPurch=(data.purchases||[]).some(p=>(p.items||[]).some(it=>it.product===form.product));
    if(!_hasPurch&&!window.confirm("⚠️ No purchase invoice found for '"+form.product+"'.\nRecord a purchase invoice before adding batch stock.\n\nAdd batch anyway?")) return;
    if(editBatchId){
      setData(d=>({...d,batches:d.batches.map(b=>b.id===editBatchId?{...b,...form,opening:Number(form.opening),in:Number(form.in),out:Number(form.out),costPrice:Number(form.costPrice||0)}:b)}));
      setEditBatchId(null);
    } else {
      setData(d=>({...d,batches:[...d.batches,{id:uid(),...form,opening:Number(form.opening),in:Number(form.in),out:Number(form.out),costPrice:Number(form.costPrice||0)}]}));
    }
    setForm(emptyF);
    setTab("list");
  };
  const saveMv=()=>{
    if(!mvForm.product||!mvForm.batch) return alert("Product and batch required");
    setData(d=>({...d,
      batchMovements:[...d.batchMovements,{id:uid(),...mvForm,qty:Number(mvForm.qty)}],
      batches:d.batches.map(b=>b.product===mvForm.product&&b.batch===mvForm.batch?{...b,[mvForm.type==="IN"?"in":"out"]:b[mvForm.type==="IN"?"in":"out"]+Number(mvForm.qty)}:b)
    }));
    setMvForm(emptyMv);
  };
  const histBatches=data.batches.filter(b=>b.product===histProduct);
  const histMovements=data.batchMovements.filter(m=>m.product===histProduct);

  // Build OUT drill-down: invoices that consumed this batch
  const getOutDetail=(product,batch)=>{
    const invoiceSales=(data.invoices||[]).filter(inv=>inv.status!=="Cancelled")
      .flatMap(inv=>{
        const items=(inv.items||[]).filter(it=>it.product===product&&it.batch===batch);
        return items.map(it=>({
          date:inv.date, invoiceNo:inv.no, party:inv.party,
          qty:Number(it.qty)||0, type:"Invoice Sale"
        }));
      });
    const manualOuts=(data.batchMovements||[]).filter(m=>m.product===product&&m.batch===batch&&m.type==="OUT")
      .map(m=>({date:m.date,invoiceNo:m.ref||"Manual",party:m.remarks||"Manual Adjustment",qty:Number(m.qty)||0,type:"Manual"}));
    return [...invoiceSales,...manualOuts].sort((a,b)=>b.date>a.date?1:-1);
  };

  return (
    <div className="fade">
      <PH title="Batch Management" sub="Track inventory batches, movements and expiry"
        action={<div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <Btn v="outline" sz="sm" onClick={recalcOut} title="Recalculate OUT from all invoices">🔄 Sync Stock</Btn>
          <BulkUpload title="Opening Stock"
            templateHeaders={["Product","Batch No","Mfg Date","Exp Date","Opening Qty","Manufacturer"]}
            templateRows={[["GLURIV-C Serum","RXGC-001","01/01/2025","31/12/2026","500","Sun Pharma"]]}
            parseRow={(r,i)=>r.Product&&r["Batch No"]?{id:"B"+Date.now()+"_"+i,product:r.Product,batch:r["Batch No"],mfg:r["Mfg Date"]||"",exp:r["Exp Date"]||"",opening:parseInt(r["Opening Qty"])||0,in:0,out:0,manufacturer:r.Manufacturer||"",notes:"Bulk import"}:null}
            onUpload={(rows,mode)=>setData(d=>({...d,batches:mode==="replace"?rows:[...d.batches,...rows.filter(r=>!d.batches.find(x=>x.product===r.product&&x.batch===r.batch))]}))}
          />
          {[["list","📋 Batches"],["entry","➕ Add Batch"],["movement","📦 Stock Movement"],["history","📈 History"]].map(([t,l])=>(
            <Btn key={t} v={tab===t?"navy":"outline"} onClick={()=>setTab(t)}>{l}</Btn>
          ))}
        </div>}
      />

      {/* ── OUT DRILL-DOWN MODAL ── */}
      {outModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:3000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{background:"#fff",borderRadius:16,width:"100%",maxWidth:640,maxHeight:"80vh",display:"flex",flexDirection:"column",boxShadow:"0 20px 60px rgba(0,0,0,0.25)"}}>
            <div style={{padding:"16px 20px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontSize:15,fontWeight:700,color:C.navy}}>📦 Stock OUT Detail</div>
                <div style={{fontSize:12,color:C.muted,marginTop:2}}>{outModal.product} · Batch: {outModal.batch} · Total OUT: {outModal.totalOut} units</div>
              </div>
              <button onClick={()=>setOutModal(null)} style={{background:C.sectionBg,border:"none",fontSize:18,cursor:"pointer",color:C.muted,lineHeight:1,borderRadius:6,width:30,height:30,display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
            </div>
            <div style={{overflowY:"auto",flex:1,padding:"0 0 8px"}}>
              {outModal.rows.length===0&&<div style={{textAlign:"center",padding:40,color:C.muted,fontSize:13}}>No sale records found for this batch</div>}
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead>
                  <tr style={{background:C.sectionBg,position:"sticky",top:0}}>
                    {["Date","Invoice No","Party / Ref","Qty","Type"].map(h=>(
                      <th key={h} style={{padding:"9px 14px",textAlign:"left",fontWeight:700,fontSize:10,color:C.navy,textTransform:"uppercase",letterSpacing:.7,whiteSpace:"nowrap"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {outModal.rows.map((r,i)=>(
                    <tr key={i} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?"#fff":"#fafafa"}}>
                      <td style={{padding:"9px 14px",color:C.muted,whiteSpace:"nowrap"}}>{r.date}</td>
                      <td style={{padding:"9px 14px",fontFamily:"monospace",fontWeight:700,color:C.accent}}>{r.invoiceNo}</td>
                      <td style={{padding:"9px 14px",fontWeight:600,color:C.text}}>{r.party}</td>
                      <td style={{padding:"9px 14px",fontFamily:"monospace",fontWeight:800,color:"#dc2626"}}>{r.qty}</td>
                      <td style={{padding:"9px 14px"}}>
                        <span style={{background:r.type==="Invoice Sale"?"#dcfce7":"#fef3c7",color:r.type==="Invoice Sale"?"#166534":"#92400e",padding:"2px 8px",borderRadius:10,fontSize:10,fontWeight:700}}>{r.type}</span>
                      </td>
                    </tr>
                  ))}
                  <tr style={{background:"#1a3352"}}>
                    <td colSpan={3} style={{padding:"10px 14px",color:"#fff",fontWeight:700,fontSize:12}}>TOTAL OUT</td>
                    <td style={{padding:"10px 14px",fontFamily:"monospace",fontWeight:800,color:"#fca5a5",fontSize:13}}>{outModal.totalOut}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab==="list"&&(()=>{
        const filtBatches=data.batches.filter(b=>{
          const c=b.opening+b.in-b.out;
          const dl=daysLeft(b.exp);
          const st=c<=0?"OUT OF STOCK":c<=50?"LOW STOCK":"IN STOCK";
          const expSt=dl<=0?"EXPIRED":dl<=60?"CRITICAL":dl<=90?"WARNING":"OK";
          if(bfProduct&&b.product!==bfProduct) return false;
          if(bfMfr&&b.manufacturer!==bfMfr) return false;
          if(bfStatus&&st!==bfStatus) return false;
          if(bfExpiry&&expSt!==bfExpiry) return false;
          return true;
        });
        const bfHas=bfProduct||bfMfr||bfStatus||bfExpiry;
        const allMfrs=["All",...new Set(data.batches.map(b=>b.manufacturer).filter(Boolean))];
        return(<>
          <div style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:10,padding:"12px 16px",marginBottom:14}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <span style={{fontWeight:700,fontSize:12,color:C.navy}}>🔍 Filters</span>
              {bfHas&&<button onClick={()=>{setBfProduct("");setBfMfr("");setBfStatus("");setBfExpiry("");}} style={{fontSize:11,color:"#dc2626",background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>✕ Clear</button>}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,alignItems:"start"}}>
              <Field label="Product"><Sel value={bfProduct} onChange={setBfProduct} options={products.map(p=>p.name)} placeholder="All Products"/></Field>
              <Field label="Manufacturer"><Sel value={bfMfr} onChange={setBfMfr} options={allMfrs} placeholder="All Manufacturers"/></Field>
              <Field label="Stock Status"><Sel value={bfStatus} onChange={setBfStatus} options={["IN STOCK","LOW STOCK","OUT OF STOCK"]} placeholder="All Stock"/></Field>
              <Field label="Expiry Status"><Sel value={bfExpiry} onChange={setBfExpiry} options={["OK","WARNING","CRITICAL","EXPIRED"]} placeholder="All Expiry"/></Field>
            </div>
            {bfHas&&<div style={{marginTop:6,fontSize:11,color:C.muted}}>{filtBatches.length} batches · Closing value: ₹{fmt(filtBatches.reduce((s,b)=>{const p=products.find(x=>x.name===b.product);return s+(b.opening+b.in-b.out)*(p?.mrp||0);},0))}</div>}
          </div>
          <Tbl headers={["Product","Batch No","Mfg","Exp","Days Left","Cost/Unit","Opening","IN","OUT ↗","Closing","Manufacturer","Status","Action"]}
            onExport={()=>downloadCSV(filtBatches.map(b=>{const c=b.opening+b.in-b.out;const d=daysLeft(b.exp);return[b.product,b.batch,b.mfg,b.exp,d<=0?"EXPIRED":d,b.costPrice||"",b.opening,b.in,b.out,c,b.manufacturer||"",c<=0?"OUT OF STOCK":c<=50?"LOW STOCK":"IN STOCK"];}),["Product","Batch","Mfg","Exp","Days Left","Cost/Unit","Opening","IN","OUT","Closing","Manufacturer","Status"],"Batch_Inventory")}
            rows={filtBatches.map(b=>{
              const c=b.opening+b.in-b.out;const d=daysLeft(b.exp);
              const st=c<=0?"OUT OF STOCK":c<=50?"LOW STOCK":"IN STOCK";
              return[
                <span style={{fontSize:12}}>{b.product}</span>,
                <span className="mono" style={{fontWeight:700}}>{b.batch}</span>,
                b.mfg,b.exp,
                <span className="mono" style={{fontWeight:800,color:d<=0?"#dc2626":d<=60?"#ea580c":d<=90?"#d97706":"#166534"}}>{d<=0?`EXP(${Math.abs(d)}d)`:d}</span>,
                b.costPrice?<span className="mono" style={{fontWeight:700,color:"#7c3aed"}}>₹{b.costPrice}</span>:<span style={{fontSize:10,color:"#dc2626"}}>Not set</span>,
                <span className="mono">{b.opening}</span>,
                <span className="mono" style={{color:"#166534",fontWeight:700}}>+{b.in}</span>,
                /* OUT — clickable to show drill-down */
                <button onClick={()=>{
                  const rows=getOutDetail(b.product,b.batch);
                  setOutModal({product:b.product,batch:b.batch,totalOut:b.out,rows});
                }} style={{
                  background:"none",border:"none",cursor:"pointer",fontFamily:"monospace",
                  fontWeight:800,color:b.out>0?"#dc2626":"#9ca3af",
                  fontSize:13,padding:"2px 6px",borderRadius:6,
                  textDecoration:b.out>0?"underline":"none",
                  textDecorationStyle:"dotted",
                  transition:"background .1s"
                }} title="Click to see invoice-wise breakdown"
                  onMouseEnter={e=>{if(b.out>0)e.currentTarget.style.background="#fee2e2";}}
                  onMouseLeave={e=>e.currentTarget.style.background="none"}>
                  {b.out>0?`-${b.out} ↗`:`-${b.out}`}
                </button>,
                <span className="mono" style={{fontWeight:800,fontSize:13}}>{c}</span>,
                <span style={{fontSize:11}}>{b.manufacturer||"—"}</span>,
                <Badge s={st}/>,
                <div style={{display:"flex",gap:4}}>
                  <Btn sz="sm" v="ghost" onClick={()=>{setForm({...emptyF,...b,opening:String(b.opening),in:String(b.in),out:String(b.out),costPrice:String(b.costPrice||"")});setEditBatchId(b.id);setTab("entry");}}>✏️</Btn>
                  <Btn sz="sm" v="danger" onClick={()=>del(b.id)}>Del</Btn>
                </div>
              ];
            })}
            empty="No batches match filters."
          />
        </>);
      })()}
      {tab==="entry"&&(
        <Card style={{maxWidth:720}}>
          <div style={{fontWeight:700,fontSize:14,color:C.navy,marginBottom:14}}>{editBatchId?"✏️ Edit Batch":"➕ Add New Batch"}</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,alignItems:"start"}}>
            <Field label="Product" req><Sel value={form.product} onChange={v=>setForm(f=>({...f,product:v}))} options={products.map(p=>p.name)} placeholder="Select Product"/></Field>
            <Field label="Batch No" req><Inp value={form.batch} onChange={v=>setForm(f=>({...f,batch:v}))} placeholder="e.g. PCM-005"/></Field>
            <Field label="Mfg Date (Month/Year)"><MonthYearInp value={form.mfg} onChange={v=>setForm(f=>({...f,mfg:v}))}/></Field>
            <Field label="Exp Date (Month/Year)"><MonthYearInp value={form.exp} onChange={v=>setForm(f=>({...f,exp:v}))}/></Field>
            <Field label="Manufacturing Cost (₹/unit)" req>
              <Inp value={form.costPrice} onChange={v=>setForm(f=>({...f,costPrice:v}))} type="number" placeholder="Cost per unit for this batch"/>
            </Field>
            <Field label="Opening Stock"><Inp value={form.opening} onChange={v=>setForm(f=>({...f,opening:v}))} type="number"/></Field>
            <Field label="Initial Stock IN"><Inp value={form.in} onChange={v=>setForm(f=>({...f,in:v}))} type="number"/></Field>
            <Field label="Initial Stock OUT"><Inp value={form.out} onChange={v=>setForm(f=>({...f,out:v}))} type="number"/></Field>
            <Field label="Manufacturer"><Sel value={form.manufacturer} onChange={v=>setForm(f=>({...f,manufacturer:v}))} options={data.manufacturers.map(m=>m.name)} placeholder="Select Manufacturer"/></Field>
            <Field label="Notes"><Inp value={form.notes} onChange={v=>setForm(f=>({...f,notes:v}))} placeholder="Notes about this batch"/></Field>
          </div>
          <div style={{marginTop:14,display:"flex",gap:8,justifyContent:"flex-end"}}>
            <Btn v="outline" onClick={()=>{setForm(emptyF);setEditBatchId(null);setTab("list");}}>Cancel</Btn>
            <Btn v="gold" onClick={saveBatch}>{editBatchId?"💾 Update Batch":"💾 Add Batch"}</Btn>
          </div>
        </Card>
      )}
      {tab==="movement"&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 360px",gap:16,alignItems:"start"}}>
          <Tbl headers={["Date","Product","Batch","Type","Qty","Reference","Remarks"]}
            onExport={()=>downloadCSV(data.batchMovements.map(m=>[m.date,m.product,m.batch,m.type,m.qty,m.ref,m.remarks]),["Date","Product","Batch","Type","Qty","Reference","Remarks"],"Batch_Movements")}
            rows={[...data.batchMovements].reverse().map(m=>[
              m.date,<span style={{fontSize:11}}>{m.product}</span>,
              <span className="mono" style={{fontWeight:600}}>{m.batch}</span>,
              <Badge s={m.type}/>,<span className="mono" style={{fontWeight:700}}>{m.qty}</span>,
              <span className="mono" style={{fontSize:11}}>{m.ref}</span>,
              <span style={{fontSize:11,color:C.muted}}>{m.remarks}</span>,
            ])}
          />
          <Card style={{height:"fit-content"}}>
            <div style={{fontWeight:700,fontSize:13,color:C.navy,marginBottom:12}}>Record Movement</div>
            <Field label="Date"><DateInp value={mvForm.date} onChange={v=>setMvForm(f=>({...f,date:v}))}/></Field>
            <Field label="Product"><Sel value={mvForm.product} onChange={v=>setMvForm(f=>({...f,product:v,batch:""}))} options={products.map(p=>p.name)} placeholder="Select Product"/></Field>
            <Field label="Batch No"><Sel value={mvForm.batch} onChange={v=>setMvForm(f=>({...f,batch:v}))} options={data.batches.filter(b=>b.product===mvForm.product).map(b=>b.batch)} placeholder="Select Batch"/></Field>
            <Field label="Type"><Sel value={mvForm.type} onChange={v=>setMvForm(f=>({...f,type:v}))} options={["IN","OUT"]}/></Field>
            <Field label="Qty"><Inp value={mvForm.qty} onChange={v=>setMvForm(f=>({...f,qty:v}))} type="number"/></Field>
            <Field label="Reference"><Inp value={mvForm.ref} onChange={v=>setMvForm(f=>({...f,ref:v}))} placeholder="INV-007 / PE-005"/></Field>
            <Field label="Remarks"><Inp value={mvForm.remarks} onChange={v=>setMvForm(f=>({...f,remarks:v}))} placeholder="Sale / Purchase / Adjustment"/></Field>
            <div style={{marginTop:8}}><Btn v="gold" full onClick={saveMv}>💾 Save Movement</Btn></div>
          </Card>
        </div>
      )}
      {tab==="history"&&(
        <div>
          <Card style={{marginBottom:16}}>
            <div style={{display:"flex",gap:12,alignItems:"flex-end"}}>
              <div style={{flex:1}}><Field label="Select Product to View Full History"><Sel value={histProduct} onChange={setHistProduct} options={products.map(p=>p.name)} placeholder="Select a product"/></Field></div>
              {histProduct&&<Btn v="outline" sz="sm" onClick={()=>downloadCSV(histMovements.map(m=>[m.date,m.product,m.batch,m.type,m.qty,m.ref,m.remarks]),["Date","Product","Batch","Type","Qty","Ref","Remarks"],`Batch_History_${histProduct}`)}>⬇ Export</Btn>}
            </div>
          </Card>
          {histProduct&&(
            <>
              <div style={{display:"flex",gap:10,marginBottom:14,flexWrap:"wrap"}}>
                {histBatches.map(b=>{const c=b.opening+b.in-b.out;const d=daysLeft(b.exp);return(
                  <Card key={b.id} style={{flex:1,minWidth:150,borderTop:`3px solid ${d<=0?"#dc2626":d<=60?"#ea580c":"#166534"}`}}>
                    <div className="mono" style={{fontWeight:800,fontSize:15,color:C.navy}}>{b.batch}</div>
                    <div style={{fontSize:10,color:C.muted}}>Exp: {b.exp}</div>
                    <div style={{fontSize:11,marginTop:3}}>Closing: <strong style={{color:C.navy}}>{c} units</strong></div>
                    <div style={{marginTop:4}}><Badge s={d<=0?"EXPIRED":d<=60?"CRITICAL":"OK"}/></div>
                  </Card>
                );})}
              </div>
              <Tbl headers={["Date","Batch","Type","Qty","Reference","Remarks"]}
                rows={histMovements.length===0?[]:histMovements.map(m=>[
                  m.date,<span className="mono" style={{fontWeight:600}}>{m.batch}</span>,
                  <Badge s={m.type}/>,<span className="mono" style={{fontWeight:700}}>{m.qty}</span>,
                  <span className="mono" style={{fontSize:11}}>{m.ref}</span>,
                  <span style={{fontSize:11,color:C.muted}}>{m.remarks}</span>
                ])}
                empty={`No movements recorded for ${histProduct} yet.`}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};


const DoctorCRM = ({data,setData,requestDelete,users}) => {
  const products=data.products||[];
  const [view,setView]=useState("doctors");
  const [showForm,setShowForm]=useState(false);
  const emptyD={name:"",spec:"",clinic:"",address1:"",address2:"",city:"",state:"",region:"",phone:"",email:"",assignedMrId:"",billingChannel:"Direct",linkedPartyId:""};
  const [dfSearch,setDfSearch]=useState("");
  const [dfSpec,setDfSpec]=useState("");
  const [dfCity,setDfCity]=useState("");
  const [dfState,setDfState]=useState("");
  const [vfDoctor,setVfDoctor]=useState("");
  const [vfFrom,setVfFrom]=useState("");
  const [vfTo,setVfTo]=useState("");
  const emptyV={date:today(),doctor:"",spec:"",clinic:"",products:"",samples:"",notes:"",followUp:""};
  const emptyS={no:"",date:today(),doctor:"",product:"",batch:"",qty:1,remarks:""};
  const DIT_TYPES=["Gift","CME/Event","Cash Incentive","Digital Support","Training","Tour/Travel","Other"];
  const emptyDIT={date:today(),doctorId:"",serviceType:"Gift",description:"",value:"",notes:""};
  const [actDoc,setActDoc]=useState("");
  const [ditF,setDitF]=useState(emptyDIT);
  const [dF,setDF]=useState(emptyD);
  const [editDocId,setEditDocId]=useState(null);
  const [vF,setVF]=useState(emptyV);
  const [sF,setSF]=useState(emptyS);
  const saveD=()=>{
    if(!dF.name) return alert("Name required");
    const {assignedMrId,...docFields}=dF;
    if(editDocId){
      const updatedDoctor = normalizeDoctor({ ...data.doctors.find(x=>x.id===editDocId), ...docFields });
      setData(d=>{
        const newAssign=(d.mrDoctorAssign||[]).filter(a=>a.doctorId!==editDocId);
        if(assignedMrId) newAssign.push({mrId:assignedMrId,doctorId:editDocId});
        return {...d,doctors:d.doctors.map(x=>x.id===editDocId?updatedDoctor:x),mrDoctorAssign:newAssign};
      });
      setEditDocId(null);
    } else {
      const id="DR"+Date.now();
      setData(d=>{
        const newDocs=[...d.doctors,normalizeDoctor({id,...docFields})];
        const newAssign=(d.mrDoctorAssign||[]).filter(a=>a.doctorId!==id);
        if(assignedMrId) newAssign.push({mrId:assignedMrId,doctorId:id});
        return {...d,doctors:newDocs,mrDoctorAssign:newAssign};
      });
    }
    setDF(emptyD); setShowForm(false);
  };
  const startEditDoc=(doc)=>{
    const currentAssign=(data.mrDoctorAssign||[]).find(a=>a.doctorId===doc.id);
    setDF({name:doc.name||"",spec:doc.spec||"",clinic:doc.clinic||"",address1:doc.address1||"",address2:doc.address2||"",city:doc.city||"",state:doc.state||"",region:doc.region||"",phone:doc.phone||"",email:doc.email||"",assignedMrId:currentAssign?.mrId||"",billingChannel:doc.billingChannel||"Direct",linkedPartyId:doc.linkedPartyId||""});
    setEditDocId(doc.id);setShowForm(true);
  };
  const saveV=()=>{
    if(!vF.doctor) return alert("Doctor required");
    setData(d=>({...d,doctorVisits:[...d.doctorVisits,{id:uid(),...vF}],doctors:d.doctors.map(doc=>doc.name===vF.doctor?{...doc,lastVisit:vF.date}:doc)}));
    setVF(emptyV);
  };
  const saveS=()=>{
    if(!sF.doctor||!sF.product) return alert("Doctor and product required");
    const p=products.find(x=>x.name===sF.product);
    setData(d=>({...d,samples:[...d.samples,{id:uid(),...sF,qty:Number(sF.qty),value:(p?.mrp||0)*Number(sF.qty)}]}));
    setSF(emptyS);
  };
  const selDoc=actDoc?data.doctors.find(d=>d.id===actDoc):null;
  const docServices=(data.doctorServices||[]).filter(s=>!actDoc||s.doctorId===actDoc);
  const linkedParty=selDoc?.linkedPartyId?data.parties.find(p=>p.id===selDoc.linkedPartyId):null;
  const sirInvoices=(data.invoices||[]).filter(inv=>{
    if(!selDoc) return false;
    if(selDoc.billingChannel==="Direct"&&inv.billDoctor===selDoc.name) return true;
    if(selDoc.billingChannel==="Through Medical Store"&&linkedParty&&inv.party===linkedParty.name) return true;
    return false;
  });
  const totalDIT=docServices.reduce((s,x)=>s+Number(x.value||0),0);
  const totalSIR=sirInvoices.reduce((s,i)=>s+Number(i.total||0),0);
  const saveDIT=()=>{
    if(!ditF.doctorId||!ditF.serviceType||!ditF.value) return alert("Doctor, Service Type aur Value required hain");
    const doc=data.doctors.find(d=>d.id===ditF.doctorId);
    setData(d=>({...d,doctorServices:[...(d.doctorServices||[]),{id:"DIT"+Date.now(),...ditF,doctorName:doc?.name||"",value:Number(ditF.value)}]}));
    setDitF(emptyDIT);
  };
  return (
    <div className="fade">
      <PH title="Doctor CRM" sub="Doctor database, visits, samples and ROI tracking"
        action={<div style={{display:"flex",gap:8}}>
          {[["doctors","👨‍⚕️ Doctors"],["visits","📅 Visits"],["samples","💊 Samples"],["activity","🎯 Activity & ROI"]].map(([t,l])=>(
            <Btn key={t} v={view===t?"navy":"outline"} onClick={()=>setView(t)}>{l}</Btn>
          ))}
        </div>}
      />
      {view==="doctors"&&(<>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginBottom:10}}>
          <BulkUpload title="Doctor CRM"
            templateHeaders={["Name","Specialization","Clinic","City","State","Phone","Email","Category"]}
            templateRows={[["Dr. Sharma","Dermatologist","Skin Clinic","Delhi","Delhi","9811001001","dr@mail.com","A"]]}
            parseRow={(r,i)=>r.Name?{id:"DR"+Date.now()+"_"+i,name:r.Name,spec:r.Specialization||"",clinic:r.Clinic||"",city:r.City||"",state:r.State||"",phone:r.Phone||"",email:r.Email||"",category:r.Category||"B",lastVisit:""}:null}
            onUpload={(rows,mode)=>setData(d=>({...d,doctors:mode==="replace"?rows:[...d.doctors,...rows.filter(r=>!d.doctors.find(x=>x.name?.toLowerCase()===r.name?.toLowerCase()))]}))}
          />
          <Btn v="gold" onClick={()=>setShowForm(true)}>+ Add Doctor</Btn>
        </div>
        {(()=>{
          const allSpecs=["All",...new Set(data.doctors.map(d=>d.spec).filter(Boolean))];
          const allCities=["All",...new Set(data.doctors.map(d=>d.city).filter(Boolean))];
          const allStates=["All",...new Set(data.doctors.map(d=>d.state).filter(Boolean))];
          const filtDocs=data.doctors.filter(d=>{
            if(dfSearch&&!d.name.toLowerCase().includes(dfSearch.toLowerCase())&&!d.clinic.toLowerCase().includes(dfSearch.toLowerCase())) return false;
            if(dfSpec&&dfSpec!=="All"&&d.spec!==dfSpec) return false;
            if(dfCity&&dfCity!=="All"&&d.city!==dfCity) return false;
            if(dfState&&dfState!=="All"&&d.state!==dfState) return false;
            return true;
          });
          const dfHas=dfSearch||dfSpec||dfCity||dfState;
          return(<>
            <div style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:10,padding:"12px 16px",marginBottom:14}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <span style={{fontWeight:700,fontSize:12,color:C.navy}}>🔍 Filters</span>
                {dfHas&&<button onClick={()=>{setDfSearch("");setDfSpec("");setDfCity("");setDfState("");}} style={{fontSize:11,color:"#dc2626",background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>✕ Clear</button>}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,alignItems:"start"}}>
                <Field label="Search Name/Clinic"><Inp value={dfSearch} onChange={setDfSearch} placeholder="Search..."/></Field>
                <Field label="Specialization"><Sel value={dfSpec} onChange={setDfSpec} options={allSpecs} placeholder="All Specs"/></Field>
                <Field label="City"><Sel value={dfCity} onChange={setDfCity} options={allCities} placeholder="All Cities"/></Field>
                <Field label="State"><Sel value={dfState} onChange={setDfState} options={allStates} placeholder="All States"/></Field>
              </div>
              {dfHas&&<div style={{marginTop:6,fontSize:11,color:C.muted}}>{filtDocs.length} doctors found</div>}
            </div>
            <Tbl headers={["ID","Name","Specialization","Clinic","City","State","Region","Phone","Last Visit","Assigned MR","Action"]}
              onExport={()=>downloadCSV(filtDocs.map(d=>[d.id,d.name,d.spec,d.clinic,d.city,d.state,d.phone,d.lastVisit||""]),["ID","Name","Spec","Clinic","City","State","Phone","Last Visit"],"Doctors")}
              rows={filtDocs.map(doc=>[
                <span className="mono" style={{fontSize:10,color:C.muted}}>{doc.id}</span>,
                <strong style={{fontSize:12}}>{doc.name}</strong>,<Badge s={doc.spec}/>,
                <span style={{fontSize:11}}>{doc.clinic}</span>,doc.city,doc.state,
                doc.region?<span style={{background:"#e0f2fe",color:"#0369a1",padding:"2px 7px",borderRadius:8,fontSize:10,fontWeight:600}}>{doc.region}</span>:"—",
                doc.phone,
                <span style={{fontSize:11,color:C.accent}}>{doc.lastVisit||"—"}</span>,
                (()=>{const assign=data.mrDoctorAssign?.find(a=>a.doctorId===doc.id);const mr=assign&&users.find(u=>u.id===assign.mrId);return mr?<span style={{fontSize:10,fontWeight:600,color:"#065f46",background:"#dcfce7",padding:"2px 8px",borderRadius:6}}>👤 {mr.name}</span>:<span style={{fontSize:10,color:"#b45309",background:"#FFF4E5",padding:"2px 8px",borderRadius:6}}>⚠️ Unassigned</span>;})(),
                <div style={{display:"flex",gap:4}}>
                  <Btn sz="sm" v="ghost" onClick={()=>startEditDoc(doc)}>✏️</Btn>
                  <Btn sz="sm" v="danger" onClick={()=>{if(requestDelete("doctors",doc.id,"Doctor"))setData(d=>({...d,doctors:d.doctors.filter(x=>x.id!==doc.id)}));}}>Del</Btn>
                </div>
              ])}
              empty="No doctors match filters."
            />
          </>);
        })()}
        {showForm&&(<Modal title={editDocId?"Edit Doctor":"Add Doctor"} onClose={()=>{setShowForm(false);setEditDocId(null);setDF(emptyD);}} w={560}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,alignItems:"start"}}>
            <Field label="Name" req><Inp value={dF.name} onChange={v=>setDF(f=>({...f,name:v}))} placeholder="Dr. Name"/></Field>
            <Field label="Specialization"><Sel value={dF.spec} onChange={v=>setDF(f=>({...f,spec:v}))} options={SPECIALIZATIONS} placeholder="Select"/></Field>
            <Field label="Clinic"><Inp value={dF.clinic} onChange={v=>setDF(f=>({...f,clinic:v}))} placeholder="Clinic / Hospital"/></Field>
            <Field label="City"><Inp value={dF.city} onChange={v=>setDF(f=>({...f,city:v}))} placeholder="Delhi"/></Field>
            <Field label="Address 1" style={{gridColumn:"span 2"}}><Inp value={dF.address1||""} onChange={v=>setDF(f=>({...f,address1:v}))} placeholder="Street / Area / Building"/></Field>
            <Field label="Address 2" style={{gridColumn:"span 2"}}><Inp value={dF.address2||""} onChange={v=>setDF(f=>({...f,address2:v}))} placeholder="Landmark / Pincode (optional)"/></Field>
            <Field label="State"><Sel value={dF.state} onChange={v=>setDF(f=>({...f,state:v}))} options={INDIAN_STATES} placeholder="Select State"/></Field>
            <Field label="Phone"><Inp value={dF.phone} onChange={v=>setDF(f=>({...f,phone:v}))} placeholder="9811001005"/></Field>
            <Field label="Email"><Inp value={dF.email} onChange={v=>setDF(f=>({...f,email:v}))} placeholder="doctor@clinic.com"/></Field>
            <Field label="Region"><Sel value={dF.region||""} onChange={v=>setDF(f=>({...f,region:v}))} options={REGIONS} placeholder="Select Region"/></Field>
            <Field label="Assigned To (MR)"><Sel value={dF.assignedMrId||""} onChange={v=>setDF(f=>({...f,assignedMrId:v}))} options={users.filter(u=>u.role==="mr"||u.role==="asm").map(u=>({label:u.name,value:u.id}))} placeholder="Select MR / ASM (Optional)"/></Field>
            <Field label="Billing Channel">
              <Sel value={dF.billingChannel||"Direct"} onChange={v=>setDF(f=>({...f,billingChannel:v,linkedPartyId:v==="Direct"?"":f.linkedPartyId}))} options={["Direct","Through Medical Store"]}/>
            </Field>
            {(dF.billingChannel==="Through Medical Store")&&(
              <Field label="Linked Medical Store (Party)">
                <Sel value={dF.linkedPartyId||""} onChange={v=>setDF(f=>({...f,linkedPartyId:v}))} options={[{label:"-- Select Store --",value:""},...(data.parties||[]).map(p=>({label:p.name,value:p.id}))]} placeholder="Select Party"/>
              </Field>
            )}
          </div>
          <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:14}}>
            <Btn v="outline" onClick={()=>setShowForm(false)}>Cancel</Btn>
            <Btn v="gold" onClick={saveD}>💾 {editDocId?"Update":"Save"}</Btn>
          </div>
        </Modal>)}
      </>)}
      {view==="visits"&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 320px",gap:16,alignItems:"start"}}>
          <div>
          {(()=>{
            const parseD=s=>{if(!s||!s.includes("/"))return null;const[dd,mm,yyyy]=s.split("/");return new Date(+yyyy,+mm-1,+dd);};
            const filtVis=[...data.doctorVisits].reverse().filter(v=>{
              if(vfDoctor&&v.doctor!==vfDoctor) return false;
              const d=parseD(v.date);
              if(vfFrom&&parseD(vfFrom)&&d&&d<parseD(vfFrom)) return false;
              if(vfTo&&parseD(vfTo)&&d&&d>parseD(vfTo)) return false;
              return true;
            });
            return(<>
              <div style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:10,padding:"12px 16px",marginBottom:12}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,alignItems:"flex-end"}}>
                  <Field label="Doctor"><Sel value={vfDoctor} onChange={setVfDoctor} options={data.doctors.map(d=>d.name)} placeholder="All Doctors"/></Field>
                  <Field label="From Date"><DateInp value={vfFrom} onChange={setVfFrom}/></Field>
                  <Field label="To Date"><DateInp value={vfTo} onChange={setVfTo}/></Field>
                </div>
                {(vfDoctor||vfFrom||vfTo)&&<div style={{marginTop:6,fontSize:11,color:C.muted}}>{filtVis.length} visits · <button onClick={()=>{setVfDoctor("");setVfFrom("");setVfTo("");}} style={{fontSize:11,color:"#dc2626",background:"none",border:"none",cursor:"pointer",fontFamily:"inherit"}}>Clear</button></div>}
              </div>
              <Tbl headers={["Date","Doctor","Spec","Clinic","Products","Samples","Notes","Follow-up","Action"]}
                onExport={()=>downloadCSV(filtVis.map(v=>[v.date,v.doctor,v.spec,v.clinic,v.products,v.samples,v.notes,v.followUp]),["Date","Doctor","Spec","Clinic","Products","Samples","Notes","Follow-up"],"Doctor_Visits")}
                rows={filtVis.map(v=>[
                  v.date,<strong style={{fontSize:11}}>{v.doctor}</strong>,<Badge s={v.spec}/>,
                  <span style={{fontSize:11}}>{v.clinic}</span>,<span style={{fontSize:11}}>{v.products}</span>,
                  <span style={{fontSize:11}}>{v.samples}</span>,<span style={{fontSize:11,color:C.muted}}>{v.notes}</span>,
              <span style={{fontSize:11,color:C.accent}}>{v.followUp}</span>,
              <Btn sz="sm" v="danger" onClick={()=>{if(requestDelete("doctorVisits",v.id,"Doctor Visit"))setData(d=>({...d,doctorVisits:d.doctorVisits.filter(x=>x.id!==v.id)}));}}>Del</Btn>
            ])}
              />
            </>);
          })()}
          </div>
          <Card style={{height:"fit-content"}}>
            <div style={{fontWeight:700,fontSize:13,color:"#0f766e",marginBottom:12}}>Log Visit</div>
            <Field label="Date"><DateInp value={vF.date} onChange={v=>setVF(f=>({...f,date:v}))}/></Field>
            <Field label="Doctor"><Sel value={vF.doctor} onChange={v=>{const d=data.doctors.find(x=>x.name===v);setVF(f=>({...f,doctor:v,spec:d?.spec||"",clinic:d?.clinic||""}));}} options={data.doctors.map(d=>d.name)} placeholder="Select Doctor"/></Field>
            <Field label="Products Detailed"><Inp value={vF.products} onChange={v=>setVF(f=>({...f,products:v}))} placeholder="Vitamin C, Paracetamol"/></Field>
            <Field label="Samples Given"><Inp value={vF.samples} onChange={v=>setVF(f=>({...f,samples:v}))} placeholder="2 strips Vitamin C"/></Field>
            <Field label="Notes"><Inp value={vF.notes} onChange={v=>setVF(f=>({...f,notes:v}))} placeholder="Discussion / feedback"/></Field>
            <Field label="Follow-up Date"><DateInp value={vF.followUp} onChange={v=>setVF(f=>({...f,followUp:v}))}/></Field>
            <div style={{marginTop:8}}><Btn v="gold" full onClick={saveV}>💾 Save Visit</Btn></div>
          </Card>
        </div>
      )}
      {view==="samples"&&(
        <div>
          <div>
            <KPI label="Total Qty" val={data.samples.reduce((s,x)=>s+x.qty,0)} color={C.navy} icon="💊"/>
            <KPI label="Sample Value (MRP)" val={`₹${fmt(data.samples.reduce((s,x)=>s+x.value,0))}`} color="#0369a1" icon="₹"/>
            <KPI label="Doctors Covered" val={new Set(data.samples.map(s=>s.doctor)).size} color="#0f766e" icon="👨‍⚕️"/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 300px",gap:16,alignItems:"start"}}>
            <Tbl headers={["Sample No","Date","Doctor","Product","Batch","Qty","Value (MRP)","Remarks","Action"]}
              onExport={()=>downloadCSV(data.samples.map(s=>[s.no,s.date,s.doctor,s.product,s.batch,s.qty,s.value,s.remarks]),["No","Date","Doctor","Product","Batch","Qty","Value","Remarks"],"Samples")}
              rows={[...data.samples].reverse().map(s=>[
                <span className="mono" style={{fontWeight:700,color:"#0369a1",fontSize:11}}>{s.no}</span>,
                s.date,s.doctor,s.product,<span className="mono" style={{fontSize:11}}>{s.batch}</span>,
                <span className="mono">{s.qty}</span>,<span className="mono">₹{fmt(s.value)}</span>,
                <span style={{fontSize:11,color:C.muted}}>{s.remarks}</span>,
                <Btn sz="sm" v="danger" onClick={()=>{if(requestDelete("samples",s.id,"Sample"))setData(d=>({...d,samples:d.samples.filter(x=>x.id!==s.id)}));}}>Del</Btn>
              ])}
            />
            <Card style={{height:"fit-content"}}>
              <div style={{fontWeight:700,fontSize:13,color:"#0369a1",marginBottom:12}}>Issue Sample</div>
              <Field label="Sample No"><Inp value={sF.no} onChange={v=>setSF(f=>({...f,no:v}))} placeholder="SMP-002"/></Field>
              <Field label="Date"><DateInp value={sF.date} onChange={v=>setSF(f=>({...f,date:v}))}/></Field>
              <Field label="Doctor"><Sel value={sF.doctor} onChange={v=>setSF(f=>({...f,doctor:v}))} options={data.doctors.map(d=>d.name)} placeholder="Select Doctor"/></Field>
              <Field label="Product"><Sel value={sF.product} onChange={v=>setSF(f=>({...f,product:v}))} options={products.map(p=>p.name)} placeholder="Select Product"/></Field>
              <Field label="Batch No"><Inp value={sF.batch} onChange={v=>setSF(f=>({...f,batch:v}))} placeholder="VTC-001"/></Field>
              <Field label="Qty"><Inp value={sF.qty} onChange={v=>setSF(f=>({...f,qty:v}))} type="number"/></Field>
              <Field label="Remarks"><Inp value={sF.remarks} onChange={v=>setSF(f=>({...f,remarks:v}))} placeholder="Purpose"/></Field>
              <div style={{marginTop:8}}><Btn v="gold" full onClick={saveS}>💊 Issue Sample</Btn></div>
            </Card>
          </div>
        </div>
      )}
      {view==="activity"&&(
          <div>
            {/* ── Doctor Selector ── */}
            <div style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:10,padding:"12px 16px",marginBottom:16}}>
              <div style={{display:"flex",gap:12,alignItems:"flex-end",flexWrap:"wrap"}}>
                <div style={{flex:1,minWidth:220}}>
                  <Field label="Doctor Select karo (DIT + SIR filter)">
                    <Sel value={actDoc} onChange={v=>{setActDoc(v);setDitF(f=>({...f,doctorId:v}));}}
                      options={[{label:"All Doctors (Summary)",value:""},...data.doctors.map(d=>({label:d.name+" · "+d.spec,value:d.id}))]}
                      placeholder="All Doctors"/>
                  </Field>
                </div>
                {selDoc&&(
                  <div style={{display:"flex",gap:8,paddingBottom:4,flexWrap:"wrap"}}>
                    <span style={{fontSize:11,background:selDoc.billingChannel==="Through Medical Store"?"#e0f2fe":"#f0fdf4",
                      color:selDoc.billingChannel==="Through Medical Store"?"#0369a1":"#166534",
                      padding:"4px 10px",borderRadius:8,fontWeight:600}}>
                      {selDoc.billingChannel==="Through Medical Store"
                        ?`🏪 Via: ${linkedParty?.name||"Store Not Linked"}`
                        :"🧾 Direct Billing"}
                    </span>
                    <span style={{fontSize:11,background:"#f5f3ff",color:"#6d28d9",padding:"4px 10px",borderRadius:8,fontWeight:600}}>
                      📍 {selDoc.clinic||"—"}, {selDoc.city||"—"}
                    </span>
                    {selDoc.phone&&<span style={{fontSize:11,background:"#fefce8",color:"#854d0e",padding:"4px 10px",borderRadius:8,fontWeight:600}}>📞 {selDoc.phone}</span>}
                  </div>
                )}
              </div>
            </div>

            {/* ── KPI Summary ── */}
            <div style={{display:"flex",gap:12,marginBottom:18,flexWrap:"wrap"}}>
              <KPI label="Total Investment (DIT)" val={`₹${fmt(totalDIT)}`} icon="💸" color="#dc2626" bg="#fef2f2" sub={`${docServices.length} entries`}/>
              <KPI label="Total Business (SIR)" val={`₹${fmt(totalSIR)}`} icon="📦" color="#16a34a" bg="#f0fdf4" sub={`${sirInvoices.length} invoices`}/>
              <KPI label="ROI Ratio" val={totalDIT>0?`${(totalSIR/totalDIT).toFixed(1)}x`:"—"} icon="📈" color="#7c3aed" bg="#f5f3ff" sub="SIR ÷ DIT"/>
              <KPI label="Avg. Service Value" val={docServices.length>0?`₹${fmt(totalDIT/docServices.length)}`:"—"} icon="🎁" color="#ca8a04" bg="#fffbeb" sub="Per entry"/>
            </div>

            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,alignItems:"start"}}>

              {/* ── LEFT: DIT Panel ── */}
              <div>
                <div style={{fontWeight:700,fontSize:13,color:"#dc2626",marginBottom:10,display:"flex",alignItems:"center",gap:6}}>
                  💸 DIT — Doctor Investment Tracker
                </div>

                {/* DIT Add Form */}
                <Card style={{marginBottom:14,border:"1px solid #fecaca"}}>
                  <div style={{fontWeight:600,fontSize:12,color:C.navy,marginBottom:10}}>➕ New Service Entry</div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,alignItems:"start"}}>
                    <Field label="Doctor" req>
                      <Sel value={ditF.doctorId} onChange={v=>setDitF(f=>({...f,doctorId:v}))}
                        options={[{label:"Select Doctor",value:""},...data.doctors.map(d=>({label:d.name,value:d.id}))]}
                        placeholder="Select"/>
                    </Field>
                    <Field label="Date"><DateInp value={ditF.date} onChange={v=>setDitF(f=>({...f,date:v}))}/></Field>
                    <Field label="Service Type" req>
                      <Sel value={ditF.serviceType} onChange={v=>setDitF(f=>({...f,serviceType:v}))} options={DIT_TYPES}/>
                    </Field>
                    <Field label="Value (₹)" req><Inp value={ditF.value} onChange={v=>setDitF(f=>({...f,value:v}))} type="number" placeholder="500"/></Field>
                  </div>
                  <Field label="Description"><Inp value={ditF.description} onChange={v=>setDitF(f=>({...f,description:v}))} placeholder="e.g. Diwali Gift Box, CME Dinner Sponsor"/></Field>
                  <Field label="Notes"><Inp value={ditF.notes} onChange={v=>setDitF(f=>({...f,notes:v}))} placeholder="Optional"/></Field>
                  <div style={{marginTop:10}}><Btn v="danger" full onClick={saveDIT}>💾 Log Service</Btn></div>
                </Card>

                {/* DIT Table */}
                <Tbl
                  headers={["Date","Doctor","Type","Description","Value","Del"]}
                  rows={[...docServices].reverse().map(s=>[
                    <span style={{fontSize:11}}>{s.date}</span>,
                    <span style={{fontSize:11,fontWeight:600}}>{s.doctorName||"—"}</span>,
                    <span style={{background:"#fef2f2",color:"#dc2626",padding:"2px 8px",borderRadius:6,fontSize:10,fontWeight:700}}>{s.serviceType}</span>,
                    <span style={{fontSize:11,color:C.muted}}>{s.description||"—"}</span>,
                    <span className="mono" style={{fontWeight:700,color:"#dc2626",fontSize:12}}>₹{fmt(s.value)}</span>,
                    <Btn sz="sm" v="danger" onClick={()=>{if(window.confirm("Delete?"))setData(d=>({...d,doctorServices:(d.doctorServices||[]).filter(x=>x.id!==s.id)}));}}>Del</Btn>
                  ])}
                  empty="Abhi tak koi service log nahi hua."
                />
                {docServices.length>0&&(
                  <div style={{marginTop:8,background:"#fef2f2",border:"1px solid #fecaca",borderRadius:8,padding:"8px 14px",display:"flex",justifyContent:"space-between",fontSize:12}}>
                    <span style={{fontWeight:600,color:"#dc2626"}}>Total Investment</span>
                    <span className="mono" style={{fontWeight:800,color:"#dc2626"}}>₹{fmt(totalDIT)}</span>
                  </div>
                )}
              </div>

              {/* ── RIGHT: SIR Panel ── */}
              <div>
                <div style={{fontWeight:700,fontSize:13,color:"#16a34a",marginBottom:10,display:"flex",alignItems:"center",gap:6}}>
                  📦 SIR — Sales Invoice Records
                </div>

                {!selDoc&&(
                  <div style={{background:"#f8fafc",border:`2px dashed ${C.border}`,borderRadius:12,padding:40,textAlign:"center"}}>
                    <div style={{fontSize:24,marginBottom:8}}>👆</div>
                    <div style={{fontSize:13,fontWeight:600,color:C.navy,marginBottom:4}}>Doctor Select Karo</div>
                    <div style={{fontSize:11,color:C.muted}}>Ek doctor select karo uski prescription se aane wali SIR dekhne ke liye</div>
                  </div>
                )}

                {selDoc&&(
                  <>
                    <div style={{background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:8,padding:"10px 14px",marginBottom:12,fontSize:11,lineHeight:1.6}}>
                      {selDoc.billingChannel==="Through Medical Store"
                        ?<><strong>{selDoc.name}</strong> ke prescriptions <strong style={{color:"#0369a1"}}>{linkedParty?.name||"⚠️ Store not linked"}</strong> se ja rahe hain.<br/>Neeche us store ke saare invoices dikh rahe hain.</>
                        :<><strong>{selDoc.name}</strong> ko directly bill hota hai.<br/>Neeche <code>billDoctor</code> se matched invoices hain.</>
                      }
                    </div>

                    <Tbl
                      headers={["Date","Inv No","Party / Dr.","Amount","Status"]}
                      rows={[...sirInvoices].reverse().map(inv=>[
                        <span style={{fontSize:11}}>{inv.date}</span>,
                        <span className="mono" style={{fontSize:10,fontWeight:700,color:"#0369a1"}}>{inv.no}</span>,
                        <span style={{fontSize:11}}>{inv.party||inv.billDoctor||"—"}</span>,
                        <span className="mono" style={{fontWeight:700,color:"#16a34a",fontSize:12}}>₹{fmt(inv.total)}</span>,
                        <span style={{background:inv.status==="Cancelled"?"#fee2e2":"#dcfce7",color:inv.status==="Cancelled"?"#dc2626":"#166534",padding:"2px 8px",borderRadius:6,fontSize:10,fontWeight:700}}>{inv.status||"Issued"}</span>
                      ])}
                      empty="Is doctor ke liye koi invoice nahi mila."
                    />

                    {sirInvoices.length>0&&(
                      <div style={{marginTop:8,background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:8,padding:"8px 14px",display:"flex",justifyContent:"space-between",fontSize:12}}>
                        <span style={{fontWeight:600,color:"#166534"}}>Total Business Generated</span>
                        <span className="mono" style={{fontWeight:800,color:"#16a34a"}}>₹{fmt(totalSIR)}</span>
                      </div>
                    )}

                    {sirInvoices.length===0&&selDoc.billingChannel==="Through Medical Store"&&!linkedParty&&(
                      <div style={{marginTop:8,background:"#fffbeb",border:"1px solid #fde68a",borderRadius:8,padding:"10px 14px",fontSize:11,color:"#92400e"}}>
                        ⚠️ <strong>Linked store set nahi hua.</strong> Doctor ko edit karo aur "Linked Medical Store" assign karo.
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
      )}
    </div>
  );
};


const PartyMaster = ({data,setData,drafts,setDrafts,requestDelete,setParties,users}) => {
  const parties=data.parties||[];
  const emptyF={name:"",address1:"",address2:"",city:"",state:"",state_code:"",region:"",party_type:"",gstin:"",pan_no:"",phone:"",email:"",credit_days:"30",credit_limit:"",dl_no:"",assignedMrId:""};
  
  const [showForm,setShowForm]=useState(()=>drafts?.["draft_parties"]?._show||false);
  const [form,setForm]=useState(()=>drafts?.["draft_parties"]?.form||emptyF);
  const [editId,setEditId]=useState(null);
  const [pmSearch,setPmSearch]=useState("");
  const [pmType,setPmType]=useState("");
  const [pmRegion,setPmRegion]=useState("");
  const [pmState,setPmState]=useState("");

  // Auto-save draft when form changes
  useEffect(()=>{
    if(setDrafts) setDrafts(d=>showForm?{...d,"draft_parties":{form,_show:true,_label:"+ Add Party"}}:{...d,"draft_parties":undefined});
  },[form,showForm]);
  const save=async()=>{
    if(!form.name) return alert("Party name required");
    const {assignedMrId,...partyFields}=form;
    if(editId){
      const updatedParty = normalizeParty({ ...parties.find(p=>p.id===editId), ...partyFields });
      setParties(prev=>prev.map(p=>p.id===editId?updatedParty:p));
      setData(d=>{
        const newAssign=(d.mrDoctorAssign||[]).filter(a=>a.partyId!==editId);
        if(assignedMrId) newAssign.push({mrId:assignedMrId,partyId:editId});
        return {...d,parties:d.parties.map(p=>p.id===editId?updatedParty:p),mrDoctorAssign:newAssign};
      });
      setEditId(null);
    } else {
      try {
        // ✅ Exact Supabase column names — parties table
        const payload = {
          name:         partyFields.name         || "",
          address1:     partyFields.address1     || "",
          address2:     partyFields.address2     || "",
          city:         partyFields.city         || "",
          state:        partyFields.state        || "",
          state_code:   partyFields.state_code   || "",
          gstin:        partyFields.gstin        || "",
          dl_no:        partyFields.dl_no        || "",
          pan_no:       partyFields.pan_no       || "",
          phone:        partyFields.phone        || "",
          email:        partyFields.email        || "",
          party_type:   partyFields.party_type   || "",
          region:       partyFields.region       || "",
          credit_days:  Number(partyFields.credit_days  || 0),
          credit_limit: Number(partyFields.credit_limit || 0),
        };
        const res = await addParty(payload);
        if(res && res.length > 0){
          const added = normalizeParty(res[0]);
          setParties(prev=>[...prev, added]);
          setData(d=>{
            const newAssign=(d.mrDoctorAssign||[]).filter(a=>a.partyId!==added.id);
            if(assignedMrId) newAssign.push({mrId:assignedMrId,partyId:added.id});
            return {...d,parties:[...d.parties, added],mrDoctorAssign:newAssign};
          });
          alert("✅ Party Added");
        } else {
          alert("❌ Failed to add party — check Supabase table/RLS");
          return;
        }
      } catch(err) {
        console.error("addParty error:", err);
        alert("❌ Network error adding party");
        return;
      }
    }
    setForm(emptyF); setShowForm(false);
  };
  const startEdit=(p)=>{
    const currentAssign=(data.mrDoctorAssign||[]).find(a=>a.partyId===p.id);
    setForm({name:p.name||"",address1:p.address1||p.address||"",address2:p.address2||"",city:p.city||"",state:p.state||"",state_code:p.state_code||"",region:p.region||"",party_type:p.party_type||p.partyType||"",gstin:p.gstin||"",pan_no:p.pan_no||"",phone:p.phone||"",email:p.email||"",credit_days:String(p.credit_days||p.creditDays||"30"),credit_limit:String(p.credit_limit||p.creditLimit||""),dl_no:p.dl_no||p.dl1||"",assignedMrId:currentAssign?.mrId||""});
    setEditId(p.id);
    setShowForm(true);
  };
  return (
    <div className="fade">
      <PH title="Party Master" sub="Customers — with party type, region, DL numbers"
        action={<div style={{display:"flex",gap:8}}>
          <BulkUpload title="Party Master"
            templateHeaders={["Name","Party Type","City","State","GSTIN","Phone","Email","DL1","DL2","Credit Days","Region","Address"]}
            templateRows={[["Apollo Pharmacy","Retail Pharmacy","Delhi","Delhi","07AABCA1234A1Z5","9811001001","apollo@mail.com","DL-DL-001","","30","North","123 Main St"]]}
            parseRow={(r,i)=>r.Name?{id:"PT"+Date.now()+"_"+i,name:r.Name,partyType:r["Party Type"]||"",city:r.City||"",state:r.State||"",gstin:r.GSTIN||"",phone:r.Phone||"",email:r.Email||"",dl1:r.DL1||"",dl2:r.DL2||"",creditDays:r["Credit Days"]||30,region:r.Region||"",address:r.Address||""}:null}
            onUpload={(rows,mode)=>setData(d=>({...d,parties:mode==="replace"?rows:[...d.parties,...rows.filter(r=>!d.parties.find(p=>p.name?.toLowerCase()===r.name?.toLowerCase()))]}))}
          />
          <Btn v="gold" onClick={()=>setShowForm(true)}>+ Add Party</Btn>
        </div>}
      />
      {(()=>{
        const filtParties=parties.filter(p=>{
          if(pmSearch&&!p.name.toLowerCase().includes(pmSearch.toLowerCase())&&!p.phone.includes(pmSearch)&&!p.gstin.toLowerCase().includes(pmSearch.toLowerCase())) return false;
          if(pmType&&p.partyType!==pmType) return false;
          if(pmRegion&&p.region!==pmRegion) return false;
          if(pmState&&p.state!==pmState) return false;
          return true;
        });
        const pmHas=pmSearch||pmType||pmRegion||pmState;
        return(<>
          <div style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:10,padding:"12px 16px",marginBottom:14}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <span style={{fontWeight:700,fontSize:12,color:C.navy}}>🔍 Filters</span>
              {pmHas&&<button onClick={()=>{setPmSearch("");setPmType("");setPmRegion("");setPmState("");}} style={{fontSize:11,color:"#dc2626",background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>✕ Clear</button>}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,alignItems:"start"}}>
              <Field label="Search Name/GSTIN/Phone"><Inp value={pmSearch} onChange={setPmSearch} placeholder="Search..."/></Field>
              <Field label="Party Type"><Sel value={pmType} onChange={setPmType} options={PARTY_TYPES} placeholder="All Types"/></Field>
              <Field label="Region"><Sel value={pmRegion} onChange={setPmRegion} options={REGIONS} placeholder="All Regions"/></Field>
              <Field label="State"><Sel value={pmState} onChange={setPmState} options={INDIAN_STATES} placeholder="All States"/></Field>
            </div>
            {pmHas&&<div style={{marginTop:6,fontSize:11,color:C.muted}}>{filtParties.length} parties found</div>}
          </div>
          <Tbl headers={["ID","Party Name","Type","City","State","Region","GSTIN","Phone","Credit Days","Assigned MR","Action"]}
            onExport={()=>downloadCSV(filtParties.map(p=>[p.id,p.name,p.partyType,p.city,p.state,p.region,p.gstin,p.phone,p.creditDays,p.dl1]),["ID","Name","Type","City","State","Region","GSTIN","Phone","Credit Days","DL1"],"Party_Master")}
            rows={filtParties.map(p=>[
              <span className="mono" style={{fontSize:10,color:C.muted}}>{p.id}</span>,
              <strong style={{fontSize:12}}>{p.name}</strong>,
              <Badge s={p.partyType}/>,p.city,p.state,
              p.region?<Badge s={p.region}/>:"—",
              <span className="mono" style={{fontSize:10}}>{p.gstin}</span>,
              <span style={{fontSize:11}}>{p.phone}</span>,
              <Badge s={`${p.creditDays}d`}/>,
              (data.mrDoctorAssign?.find(a=>a.partyId===p.id) && users.find(u=>u.id===data.mrDoctorAssign.find(a=>a.partyId===p.id)?.mrId))
                ?<span style={{fontSize:10,fontWeight:600,color:"#065f46",background:"#dcfce7",padding:"2px 8px",borderRadius:6}}>👤 {users.find(u=>u.id===data.mrDoctorAssign.find(a=>a.partyId===p.id)?.mrId)?.name}</span>
                :<span style={{fontSize:10,color:"#b45309",background:"#FFF4E5",padding:"2px 8px",borderRadius:6}}>⚠️ Unassigned</span>,
              <div style={{display:"flex",gap:5}}>
                <Btn sz="sm" v="outline" onClick={()=>startEdit(p)}>✏️ Edit</Btn>
                <Btn sz="sm" v="danger" onClick={()=>{if(requestDelete("parties",p.id,"Party"))setData(d=>({...d,parties:d.parties.filter(x=>x.id!==p.id)}));}}>Del</Btn>
              </div>
            ])}
            empty="No parties match filters."
          />
        </>);
      })()}
      {showForm&&(<Modal title={editId?"Edit Party":"Add Party"} onClose={()=>{setShowForm(false);setEditId(null);setForm(emptyF);}} w={640}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,alignItems:"start"}}>
          <Field label="Party Name" req><Inp value={form.name} onChange={v=>setForm(f=>({...f,name:v}))} placeholder="Apollo Pharmacy"/></Field>
          <Field label="Party Type"><Sel value={form.party_type} onChange={v=>setForm(f=>({...f,party_type:v}))} options={PARTY_TYPES} placeholder="Select Type"/></Field>
          <Field label="Address Line 1"><Inp value={form.address1} onChange={v=>setForm(f=>({...f,address1:v}))} placeholder="F-34/8, 2nd Floor, Okhla Ind Area"/></Field>
          <Field label="Address Line 2"><Inp value={form.address2} onChange={v=>setForm(f=>({...f,address2:v}))} placeholder="Phase 2 (optional)"/></Field>
          <Field label="City"><Inp value={form.city} onChange={v=>setForm(f=>({...f,city:v}))} placeholder="New Delhi"/></Field>
          <Field label="State"><Sel value={form.state} onChange={v=>setForm(f=>({...f,state:v}))} options={INDIAN_STATES} placeholder="Select State"/></Field>
          <Field label="State Code"><Inp value={form.state_code} onChange={v=>setForm(f=>({...f,state_code:v}))} placeholder="07"/></Field>
          <Field label="Region"><Sel value={form.region} onChange={v=>setForm(f=>({...f,region:v}))} options={REGIONS} placeholder="Select Region"/></Field>
          <Field label="GSTIN"><Inp value={form.gstin} onChange={v=>setForm(f=>({...f,gstin:v}))} placeholder="27AABCA1234A1Z5"/></Field>
          <Field label="PAN No"><Inp value={form.pan_no} onChange={v=>setForm(f=>({...f,pan_no:v}))} placeholder="AABCA1234A"/></Field>
          <Field label="Phone"><Inp value={form.phone} onChange={v=>setForm(f=>({...f,phone:v}))} placeholder="9876543210"/></Field>
          <Field label="Email"><Inp value={form.email} onChange={v=>setForm(f=>({...f,email:v}))} placeholder="party@email.com"/></Field>
          <Field label="Credit Days"><Inp value={form.credit_days} onChange={v=>setForm(f=>({...f,credit_days:v}))} type="number"/></Field>
          <Field label="Credit Limit (₹)"><Inp value={form.credit_limit} onChange={v=>setForm(f=>({...f,credit_limit:v}))} type="number" placeholder="0"/></Field>
          <Field label="DL No"><Inp value={form.dl_no} onChange={v=>setForm(f=>({...f,dl_no:v}))} placeholder="DL-MH-123456"/></Field>
          <Field label="Assigned To (MR)"><Sel value={form.assignedMrId||""} onChange={v=>setForm(f=>({...f,assignedMrId:v}))} options={users.filter(u=>u.role==="mr"||u.role==="asm").map(u=>({label:u.name,value:u.id}))} placeholder="Select MR / ASM (Optional)"/></Field>
        </div>
        <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:14}}>
          <Btn v="outline" onClick={()=>{setShowForm(false);setEditId(null);setForm(emptyF);}}>Cancel</Btn>
          <Btn v="gold" onClick={save}>{editId?"💾 Update Party":"💾 Save Party"}</Btn>
        </div>
      </Modal>)}
    </div>
  );
};


const SettingsModule = ({data,setData,currentUser}) => {
  const [s,setS]=useState({...data.settings});
  const [saved,setSaved]=useState(false);
  const [curPwd,setCurPwd]=useState("");
  const [newPwd,setNewPwd]=useState("");
  const [confirmPwd,setConfirmPwd]=useState("");
  const [pwdMsg,setPwdMsg]=useState(null);

  const save=()=>{setData(d=>({...d,settings:s}));setSaved(true);setTimeout(()=>setSaved(false),2000);};

  // Stable input style — no sub-component, prevents focus loss
  const inpStyle={width:"100%",padding:"8px 11px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,background:"#fff",outline:"none",fontFamily:"inherit",boxSizing:"border-box"};
  const onFocus=e=>e.target.style.borderColor="#5FA8A4";
  const onBlur=e=>e.target.style.borderColor=C.border;

  const changePassword=async ()=>{
    if(!newPwd||newPwd.length<6){setPwdMsg({ok:false,msg:"New password must be at least 6 characters"});return;}
    if(newPwd!==confirmPwd){setPwdMsg({ok:false,msg:"New passwords do not match"});return;}
    // Re-authenticate with current password first to validate it
    const email=currentUser?.email;
    if(!email){setPwdMsg({ok:false,msg:"No email associated with this account"});return;}
    try {
      // Verify current password via Supabase sign-in
      const {error:signInErr}=await supabase.auth.signInWithPassword({email,password:curPwd});
      if(signInErr){setPwdMsg({ok:false,msg:"Current password is incorrect"});return;}
      // Update password through Supabase Auth only — never stored in app state
      const {error:updateErr}=await supabase.auth.updateUser({password:newPwd});
      if(updateErr){setPwdMsg({ok:false,msg:"Failed to update password: "+updateErr.message});return;}
      if(currentUser?.role!=="owner"){
        // Log the change request as resolved so owner sees it in the audit trail
        setData(d=>({...d,pwdChangeRequests:[...(d.pwdChangeRequests||[]),{
          id:"PWD"+Date.now(),userId:currentUser.id,userName:currentUser.name,
          requestedOn:today(),status:"Approved",resolvedOn:today(),
          note:"Changed via Supabase Auth"
          // newPassword intentionally omitted — never stored
        }]}));
      }
      setPwdMsg({ok:true,msg:"✅ Password changed successfully!"});
    } catch(e){
      setPwdMsg({ok:false,msg:"Error: "+(e?.message||"Unknown error")});
    }
    setCurPwd("");setNewPwd("");setConfirmPwd("");
    setTimeout(()=>setPwdMsg(null),5000);
  };

  return (
    <div className="fade">
      <PH title="Settings" sub="Company details, bank info and security"
        action={<div style={{display:"flex",gap:8,alignItems:"center"}}>
          {saved&&<span style={{color:"#166534",fontSize:13,fontWeight:700}}>✅ Saved!</span>}
          <Btn v="gold" onClick={save}>💾 Save Settings</Btn>
        </div>}
      />
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16,alignItems:"start"}}>

        {/* Company Info */}
        <Card>
          <div style={{fontWeight:700,fontSize:13,color:C.navy,marginBottom:12}}>Company Information</div>
          <Field label="Company Name"><input style={inpStyle} onFocus={onFocus} onBlur={onBlur} value={s.companyName||""} onChange={e=>setS(x=>({...x,companyName:e.target.value}))}/></Field>
          <Field label="Address Line 1"><input style={inpStyle} onFocus={onFocus} onBlur={onBlur} value={s.address1||""} onChange={e=>setS(x=>({...x,address1:e.target.value}))}/></Field>
          <Field label="Address Line 2"><input style={inpStyle} onFocus={onFocus} onBlur={onBlur} value={s.address2||""} onChange={e=>setS(x=>({...x,address2:e.target.value}))}/></Field>
          <Field label="City"><input style={inpStyle} onFocus={onFocus} onBlur={onBlur} value={s.city||""} onChange={e=>setS(x=>({...x,city:e.target.value}))}/></Field>
          <Field label="State"><Sel value={s.state||""} onChange={v=>setS(x=>({...x,state:v}))} options={INDIAN_STATES} placeholder="Select State"/></Field>
          <Field label="PIN Code"><input style={inpStyle} onFocus={onFocus} onBlur={onBlur} value={s.pin||""} onChange={e=>setS(x=>({...x,pin:e.target.value}))}/></Field>
          <Field label="Mobile"><input style={inpStyle} onFocus={onFocus} onBlur={onBlur} value={s.mobile||""} onChange={e=>setS(x=>({...x,mobile:e.target.value}))}/></Field>
        </Card>

        {/* Legal & Tax */}
        <Card>
          <div style={{fontWeight:700,fontSize:13,color:C.navy,marginBottom:12}}>Legal & Tax Details</div>
          <Field label="GSTIN"><input style={inpStyle} onFocus={onFocus} onBlur={onBlur} value={s.gstin||""} onChange={e=>setS(x=>({...x,gstin:e.target.value}))}/></Field>
          <Field label="PAN"><input style={inpStyle} onFocus={onFocus} onBlur={onBlur} value={s.pan||""} onChange={e=>setS(x=>({...x,pan:e.target.value}))}/></Field>
          <Field label="CIN"><input style={inpStyle} onFocus={onFocus} onBlur={onBlur} value={s.cin||""} onChange={e=>setS(x=>({...x,cin:e.target.value}))}/></Field>
          <Field label="Drug Licence No 1"><input style={inpStyle} onFocus={onFocus} onBlur={onBlur} value={s.dl1||""} onChange={e=>setS(x=>({...x,dl1:e.target.value}))}/></Field>
          <Field label="Drug Licence No 2"><input style={inpStyle} onFocus={onFocus} onBlur={onBlur} value={s.dl2||""} onChange={e=>setS(x=>({...x,dl2:e.target.value}))}/></Field>
          <Field label="State Code"><input style={inpStyle} onFocus={onFocus} onBlur={onBlur} value={s.stateCode||""} onChange={e=>setS(x=>({...x,stateCode:e.target.value}))}/></Field>
        </Card>

        {/* Bank Details */}
        <Card>
          <div style={{fontWeight:700,fontSize:13,color:C.navy,marginBottom:12}}>Bank Details</div>
          <Field label="Bank Name"><input style={inpStyle} onFocus={onFocus} onBlur={onBlur} value={s.bankName||""} onChange={e=>setS(x=>({...x,bankName:e.target.value}))}/></Field>
          <Field label="Account No"><input style={inpStyle} onFocus={onFocus} onBlur={onBlur} value={s.bankAcc||""} onChange={e=>setS(x=>({...x,bankAcc:e.target.value}))}/></Field>
          <Field label="IFSC Code"><input style={inpStyle} onFocus={onFocus} onBlur={onBlur} value={s.ifsc||""} onChange={e=>setS(x=>({...x,ifsc:e.target.value}))}/></Field>
          <Field label="Branch"><input style={inpStyle} onFocus={onFocus} onBlur={onBlur} value={s.branch||""} onChange={e=>setS(x=>({...x,branch:e.target.value}))}/></Field>
          <Field label="Email"><input style={inpStyle} onFocus={onFocus} onBlur={onBlur} value={s.email||""} onChange={e=>setS(x=>({...x,email:e.target.value}))}/></Field>
        </Card>
      </div>

      {/* Change Password */}
      <Card style={{marginTop:16,maxWidth:480}}>
        <div style={{fontWeight:700,fontSize:13,color:"#991b1b",marginBottom:4}}>🔐 {currentUser?.role==="owner"?"Change My Password":"Request Password Change"}</div>
        <div style={{fontSize:11,color:C.muted,marginBottom:14}}>Logged in as <strong>{currentUser?.name}</strong> ({currentUser?.role}){currentUser?.role!=="owner"&&" · Requires Owner approval"}</div>
        <Field label="Current Password">
          <input style={inpStyle} onFocus={onFocus} onBlur={onBlur} type="password" value={curPwd} onChange={e=>setCurPwd(e.target.value)} placeholder="Enter your current password"/>
        </Field>
        <Field label="New Password">
          <input style={inpStyle} onFocus={onFocus} onBlur={onBlur} type="password" value={newPwd} onChange={e=>setNewPwd(e.target.value)} placeholder="Minimum 4 characters"/>
        </Field>
        <Field label="Confirm New Password">
          <input style={inpStyle} onFocus={onFocus} onBlur={onBlur} type="password" value={confirmPwd} onChange={e=>setConfirmPwd(e.target.value)} placeholder="Re-enter new password"/>
        </Field>
        {pwdMsg&&(
          <div style={{marginTop:8,padding:"8px 12px",borderRadius:7,background:pwdMsg.ok?"#dcfce7":"#fee2e2",color:pwdMsg.ok?"#166534":"#dc2626",fontSize:12,fontWeight:600}}>
            {pwdMsg.msg}
          </div>
        )}
        <div style={{marginTop:12}}>
          <Btn v="navy" onClick={changePassword}>
            {currentUser?.role==="owner"?"🔐 Update Password":"📤 Submit Request to Owner"}
          </Btn>
        </div>
      </Card>

      <Card style={{marginTop:16}}>
        <div style={{fontWeight:700,fontSize:13,color:C.navy,marginBottom:8}}>Invoice Terms & Conditions</div>
        <textarea value={s.terms||""} onChange={e=>setS(x=>({...x,terms:e.target.value}))} style={{width:"100%",padding:"8px 12px",border:`1px solid ${C.border}`,borderRadius:7,fontSize:12,height:70,resize:"vertical",fontFamily:"inherit",color:C.text}}/>
      </Card>
    </div>
  );
};


// ══════════════════════════════════════════════════════════════
// MODULE 1: PRODUCT P&L / MARGIN TRACKER
// ══════════════════════════════════════════════════════════════

const PartySearchSel = ({value,onChange,parties}) => {
  const [q,setQ]=useState("");
  const filtered=q?parties.filter(p=>p.name.toLowerCase().includes(q.toLowerCase())||(p.partyType||"").toLowerCase().includes(q.toLowerCase())):parties;
  const sel=parties.find(p=>p.id===value);
  return(
    <div>
      <input value={q} onChange={e=>setQ(e.target.value)}
        placeholder={sel?`✓ ${sel.name}`:"🔍 Type to filter parties..."}
        style={{width:"100%",padding:"7px 11px",border:`1px solid ${C.border}`,
          borderRadius:"8px 8px 0 0",fontSize:12,fontFamily:"inherit",outline:"none",
          boxSizing:"border-box",borderBottom:"none",background:"#f8fafc"}}
        onFocus={e=>e.target.style.borderColor="#5FA8A4"}
        onBlur={e=>e.target.style.borderColor=C.border}/>
      <select value={value||""} onChange={e=>{onChange(e.target.value);setQ("");}}
        size={Math.min(filtered.length+1,10)}
        style={{width:"100%",border:`1px solid ${C.border}`,borderRadius:"0 0 8px 8px",
          fontSize:12,fontFamily:"inherit",background:"#fff",outline:"none",
          display:"block",boxSizing:"border-box"}}>
        <option value="">— Select a party —</option>
        {filtered.map(p=><option key={p.id} value={p.id}>{p.name} ({p.partyType||""})</option>)}
      </select>
    </div>
  );
};


const PartyRatesModule = ({data,setData}) => {
  const parties=data.parties||[];
  const products=data.products||[];
  const [selParty,setSelParty]=useState("");
  const [q,setQ]=useState("");
  const [filterProd,setFilterProd]=useState("");

  // One-time migration: reset legacy array shape to object.
  // Empty dep array — must NOT include data.partyRates or setData would
  // trigger another render, re-run this effect, and loop infinitely.
  useEffect(()=>{
    if(Array.isArray(data.partyRates)){
      setData(d=>({...d,partyRates:{}}));
    }
  },[]); // eslint-disable-line react-hooks/exhaustive-deps

  const party=parties.find(p=>p.id===selParty);
  const _pr=(!data.partyRates||Array.isArray(data.partyRates))?{}:data.partyRates;
  const partyRates=selParty?(_pr[selParty]||{}):{};
  // (removed: if(Array.isArray(data.partyRates)) setTimeout(()=>setData(...),0) — was a render side-effect)

  const setRate=(productName,field,val)=>{
    setData(d=>{
      const pr=(!d.partyRates||Array.isArray(d.partyRates))?{}:{...d.partyRates};
      if(!pr[selParty]) pr[selParty]={};
      pr[selParty]={...pr[selParty],[productName]:{...(pr[selParty][productName]||{}),[field]:val===''?'':parseFloat(val)||0}};
      return {...d,partyRates:pr};
    });
  };

  const copyToAll=(productName,field)=>{
    const val=partyRates[productName]?.[field];
    if(!val){alert("Set a value first");return;}
    if(!window.confirm(`Copy ${field.toUpperCase()} ₹${val} of ${productName} to ALL parties?`)) return;
    setData(d=>{
      const pr=(!d.partyRates||Array.isArray(d.partyRates))?{}:{...d.partyRates};
      d.parties.forEach(p=>{
        if(!pr[p.id]) pr[p.id]={};
        pr[p.id]={...pr[p.id],[productName]:{...(pr[p.id][productName]||{}),[field]:val}};
      });
      return {...d,partyRates:pr};
    });
    alert(`✅ ${field.toUpperCase()} copied to all ${parties.length} parties`);
  };

  const pullFromProductMaster=()=>{
    if(!selParty) return;
    setData(d=>{
      const pr=(!d.partyRates||Array.isArray(d.partyRates))?{}:{...d.partyRates};
      if(!pr[selParty]) pr[selParty]={};
      d.products.forEach(p=>{
        pr[selParty][p.name]={...(pr[selParty][p.name]||{}),mrp:Number(p.mrp)||0,ptr:pr[selParty][p.name]?.ptr||(Number(p.ptr)||0),pts:pr[selParty][p.name]?.pts||(Number(p.pts)||0),gst:Number(p.gst)||18};
      });
      return {...d,partyRates:pr};
    });
    alert("✅ MRP & GST pulled from Product Master.");
  };

  const ratesFilled=Object.keys(partyRates).filter(k=>(Number(partyRates[k]?.ptr)||0)>0||(Number(partyRates[k]?.pts)||0)>0).length;
  const filtParties=q?parties.filter(p=>p.name.toLowerCase().includes(q.toLowerCase())||(p.partyType||"").toLowerCase().includes(q.toLowerCase())):parties;
  const filtProds=products.filter(p=>!filterProd||p.name.toLowerCase().includes(filterProd.toLowerCase()));

  return (
    <div className="fade">
      <PH title="Party Rates" sub="Set product-wise PTR & PTS per party"
        action={<BulkUpload title="Party Rates"
          templateHeaders={["Party Name","Product Name","MRP","PTR","PTS"]}
          templateRows={[["Apollo Pharmacy","GLURIV-C Serum","899","720","630"],["MedPlus Health","Melalure+ Face Wash","649","520","460"]]}
          parseRow={r=>r["Party Name"]&&r["Product Name"]?{partyName:r["Party Name"].trim(),productName:r["Product Name"].trim(),mrp:parseFloat(r.MRP)||0,ptr:parseFloat(r.PTR)||0,pts:parseFloat(r.PTS)||0}:null}
          onUpload={(rows,mode)=>{setData(d=>{
            const pr=mode==="replace"?{}:((!d.partyRates||Array.isArray(d.partyRates))?{}:{...d.partyRates});
            let matched=0,skipped=[];
            rows.forEach(r=>{
              const p=d.parties.find(x=>x.name.trim().toLowerCase()===r.partyName.toLowerCase());
              if(!p){skipped.push(r.partyName);return;}
              const prod=d.products.find(x=>x.name.trim().toLowerCase()===r.productName.toLowerCase());
              const prodName=prod?prod.name:r.productName;
              if(!pr[p.id]) pr[p.id]={};
              pr[p.id][prodName]={mrp:r.mrp,ptr:r.ptr,pts:r.pts};
              matched++;
            });
            if(skipped.length) alert(`⚠️ ${skipped.length} skipped:\n${[...new Set(skipped)].join(", ")}\n\n✅ ${matched} imported.`);
            return {...d,partyRates:pr};
          });}}
        />}
      />

      <div style={{display:"grid",gridTemplateColumns:"280px 1fr",gap:16,alignItems:"start"}}>

        {/* LEFT — Party list */}
        <div style={{background:"#fff",borderRadius:10,border:`1px solid ${C.border}`,overflow:"hidden"}}>
          <div style={{padding:"10px 12px",borderBottom:`1px solid ${C.border}`,background:C.sectionBg}}>
            <div style={{fontWeight:700,fontSize:12,color:C.navy,marginBottom:8}}>🏢 Select Party</div>
            <input value={q} onChange={e=>setQ(e.target.value)} placeholder="🔍 Search..."
              style={{width:"100%",padding:"6px 10px",border:`1px solid ${C.border}`,borderRadius:6,fontSize:12,fontFamily:"inherit",outline:"none",boxSizing:"border-box"}}/>
          </div>
          <div style={{maxHeight:550,overflowY:"auto"}}>
            {filtParties.map(p=>{
              const pr=(_pr[p.id]||{});
              const filled=Object.keys(pr).filter(k=>(Number(pr[k]?.ptr)||0)>0||(Number(pr[k]?.pts)||0)>0).length;
              const isSel=selParty===p.id;
              return(
                <div key={p.id} onClick={()=>{setSelParty(p.id);setFilterProd("");}}
                  style={{padding:"10px 14px",cursor:"pointer",borderLeft:`3px solid ${isSel?C.accent:"transparent"}`,
                    background:isSel?"#f0fdf4":"#fff",borderBottom:`1px solid ${C.border}`,transition:"all .1s"}}
                  onMouseEnter={e=>{if(!isSel)e.currentTarget.style.background="#f8fafc";}}
                  onMouseLeave={e=>{e.currentTarget.style.background=isSel?"#f0fdf4":"#fff";}}>
                  <div style={{fontWeight:isSel?700:500,fontSize:12,color:isSel?C.accent:C.text}}>{p.name}</div>
                  <div style={{fontSize:10,color:C.muted,marginTop:2}}>
                    {p.partyType}
                    {filled>0&&<span style={{color:"#16a34a",fontWeight:600}}> · {filled} rates set</span>}
                    {filled===0&&<span style={{color:"#dc2626"}}> · No rates</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT — Product rates table */}
        <div>
          {!selParty?(
            <div style={{background:"#fff",borderRadius:10,border:`1px solid ${C.border}`,padding:48,textAlign:"center",color:C.muted}}>
              <div style={{fontSize:32,marginBottom:8}}>🏢</div>
              <div style={{fontWeight:700,fontSize:14}}>Select a party from the left</div>
              <div style={{fontSize:12,marginTop:4}}>Click any party to set their PTR & PTS rates per product</div>
            </div>
          ):(
            <>
              {/* Party header */}
              <div style={{background:C.navy,color:"#fff",borderRadius:"10px 10px 0 0",padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
                <div>
                  <div style={{fontWeight:700,fontSize:14}}>{party?.name}</div>
                  <div style={{fontSize:11,opacity:.65}}>{party?.partyType} · {party?.city} · Credit: {party?.creditDays}d · {ratesFilled}/{products.length} products set</div>
                </div>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <input value={filterProd} onChange={e=>setFilterProd(e.target.value)} placeholder="🔍 Filter product..."
                    style={{padding:"5px 10px",borderRadius:6,border:"1px solid rgba(255,255,255,.3)",background:"rgba(255,255,255,.1)",color:"#fff",fontSize:11,outline:"none",fontFamily:"inherit"}}/>
                  <Btn v="outline" sz="sm" onClick={pullFromProductMaster} style={{borderColor:"rgba(255,255,255,.4)",color:"#fff"}}>📥 Pull from Master</Btn>
                </div>
              </div>

              <div style={{background:"#fff",border:`1px solid ${C.border}`,borderTop:"none",borderRadius:"0 0 10px 10px",overflow:"hidden"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                  <thead><tr style={{background:C.sectionBg}}>
                    {["Product","Category","MRP","PTR (Retailer)","PTS (Stockist)","Disc %","GST %","Rate on Invoice"].map(h=>(
                      <th key={h} style={{padding:"9px 12px",textAlign:"left",fontWeight:600,fontSize:10,color:C.muted,whiteSpace:"nowrap"}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {filtProds.map(p=>{
                      const r=partyRates[p.name]||{};
                      const pts=Number(r.pts)||0,ptr=Number(r.ptr)||0;
                      const isStockist=party?.partyType?.toLowerCase().includes("stockist")||party?.partyType?.toLowerCase().includes("distributor");
                      const invoiceRate=isStockist?pts:ptr;
                      const disc=invoiceRate&&p.mrp?Math.round((1-invoiceRate/Number(p.mrp))*100):0;
                      const hasRates=pts>0||ptr>0;
                      return(
                        <tr key={p.id} style={{borderBottom:`1px solid ${C.border}`,background:hasRates?"#fff":"#fffbeb"}}>
                          <td style={{padding:"8px 12px",fontWeight:600,fontSize:11}}>
                            {p.name}
                            {!hasRates&&<span style={{marginLeft:6,fontSize:9,color:"#d97706",background:"#fef3c7",padding:"1px 5px",borderRadius:4}}>Not set</span>}
                          </td>
                          <td style={{padding:"8px 12px",fontSize:10,color:C.muted}}>{p.category||"—"}</td>
                          <td style={{padding:"8px 12px",fontFamily:"monospace",fontWeight:600}}>₹{p.mrp}</td>
                          <td style={{padding:"4px 8px"}}>
                            <div style={{display:"flex",gap:4,alignItems:"center"}}>
                              <input value={r.ptr||""} onChange={e=>setRate(p.name,"ptr",e.target.value)} placeholder="₹" type="number"
                                style={{width:85,padding:"5px 8px",border:`1.5px solid ${ptr>0?C.accent:C.border}`,borderRadius:6,fontSize:11,fontFamily:"inherit",outline:"none",background:ptr>0?"#f0fdfa":"#fff"}}/>
                              <button onClick={()=>copyToAll(p.name,"ptr")} title="Copy to all parties" style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:C.muted}}>📋</button>
                            </div>
                          </td>
                          <td style={{padding:"4px 8px"}}>
                            <div style={{display:"flex",gap:4,alignItems:"center"}}>
                              <input value={r.pts||""} onChange={e=>setRate(p.name,"pts",e.target.value)} placeholder="₹" type="number"
                                style={{width:85,padding:"5px 8px",border:`1.5px solid ${pts>0?"#7c3aed":C.border}`,borderRadius:6,fontSize:11,fontFamily:"inherit",outline:"none",background:pts>0?"#f5f3ff":"#fff"}}/>
                              <button onClick={()=>copyToAll(p.name,"pts")} title="Copy to all parties" style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:C.muted}}>📋</button>
                            </div>
                          </td>
                          <td style={{padding:"8px 12px",fontSize:11,fontWeight:disc>0?700:400,color:disc>0?"#166534":C.muted}}>{invoiceRate?`${disc}%`:"—"}</td>
                          <td style={{padding:"4px 8px"}}>
                            <input value={r.gst!==undefined?r.gst:(p.gst||18)} onChange={e=>setRate(p.name,"gst",e.target.value)}
                              type="number" style={{width:55,padding:"4px 7px",border:`1px solid ${C.border}`,borderRadius:6,fontSize:11,fontFamily:"inherit",outline:"none"}}/>
                          </td>
                          <td style={{padding:"8px 12px"}}>
                            {invoiceRate>0
                              ?<span style={{fontSize:11,color:"#166534",fontWeight:700,background:"#dcfce7",padding:"3px 10px",borderRadius:20}}>₹{invoiceRate} ✓</span>
                              :<span style={{fontSize:10,color:"#d97706",background:"#fef3c7",padding:"2px 8px",borderRadius:20}}>Set PTR/PTS</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div style={{padding:"8px 16px",background:C.sectionBg,fontSize:11,color:C.muted}}>
                  💡 PTR = Retailer/Chemist · PTS = Stockist/Distributor · 📋 = Copy to all parties · Yellow = not set
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};


const DoctorRatesModule = ({data,setData}) => {
  const products=data.products||[];
  const [selDoctor,setSelDoctor]=useState("");
  const [q,setQ]=useState("");
  const [filterProd,setFilterProd]=useState("");

  const doctor=data.doctors.find(d=>d.id===selDoctor);
  const _dr=(!data.doctorRates||Array.isArray(data.doctorRates))?{}:data.doctorRates;
  const doctorRates=selDoctor?(_dr[selDoctor]||{}):{};

  const setRate=(productName,field,val)=>{
    setData(d=>{
      const dr=(!d.doctorRates||Array.isArray(d.doctorRates))?{}:{...d.doctorRates};
      if(!dr[selDoctor]) dr[selDoctor]={};
      dr[selDoctor]={...dr[selDoctor],[productName]:{...(dr[selDoctor][productName]||{}),[field]:val===''?'':parseFloat(val)||0}};
      return {...d,doctorRates:dr};
    });
  };

  const copyToAll=(productName,field)=>{
    const val=doctorRates[productName]?.[field];
    if(!val){alert("Set a value first");return;}
    if(!window.confirm(`Copy ${field.toUpperCase()} ₹${val} of ${productName} to ALL doctors?`)) return;
    setData(d=>{
      const dr=(!d.doctorRates||Array.isArray(d.doctorRates))?{}:{...d.doctorRates};
      d.doctors.forEach(doc=>{
        if(!dr[doc.id]) dr[doc.id]={};
        dr[doc.id]={...dr[doc.id],[productName]:{...(dr[doc.id][productName]||{}),[field]:val}};
      });
      return {...d,doctorRates:dr};
    });
    alert(`✅ Copied to all ${data.doctors.length} doctors`);
  };

  const pullFromProductMaster=()=>{
    if(!selDoctor) return;
    setData(d=>{
      const dr=(!d.doctorRates||Array.isArray(d.doctorRates))?{}:{...d.doctorRates};
      if(!dr[selDoctor]) dr[selDoctor]={};
      d.products.forEach(p=>{
        dr[selDoctor][p.name]={...(dr[selDoctor][p.name]||{}),mrp:Number(p.mrp)||0,pts:dr[selDoctor][p.name]?.pts||(Number(p.pts)||0),gst:Number(p.gst)||18};
      });
      return {...d,doctorRates:dr};
    });
    alert("✅ MRP pulled from Product Master.");
  };

  const ratesFilled=Object.keys(doctorRates).filter(k=>(Number(doctorRates[k]?.pts)||0)>0||(Number(doctorRates[k]?.ptr)||0)>0).length;
  const filtDoctors=q?data.doctors.filter(d=>d.name.toLowerCase().includes(q.toLowerCase())||(d.spec||"").toLowerCase().includes(q.toLowerCase())):data.doctors;
  const filtProds=products.filter(p=>!filterProd||p.name.toLowerCase().includes(filterProd.toLowerCase()));

  return(
    <div className="fade">
      <PH title="Doctor Rates" sub="Set special product rates per doctor for billing"/>

      <div style={{display:"grid",gridTemplateColumns:"280px 1fr",gap:16,alignItems:"start"}}>

        {/* LEFT — Doctor list */}
        <div style={{background:"#fff",borderRadius:10,border:`1px solid ${C.border}`,overflow:"hidden"}}>
          <div style={{padding:"10px 12px",borderBottom:`1px solid ${C.border}`,background:C.sectionBg}}>
            <div style={{fontWeight:700,fontSize:12,color:C.navy,marginBottom:8}}>👨‍⚕️ Select Doctor</div>
            <input value={q} onChange={e=>setQ(e.target.value)} placeholder="🔍 Search doctor..."
              style={{width:"100%",padding:"6px 10px",border:`1px solid ${C.border}`,borderRadius:6,fontSize:12,fontFamily:"inherit",outline:"none",boxSizing:"border-box"}}/>
          </div>
          <div style={{maxHeight:550,overflowY:"auto"}}>
            {filtDoctors.length===0&&(
              <div style={{padding:20,textAlign:"center",color:C.muted,fontSize:12}}>No doctors found</div>
            )}
            {filtDoctors.map(doc=>{
              const dr=(_dr[doc.id]||{});
              const filled=Object.keys(dr).filter(k=>(Number(dr[k]?.pts)||0)>0||(Number(dr[k]?.ptr)||0)>0).length;
              const isSel=selDoctor===doc.id;
              return(
                <div key={doc.id} onClick={()=>{setSelDoctor(doc.id);setFilterProd("");}}
                  style={{padding:"10px 14px",cursor:"pointer",borderLeft:`3px solid ${isSel?"#7c3aed":"transparent"}`,
                    background:isSel?"#f5f3ff":"#fff",borderBottom:`1px solid ${C.border}`,transition:"all .1s"}}
                  onMouseEnter={e=>{if(!isSel)e.currentTarget.style.background="#f8fafc";}}
                  onMouseLeave={e=>{e.currentTarget.style.background=isSel?"#f5f3ff":"#fff";}}>
                  <div style={{fontWeight:isSel?700:500,fontSize:12,color:isSel?"#7c3aed":C.text}}>Dr. {doc.name}</div>
                  <div style={{fontSize:10,color:C.muted,marginTop:2}}>
                    {doc.spec||"—"} · {doc.city||"—"}
                    {filled>0&&<span style={{color:"#16a34a",fontWeight:600}}> · {filled} rates set</span>}
                    {filled===0&&<span style={{color:"#dc2626"}}> · No rates</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT — Product rates */}
        <div>
          {!selDoctor?(
            <div style={{background:"#fff",borderRadius:10,border:`1px solid ${C.border}`,padding:48,textAlign:"center",color:C.muted}}>
              <div style={{fontSize:32,marginBottom:8}}>👨‍⚕️</div>
              <div style={{fontWeight:700,fontSize:14}}>Select a doctor from the left</div>
              <div style={{fontSize:12,marginTop:4}}>Set special rates for each product when billing to this doctor</div>
            </div>
          ):(
            <>
              <div style={{background:"#7c3aed",color:"#fff",borderRadius:"10px 10px 0 0",padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
                <div>
                  <div style={{fontWeight:700,fontSize:14}}>Dr. {doctor?.name}</div>
                  <div style={{fontSize:11,opacity:.7}}>{doctor?.spec} · {doctor?.clinic} · {ratesFilled}/{products.length} products set</div>
                </div>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <input value={filterProd} onChange={e=>setFilterProd(e.target.value)} placeholder="🔍 Filter product..."
                    style={{padding:"5px 10px",borderRadius:6,border:"1px solid rgba(255,255,255,.3)",background:"rgba(255,255,255,.1)",color:"#fff",fontSize:11,outline:"none",fontFamily:"inherit"}}/>
                  <Btn v="outline" sz="sm" onClick={pullFromProductMaster} style={{borderColor:"rgba(255,255,255,.4)",color:"#fff"}}>📥 Pull from Master</Btn>
                </div>
              </div>

              <div style={{background:"#fff",border:`1px solid ${C.border}`,borderTop:"none",borderRadius:"0 0 10px 10px",overflow:"hidden"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                  <thead><tr style={{background:C.sectionBg}}>
                    {["Product","Category","MRP","Doctor Rate (PTS)","PTR (Alt)","Disc %","GST %","Rate on Invoice"].map(h=>(
                      <th key={h} style={{padding:"9px 12px",textAlign:"left",fontWeight:600,fontSize:10,color:C.muted,whiteSpace:"nowrap"}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {filtProds.map(p=>{
                      const r=doctorRates[p.name]||{};
                      const pts=Number(r.pts)||0,ptr=Number(r.ptr)||0;
                      const invoiceRate=pts||ptr;
                      const disc=invoiceRate&&p.mrp?Math.round((1-invoiceRate/Number(p.mrp))*100):0;
                      const hasRate=pts>0||ptr>0;
                      return(
                        <tr key={p.id} style={{borderBottom:`1px solid ${C.border}`,background:hasRate?"#fff":"#fffbeb"}}>
                          <td style={{padding:"8px 12px",fontWeight:600,fontSize:11}}>
                            {p.name}
                            {!hasRate&&<span style={{marginLeft:6,fontSize:9,color:"#d97706",background:"#fef3c7",padding:"1px 5px",borderRadius:4}}>Not set</span>}
                          </td>
                          <td style={{padding:"8px 12px",fontSize:10,color:C.muted}}>{p.category||"—"}</td>
                          <td style={{padding:"8px 12px",fontFamily:"monospace",fontWeight:600}}>₹{p.mrp}</td>
                          <td style={{padding:"4px 8px"}}>
                            <div style={{display:"flex",gap:4,alignItems:"center"}}>
                              <input value={r.pts||""} onChange={e=>setRate(p.name,"pts",e.target.value)} placeholder="₹" type="number"
                                style={{width:85,padding:"5px 8px",border:`1.5px solid ${pts>0?"#7c3aed":C.border}`,borderRadius:6,fontSize:11,fontFamily:"inherit",outline:"none",background:pts>0?"#f5f3ff":"#fff"}}/>
                              <button onClick={()=>copyToAll(p.name,"pts")} title="Copy to all doctors" style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:C.muted}}>📋</button>
                            </div>
                          </td>
                          <td style={{padding:"4px 8px"}}>
                            <div style={{display:"flex",gap:4,alignItems:"center"}}>
                              <input value={r.ptr||""} onChange={e=>setRate(p.name,"ptr",e.target.value)} placeholder="₹" type="number"
                                style={{width:85,padding:"5px 8px",border:`1.5px solid ${ptr>0?C.accent:C.border}`,borderRadius:6,fontSize:11,fontFamily:"inherit",outline:"none",background:ptr>0?"#f0fdfa":"#fff"}}/>
                              <button onClick={()=>copyToAll(p.name,"ptr")} title="Copy to all doctors" style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:C.muted}}>📋</button>
                            </div>
                          </td>
                          <td style={{padding:"8px 12px",fontSize:11,fontWeight:disc>0?700:400,color:disc>0?"#166534":C.muted}}>{invoiceRate?`${disc}%`:"—"}</td>
                          <td style={{padding:"4px 8px"}}>
                            <input value={r.gst!==undefined?r.gst:(p.gst||18)} onChange={e=>setRate(p.name,"gst",e.target.value)}
                              type="number" style={{width:55,padding:"4px 7px",border:`1px solid ${C.border}`,borderRadius:6,fontSize:11,fontFamily:"inherit",outline:"none"}}/>
                          </td>
                          <td style={{padding:"8px 12px"}}>
                            {invoiceRate>0
                              ?<span style={{fontSize:11,color:"#7c3aed",fontWeight:700,background:"#f5f3ff",padding:"3px 10px",borderRadius:20}}>₹{invoiceRate} ✓</span>
                              :<span style={{fontSize:10,color:"#d97706",background:"#fef3c7",padding:"2px 8px",borderRadius:20}}>Set Rate</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div style={{padding:"8px 16px",background:C.sectionBg,fontSize:11,color:C.muted}}>
                  💡 Doctor Rate (PTS) = special rate when billing this doctor · 📋 = Copy to all doctors · Yellow = not set
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};


const SchemeMasterModule = ({data,setData,requestDelete}) => {
  const parties=data.parties||[];
  const products=data.products||[];
  const [selPartyId,setSelPartyId]=useState("");
  const [schemeForm,setSchemeForm]=useState({});// {productName: {buyQty,freeQty,discPct,notes,active}}
  const [editing,setEditing]=useState(null);
  const [filterParty,setFilterParty]=useState("All");
  const [q,setQ]=useState("");

  const schemes=data.schemeMaster||[];
  const selParty=parties.find(p=>p.id===selPartyId);
  const filteredParties=q?parties.filter(p=>p.name.toLowerCase().includes(q.toLowerCase())||(p.partyType||"").toLowerCase().includes(q.toLowerCase())):parties;

  // Load existing schemes for selected party into form
  const loadParty=(pid)=>{
    setSelPartyId(pid);
    const partySchemes=schemes.filter(s=>s.partyId===pid);
    const fm={};
    partySchemes.forEach(s=>{
      fm[s.product||"__all__"]={buyQty:s.buyQty||"",freeQty:s.freeQty||"",discPct:s.discPct||"",notes:s.notes||"",id:s.id,status:s.status||"Active"};
    });
    setSchemeForm(fm);
  };

  const updateRow=(product,field,val)=>{
    setSchemeForm(f=>({...f,[product]:{...f[product],[field]:val}}));
  };

  const saveAll=()=>{
    if(!selPartyId) return alert("Select a party first");
    setData(d=>{
      // Remove all existing schemes for this party
      let updated=d.schemeMaster.filter(s=>s.partyId!==selPartyId);
      // Add new ones from form
      Object.entries(schemeForm).forEach(([product,row])=>{
        if(row.buyQty||row.freeQty||row.discPct){
          updated=[...updated,{
            id:row.id||("SCH"+Date.now()+Math.random().toString(36).slice(2,5)),
            partyId:selPartyId,
            product:product==="__all__"?"":product,
            buyQty:row.buyQty||"",
            freeQty:row.freeQty||"",
            discPct:row.discPct||"",
            notes:row.notes||"",
            status:row.status||"Active"
          }];
        }
      });
      return {...d,schemeMaster:updated};
    });
    alert(`✅ Schemes saved for ${selParty?.name}`);
  };

  const toggle=id=>setData(d=>({...d,schemeMaster:d.schemeMaster.map(s=>s.id===id?{...s,status:s.status==="Active"?"Inactive":"Active"}:s)}));
  const del=id=>{if(requestDelete("schemeMaster",id,"Scheme"))setData(d=>({...d,schemeMaster:d.schemeMaster.filter(s=>s.id!==id)}));};

  const schemeLabel=s=>{
    const parts=[];
    if(s.buyQty&&s.freeQty) parts.push(`Buy ${s.buyQty} + ${s.freeQty} Free`);
    if(s.discPct) parts.push(`${s.discPct}% Disc`);
    return parts.join(" · ")||"—";
  };

  let filteredSchemes=schemes;
  if(filterParty!=="All") filteredSchemes=filteredSchemes.filter(s=>s.partyId===filterParty);

  return (
    <div className="fade">
      <PH title="Scheme Master" sub={`${schemes.filter(s=>s.status==="Active").length} active schemes`}
        action={<BulkUpload title="Scheme Master"
          templateHeaders={["Party Name","Product Name","Buy Qty","Free Qty","Discount %","Notes"]}
          templateRows={[["Care Pharmacy","GLURIV-C Serum","10","1","",""],["Apollo Pharmacy","Melalure+ Face Wash","5","1","5","Special offer"]]}
          parseRow={(r,i)=>r["Party Name"]?{_partyName:r["Party Name"],product:r["Product Name"]||"",buyQty:r["Buy Qty"]||"",freeQty:r["Free Qty"]||"",discPct:r["Discount %"]||"",notes:r["Notes"]||"",status:"Active",_rowIdx:i}:null}
          onUpload={(rows,mode)=>setData(d=>{
            const mapped=rows.map((r,i)=>{
              const party=d.parties.find(p=>p.name.trim().toLowerCase()===r._partyName.trim().toLowerCase());
              if(!party){console.warn("Party not found:",r._partyName);return null;}
              const {_partyName:_,_rowIdx:__,...clean}=r;
              return {...clean,id:"SCH"+Date.now()+"_"+i,partyId:party.id};
            }).filter(Boolean);
            if(!mapped.length){alert("No matching parties found.");return d;}
            return {...d,schemeMaster:mode==="replace"?mapped:[...(d.schemeMaster||[]),...mapped]};
          })}
        />}
      />

      <div style={{display:"grid",gridTemplateColumns:"280px 1fr",gap:16,alignItems:"start"}}>

        {/* LEFT — Party list with search */}
        <div style={{background:"#fff",borderRadius:10,border:`1px solid ${C.border}`,overflow:"hidden"}}>
          <div style={{padding:"10px 12px",borderBottom:`1px solid ${C.border}`,background:C.sectionBg}}>
            <div style={{fontWeight:700,fontSize:12,color:C.navy,marginBottom:8}}>🏢 Select Party</div>
            <input value={q} onChange={e=>setQ(e.target.value)}
              placeholder="🔍 Search party..."
              style={{width:"100%",padding:"6px 10px",border:`1px solid ${C.border}`,borderRadius:6,fontSize:12,fontFamily:"inherit",outline:"none",boxSizing:"border-box"}}/>
          </div>
          <div style={{maxHeight:500,overflowY:"auto"}}>
            {filteredParties.map(p=>{
              const cnt=schemes.filter(s=>s.partyId===p.id&&s.status==="Active").length;
              const isSel=selPartyId===p.id;
              return(
                <div key={p.id} onClick={()=>loadParty(p.id)}
                  style={{padding:"10px 14px",cursor:"pointer",borderLeft:`3px solid ${isSel?C.accent:"transparent"}`,
                    background:isSel?"#f0fdf4":"#fff",borderBottom:`1px solid ${C.border}`,
                    transition:"all .1s"}}
                  onMouseEnter={e=>{if(!isSel)e.currentTarget.style.background="#f8fafc";}}
                  onMouseLeave={e=>{e.currentTarget.style.background=isSel?"#f0fdf4":"#fff";}}>
                  <div style={{fontWeight:isSel?700:500,fontSize:12,color:isSel?C.accent:C.text}}>{p.name}</div>
                  <div style={{fontSize:10,color:C.muted,marginTop:2}}>{p.partyType} {cnt>0&&<span style={{color:"#16a34a",fontWeight:600}}>· {cnt} scheme{cnt>1?"s":""}</span>}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT — Product scheme rows */}
        <div>
          {!selPartyId?(
            <div style={{background:"#fff",borderRadius:10,border:`1px solid ${C.border}`,padding:48,textAlign:"center",color:C.muted}}>
              <div style={{fontSize:32,marginBottom:8}}>🎁</div>
              <div style={{fontWeight:600,fontSize:14}}>Select a party from the left</div>
              <div style={{fontSize:12,marginTop:4}}>Then set buy qty, free qty and discount for each product</div>
            </div>
          ):(
            <div>
              {/* Party header */}
              <div style={{background:C.navy,color:"#fff",borderRadius:"10px 10px 0 0",padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{fontWeight:700,fontSize:14}}>{selParty?.name}</div>
                  <div style={{fontSize:11,opacity:.6}}>{selParty?.partyType} · {selParty?.city}</div>
                </div>
                <Btn v="gold" onClick={saveAll}>💾 Save All Schemes</Btn>
              </div>

              {/* Product rows */}
              <div style={{background:"#fff",border:`1px solid ${C.border}`,borderTop:"none",borderRadius:"0 0 10px 10px",overflow:"hidden"}}>
                {/* Header */}
                <div style={{display:"grid",gridTemplateColumns:"2fr 80px 80px 80px 140px 60px",gap:0,background:C.sectionBg,borderBottom:`1px solid ${C.border}`,padding:"8px 14px"}}>
                  {["Product","Buy Qty","Free Qty","Disc %","Notes","Status"].map(h=>(
                    <div key={h} style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:.5}}>{h}</div>
                  ))}
                </div>

                {products.map((prod,idx)=>{
                  const row=schemeForm[prod.name]||{buyQty:"",freeQty:"",discPct:"",notes:"",status:"Active"};
                  const hasScheme=row.buyQty||row.freeQty||row.discPct;
                  return(
                    <div key={prod.id} style={{display:"grid",gridTemplateColumns:"2fr 80px 80px 80px 140px 60px",gap:0,
                      padding:"8px 14px",borderBottom:idx<products.length-1?`1px solid ${C.border}`:"none",
                      background:hasScheme?"#f0fdf4":"#fff",alignItems:"center"}}>
                      <div>
                        <div style={{fontWeight:600,fontSize:12}}>{prod.name}</div>
                        <div style={{fontSize:10,color:C.muted}}>MRP ₹{prod.mrp} · {prod.category}</div>
                      </div>
                      <input value={row.buyQty} onChange={e=>updateRow(prod.name,"buyQty",e.target.value)}
                        placeholder="e.g. 10" inputMode="numeric"
                        style={{width:"70px",padding:"5px 6px",border:`1px solid ${hasScheme?"#86efac":C.border}`,borderRadius:6,fontSize:12,fontFamily:"inherit",outline:"none"}}/>
                      <input value={row.freeQty} onChange={e=>updateRow(prod.name,"freeQty",e.target.value)}
                        placeholder="e.g. 1" inputMode="numeric"
                        style={{width:"70px",padding:"5px 6px",border:`1px solid ${hasScheme?"#86efac":C.border}`,borderRadius:6,fontSize:12,fontFamily:"inherit",outline:"none"}}/>
                      <input value={row.discPct} onChange={e=>updateRow(prod.name,"discPct",e.target.value)}
                        placeholder="%" inputMode="numeric"
                        style={{width:"70px",padding:"5px 6px",border:`1px solid ${hasScheme?"#86efac":C.border}`,borderRadius:6,fontSize:12,fontFamily:"inherit",outline:"none"}}/>
                      <input value={row.notes} onChange={e=>updateRow(prod.name,"notes",e.target.value)}
                        placeholder="Notes..."
                        style={{width:"130px",padding:"5px 6px",border:`1px solid ${C.border}`,borderRadius:6,fontSize:11,fontFamily:"inherit",outline:"none"}}/>
                      <div style={{fontSize:10,color:hasScheme?"#16a34a":"#9ca3af",fontWeight:hasScheme?700:400}}>
                        {hasScheme?"✅ Set":"—"}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{marginTop:10,padding:"8px 12px",background:"#f0fdf4",borderRadius:8,border:"1px solid #bbf7d0",fontSize:11,color:"#166534"}}>
                💡 Fill Buy Qty + Free Qty for "Buy X Get Y Free" schemes, or just Disc% for discount schemes. Leave blank to remove. Click <strong>Save All Schemes</strong> when done.
              </div>
            </div>
          )}

          {/* All schemes summary */}
          <div style={{marginTop:20}}>
            <div style={{fontWeight:700,fontSize:13,color:C.navy,marginBottom:10}}>📋 All Active Schemes</div>
            <div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap"}}>
              <button onClick={()=>setFilterParty("All")}
                style={{padding:"3px 12px",borderRadius:20,border:`1px solid ${C.border}`,background:filterParty==="All"?C.navy:"#fff",color:filterParty==="All"?"#fff":C.text,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>
                All ({schemes.filter(s=>s.status==="Active").length})
              </button>
              {parties.filter(p=>schemes.some(s=>s.partyId===p.id&&s.status==="Active")).map(p=>(
                <button key={p.id} onClick={()=>setFilterParty(p.id)}
                  style={{padding:"3px 12px",borderRadius:20,border:`1px solid ${filterParty===p.id?"#7c3aed":C.border}`,
                    background:filterParty===p.id?"#7c3aed":"#fff",color:filterParty===p.id?"#fff":C.text,
                    fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>
                  {p.name} ({schemes.filter(s=>s.partyId===p.id&&s.status==="Active").length})
                </button>
              ))}
            </div>
            <Tbl headers={["Party","Product","Scheme","Notes","Status","Action"]}
              rows={filteredSchemes.map(s=>{
                const party=parties.find(p=>p.id===s.partyId);
                return[
                  <span style={{fontSize:12,fontWeight:600}}>{party?.name||"—"}</span>,
                  <span style={{fontSize:12}}>{s.product||"All Products"}</span>,
                  <span style={{fontWeight:700,color:"#7c3aed",fontSize:12}}>{schemeLabel(s)}</span>,
                  <span style={{fontSize:11,color:C.muted}}>{s.notes||"—"}</span>,
                  <Badge s={s.status}/>,
                  <div style={{display:"flex",gap:4}}>
                    <Btn sz="sm" v="ghost" onClick={()=>loadParty(s.partyId)}>✏️</Btn>
                    <Btn sz="sm" v={s.status==="Active"?"outline":"navy"} onClick={()=>toggle(s.id)}>{s.status==="Active"?"Pause":"On"}</Btn>
                    <Btn sz="sm" v="danger" onClick={()=>del(s.id)}>Del</Btn>
                  </div>
                ];
              })}
              empty="No schemes yet."
            />
          </div>
        </div>
      </div>
    </div>
  );
};



const ReturnModule = ({data,setData,requestDelete}) => {
  const parties=data.parties||[];
  const products=data.products||[];

  // ── SALEABLE = stock reusable (good condition), NON-SALEABLE = damage/expiry bucket ──
  const SALEABLE_TYPES   = ["Saleable Return","Unsold Goods"];
  const NONSALEABLE_TYPES= ["Non Saleable Return","Damage Return","Quality Issue","Expiry Return","Short Expiry Return"];
  const ALL_RETURN_TYPES = [...SALEABLE_TYPES,...NONSALEABLE_TYPES];

  const blank={returnNo:"",date:today(),party:"",refInvoice:"",product:"",batch:"",qty:"",
    reason:"",returnCategory:"Saleable",returnType:"Saleable Return",condition:"Good",notes:""};
  const [form,setForm]=useState(blank);
  const [show,setShow]=useState(false);
  const [filter,setFilter]=useState("All");
  const [retSearch,setRetSearch]=useState("");
  const [retFrom,setRetFrom]=useState("");
  const [retTo,setRetTo]=useState("");
  const sf=k=>v=>setForm(p=>({...p,[k]:v}));
  const returns=data.salesReturns||[];
  const rid=()=>"SR"+Date.now();
  const nextNo=()=>`RTN-${String((returns.length+1)).padStart(3,"0")}`;

  // When category changes, auto-set a sensible default type
  const setCategory=cat=>{
    const defType=cat==="Saleable"?"Saleable Return":"Non Saleable Return";
    setForm(p=>({...p,returnCategory:cat,returnType:defType}));
  };

  const save=()=>{
    if(!form.party||!form.product||!form.qty){alert("Party, product and qty required");return;}
    const qty=parseFloat(form.qty);
    const refInv=data.invoices.find(i=>i.no===form.refInvoice);
    const rate=refInv?.items?.find(it=>it.product===form.product)?.rate||0;
    const isSaleable=SALEABLE_TYPES.includes(form.returnType);
    const mrId=refInv?.mrId||"";
    const newReturn={...form,id:rid(),returnNo:form.returnNo||nextNo(),qty,
      value:rate*qty,status:"Pending",isSaleable,deductFromMR:isSaleable,mrId};
    // IMPORTANT: do NOT touch batches here. Stock is only adjusted on Approval.
    // Premature mutation on Pending creation caused stock corruption.
    setData(d=>({...d,salesReturns:[...(d.salesReturns||[]),newReturn]}));
    setForm(blank);setShow(false);
  };

  // ── On Approve → auto Credit Note + park in party payment ledger ──
  const updateStatus=(id,status)=>setData(d=>{
    const ret=d.salesReturns.find(r=>r.id===id);
    let creditDebitNotes=d.creditDebitNotes||[];
    let invoices=d.invoices||[];
    let batches=[...d.batches];

    // Apply stock change ONLY at approval
    if(status==="Approved"&&ret&&ret.status!=="Approved"){
      if(ret.product&&ret.batch){
        const idx=batches.findIndex(b=>b.product===ret.product&&b.batch===ret.batch);
        if(idx>=0){
          if(ret.isSaleable!==false){
            // Saleable → reverse the outward (stock comes back)
            batches[idx]={...batches[idx],out:Math.max(0,(batches[idx].out||0)-ret.qty)};
          } else {
            // Non-Saleable → move to damaged bucket
            batches[idx]={...batches[idx],
              out:Math.max(0,(batches[idx].out||0)-ret.qty),
              damaged:(batches[idx].damaged||0)+ret.qty
            };
          }
        }
      }
      const cnNo="CN-"+String((creditDebitNotes.length+1)).padStart(3,"0");
      // Derive GST from the source invoice line or product master — never hardcode 18%
      const refInvObj=(d.invoices||[]).find(i=>i.no===ret.refInvoice);
      const refItem=refInvObj?.items?.find(it=>it.product===ret.product);
      const prodMaster=(d.products||[]).find(p=>p.name===ret.product);
      const gst=refItem?.gst??prodMaster?.gst??18; // fallback to 18 only if truly unknown
      const taxable=ret.value||0;
      const cgst=Math.round(taxable*gst/200*100)/100;
      const total=Math.round(taxable+cgst*2);
      const cn={
        id:"CN"+Date.now(),no:cnNo,date:today(),type:"Credit Note",
        party:ret.party,product:ret.product,batch:ret.batch||"",
        qty:ret.qty,rate:(ret.value/ret.qty)||0,gst,
        taxable,cgst,sgst:cgst,total,
        reason:ret.reason||ret.returnType,
        returnCategory:ret.returnCategory||"Saleable",
        isSaleable:ret.isSaleable!==false,
        status:"Approved",
        mrName:ret.mrName||"",refReturn:ret.returnNo,
        deductFromMR:ret.deductFromMR,
        parkedInLedger:true
      };
      creditDebitNotes=[...creditDebitNotes,cn];
      if(ret.refInvoice){
        invoices=invoices.map(inv=>{
          if(inv.no!==ret.refInvoice) return inv;
          const newPaid=Math.min(inv.total,Math.max(0,(inv.paid||0)+total));
          const newStatus=newPaid>=inv.total?"Paid":newPaid>0?"Partial":inv.status;
          return {...inv,paid:newPaid,status:inv.status==="Cancelled"?"Cancelled":newStatus};
        });
      }
    } else if(status==="Rejected"&&ret&&ret.status==="Approved"){
      // Reverting an approval — undo the stock change
      if(ret.product&&ret.batch){
        const idx=batches.findIndex(b=>b.product===ret.product&&b.batch===ret.batch);
        if(idx>=0){
          if(ret.isSaleable!==false){
            batches[idx]={...batches[idx],out:(batches[idx].out||0)+ret.qty};
          } else {
            batches[idx]={...batches[idx],
              out:(batches[idx].out||0)+ret.qty,
              damaged:Math.max(0,(batches[idx].damaged||0)-ret.qty)
            };
          }
        }
      }
    }
    return {
      ...d,
      creditDebitNotes,
      invoices,
      batches,
      salesReturns:d.salesReturns.map(r=>r.id===id?{...r,status}:r)
    };
  });
  const del=id=>{
    if(requestDelete("salesReturns",id,"Sales Return"))
      setData(d=>{
        const ret=(d.salesReturns||[]).find(r=>r.id===id);
        let batches=[...d.batches];
        // If the return was already Approved, reverse the stock change on delete
        if(ret?.status==="Approved"&&ret.product&&ret.batch){
          const idx=batches.findIndex(b=>b.product===ret.product&&b.batch===ret.batch);
          if(idx>=0){
            if(ret.isSaleable!==false){
              batches[idx]={...batches[idx],out:(batches[idx].out||0)+ret.qty};
            } else {
              batches[idx]={...batches[idx],
                out:(batches[idx].out||0)+ret.qty,
                damaged:Math.max(0,(batches[idx].damaged||0)-ret.qty)
              };
            }
          }
        }
        return {...d,salesReturns:(d.salesReturns||[]).filter(r=>r.id!==id),batches};
      });
  };

  const filtered=(()=>{
    const parseD=s=>{if(!s||!s.includes("/"))return null;const[d,m,y]=s.split("/");return new Date(+y,+m-1,+d);};
    return returns.filter(r=>{
      if(filter!=="All"&&filter==="Saleable"&&r.isSaleable===false) return false;
      if(filter!=="All"&&filter==="Non Saleable"&&r.isSaleable!==false) return false;
      if(filter!=="All"&&!["All","Saleable","Non Saleable"].includes(filter)&&r.status!==filter) return false;
      if(retSearch&&!((r.party||"")+(r.product||"")).toLowerCase().includes(retSearch.toLowerCase())) return false;
      const rd=parseD(r.date);
      if(retFrom&&parseD(retFrom)&&rd&&rd<parseD(retFrom)) return false;
      if(retTo&&parseD(retTo)&&rd&&rd>parseD(retTo)) return false;
      return true;
    });
  })();
  const FILTER_TABS=["All","Saleable","Non Saleable","Pending","Approved","Rejected"];
  const STATUS_C={Pending:"#ca8a04",Approved:"#16a34a",Rejected:"#dc2626","Credit Issued":"#2563eb"};
  const saleableTypes=SALEABLE_TYPES;
  const nonSaleableTypes=NONSALEABLE_TYPES;

  const sCount=returns.filter(r=>r.isSaleable!==false).length;
  const nsCount=returns.filter(r=>r.isSaleable===false).length;
  const pendCount=returns.filter(r=>r.status==="Pending").length;
  const sVal=returns.filter(r=>r.isSaleable!==false&&r.status==="Approved").reduce((s,r)=>s+(r.value||0),0);
  const nsVal=returns.filter(r=>r.isSaleable===false&&r.status==="Approved").reduce((s,r)=>s+(r.value||0),0);

  return (
    <div>
      <PH title="Sales Returns" sub="Saleable returns go back to stock · Non-Saleable go to damage/expiry bucket"/>

      {/* ── Summary KPI cards ── */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:16}}>
        <div style={{background:"#F0FDF4",borderRadius:10,padding:16,border:"1px solid #BBF7D0"}}>
          <div style={{fontSize:11,fontWeight:700,color:"#166534",marginBottom:4}}>↩️ Saleable Returns</div>
          <div style={{fontSize:26,fontWeight:900,color:"#166534"}}>{sCount}</div>
          <div style={{fontSize:11,color:"#166534",opacity:.7}}>₹{fmt(sVal)} credited · Back to stock</div>
        </div>
        <div style={{background:"#FEF2F2",borderRadius:10,padding:16,border:"1px solid #FECACA"}}>
          <div style={{fontSize:11,fontWeight:700,color:"#991B1B",marginBottom:4}}>💥 Non-Saleable Returns</div>
          <div style={{fontSize:26,fontWeight:900,color:"#991B1B"}}>{nsCount}</div>
          <div style={{fontSize:11,color:"#991B1B",opacity:.7}}>₹{fmt(nsVal)} credited · Damage bucket</div>
        </div>
        <div style={{background:"#FFFBEB",borderRadius:10,padding:16,border:"1px solid #FDE68A"}}>
          <div style={{fontSize:11,fontWeight:700,color:"#92400E",marginBottom:4}}>⏳ Pending Approval</div>
          <div style={{fontSize:26,fontWeight:900,color:"#92400E"}}>{pendCount}</div>
          <div style={{fontSize:11,color:"#92400E",opacity:.7}}>Awaiting owner action</div>
        </div>
      </div>

      {/* ── Filter tabs + New button ── */}
      <div style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:10,padding:"12px 16px",marginBottom:14}}>
        <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"flex-end"}}>
          <Field label="Search Party/Product"><Inp value={retSearch} onChange={setRetSearch} placeholder="Search..."/></Field>
          <Field label="From"><DateInp value={retFrom} onChange={setRetFrom}/></Field>
          <Field label="To"><DateInp value={retTo} onChange={setRetTo}/></Field>
          {(retSearch||retFrom||retTo)&&<Btn v="ghost" onClick={()=>{setRetSearch("");setRetFrom("");setRetTo("");}}>✕ Clear</Btn>}
        </div>
      </div>
      <div style={{display:"flex",gap:8,marginBottom:16,alignItems:"center",justifyContent:"space-between",flexWrap:"wrap"}}>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {FILTER_TABS.map(t=>(
            <button key={t} onClick={()=>setFilter(t)}
              style={{padding:"5px 12px",border:`1px solid ${C.border}`,borderRadius:20,
                background:filter===t?C.navy:"#fff",color:filter===t?"#fff":C.text,
                fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:filter===t?700:400}}>
              {t}
            </button>
          ))}
        </div>
        <Btn v="navy" onClick={()=>{setShow(s=>!s);setForm(blank);}}>+ New Return</Btn>
      </div>

      {/* ── New Return Form ── */}
      {show&&(
        <div style={{background:"#fff",borderRadius:10,padding:20,marginBottom:20,border:`1px solid ${C.border}`}}>
          <div style={{fontWeight:700,fontSize:14,marginBottom:6}}>Record New Return</div>

          {/* ── Category selector — PRIMARY CHOICE ── */}
          <div style={{display:"flex",gap:10,marginBottom:16}}>
            {[["Saleable","↩️ Saleable Return","Stock reusable — good condition product","#F0FDF4","#166534","#BBF7D0"],
              ["Non Saleable","💥 Non Saleable Return","Damage / Expired — separate bucket","#FEF2F2","#991B1B","#FECACA"]
            ].map(([cat,label,sub,bg,fg,border])=>(
              <div key={cat} onClick={()=>setCategory(cat)}
                style={{flex:1,padding:"12px 16px",borderRadius:10,border:`2px solid ${form.returnCategory===cat?fg:border}`,
                  background:form.returnCategory===cat?bg:"#fff",cursor:"pointer",transition:"all .15s"}}>
                <div style={{fontWeight:700,color:fg,fontSize:13}}>{label}</div>
                <div style={{fontSize:11,color:fg,opacity:.7,marginTop:2}}>{sub}</div>
              </div>
            ))}
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,alignItems:"start"}}>
            <Field label="Return No"><Inp value={form.returnNo} onChange={sf("returnNo")} placeholder={nextNo()}/></Field>
            <Field label="Date"><DateInp value={form.date} onChange={sf("date")}/></Field>
            <Field label="Return Type">
              <Sel value={form.returnType} onChange={sf("returnType")}
                options={form.returnCategory==="Saleable"?saleableTypes:nonSaleableTypes}/>
            </Field>
            <Field label="Party">
              <Sel value={form.party} onChange={sf("party")} options={parties.map(p=>p.name)} placeholder="Select party"/>
            </Field>
            <Field label="Reference Invoice"><Inp value={form.refInvoice} onChange={sf("refInvoice")} placeholder="INV-001"/></Field>
            <Field label="Product">
              <Sel value={form.product} onChange={sf("product")} options={products.map(p=>p.name)} placeholder="Select product"/>
            </Field>
            <Field label="Batch No"><Inp value={form.batch} onChange={sf("batch")} placeholder="Batch number"/></Field>
            <Field label="Qty Returned"><Inp value={form.qty} onChange={sf("qty")} type="number" placeholder="Quantity"/></Field>
            <Field label="Condition">
              <Sel value={form.condition} onChange={sf("condition")} options={["Good","Damaged","Expired","Near Expiry"]}/>
            </Field>
          </div>
          <Field label="Reason for Return"><Inp value={form.reason} onChange={sf("reason")} placeholder="Customer complaint / Damaged in transit / Near expiry..."/></Field>
          <Field label="Notes"><Inp value={form.notes} onChange={sf("notes")} placeholder="Any additional notes"/></Field>

          {/* ── Info strip based on category ── */}
          <div style={{marginTop:12,padding:"10px 14px",borderRadius:8,fontSize:11,
            background:form.returnCategory==="Saleable"?"#F0FDF4":"#FEF2F2",
            color:form.returnCategory==="Saleable"?"#166534":"#991B1B",
            border:`1px solid ${form.returnCategory==="Saleable"?"#BBF7D0":"#FECACA"}`}}>
            {form.returnCategory==="Saleable"
              ? "✅ Stock will be reversed back to main inventory. Credit Note will be generated and parked in party ledger on approval."
              : "⚠️ Stock will move to Damaged/Expired bucket — NOT back to main stock. Credit Note will be generated and parked in party ledger on approval."}
          </div>

          <div style={{display:"flex",gap:8,marginTop:14}}>
            <Btn v="navy" onClick={save}>💾 Save Return</Btn>
            <Btn v="ghost" onClick={()=>setShow(false)}>Cancel</Btn>
          </div>
        </div>
      )}

      {/* ── Table ── */}
      {filtered.length===0&&<div style={{color:C.muted,textAlign:"center",padding:40,background:"#fff",borderRadius:10,border:`1px solid ${C.border}`}}>No returns found.</div>}
      <div style={{background:"#fff",borderRadius:10,border:`1px solid ${C.border}`,overflow:"hidden"}}>
        {filtered.length>0&&<table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
          <thead><tr style={{background:C.navy}}>
            {["Return No","Date","Category","Type","Party","Product","Batch","Qty","Value","Reason","Status","Action"].map(h=>(
              <th key={h} style={{padding:"9px 10px",textAlign:"left",color:"#fff",fontWeight:600,fontSize:10,whiteSpace:"nowrap"}}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {filtered.slice().reverse().map(r=>(
              <tr key={r.id} style={{borderBottom:`1px solid ${C.border}`,background:r.isSaleable===false?"#FFF8F8":"#fff"}}>
                <td style={{padding:"9px 10px",fontWeight:700,fontFamily:"monospace",fontSize:11}}>{r.returnNo}</td>
                <td style={{padding:"9px 10px",color:C.muted,fontSize:11}}>{r.date}</td>
                <td style={{padding:"9px 10px"}}>
                  <span style={{background:r.isSaleable===false?"#FEF2F2":"#F0FDF4",
                    color:r.isSaleable===false?"#991B1B":"#166534",
                    padding:"2px 8px",borderRadius:10,fontSize:10,fontWeight:700}}>
                    {r.isSaleable===false?"💥 Non-Saleable":"↩️ Saleable"}
                  </span>
                </td>
                <td style={{padding:"9px 10px"}}><span style={{background:"#f1f5f9",padding:"2px 7px",borderRadius:10,fontSize:10}}>{r.returnType}</span></td>
                <td style={{padding:"9px 10px",fontWeight:600}}>{r.party}</td>
                <td style={{padding:"9px 10px"}}>{r.product}</td>
                <td style={{padding:"9px 10px",fontFamily:"monospace",fontSize:11}}>{r.batch}</td>
                <td style={{padding:"9px 10px",textAlign:"center",fontWeight:700}}>{r.qty}</td>
                <td style={{padding:"9px 10px",fontFamily:"monospace",fontWeight:700,color:"#166534"}}>₹{fmt(r.value||0)}</td>
                <td style={{padding:"9px 10px",color:C.muted,maxWidth:120,overflow:"hidden",textOverflow:"ellipsis",fontSize:11}}>{r.reason}</td>
                <td style={{padding:"9px 10px"}}>
                  <select value={r.status} onChange={e=>updateStatus(r.id,e.target.value)}
                    style={{padding:"3px 6px",border:`1px solid ${C.border}`,borderRadius:5,fontSize:10,fontFamily:"inherit",color:STATUS_C[r.status]||"#374151"}}>
                    {["Pending","Approved","Rejected"].map(s=><option key={s}>{s}</option>)}
                  </select>
                </td>
                <td style={{padding:"9px 10px"}}>
                  <button onClick={()=>del(r.id)} style={{background:"none",border:"none",cursor:"pointer",color:"#dc2626",fontSize:12}}>🗑</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// MODULE 4: DOCTOR VISIT LOGGER (MR)
// ══════════════════════════════════════════════════════════════



const Login = ({onLogin,users}) => {
  const [email,setEmail]=useState("");
  const [pwd,setPwd]=useState("");
  const [err,setErr]=useState("");
  const [loading,setLoading]=useState(false);
  const attempt=async()=>{
    setLoading(true);
    setErr("");
    try {
      // ✅ Supabase Auth — password is never compared in the browser
      const { data: authData, error: authError } =
        await supabase.auth.signInWithPassword({
          email: email.trim().toLowerCase(),
          password: pwd,
        });

      if (authError || !authData?.user) {
        setErr("Invalid credentials or account inactive");
        setLoading(false);
        setTimeout(() => setErr(""), 4000);
        return;
      }

      // ✅ Fetch role + name from users table using the Auth UUID
      const { data: profile, error: profileError } = await supabase
        .from("users")
        .select("*")
        .eq("id", authData.user.id)
        .single();

      if (profileError || !profile) {
        setErr("Account setup incomplete. Contact admin.");
        await supabase.auth.signOut();
        setLoading(false);
        return;
      }

      if (profile.active === false || profile.is_active === false) {
        await supabase.auth.signOut();
        setErr("Your account has been deactivated.");
        setLoading(false);
        return;
      }

      // ✅ Same shape the app already expects — nothing else changes
      const normalizedUser = {
        id: authData.user.id,
        email: authData.user.email,
        name: profile.name || authData.user.email,
        role: (profile.role || "mr").toLowerCase(),
        phone: profile.phone || "",
        active: true,
        asmId: profile.asm_id || profile.asmId || "",
        region: profile.region || "",
      };

      setLoading(false);
      onLogin(normalizedUser);

    } catch (e) {
      console.error("Login error:", e);
      setErr("Something went wrong. Please try again.");
      setLoading(false);
    }
  };
  const handleKey=e=>{if(e.key==="Enter")attempt();};
  return (
    <div style={{minHeight:"100vh",display:"flex",fontFamily:"'DM Sans',sans-serif"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=DM+Sans:wght@300;400;500;600;700&display=swap');
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes fadeIn{from{opacity:0;transform:translateX(20px)}to{opacity:1;transform:none}}
        .login-input{width:100%;padding:12px 14px;border:1.5px solid #E4DDD1;border-radius:8px;font-size:13px;font-family:'DM Sans',sans-serif;outline:none;transition:border .15s;box-sizing:border-box;background:#FDFCFB;color:#2C2C2C;}
        .login-input:focus{border-color:#B8972A;box-shadow:0 0 0 3px rgba(184,151,42,.14);}
        .login-input::placeholder{color:#B0A898;}
      `}</style>

      {/* LEFT PANEL — Yapiderma Navy */}
      <div style={{
        width:"50%",minHeight:"100vh",
        background:"linear-gradient(160deg,#0A1729 0%,#0F1E38 50%,#1C3353 100%)",
        display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
        padding:"40px 48px",position:"relative",overflow:"hidden"
      }}>
        {/* Gold decorative rings */}
        <div style={{position:"absolute",top:-80,right:-80,width:320,height:320,borderRadius:"50%",border:"1px solid rgba(184,151,42,.10)",pointerEvents:"none"}}/>
        <div style={{position:"absolute",bottom:-60,left:-60,width:240,height:240,borderRadius:"50%",border:"1px solid rgba(184,151,42,.08)",pointerEvents:"none"}}/>
        <div style={{position:"absolute",top:"40%",left:-40,width:160,height:160,borderRadius:"50%",background:"rgba(184,151,42,.04)",pointerEvents:"none"}}/>
        <div style={{marginBottom:32,textAlign:"center",zIndex:1}}>
          <img src={YAPIDERMA_LOGO} alt="Yapiderma" style={{height:72,width:"auto",filter:"brightness(0) invert(1)",marginBottom:20}}/>
          <div style={{width:48,height:1,background:"rgba(184,151,42,.7)",margin:"0 auto 20px",borderRadius:2}}/>
          <div style={{color:"rgba(232,217,160,.9)",fontSize:12,fontWeight:400,letterSpacing:3.5,textTransform:"uppercase",fontFamily:"'Cormorant Garamond',Georgia,serif",fontStyle:"italic",fontSize:16}}>
            Dedicated to Wellness
          </div>
        </div>
        <div style={{zIndex:1,display:"flex",flexDirection:"column",gap:10,marginTop:40,width:"100%",maxWidth:300}}>
          {[["🧾","Complete GST Invoicing"],["📦","Batch & Inventory Tracking"],["👨‍⚕️","Doctor CRM & Field Force"],["📊","Real-time Sales Analytics"]].map(([icon,text])=>(
            <div key={text} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 16px",background:"rgba(184,151,42,.06)",borderRadius:8,border:"1px solid rgba(184,151,42,.16)"}}>
              <span style={{fontSize:15}}>{icon}</span>
              <span style={{color:"rgba(232,217,160,.80)",fontSize:12,fontWeight:400,letterSpacing:.2}}>{text}</span>
            </div>
          ))}
        </div>
        <div style={{position:"absolute",bottom:24,fontSize:10,color:"rgba(184,151,42,.30)",letterSpacing:.8,zIndex:1,textTransform:"uppercase"}}>
          © {new Date().getFullYear()} Yapiderma Pharmaceuticals Pvt Ltd
        </div>
      </div>

      {/* RIGHT PANEL — Cream white */}
      <div style={{
        width:"50%",minHeight:"100vh",background:"#F9F7F2",
        display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
        padding:"40px 56px",animation:"fadeIn .4s ease"
      }}>
        <div style={{width:"100%",maxWidth:380}}>
          <div style={{marginBottom:36}}>
            <div style={{fontSize:28,fontWeight:700,color:"#0F1E38",marginBottom:6,fontFamily:"'Cormorant Garamond',Georgia,serif",letterSpacing:-.5}}>Welcome back</div>
            <div style={{fontSize:13,color:"#717171",fontWeight:400}}>Sign in to your Yapiderma CRM account</div>
          </div>
          <div style={{marginBottom:18}}>
            <label style={{fontSize:10,fontWeight:600,color:"#717171",display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:1.2}}>Email Address</label>
            <input className="login-input" value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={handleKey} placeholder="Enter your email" type="email" autoComplete="off" name="crm-email"/>
          </div>
          <div style={{marginBottom:24}}>
            <label style={{fontSize:10,fontWeight:600,color:"#717171",display:"block",marginBottom:6,textTransform:"uppercase",letterSpacing:1.2}}>Password</label>
            <input className="login-input" value={pwd} onChange={e=>setPwd(e.target.value)} onKeyDown={handleKey} placeholder="Enter your password" type="password" autoComplete="new-password" name="crm-password"/>
          </div>
          {err&&<div style={{background:"#8B3A5212",border:"1px solid #8B3A5244",borderRadius:8,padding:"10px 14px",fontSize:12,color:"#8B3A52",marginBottom:18,fontWeight:500}}>⚠ {err}</div>}
          <button onClick={attempt} disabled={loading} style={{
            width:"100%",padding:"13px",
            background:loading?"#B8972A99":"linear-gradient(135deg,#0F1E38,#1C3353)",
            color:"#E8D9A0",border:"1px solid rgba(184,151,42,.25)",borderRadius:9,fontSize:14,fontWeight:600,
            cursor:loading?"default":"pointer",fontFamily:"'DM Sans',sans-serif",
            display:"flex",alignItems:"center",justifyContent:"center",gap:8,
            transition:"all .2s",boxShadow:loading?"none":"0 4px 18px rgba(15,30,56,.35)",
            letterSpacing:.3
          }}>
            {loading?<><div style={{width:15,height:15,border:"2px solid rgba(232,217,160,.4)",borderTopColor:"#E8D9A0",borderRadius:"50%",animation:"spin .7s linear infinite"}}/> Signing in...</>:"Sign In →"}
          </button>
        </div>
      </div>
    </div>
  );
};





// ── MANUFACTURERS MODULE ────────────────────────────────────────
const ManufacturersModule = ({data, setData, requestDelete}) => {
  const blank = {id:"", name:"", code:"", address:"", city:"", state:"Delhi", gstin:"", 
                 phone:"", email:"", contactPerson:"", drugLicense:"", active:true};
  const [form, setForm] = useState(blank);
  const [show, setShow] = useState(false);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  const sf = k => v => setForm(f=>({...f,[k]:v}));

  const manufacturers = data.manufacturers || [];
  const vendors = data.vendors || [];

  const save = () => {
    if(!form.name.trim()) return alert("Manufacturer name required");
    if(editId) {
      setData(d=>({...d, manufacturers: d.manufacturers.map(m=>m.id===editId?{...m,...form}:m)}));
    } else {
      const newM = {...form, id:"MFR"+Date.now()};
      setData(d=>({...d, manufacturers:[...( d.manufacturers||[]), newM]}));
    }
    setForm(blank); setShow(false); setEditId(null);
  };

  const edit = (m) => { setForm({...m}); setEditId(m.id); setShow(true); };

  const del = (id) => {
    // Check if used in purchases
    const usedInPurchase = (data.purchases||[]).some(p=>p.manufacturer===manufacturers.find(m=>m.id===id)?.name);
    if(usedInPurchase) return alert("Cannot delete — this manufacturer has purchase records.");
    if(requestDelete("manufacturers", id, "Manufacturer"))
      setData(d=>({...d, manufacturers:(d.manufacturers||[]).filter(m=>m.id!==id)}));
  };

  const filtered = manufacturers.filter(m=>
    !search || m.name.toLowerCase().includes(search.toLowerCase()) ||
    (m.city||"").toLowerCase().includes(search.toLowerCase())
  );

  // Count purchases per manufacturer
  const purchaseCount = {};
  (data.purchases||[]).forEach(p=>{ purchaseCount[p.manufacturer]=(purchaseCount[p.manufacturer]||0)+1; });
  const purchaseVal = {};
  (data.purchases||[]).forEach(p=>{ purchaseVal[p.manufacturer]=(purchaseVal[p.manufacturer]||0)+(p.total||0); });

  return (
    <div>
      <PH title="Manufacturers / Vendors" sub="Add manufacturers before creating Purchase Orders or Purchase Invoices"
        action={<Btn v="navy" onClick={()=>{setForm(blank);setEditId(null);setShow(true);}}>+ Add Manufacturer</Btn>}
      />

      {/* Workflow hint */}
      <div style={{background:"#E6FFFA",border:"1px solid #99F6E4",borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:12,color:"#0F766E",display:"flex",gap:8,alignItems:"center"}}>
        <span style={{fontSize:16}}>ℹ️</span>
        <span><strong>Workflow:</strong> Add Manufacturer → Create Purchase Order → Record Purchase Invoice → Batch/Stock auto-updates</span>
      </div>

      {/* Search */}
      <div style={{marginBottom:14}}>
        <Inp value={search} onChange={setSearch} placeholder="Search manufacturer by name or city..."/>
      </div>

      {/* Form */}
      {show&&(
        <Modal title={editId?"Edit Manufacturer":"Add New Manufacturer"} onClose={()=>{setShow(false);setForm(blank);setEditId(null);}} w={640}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,alignItems:"start"}}>
            <Field label="Manufacturer Name" req><Inp value={form.name} onChange={sf("name")} placeholder="e.g. Sun Pharma Ltd"/></Field>
            <Field label="Code"><Inp value={form.code} onChange={sf("code")} placeholder="e.g. SUN001"/></Field>
            <Field label="Contact Person"><Inp value={form.contactPerson} onChange={sf("contactPerson")} placeholder="Sales manager name"/></Field>
            <Field label="Phone"><Inp value={form.phone} onChange={sf("phone")} placeholder="9876543210"/></Field>
            <Field label="Email"><Inp value={form.email} onChange={sf("email")} placeholder="contact@manufacturer.com"/></Field>
            <Field label="Drug License No"><Inp value={form.drugLicense} onChange={sf("drugLicense")} placeholder="DL-XX-XXXXXX"/></Field>
            <Field label="GSTIN"><Inp value={form.gstin} onChange={sf("gstin")} placeholder="07AAAAA0000A1Z5"/></Field>
            <Field label="City"><Inp value={form.city} onChange={sf("city")} placeholder="Mumbai"/></Field>
            <Field label="State"><Sel value={form.state} onChange={sf("state")} options={INDIAN_STATES}/></Field>
            <Field label="Active"><Sel value={String(form.active)} onChange={v=>setForm(f=>({...f,active:v==="true"}))} options={["true","false"]}/></Field>
          </div>
          <Field label="Address"><Inp value={form.address} onChange={sf("address")} placeholder="Full address"/></Field>
          <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:14}}>
            <Btn v="outline" onClick={()=>{setShow(false);setForm(blank);setEditId(null);}}>Cancel</Btn>
            <Btn v="navy" onClick={save}>{editId?"Update":"Save"} Manufacturer</Btn>
          </div>
        </Modal>
      )}

      {/* Stats */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:16}}>
        {[
          {icon:"🏭",label:"Total Manufacturers",val:manufacturers.length,bg:"#E6FFFA",fg:"#0F766E",border:"#99F6E4"},
          {icon:"🛒",label:"With Purchases",val:Object.keys(purchaseCount).length,bg:"#EFF6FF",fg:"#1D4ED8",border:"#BFDBFE"},
          {icon:"💰",label:"Total Purchase Value",val:"₹"+fmt(Object.values(purchaseVal).reduce((s,v)=>s+v,0)),bg:"#F0FDF4",fg:"#166534",border:"#BBF7D0",noFmt:true},
        ].map((k,i)=>(
          <div key={i} style={{background:k.bg,border:`1px solid ${k.border}`,borderRadius:10,padding:"14px 18px"}}>
            <div style={{fontSize:20,marginBottom:4}}>{k.icon}</div>
            <div style={{fontSize:22,fontWeight:900,color:k.fg}}>{k.val}</div>
            <div style={{fontSize:11,color:k.fg,fontWeight:600,marginTop:2}}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      {filtered.length===0
        ? <div style={{textAlign:"center",padding:48,background:"#fff",borderRadius:10,border:`1px solid ${C.border}`,color:C.muted}}>
            <div style={{fontSize:32,marginBottom:8}}>🏭</div>
            <div style={{fontSize:14,fontWeight:700,marginBottom:4}}>No manufacturers yet</div>
            <div style={{fontSize:12,marginBottom:16}}>Add your first manufacturer to start the purchase workflow</div>
            <Btn v="navy" onClick={()=>{setForm(blank);setEditId(null);setShow(true);}}>+ Add Manufacturer</Btn>
          </div>
        : <Tbl
            headers={["Name","Code","Contact","Phone","GSTIN","City","Drug License","Purchases","Total Value","Active","Action"]}
            rows={filtered.map(m=>[
              <span style={{fontWeight:700,color:"#0F766E"}}>{m.name}</span>,
              <span className="mono" style={{fontSize:11}}>{m.code||"—"}</span>,
              m.contactPerson||"—",
              m.phone||"—",
              <span className="mono" style={{fontSize:10}}>{m.gstin||"—"}</span>,
              m.city||"—",
              <span className="mono" style={{fontSize:10}}>{m.drugLicense||"—"}</span>,
              <span style={{fontWeight:700,color:"#1D4ED8"}}>{purchaseCount[m.name]||0}</span>,
              <span className="mono" style={{fontWeight:700,color:"#166534"}}>₹{fmt(purchaseVal[m.name]||0)}</span>,
              <Badge s={m.active!==false?"Active":"Inactive"}/>,
              <div style={{display:"flex",gap:4}}>
                <Btn sz="sm" v="outline" onClick={()=>edit(m)}>✏️</Btn>
                <Btn sz="sm" v="danger" onClick={()=>del(m.id)}>Del</Btn>
              </div>
            ])}
          />
      }
    </div>
  );
};

// ── YAPIDERMA DASHBOARD ────────────────────────────────────────
const YapidermaDashboard = ({data, setActive}) => {
  const invoices  = data.invoices  || [];
  const purchases = data.purchases || [];
  const payments  = data.payments  || [];
  const vpay      = data.vendorPayments || [];
  const parties   = data.parties   || [];
  const products  = data.products  || [];
  const returns   = data.salesReturns || [];
  const batches   = data.batches   || [];
  const pos       = data.purchaseOrders || [];
  const vendors   = data.vendors   || [];
  const manufacturers = data.manufacturers || [];

  const go = page => setActive && setActive(page);

  // ── Date helpers
  const now = new Date();
  const cm = now.getMonth()+1, cy = now.getFullYear();
  const parseD = s => { if(!s||!s.includes("/")) return null; const[d,m,y]=s.split("/"); return {m:+m,y:+y}; };
  const isThisMonth = s => { const p=parseD(s); return p&&p.m===cm&&p.y===cy; };

  // ── ALL TIME KPIs
  const totalSales   = invoices.reduce((s,i)=>s+(i.total||0),0);
  const totalPurch   = purchases.reduce((s,p)=>s+(p.total||0),0);
  const totalRecv    = payments.reduce((s,p)=>s+(p.amount||0),0);
  const totalVPay    = vpay.reduce((s,p)=>s+(p.amount||0),0);
  const outstanding  = invoices.reduce((s,i)=>s+Math.max(0,(i.total||0)-(i.paid||0)),0);
  const pendingInv   = invoices.filter(i=>i.status!=="Paid"&&i.status!=="Cancelled").length;
  const returnVal    = returns.filter(r=>r.status==="Approved").reduce((s,r)=>s+(r.value||0),0);

  // ── THIS MONTH KPIs
  const mSales  = invoices.filter(i=>isThisMonth(i.date)).reduce((s,i)=>s+(i.total||0),0);
  const mPurch  = purchases.filter(p=>isThisMonth(p.date)).reduce((s,p)=>s+(p.total||0),0);
  const mRecv   = payments.filter(p=>isThisMonth(p.date)).reduce((s,p)=>s+(p.amount||0),0);
  const mVPay   = vpay.filter(p=>isThisMonth(p.date)).reduce((s,p)=>s+(p.amount||0),0);
  const mInvCnt = invoices.filter(i=>isThisMonth(i.date)).length;
  const mPurchCnt=purchases.filter(p=>isThisMonth(p.date)).length;

  // ── Alerts
  const overdueInv   = invoices.filter(i=>{
    if(i.status==="Paid"||i.status==="Cancelled") return false;
    if(!i.dueDate) return false;
    const p=parseD(i.dueDate); if(!p) return false;
    return p.y<cy||(p.y===cy&&p.m<cm);
  }).length;
  const lowStock  = batches.filter(b=>(b.qty||0)-(b.out||0)-(b.damaged||0)<10).length;
  const pendingPO = pos.filter(p=>p.status==="Pending"||p.status==="Draft"||p.status==="Ordered").length;

  // ── Workflow checklist
  const workflowSteps = [
    {id:"manufacturers",done:manufacturers.length>0, label:"1. Add Manufacturer",  icon:"🏭",goto:"manufacturers"},
    {id:"products",     done:products.length>0,      label:"2. Add Products",      icon:"💊",goto:"products"},
    {id:"parties",      done:parties.length>0,        label:"3. Add Parties",       icon:"🏢",goto:"parties"},
    {id:"po",           done:pos.length>0,            label:"4. Purchase Order",    icon:"📄",goto:"po"},
    {id:"purchase",     done:purchases.length>0,      label:"5. Purchase Invoice",  icon:"🛒",goto:"purchase"},
    {id:"batches",      done:batches.length>0,        label:"6. Update Stock",      icon:"📦",goto:"batches"},
    {id:"invoices",     done:invoices.length>0,       label:"7. Sales Invoice",     icon:"🧾",goto:"invoices"},
    {id:"payments",     done:payments.length>0,       label:"8. Record Payment",    icon:"💰",goto:"payments"},
  ];
  const workflowDone = workflowSteps.filter(s=>s.done).length;

  // ── Monthly trend (last 6)
  const monthlyData = Array.from({length:6},(_,i)=>{
    const d=new Date(cy, cm-1-5+i, 1);
    const m=d.getMonth()+1, y=d.getFullYear();
    const sales=invoices.filter(inv=>{const p=parseD(inv.date);return p&&p.m===m&&p.y===y;}).reduce((s,inv)=>s+(inv.total||0),0);
    const purch=purchases.filter(pu=>{const p=parseD(pu.date);return p&&p.m===m&&p.y===y;}).reduce((s,pu)=>s+(pu.total||0),0);
    return {label:["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][m-1]+"'"+String(y).slice(2), sales, purch};
  });
  const maxBar = Math.max(...monthlyData.map(m=>Math.max(m.sales,m.purch)),1);

  // ── Top parties
  const partyTotals={};
  invoices.forEach(inv=>{ partyTotals[inv.party]=(partyTotals[inv.party]||0)+(inv.total||0); });
  const topParties=Object.entries(partyTotals).sort((a,b)=>b[1]-a[1]).slice(0,5);

  // ── Top products
  const prodQty={};
  invoices.forEach(inv=>(inv.items||[]).forEach(it=>{ prodQty[it.product]=(prodQty[it.product]||0)+(it.qty||0); }));
  const topProducts=Object.entries(prodQty).sort((a,b)=>b[1]-a[1]).slice(0,5);

  // ── Recent invoices
  const recentInv=[...invoices].sort((a,b)=>(b.no||"").localeCompare(a.no||"")).slice(0,6);
  const STATUS_C={Paid:"#16a34a",Partial:"#ca8a04",Current:"#2563eb",Overdue:"#dc2626",Cancelled:"#6b7280"};

  const months=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const thisMonthLabel = months[cm-1]+" "+cy;

  return (
    <div style={{padding:24,maxWidth:1400}}>
      {/* Header */}
      <div style={{marginBottom:20,display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12}}>
        <div>
          <div style={{fontSize:24,fontWeight:900,color:"#0F766E",letterSpacing:"-0.5px"}}>
            YAPIDERMA <span style={{fontWeight:400,color:"#64748b",fontSize:18}}>Dashboard</span>
          </div>
          <div style={{fontSize:12,color:"#64748b",marginTop:2}}>
            {new Date().toLocaleDateString("en-IN",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}
          </div>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {[
            {label:"+ Sales Invoice",page:"invoices",bg:"#0F766E"},
            {label:"+ Purchase",page:"purchase",bg:"#1D4ED8"},
            {label:"+ Payment",page:"payments",bg:"#166534"},
            {label:"+ Batch",page:"batches",bg:"#92400E"},
          ].map(b=>(
            <button key={b.page} onClick={()=>go(b.page)}
              style={{background:b.bg,color:"#fff",border:"none",borderRadius:8,padding:"8px 14px",
                fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
              {b.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── WORKFLOW CHECKLIST ── */}
      {workflowDone<8&&(
        <div style={{background:"#fff",borderRadius:12,border:"1px solid #e2e8f0",padding:20,marginBottom:20}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <div style={{fontSize:13,fontWeight:700,color:"#1e293b"}}>🗺️ Setup Workflow</div>
            <div style={{fontSize:12,color:"#0F766E",fontWeight:700}}>{workflowDone}/8 done</div>
          </div>
          <div style={{height:5,background:"#f1f5f9",borderRadius:3,marginBottom:14}}>
            <div style={{height:"100%",width:(workflowDone/8*100)+"%",background:"#0F766E",borderRadius:3,transition:"width .5s"}}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,alignItems:"start"}}>
            {workflowSteps.map((step,idx)=>{
              const isNext=!step.done&&workflowSteps.slice(0,idx).every(s=>s.done);
              return (
                <div key={step.id} onClick={()=>go(step.goto)}
                  style={{padding:"10px 12px",borderRadius:8,cursor:"pointer",
                    border:`1.5px solid ${step.done?"#BBF7D0":isNext?"#0F766E":"#e2e8f0"}`,
                    background:step.done?"#F0FDF4":isNext?"#E6FFFA":"#fafafa"}}
                  onMouseOver={e=>{e.currentTarget.style.boxShadow="0 2px 8px rgba(0,0,0,.08)";}}
                  onMouseOut={e=>{e.currentTarget.style.boxShadow="none";}}>
                  <div style={{fontSize:15,marginBottom:3}}>{step.done?"✅":isNext?"⏭️":step.icon}</div>
                  <div style={{fontSize:10,fontWeight:600,color:step.done?"#166534":isNext?"#0F766E":"#94a3b8"}}>{step.label}</div>
                  {isNext&&<div style={{fontSize:9,color:"#0F766E",marginTop:2,fontWeight:700}}>← Next step</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── ALERTS ── */}
      {(overdueInv>0||lowStock>0||pendingPO>0)&&(
        <div style={{display:"flex",gap:10,marginBottom:20,flexWrap:"wrap"}}>
          {overdueInv>0&&<div onClick={()=>go("invoices")} style={{flex:1,minWidth:180,background:"#FEF2F2",border:"1.5px solid #FECACA",borderRadius:10,padding:"12px 16px",cursor:"pointer",display:"flex",gap:10,alignItems:"center"}}>
            <span style={{fontSize:20}}>🚨</span>
            <div><div style={{fontWeight:700,color:"#991B1B",fontSize:12}}>{overdueInv} Overdue Invoice{overdueInv>1?"s":""}</div><div style={{fontSize:10,color:"#991B1B",opacity:.8}}>Click to view</div></div>
          </div>}
          {lowStock>0&&<div onClick={()=>go("batches")} style={{flex:1,minWidth:180,background:"#FFFBEB",border:"1.5px solid #FDE68A",borderRadius:10,padding:"12px 16px",cursor:"pointer",display:"flex",gap:10,alignItems:"center"}}>
            <span style={{fontSize:20}}>⚠️</span>
            <div><div style={{fontWeight:700,color:"#92400E",fontSize:12}}>{lowStock} Low Stock Batch{lowStock>1?"es":""}</div><div style={{fontSize:10,color:"#92400E",opacity:.8}}>Click to update</div></div>
          </div>}
          {pendingPO>0&&<div onClick={()=>go("po")} style={{flex:1,minWidth:180,background:"#EFF6FF",border:"1.5px solid #BFDBFE",borderRadius:10,padding:"12px 16px",cursor:"pointer",display:"flex",gap:10,alignItems:"center"}}>
            <span style={{fontSize:20}}>📋</span>
            <div><div style={{fontWeight:700,color:"#1D4ED8",fontSize:12}}>{pendingPO} Pending PO{pendingPO>1?"s":""}</div><div style={{fontSize:10,color:"#1D4ED8",opacity:.8}}>Click to process</div></div>
          </div>}
        </div>
      )}

      {/* ── THIS MONTH KPIs ── */}
      <div style={{background:"linear-gradient(135deg,#0F766E,#0D9488)",borderRadius:12,padding:"16px 20px",marginBottom:14}}>
        <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,.7)",textTransform:"uppercase",letterSpacing:"1px",marginBottom:14}}>
          📅 This Month — {thisMonthLabel}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,alignItems:"start"}}>
          {[
            {icon:"📦",label:"Sales",val:mSales,sub:mInvCnt+" invoices",page:"invoices"},
            {icon:"🛒",label:"Purchases",val:mPurch,sub:mPurchCnt+" entries",page:"purchase"},
            {icon:"💰",label:"Received",val:mRecv,sub:"payments in",page:"payments"},
            {icon:"💸",label:"Paid Out",val:mVPay,sub:"vendor payments",page:"vpay"},
          ].map((k,i)=>(
            <div key={i} onClick={()=>go(k.page)}
              style={{background:"rgba(255,255,255,.15)",borderRadius:10,padding:"14px 16px",cursor:"pointer",transition:"background .15s"}}
              onMouseOver={e=>e.currentTarget.style.background="rgba(255,255,255,.25)"}
              onMouseOut={e=>e.currentTarget.style.background="rgba(255,255,255,.15)"}>
              <div style={{fontSize:18,marginBottom:4}}>{k.icon}</div>
              <div style={{fontSize:20,fontWeight:900,color:"#fff",fontFamily:"monospace"}}>₹{fmt(k.val)}</div>
              <div style={{fontSize:11,color:"rgba(255,255,255,.85)",fontWeight:600,marginTop:2}}>{k.label}</div>
              <div style={{fontSize:10,color:"rgba(255,255,255,.6)",marginTop:1}}>{k.sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── ALL TIME KPIs ROW 1 ── */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:14}}>
        {[
          {icon:"📦",label:"Total Sales",val:totalSales,sub:invoices.length+" invoices",bg:"#E6FFFA",fg:"#0F766E",border:"#99F6E4",page:"invoices"},
          {icon:"🛒",label:"Total Purchases",val:totalPurch,sub:purchases.length+" entries",bg:"#EFF6FF",fg:"#1D4ED8",border:"#BFDBFE",page:"purchase"},
          {icon:"💰",label:"Total Received",val:totalRecv,sub:"from parties",bg:"#F0FDF4",fg:"#166534",border:"#BBF7D0",page:"payments"},
          {icon:"⏳",label:"Outstanding",val:outstanding,sub:pendingInv+" unpaid",bg:"#FFFBEB",fg:"#92400E",border:"#FDE68A",page:"outstanding",alert:overdueInv>0?overdueInv+" overdue":null},
        ].map((k,i)=>(
          <div key={i} onClick={()=>go(k.page)}
            style={{background:k.bg,border:`1.5px solid ${k.border}`,borderRadius:12,padding:"16px 18px",cursor:"pointer",position:"relative",transition:"transform .15s,box-shadow .15s"}}
            onMouseOver={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 4px 16px rgba(0,0,0,.1)";}}
            onMouseOut={e=>{e.currentTarget.style.transform="none";e.currentTarget.style.boxShadow="none";}}>
            {k.alert&&<div style={{position:"absolute",top:10,right:10,background:"#dc2626",color:"#fff",borderRadius:20,fontSize:10,fontWeight:700,padding:"2px 7px"}}>{k.alert}</div>}
            <div style={{fontSize:22,marginBottom:6}}>{k.icon}</div>
            <div style={{fontSize:22,fontWeight:900,color:k.fg,fontFamily:"monospace"}}>₹{fmt(k.val)}</div>
            <div style={{fontSize:11,fontWeight:700,color:k.fg,marginTop:2}}>{k.label}</div>
            <div style={{fontSize:10,color:k.fg,opacity:.7,marginTop:2}}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* ── ALL TIME KPIs ROW 2 ── */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:20}}>
        {[
          {icon:"🏭",label:"Vendor Payments",val:totalVPay,sub:vpay.length+" payments",bg:"#F5F3FF",fg:"#6D28D9",border:"#DDD6FE",page:"vpay"},
          {icon:"↩️",label:"Sales Returns",val:returnVal,sub:returns.length+" returns",bg:"#FEF2F2",fg:"#991B1B",border:"#FECACA",page:"returns"},
          {icon:"🏢",label:"Total Parties",noFmt:true,val:parties.length,sub:"active parties",bg:"#F0FDF4",fg:"#166534",border:"#BBF7D0",page:"parties"},
          {icon:"💊",label:"Products",noFmt:true,val:products.length,sub:batches.length+" batches",bg:"#F0F9FF",fg:"#0369A1",border:"#BAE6FD",page:"products"},
        ].map((k,i)=>(
          <div key={i} onClick={()=>go(k.page)}
            style={{background:k.bg,border:`1.5px solid ${k.border}`,borderRadius:12,padding:"16px 18px",cursor:"pointer",transition:"transform .15s"}}
            onMouseOver={e=>{e.currentTarget.style.transform="translateY(-2px)";}}
            onMouseOut={e=>{e.currentTarget.style.transform="none";}}>
            <div style={{fontSize:22,marginBottom:6}}>{k.icon}</div>
            <div style={{fontSize:22,fontWeight:900,color:k.fg,fontFamily:"monospace"}}>{k.noFmt?k.val:"₹"+fmt(k.val)}</div>
            <div style={{fontSize:11,fontWeight:700,color:k.fg,marginTop:2}}>{k.label}</div>
            <div style={{fontSize:10,color:k.fg,opacity:.7,marginTop:2}}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* ── CHART + TOP PARTIES ── */}
      <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:16,marginBottom:16}}>
        <div style={{background:"#fff",borderRadius:12,padding:20,border:"1px solid #e2e8f0"}}>
          <div style={{fontSize:13,fontWeight:700,color:"#1e293b",marginBottom:16}}>📊 Sales vs Purchases (Last 6 Months)</div>
          <div style={{display:"flex",alignItems:"flex-end",gap:10,height:150}}>
            {monthlyData.map((m,i)=>(
              <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                <div style={{width:"100%",display:"flex",gap:2,alignItems:"flex-end",height:120}}>
                  <div title={"Sales: ₹"+fmt(m.sales)} onClick={()=>go("invoices")}
                    style={{flex:1,background:"#0F766E",borderRadius:"3px 3px 0 0",cursor:"pointer",
                      height:Math.max(3,Math.round((m.sales/maxBar)*110))+"px",transition:"height .4s"}}/>
                  <div title={"Purchase: ₹"+fmt(m.purch)} onClick={()=>go("purchase")}
                    style={{flex:1,background:"#93C5FD",borderRadius:"3px 3px 0 0",cursor:"pointer",
                      height:Math.max(3,Math.round((m.purch/maxBar)*110))+"px",transition:"height .4s"}}/>
                </div>
                <div style={{fontSize:9,color:"#94a3b8",whiteSpace:"nowrap"}}>{m.label}</div>
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:16,marginTop:10}}>
            {[["#0F766E","Sales"],["#93C5FD","Purchases"]].map(([c,l])=>(
              <div key={l} style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:"#64748b"}}>
                <div style={{width:10,height:10,borderRadius:2,background:c}}/>{l}
              </div>
            ))}
          </div>
        </div>
        <div style={{background:"#fff",borderRadius:12,padding:20,border:"1px solid #e2e8f0"}}>
          <div style={{fontSize:13,fontWeight:700,color:"#1e293b",marginBottom:14}}>🏆 Top Parties by Sales</div>
          {topParties.length===0
            ? <div style={{textAlign:"center",padding:24,color:"#94a3b8"}}>
                <div style={{fontSize:24,marginBottom:8}}>🏢</div>
                <div style={{fontSize:12}}>No invoices yet</div>
                <button onClick={()=>go("parties")} style={{marginTop:10,padding:"6px 14px",background:"#0F766E",color:"#fff",border:"none",borderRadius:6,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Add Party →</button>
              </div>
            : topParties.map(([name,amt],i)=>{
                const pct=Math.round((amt/topParties[0][1])*100);
                return (
                  <div key={name} style={{marginBottom:10,cursor:"pointer"}} onClick={()=>go("ledger")}>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:3}}>
                      <span style={{fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:130}}>{i+1}. {name}</span>
                      <span style={{color:"#0F766E",fontWeight:700,fontFamily:"monospace"}}>₹{fmt(amt)}</span>
                    </div>
                    <div style={{height:5,background:"#f1f5f9",borderRadius:3}}>
                      <div style={{height:"100%",width:pct+"%",background:"#0F766E",borderRadius:3}}/>
                    </div>
                  </div>
                );
              })
          }
        </div>
      </div>

      {/* ── RECENT INVOICES + QUICK NAV ── */}
      <div style={{display:"grid",gridTemplateColumns:"3fr 2fr",gap:16,alignItems:"start"}}>
        <div style={{background:"#fff",borderRadius:12,padding:20,border:"1px solid #e2e8f0"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <div style={{fontSize:13,fontWeight:700,color:"#1e293b"}}>🧾 Recent Invoices</div>
            <button onClick={()=>go("invoices")} style={{fontSize:11,color:"#0F766E",background:"none",border:"none",cursor:"pointer",fontWeight:600,fontFamily:"inherit"}}>View all →</button>
          </div>
          {recentInv.length===0
            ? <div style={{textAlign:"center",padding:30,color:"#94a3b8"}}>
                <div style={{fontSize:24,marginBottom:8}}>🧾</div>
                <div style={{fontSize:12,marginBottom:10}}>No invoices yet</div>
                <button onClick={()=>go("invoices")} style={{padding:"6px 14px",background:"#0F766E",color:"#fff",border:"none",borderRadius:6,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Create Invoice →</button>
              </div>
            : <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead><tr style={{borderBottom:"2px solid #f1f5f9"}}>
                  {["Invoice","Party","Date","Amount","Status"].map(h=>(
                    <th key={h} style={{textAlign:"left",padding:"4px 8px",color:"#94a3b8",fontWeight:600,fontSize:10}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {recentInv.map(inv=>(
                    <tr key={inv.id} style={{borderBottom:"1px solid #f8fafc",cursor:"pointer"}}
                      onClick={()=>go("invoices")}
                      onMouseOver={e=>e.currentTarget.style.background="#f8fafc"}
                      onMouseOut={e=>e.currentTarget.style.background="transparent"}>
                      <td style={{padding:"7px 8px",fontFamily:"monospace",fontSize:11,color:"#0F766E",fontWeight:700}}>{inv.no}</td>
                      <td style={{padding:"7px 8px",maxWidth:110,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{inv.party}</td>
                      <td style={{padding:"7px 8px",color:"#64748b",fontSize:11}}>{inv.date}</td>
                      <td style={{padding:"7px 8px",fontFamily:"monospace",fontWeight:700}}>₹{fmt(inv.total||0)}</td>
                      <td style={{padding:"7px 8px"}}>
                        <span style={{background:(STATUS_C[inv.status]||"#6b7280")+"22",color:STATUS_C[inv.status]||"#6b7280",padding:"2px 8px",borderRadius:10,fontSize:10,fontWeight:700}}>
                          {inv.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
          }
        </div>

        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {/* Top products */}
          <div style={{background:"#fff",borderRadius:12,padding:18,border:"1px solid #e2e8f0",flex:1}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div style={{fontSize:13,fontWeight:700,color:"#1e293b"}}>💊 Top Products</div>
              <button onClick={()=>go("products")} style={{fontSize:11,color:"#0F766E",background:"none",border:"none",cursor:"pointer",fontWeight:600,fontFamily:"inherit"}}>View →</button>
            </div>
            {topProducts.length===0
              ? <div style={{textAlign:"center",padding:16,color:"#94a3b8",fontSize:12}}>No sales data yet</div>
              : topProducts.map(([name,qty],i)=>{
                  const pct=Math.round((qty/topProducts[0][1])*100);
                  return (
                    <div key={name} style={{marginBottom:9}}>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:2}}>
                        <span style={{fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:140}}>{i+1}. {name}</span>
                        <span style={{color:"#0F766E",fontWeight:700}}>{qty} units</span>
                      </div>
                      <div style={{height:4,background:"#f1f5f9",borderRadius:2}}>
                        <div style={{height:"100%",width:pct+"%",background:"#0D9488",borderRadius:2}}/>
                      </div>
                    </div>
                  );
                })
            }
          </div>

          {/* Quick nav */}
          <div style={{background:"#fff",borderRadius:12,padding:16,border:"1px solid #e2e8f0"}}>
            <div style={{fontSize:12,fontWeight:700,color:"#1e293b",marginBottom:10}}>⚡ Quick Navigation</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:5,alignItems:"start"}}>
              {[
                {icon:"📒",label:"Ledger",page:"ledger"},
                {icon:"🏭",label:"Manufacturers",page:"manufacturers"},
                {icon:"🎁",label:"Schemes",page:"schemes"},
                {icon:"🩺",label:"Doctors",page:"doctors"},
                {icon:"💲",label:"Party Rates",page:"partyrates"},
                {icon:"↩️",label:"Returns",page:"returns"},
                {icon:"📋",label:"Credit Notes",page:"cdn"},
                {icon:"⚙️",label:"Settings",page:"settings"},
              ].map(n=>(
                <div key={n.page} onClick={()=>go(n.page)}
                  style={{padding:"7px 9px",background:"#f8fafc",borderRadius:7,cursor:"pointer",
                    fontSize:11,fontWeight:600,color:"#374151",display:"flex",gap:5,alignItems:"center",
                    border:"1px solid #f1f5f9",transition:"all .15s"}}
                  onMouseOver={e=>{e.currentTarget.style.background="#E6FFFA";e.currentTarget.style.color="#0F766E";e.currentTarget.style.borderColor="#99F6E4";}}
                  onMouseOut={e=>{e.currentTarget.style.background="#f8fafc";e.currentTarget.style.color="#374151";e.currentTarget.style.borderColor="#f1f5f9";}}>
                  {n.icon} {n.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};;


// ── YAPIDERMA NAV ──────────────────────────────────────────────
const YAP_NAV = [
  // Sales
  {id:"dashboard",    label:"🏠 Dashboard",   group:"Overview"},
  {id:"invoices",     label:"🧾 Sales Invoice",group:"Sales"},
  {id:"payments",     label:"💰 Payments",     group:"Sales"},
  {id:"returns",      label:"↩️ Sales Returns", group:"Sales"},
  {id:"cdn",          label:"📋 Credit Notes",  group:"Sales"},
  {id:"outstanding",  label:"⏳ Outstanding",   group:"Sales"},
  {id:"ledger",       label:"📒 Party Ledger",  group:"Sales"},
  // Purchase
  {id:"manufacturers",label:"🏭 Manufacturers",  group:"Purchase"},
  {id:"purchase",     label:"🛒 Purchases",       group:"Purchase"},
  {id:"po",           label:"📄 Purchase Orders", group:"Purchase"},
  {id:"vpay",         label:"💸 Vendor Payments", group:"Purchase"},
  // Masters
  {id:"parties",      label:"🏢 Party Master",  group:"Masters"},
  {id:"products",     label:"💊 Product Master", group:"Masters"},
  {id:"doctors",      label:"🩺 Doctor Master",  group:"Masters"},
  {id:"partyrates",   label:"💲 Party Rates",    group:"Masters"},
  {id:"doctorrates",  label:"💲 Doctor Rates",   group:"Masters"},
  {id:"schemes",      label:"🎁 Scheme Master",  group:"Masters"},
  // Stock
  {id:"batches",      label:"📦 Batch / Stock",  group:"Stock"},
  // Reports & Settings
  {id:"settings",     label:"⚙️ Settings",       group:"System"},
];

const YapidermaNavBar = ({active, setActive, onLogout, user}) => {
  const groups = [...new Set(YAP_NAV.map(n=>n.group))];
  return (
    <div style={{width:210,minHeight:"100vh",background:"#0F766E",display:"flex",flexDirection:"column",flexShrink:0}}>
      {/* Logo */}
      <div style={{padding:"20px 16px 16px",borderBottom:"1px solid rgba(255,255,255,.15)"}}>
        <div style={{fontSize:18,fontWeight:900,color:"#fff",letterSpacing:"-0.5px"}}>YAPIDERMA</div>
        <div style={{fontSize:10,color:"rgba(255,255,255,.6)",marginTop:2}}>CRM & Business Suite</div>
      </div>

      {/* User pill */}
      <div style={{padding:"10px 14px",borderBottom:"1px solid rgba(255,255,255,.1)"}}>
        <div style={{background:"rgba(255,255,255,.12)",borderRadius:8,padding:"8px 10px"}}>
          <div style={{fontSize:11,fontWeight:700,color:"#fff"}}>{user?.name||"Admin"}</div>
          <div style={{fontSize:10,color:"rgba(255,255,255,.6)",textTransform:"capitalize"}}>{user?.role||"owner"}</div>
        </div>
      </div>

      {/* Nav items */}
      <div style={{flex:1,overflowY:"auto",padding:"8px 0"}}>
        {groups.map(grp=>(
          <div key={grp}>
            <div style={{fontSize:9,fontWeight:700,color:"rgba(255,255,255,.45)",textTransform:"uppercase",letterSpacing:"1px",padding:"12px 16px 4px"}}>{grp}</div>
            {YAP_NAV.filter(n=>n.group===grp).map(n=>{
              const isActive = active===n.id;
              return (
                <div key={n.id} onClick={()=>setActive(n.id)}
                  style={{padding:"8px 14px",cursor:"pointer",fontSize:12,fontWeight:isActive?700:400,
                    color:isActive?"#fff":"rgba(255,255,255,.7)",
                    background:isActive?"rgba(255,255,255,.18)":"transparent",
                    borderLeft:`3px solid ${isActive?"#fff":"transparent"}`,
                    borderRadius:"0 6px 6px 0",margin:"1px 6px 1px 0",
                    transition:"all .15s"}}>
                  {n.label}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Logout */}
      <div style={{padding:12,borderTop:"1px solid rgba(255,255,255,.15)"}}>
        <div onClick={onLogout}
          style={{padding:"8px 12px",cursor:"pointer",color:"rgba(255,255,255,.7)",fontSize:12,
            borderRadius:6,background:"rgba(255,255,255,.08)",textAlign:"center",
            transition:"background .15s"}}
          onMouseOver={e=>e.currentTarget.style.background="rgba(255,255,255,.18)"}
          onMouseOut={e=>e.currentTarget.style.background="rgba(255,255,255,.08)"}>
          🚪 Logout
        </div>
      </div>
    </div>
  );
};

// ── MAIN APP ────────────────────────────────────────────────────
export default function App() {
  const [user, setUser]     = useState(null);
  const [data, setData]     = useState(DEFAULT);
  const [active, setActive] = useState("dashboard");
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [printInvData, setPrintInvData] = useState(null);

  // ── Load data from Supabase on mount ──────────────────────
  useEffect(()=>{
    document.title = "YAPIDERMA";
    const stored = localStorage.getItem("yap_user");
    if(stored){ try{ setUser(JSON.parse(stored)); }catch(e){} }
    loadAllCRMData().then(remote=>{
      if(remote && Object.keys(remote).length>0){
        setData(d=>({...DEFAULT,...d,...remote}));
      }
    }).finally(()=>setLoading(false));
  },[]);

  // ── Auto-save on every data change ────────────────────────
  const saveTimer = useRef(null);
  useEffect(()=>{
    if(loading) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(()=>{
      setSaving(true);
      saveBulkCRMData(data).finally(()=>setSaving(false));
    }, 1500);
    return ()=>clearTimeout(saveTimer.current);
  },[data]);

  const setD = (updater) => setData(d => typeof updater==="function" ? updater(d) : {...d,...updater});

  const logout = ()=>{ setUser(null); localStorage.removeItem("yap_user"); setActive("dashboard"); };

  // ── Print invoice overlay ──────────────────────────────────
  if(printInvData){
    return <PrintInvoice inv={printInvData} data={data}
      onClose={()=>setPrintInvData(null)}
      settings={data.settings||{}}
    />;
  }

  // ── Login ──────────────────────────────────────────────────
  if(!user){
    return <Login
      users={data.users||[]}
      onLogin={u=>{ setUser(u); localStorage.setItem("yap_user",JSON.stringify(u)); }}
    />;
  }

  if(loading){
    return (
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",flexDirection:"column",gap:12,background:"#f0fdf9"}}>
        <div style={{fontSize:28,fontWeight:900,color:"#0F766E"}}>YAPIDERMA</div>
        <div style={{fontSize:13,color:"#64748b"}}>Loading your data...</div>
        <div style={{width:200,height:4,background:"#e2e8f0",borderRadius:2,overflow:"hidden"}}>
          <div style={{height:"100%",width:"60%",background:"#0F766E",borderRadius:2,animation:"pulse 1.5s ease-in-out infinite"}}/>
        </div>
      </div>
    );
  }

  // ── Shared props ───────────────────────────────────────────
  const requestDelete = (module, id, label) => {
    return window.confirm("Delete this " + (label||"record") + "? This cannot be undone.");
  };
  const props = {
    data, setData:setD,
    requestDelete,
    currentUser: user,
    setPrintInv: setPrintInvData,
    parties: data.parties||[],
    products: data.products||[],
    users: data.users||[],
    addAudit: ()=>{},
    drafts: {},
    setDrafts: ()=>{},
    setParties: (ps)=>setD(d=>({...d,parties:ps})),
    setProducts: (ps)=>setD(d=>({...d,products:ps})),
  };

  // ── Render active module ───────────────────────────────────
  const renderModule = () => {
    switch(active){
      case "dashboard":   return <YapidermaDashboard data={data} setActive={setActive}/>;
      case "invoices":    return <InvoiceModule {...props}/>;
      case "payments":    return <PaymentsModule {...props}/>;
      case "returns":     return <ReturnModule   {...props}/>;
      case "cdn":         return <CDNModule      {...props}/>;
      case "outstanding": return <OutstandingModule {...props}/>;
      case "ledger":      return <PartyLedgerModule {...props}/>;
      case "manufacturers": return <ManufacturersModule {...props}/>;
      case "purchase":    return <PurchaseModule {...props}/>;
      case "po":          return <POModule       {...props}/>;
      case "vpay":        return <VendorPaymentsModule {...props}/>;
      case "parties":     return <PartyMaster    {...props}/>;
      case "products":    return <ProductMaster  {...props}/>;
      case "doctors":     return <DoctorCRM      {...props}/>;
      case "partyrates":  return <PartyRatesModule {...props}/>;
      case "doctorrates": return <DoctorRatesModule {...props}/>;
      case "schemes":     return <SchemeMasterModule {...props}/>;
      case "batches":     return <BatchModule    {...props}/>;
      case "settings":    return <SettingsModule {...props}/>;
      default:            return <YapidermaDashboard data={data}/>;
    }
  };

  return (
    <AmountCtx.Provider value={{show:true}}>
      <div style={{display:"flex",minHeight:"100vh",background:"#f8fafc",fontFamily:"'Segoe UI',system-ui,sans-serif"}}>
        <YapidermaNavBar active={active} setActive={setActive} onLogout={logout} user={user}/>
        <div style={{flex:1,overflow:"auto"}}>
          {/* Top bar */}
          <div style={{background:"#fff",borderBottom:"1px solid #e2e8f0",padding:"10px 24px",
            display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,zIndex:10}}>
            <div style={{fontSize:14,fontWeight:700,color:"#0F766E"}}>
              {YAP_NAV.find(n=>n.id===active)?.label||"Dashboard"}
            </div>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              {saving&&<span style={{fontSize:11,color:"#64748b"}}>💾 Saving...</span>}
              <span style={{fontSize:11,background:"#E6FFFA",color:"#0F766E",padding:"3px 10px",borderRadius:20,fontWeight:600}}>
                {user.name}
              </span>
            </div>
          </div>
          {/* Module content */}
          <div style={{padding:0}}>
            {renderModule()}
          </div>
        </div>
      </div>
    </AmountCtx.Provider>
  );
}

